// src/App.js
import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
// Compiled by the build:css script from tailwind.css + tailwind.config.js.
// Bundled, so the styles are there on first paint - the CDN build used to
// arrive after the page had already rendered.
import './tailwind.generated.css';
import './App.css';

// ===== INJECT EXTERNAL RESOURCES =====
const injectResources = () => {
  const head = document.head;
  // The design's icons are Font Awesome; the app previously loaded only
  // Material Symbols.
  if (!document.querySelector('link[href*="font-awesome"]')) {
    const fa = document.createElement('link');
    fa.rel = 'stylesheet';
    fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
    head.appendChild(fa);
  }

  // Tailwind is compiled at build time (see tailwind.config.js and the
  // build:css script) and imported above. It used to be loaded from
  // cdn.tailwindcss.com with async=true, so the first render of every screen
  // happened before any utility class had a style - which is why a page looked
  // broken until it was refreshed and the script came from cache. The CDN build
  // is also explicitly not intended for production use.

  if (!document.querySelector('link[href*="Hanken+Grotesk"]')) {
    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&display=swap';
    fontLink.rel = 'stylesheet';
    head.appendChild(fontLink);
  }

  if (!document.querySelector('link[href*="Material+Symbols"]')) {
    const iconLink = document.createElement('link');
    iconLink.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap';
    iconLink.rel = 'stylesheet';
    head.appendChild(iconLink);
  }

  // Solution document view (SolutionDetail) type pairing: a geometric
  // display face for headings, an italic serif for the pull-quote, and a
  // technical monospace for IDs/eyebrows/numbering - so the document reads
  // like a designed spec sheet rather than plain body copy throughout.
  if (!document.querySelector('link[href*="Sora"]')) {
    const soraLink = document.createElement('link');
    soraLink.href = 'https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700;800&display=swap';
    soraLink.rel = 'stylesheet';
    head.appendChild(soraLink);
  }
  if (!document.querySelector('link[href*="Fraunces"]')) {
    const frauncesLink = document.createElement('link');
    frauncesLink.href = 'https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,600;1,500;1,600&display=swap';
    frauncesLink.rel = 'stylesheet';
    head.appendChild(frauncesLink);
  }
  if (!document.querySelector('link[href*="JetBrains+Mono"]')) {
    const monoLink = document.createElement('link');
    monoLink.href = 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@500;600;700&display=swap';
    monoLink.rel = 'stylesheet';
    head.appendChild(monoLink);
  }

  if (!document.querySelector('style[id="custom-styles"]')) {
    const styleTag = document.createElement('style');
    styleTag.id = 'custom-styles';
    styleTag.textContent = `
      body {
        font-family: 'Hanken Grotesk', sans-serif;
        background-color: #f4f5f8;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      .logo-size { max-width: 150px; }
      .bg-radial-gradient {
        background: radial-gradient(circle at top left, #003366 0%, #002147 100%);
      }
      .glass-panel {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border: 1px solid rgba(255, 255, 255, 0.2);
      }

      .material-symbols-outlined {
  font-family: 'Material Symbols Outlined'!important;
  font-weight: normal;
  font-style: normal;
  font-size: 24px;  /* or inherit from parent */
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

      .page { display: none; animation: fadeIn 0.35s ease forwards; }
      .page.active { display: block; }
      @keyframes fadeIn {
        0% { opacity: 0; transform: translateY(12px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      .solution-card .hover-line {
        transform-origin: left;
        transition: transform 0.3s ease;
      }
      .solution-card:hover .hover-line { transform: scaleX(1); }
      .solution-card .arrow-icon {
        transition: opacity 0.3s ease, transform 0.3s ease;
      }
      .solution-card:hover .arrow-icon {
        opacity: 1;
        transform: translateX(4px);
      }
      .capability-card {
        transition: box-shadow 0.25s ease, transform 0.2s ease;
      }
      .capability-card:hover {
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
        transform: translateY(-2px);
      }
      .case-card {
        transition: box-shadow 0.25s ease, transform 0.2s ease;
      }
      .case-card:hover {
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
        transform: translateY(-2px);
      }
      .custom-gradient-bg {
        background: radial-gradient(circle at top right, #003366 0%, #f9f9ff 50%);
      }
      .detail-row {
        border-bottom: 1px solid #e5e7eb;
        padding-top: 1.5rem;
        padding-bottom: 1.5rem;
      }
      .detail-row:last-child { border-bottom: none; }
      .detail-label {
        text-transform: uppercase;
        font-size: 1rem;
        letter-spacing: 0.05em;
        font-weight: 900;
        color: #000;
        width: 100%;
        margin-bottom: 0.5rem;
      }
      @media (min-width: 768px) {
        .detail-label {
          width: 25%;
          margin-bottom: 0;
        }
        .detail-content {
          width: 100%;
          padding-left: 0rem; padding-top:1rem;
        }
      }
      .summary-box {
        border-left: 4px solid #f83a4c;
        background-color: #fafafa;
      }
      ul.bullet-list {
        list-style-type: disc;
        padding-left: 1.25rem;
      }
      ul.bullet-list li { margin-bottom: 0.25rem; }
      ::-webkit-scrollbar { width: 6px; height: 6px; }
      ::-webkit-scrollbar-track { background: #f0f3ff; }
      ::-webkit-scrollbar-thumb { background: #c4c6cf; border-radius: 8px; }
      ::-webkit-scrollbar-thumb:hover { background: #74777f; }
      .get { padding: 12px; border-left: 3px solid #f83a4c; background: #f8fafc; border-radius: 8px; margin-bottom: 8px; }
      .get b { display: block; font-weight: 700; }
      .get span { font-size: 14px; color: #4b5563; }
      .diff { background: #f6f9fb; border-left: 4px solid #002147; padding: 16px; border-radius: 8px; }
      .journey { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-top: 12px; }
      .step { font-size: 12px; padding: 6px 10px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; color: #002147; font-weight: 700; }
      .arr { color: #f83a4c; }
      .tags { display: flex; flex-wrap: wrap; gap: 6px; }
      .tag { font-size: 12px; padding: 6px 10px; border-radius: 8px; background: #f1f6fa; border: 1px solid #e5e7eb; color: #002147; font-weight: 700; }
      .tag.std { background: #eef7f2; color: #187159; }
      .mini { font-size: 12px; padding: 4px 8px; border-radius: 999px; background: #f1f5f8; color: #002147; border: 1px solid #e0e8ee; font-weight: 700; }
      .heroq-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
      .heroq { background: #0B2E59; border-radius: 12px; padding: 14px; }
      .heroq b { display: block; font-size: 13px; color: #fff; }
      .heroq span { display: block; color: #bfd0df; font-size: 11px; margin-top: 4px; }
      .usecase-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 10px; }
      .usecase { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px; }
      .usecase .icon { font-size: 19px; margin-bottom: 6px; }
      .usecase b { display: block; font-size: 13px; color: #002147; }
      .usecase span { display: block; color: #6b7280; font-size: 11px; line-height: 1.45; margin-top: 4px; }
      .approach-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; margin-top: 10px; }
      .approach-step { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; }
      .approach-step .n { color: #f83a4c; font-size: 10px; font-weight: 900; }
      .approach-step b { display: block; font-size: 13px; margin: 4px 0; color: #002147; }
      .approach-step span { display: block; font-size: 11px; color: #6b7280; line-height: 1.4; }
      .bizvalue { background: linear-gradient(135deg,#FFF4F4,#fff); border: 1px solid #F2C7CB; border-radius: 12px; padding: 16px; }
      .bizvalue h4 { font-size: 16px; color: #002147; margin: 0 0 8px; }
      .bizvalue p { margin: 0 0 12px; color: #6b7280; font-size: 13px; line-height: 1.6; }
      .risk-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 8px; }
      .risk-pill { background: #fff; border: 1px solid #F0D8DA; border-radius: 8px; padding: 9px 11px; font-size: 12px; font-weight: 700; color: #002147; }
      .qa-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 8px; }
      .qa-item { background: #f1f6fa; border-radius: 8px; padding: 10px; }
      .qa-item b { display: block; font-size: 12px; color: #002147; }
      .qa-item span { display: block; font-size: 11px; color: #6b7280; margin-top: 3px; }
      .quote-line { margin-top: 10px; font-size: 14px; font-weight: 800; color: #002147; }
      .internal-guidance { background: #FFF9EB; border: 1px solid #F1D89C; border-radius: 12px; padding: 14px 16px; }
      .internal-guidance > summary { cursor: pointer; font-weight: 900; color: #7A4D00; font-size: 13px; }
      .ig-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; margin-top: 12px; }
      .ig-item { background: #fff; border: 1px solid #F1E2B8; border-radius: 9px; padding: 10px; }
      .ig-item b { font-size: 11px; display: block; color: #6C4700; }
      .ig-item span { font-size: 11px; color: #745F3B; display: block; margin-top: 4px; line-height: 1.45; }
      @media print { .internal-guidance { display: none !important; } }

      /* ===== Solution document view (SolutionDetail) - matches the
         "Solutions Refined" reference HTML look & feel, with an extra pass
         of modern polish (motion, depth, gradients). Prefixed sd- so it
         never collides with the shared .tag/.tags/.diff/.journey/.step
         classes reused by CaseDetail and the card grids above. ===== */
      .sd-doc { --sd-navy:#0B2E59; --sd-navy2:#123F72; --sd-blue:#2F6FA5; --sd-red:#E32636; --sd-red2:#FF4D5E; --sd-ink:#15283B; --sd-muted:#667A8E; --sd-line:#DDE6EE; --sd-soft:#EEF5FB; --sd-shadow:0 10px 26px rgba(11,46,89,.08); --sd-shadow-lg:0 20px 44px rgba(11,46,89,.16); --sd-display:'Sora','Hanken Grotesk',sans-serif; --sd-serif:'Fraunces',Georgia,serif; --sd-mono:'JetBrains Mono',ui-monospace,monospace; font-family:'Hanken Grotesk',sans-serif; }
      .sd-doc h1, .sd-doc h2, .sd-doc h3, .sd-qbox b, .sd-use b, .sd-step b, .sd-qa b, .sd-case-main .sd-profile { font-family: var(--sd-display); }
      .sd-doc .sd-eyebrow, .sd-doc .sd-num, .sd-doc .sd-n, .sd-doc .sd-caseid { font-family: var(--sd-mono); }
      .sd-doc .sd-quote { font-family: var(--sd-serif); }
      .sd-doc p, .sd-doc span, .sd-doc li { font-family: 'Hanken Grotesk', sans-serif; }
      .sd-hero .sd-lead { font-weight: 500; }
      @keyframes sdFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes sdFloat { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-6px,10px); } }
      @media (prefers-reduced-motion: no-preference) {
        .sd-doc main > * { opacity: 0; animation: sdFadeUp .6s cubic-bezier(.16,.8,.24,1) forwards; }
        .sd-doc main > *:nth-child(1) { animation-delay: .02s; }
        .sd-doc main > *:nth-child(2) { animation-delay: .06s; }
        .sd-doc main > *:nth-child(3) { animation-delay: .1s; }
        .sd-doc main > *:nth-child(4) { animation-delay: .14s; }
        .sd-doc main > *:nth-child(5) { animation-delay: .18s; }
        .sd-doc main > *:nth-child(n+6) { animation-delay: .22s; }
      }
      .sd-progress { position: fixed; top: 0; left: 0; right: 0; height: 3px; background: transparent; z-index: 60; pointer-events: none; }
      .sd-progress-bar { height: 100%; width: 0%; background: linear-gradient(90deg,var(--sd-red),var(--sd-blue)); transition: width .1s linear; box-shadow: 0 0 10px rgba(227,38,54,.5); }
      .sd-backtop { position: fixed; right: 22px; bottom: 22px; width: 44px; height: 44px; border-radius: 50%; background: var(--sd-navy); color: #fff; border: 0; display: grid; place-items: center; cursor: pointer; box-shadow: var(--sd-shadow-lg); z-index: 55; opacity: 0; transform: translateY(10px) scale(.9); pointer-events: none; transition: opacity .25s ease, transform .25s ease, background .2s ease; }
      .sd-backtop.on { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
      .sd-backtop:hover { background: var(--sd-red); }
      .sd-actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; margin-bottom: 16px; }
      .sd-btn { border: 0; border-radius: 10px; padding: 10px 14px; font-weight: 800; font-size: 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease, border-color .18s ease; }
      .sd-btn.primary { background: linear-gradient(135deg,var(--sd-navy),var(--sd-navy2)); color: #fff; box-shadow: 0 6px 16px rgba(11,46,89,.25); }
      .sd-btn.line { background: #fff; border: 1px solid var(--sd-line); color: var(--sd-navy); }
      .sd-btn.line:hover { border-color: var(--sd-navy); background: var(--sd-soft); transform: translateY(-2px); }
      .sd-btn.red { background: linear-gradient(135deg,var(--sd-red),var(--sd-red2)); color: #fff; box-shadow: 0 6px 16px rgba(227,38,54,.28); }
      .sd-btn.primary:hover, .sd-btn.red:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(11,46,89,.22); }
      .sd-btn:disabled { opacity: .6; cursor: default; transform: none !important; }
      .sd-hero { margin-top: 6px; background: linear-gradient(135deg,#082A52 0%,#0D3A6D 68%,#164B7D 100%); color: #fff; border-radius: 24px; padding: 38px; position: relative; overflow: hidden; box-shadow: var(--sd-shadow-lg); }
      .sd-hero:before { content: ""; position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,.09) 1px, transparent 1px); background-size: 22px 22px; opacity: .5; }
      .sd-hero:after { content: ""; position: absolute; right: -90px; top: -120px; width: 380px; height: 380px; border: 1px solid rgba(255,255,255,.08); border-radius: 50%; box-shadow: 0 0 0 70px rgba(255,255,255,.025), 0 0 0 140px rgba(255,255,255,.018); animation: sdFloat 9s ease-in-out infinite; }
      .sd-hero > * { position: relative; z-index: 1; }
      /* align-items: flex-start, not center - this label wraps to two or three
         lines on a narrow card, and centering the dot against that whole
         block drops it into the middle of the wrapped text instead of
         beside the first line. */
      .sd-eyebrow { font-size: 11px; letter-spacing: .16em; text-transform: uppercase; font-weight: 700; color: #F6A4AA; display: inline-flex; align-items: flex-start; gap: 8px; }
      .sd-eyebrow:before { content: ""; width: 6px; height: 6px; margin-top: 4px; border-radius: 50%; background: currentColor; box-shadow: 0 0 0 4px rgba(246,164,170,.25); flex-shrink: 0; }
      .sd-hero h1 { font-size: 42px; font-weight: 800; line-height: 1.05; letter-spacing: -.03em; margin: 12px 0 10px; max-width: 1000px; color: #fff; text-shadow: 0 2px 18px rgba(0,0,0,.15); }
      .sd-hero .sd-lead { font-size: 16.5px; line-height: 1.6; color: #D9E7F2; max-width: 900px; margin: 0; }
      .sd-heroq { margin-top: 22px; display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; }
      .sd-qbox { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.13); border-radius: 13px; padding: 14px; backdrop-filter: blur(6px); transition: background .2s ease, transform .2s ease; }
      .sd-qbox:hover { background: rgba(255,255,255,.14); transform: translateY(-2px); }
      .sd-qbox b { font-size: 13px; display: block; color: #fff; }
      .sd-qbox span { display: block; color: #BFD0DF; font-size: 11px; margin-top: 4px; }
      .sd-section { margin-top: 26px; }
      .sd-sechead { display: flex; align-items: flex-end; justify-content: space-between; gap: 14px; margin-bottom: 14px; }
      .sd-sechead h2 { font-size: 25px; font-weight: 700; margin: 0 0 7px; letter-spacing: -.02em; color: var(--sd-navy); position: relative; padding-bottom: 8px; }
      .sd-sechead h2:after { content: ""; position: absolute; left: 0; bottom: 0; width: 36px; height: 3px; border-radius: 3px; background: linear-gradient(90deg,var(--sd-red),var(--sd-blue)); }
      .sd-sechead p { margin: 0; color: var(--sd-muted); font-size: 12px; }
      .sd-usegrid { display: grid; grid-template-columns: repeat(auto-fill,minmax(190px,1fr)); gap: 12px; }
      .sd-use { background: #fff; border: 1px solid var(--sd-line); border-radius: 15px; padding: 16px; box-shadow: var(--sd-shadow); transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease; }
      .sd-use:hover { transform: translateY(-5px); box-shadow: var(--sd-shadow-lg); border-color: var(--sd-blue); }
      .sd-use .sd-icon { width: 40px; height: 40px; border-radius: 12px; background: linear-gradient(135deg,var(--sd-soft),#fff); box-shadow: inset 0 0 0 1px rgba(11,46,89,.06); display: grid; place-items: center; font-size: 20px; margin-bottom: 11px; transition: transform .22s ease; }
      .sd-use:hover .sd-icon { transform: scale(1.08) rotate(-2deg); }
      .sd-use b { font-size: 13px; display: block; color: var(--sd-navy); }
      .sd-use span { font-size: 11px; color: var(--sd-muted); line-height: 1.45; display: block; margin-top: 5px; }
      .sd-journey { display: grid; grid-template-columns: repeat(auto-fit,minmax(150px,1fr)); gap: 10px; }
      .sd-step { background: #fff; border: 1px solid var(--sd-line); border-radius: 14px; padding: 16px; position: relative; transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease; }
      .sd-step:hover { transform: translateY(-4px); box-shadow: var(--sd-shadow); border-color: var(--sd-blue); }
      .sd-step:not(:last-child):after { content: "→"; position: absolute; right: -12px; top: 38%; z-index: 3; color: var(--sd-blue); font-size: 20px; font-weight: 900; }
      .sd-step .sd-num { font-size: 10px; font-weight: 700; color: var(--sd-red); letter-spacing: .06em; }
      .sd-step b { display: block; font-size: 13px; margin: 4px 0; color: var(--sd-navy); }
      .sd-step span { display: block; font-size: 10.5px; line-height: 1.45; color: var(--sd-muted); }
      .sd-outputgrid { display: grid; grid-template-columns: repeat(auto-fill,minmax(220px,1fr)); gap: 14px; }
      .sd-out { background: #fff; border: 1px solid var(--sd-line); border-radius: 16px; padding: 18px; border-top: 3px solid var(--sd-blue); transition: transform .22s ease, box-shadow .22s ease; }
      .sd-out:hover { transform: translateY(-5px); box-shadow: var(--sd-shadow-lg); }
      .sd-out .sd-n { font-size: 10px; color: var(--sd-red); font-weight: 700; letter-spacing: .06em; }
      .sd-out h3 { font-size: 16.5px; font-weight: 700; margin: 6px 0 6px; color: var(--sd-navy); }
      .sd-out p { margin: 0; color: var(--sd-muted); font-size: 11.5px; line-height: 1.5; }
      .sd-value { background: linear-gradient(135deg,#FFF4F4,#FFF 70%); border: 1px solid #F2C7CB; border-radius: 18px; padding: 24px; display: grid; grid-template-columns: 1.05fr 1fr; gap: 22px; align-items: center; box-shadow: var(--sd-shadow); }
      .sd-value h2 { font-size: 27px; font-weight: 700; line-height: 1.15; margin: 0 0 8px; color: var(--sd-navy); }
      .sd-value p { font-size: 12px; color: var(--sd-muted); line-height: 1.6; margin: 0; }
      .sd-riskgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .sd-risk { background: #fff; border: 1px solid #F0D8DA; border-radius: 11px; padding: 10px; font-size: 11px; font-weight: 750; color: var(--sd-navy); transition: transform .18s ease, box-shadow .18s ease; }
      .sd-risk:hover { transform: translateY(-2px); box-shadow: var(--sd-shadow); }
      .sd-twocol { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
      .sd-card { background: #fff; border: 1px solid var(--sd-line); border-radius: 16px; padding: 20px; box-shadow: var(--sd-shadow); transition: box-shadow .22s ease, transform .22s ease; }
      .sd-card:hover { box-shadow: var(--sd-shadow-lg); transform: translateY(-3px); }
      .sd-card h3 { font-size: 18.5px; font-weight: 700; margin: 0 0 12px; color: var(--sd-navy); }
      .sd-card p { color: var(--sd-muted); font-size: 12.5px; line-height: 1.6; margin: 0 0 8px; }
      .sd-qa { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .sd-qa div { background: var(--sd-soft); border-radius: 10px; padding: 10px; transition: background .18s ease; }
      .sd-qa div:hover { background: #E4EEF8; }
      .sd-qa b { display: block; font-size: 11px; color: var(--sd-navy); }
      .sd-qa span { font-size: 10px; color: var(--sd-muted); display: block; margin-top: 3px; }
      .sd-tags { display: flex; gap: 7px; flex-wrap: wrap; }
      .sd-tag { background: var(--sd-soft); border: 1px solid var(--sd-line); color: var(--sd-navy); border-radius: 999px; padding: 7px 10px; font-size: 10.5px; font-weight: 800; transition: transform .18s ease, background .18s ease, color .18s ease, border-color .18s ease; }
      .sd-tag:hover { transform: translateY(-2px); background: var(--sd-navy); color: #fff; border-color: var(--sd-navy); }
      .sd-tag.std { background: #eef7f2; color: #187159; }
      .sd-tag.std:hover { background: #187159; color: #fff; border-color: #187159; }
      .sd-diff { background: linear-gradient(135deg,#fff,#F9FBFD); border: 1px solid var(--sd-line); border-left: 5px solid var(--sd-red); border-radius: 16px; padding: 20px; box-shadow: var(--sd-shadow); }
      .sd-diff h3 { font-size: 19px; font-weight: 700; margin: 0 0 6px; color: var(--sd-navy); }
      .sd-diff p { margin: 0 0 8px; color: #334B60; font-size: 12.5px; line-height: 1.6; }
      .sd-diff .sd-quote { margin-top: 14px; font-weight: 600; font-style: italic; color: var(--sd-navy); font-size: 17px; line-height: 1.5; padding-left: 16px; border-left: 3px solid var(--sd-red); }
      .sd-scope { background: #F7FBFF; border: 1px solid #CFE0EE; border-radius: 16px; padding: 20px; box-shadow: var(--sd-shadow); }
      .sd-scope h3 { margin: 0 0 8px; font-size: 17px; font-weight: 700; color: var(--sd-navy); }
      .sd-scope p { font-size: 12.5px; line-height: 1.6; color: var(--sd-muted); margin: 0; }
      .sd-casegrid { display: grid; grid-template-columns: repeat(2,1fr); gap: 14px; }
      .sd-case { background: #fff; border: 1px solid var(--sd-line); border-radius: 16px; overflow: hidden; cursor: pointer; transition: box-shadow .25s ease, transform .25s ease, border-color .25s ease; }
      .sd-case:hover { box-shadow: var(--sd-shadow-lg); transform: translateY(-4px); border-color: var(--sd-blue); }
      .sd-case-main { padding: 17px; }
      .sd-case-main .sd-caseid { font-size: 9.5px; color: var(--sd-red); font-weight: 700; letter-spacing: .06em; }
      .sd-case-main h3 { font-size: 17px; font-weight: 700; margin: 6px 0 7px; color: var(--sd-navy); }
      .sd-case-main .sd-profile { font-size: 10.5px; color: var(--sd-blue); font-weight: 800; margin-bottom: 7px; }
      .sd-case-main p { margin: 7px 0; color: var(--sd-muted); font-size: 10.8px; line-height: 1.5; }
      .sd-metricrow { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 10px; }
      .sd-metricpill { background: linear-gradient(135deg,var(--sd-navy),var(--sd-navy2)); color: #fff; border-radius: 999px; padding: 6px 8px; font-size: 9px; font-weight: 800; }
      .sd-industrygrid { display: flex; gap: 8px; flex-wrap: wrap; }
      .sd-industry { background: #fff; border: 1px solid var(--sd-line); border-radius: 12px; padding: 10px 12px; font-size: 11px; font-weight: 800; color: var(--sd-navy); transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
      .sd-industry:hover { transform: translateY(-2px); box-shadow: var(--sd-shadow); border-color: var(--sd-blue); }
      .sd-cta { background: linear-gradient(135deg,#082A52,#123F72 60%,#164B7D); color: #fff; border-radius: 20px; padding: 26px; display: grid; grid-template-columns: 1fr 150px; gap: 20px; align-items: center; position: relative; overflow: hidden; box-shadow: var(--sd-shadow-lg); }
      .sd-cta:before { content: ""; position: absolute; right: -60px; bottom: -80px; width: 260px; height: 260px; border-radius: 50%; background: radial-gradient(circle, rgba(255,255,255,.08), transparent 70%); }
      .sd-cta > * { position: relative; z-index: 1; }
      .sd-cta h2 { font-size: 26px; font-weight: 700; margin: 6px 0; }
      .sd-cta p { margin: 0 0 14px; color: #C7D7E5; font-size: 12px; line-height: 1.55; }
      .sd-cta img { width: 100px; height: 100px; background: #fff; padding: 8px; border-radius: 12px; box-shadow: 0 8px 20px rgba(0,0,0,.25); }
      .sd-internal { background: #FFF9EB; border: 1px solid #F1D89C; border-radius: 18px; padding: 18px; }
      .sd-internal summary { cursor: pointer; font-weight: 900; color: #7A4D00; }
      .sd-internal .sd-ig-inner { padding-top: 14px; }
      .sd-ig { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; }
      .sd-ig div { background: #fff; border: 1px solid #F1E2B8; border-radius: 11px; padding: 11px; transition: transform .18s ease, box-shadow .18s ease; }
      .sd-ig div:hover { transform: translateY(-2px); box-shadow: var(--sd-shadow); }
      .sd-ig b { font-size: 11px; display: block; color: #6C4700; }
      .sd-ig span { font-size: 10.5px; color: #745F3B; display: block; margin-top: 4px; line-height: 1.45; }
      @media print {
        .sd-internal, .sd-actions, .sd-progress, .sd-backtop, .sd-doc header, .sd-doc footer { display: none !important; }
        .sd-doc main > * { opacity: 1 !important; animation: none !important; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
        .sd-doc { background: #fff; }
        .sd-hero { box-shadow: none; border-radius: 14px; }
        .sd-use, .sd-step, .sd-out, .sd-case, .sd-card, .sd-tag, .sd-industry, .sd-ig div, .sd-value, .sd-diff, .sd-scope { box-shadow: none !important; transform: none !important; break-inside: avoid; page-break-inside: avoid; }
        .sd-section { break-inside: avoid-page; }
        .sd-doc main { max-width: 100% !important; }
      }
      @media (max-width: 1000px) { .sd-heroq, .sd-usegrid, .sd-journey, .sd-outputgrid { grid-template-columns: repeat(2,1fr); } .sd-step:not(:last-child):after { display: none; } .sd-value, .sd-twocol, .sd-casegrid { grid-template-columns: 1fr; } .sd-ig { grid-template-columns: 1fr 1fr; } }
      @media (max-width: 650px) { .sd-hero { padding: 26px; } .sd-hero h1 { font-size: 30px; } .sd-heroq, .sd-usegrid, .sd-journey, .sd-outputgrid, .sd-qa, .sd-ig { grid-template-columns: 1fr; } .sd-cta { grid-template-columns: 1fr; } }
      /* The decorative ring is sized against a full-width desktop hero; on a
         phone-width card the same 380px circle reaches in far enough to show
         through behind the eyebrow/title text instead of sitting in a corner.
         There is no size small enough to keep it clear of that text on a
         320-375px card, so it is simplest to drop it rather than fight it. */
      @media (max-width: 480px) { .sd-hero:after { display: none; } }

      .load-more-container { display: flex; justify-content: center; margin-top: 2rem; }
      .solution-card { transition: all 0.3s ease; }
      .case-card { transition: all 0.3s ease; }
      .studiotop{display:flex;justify-content:space-between;align-items:flex-end;gap:20px;flex-wrap:wrap;margin-bottom:18px;padding-bottom:16px;border-bottom:1px solid var(--border-gray);}
      .studiotop h1{margin:6px 0 2px;font-size:26px;color:#002147;letter-spacing:-.02em}
      .studiotop>div>span{font-size:12px;color:#4b5563}
      .backbtn{background:none;border:0;color:#f83a4c;font-weight:900;font-size:12px;cursor:pointer;padding:0}
      .studiometer{display:flex;align-items:center;gap:10px;min-width:280px}
      .studio{display:grid;grid-template-columns:250px minmax(0,1fr);gap:20px;align-items:start}
      .studionav{position:sticky;top:84px;background:#fff;border:1px solid #e5e7eb;border-radius:14px;overflow:hidden}
      .studionav button{display:flex;gap:10px;width:100%;text-align:left;border:0;background:#fff;border-bottom:1px solid #eef2f6;padding:11px 12px;cursor:pointer;align-items:flex-start}
      .studionav button:last-child{border-bottom:0}
      .studionav .n{display:grid;place-items:center;min-width:21px;height:21px;border-radius:999px;background:#eef3f8;color:#42617a;font-size:10px;font-weight:900}
      .studionav button b{display:block;font-size:12.5px;color:#3c5a75}
      .studionav button small{display:block;font-size:10.5px;color:#6b7280;line-height:1.4;margin-top:2px}
      .studionav button.on{background:#f7fafc}
      .studionav button.on .n{background:#f83a4c;color:#fff}
      .studionav button.on b{color:#002147}
      .substep+.substep{margin-top:16px}
      .nonav .stepnav{display:none}
      .storyq{border-left:3px solid #e5e7eb;padding:0 0 0 14px;margin-bottom:18px}
      .storyq:hover{border-left-color:#f83a4c}
      .sqn{font-size:14px;font-weight:700;color:#002147}
      .sqh{font-size:12px;color:#6b7280;margin:2px 0 7px}
      .stagerow{display:flex;gap:8px;align-items:center}
      .srn{min-width:24px;height:24px;border-radius:999px;background:#002147;color:#fff;display:grid;place-items:center;font-size:11px;font-weight:900}
      .engrid{display:grid;grid-template-columns:repeat(4,1fr);gap:9px;margin:12px 0 18px}
      .engcard{text-align:left;border:1px solid #e5e7eb;background:#fff;border-radius:12px;padding:12px;cursor:pointer;border-top:3px solid #e5e7eb}
      .engcard.on{border-top-color:#f83a4c;background:#f7fafc}
      .engcard b{display:block;color:#002147;font-size:12.5px;margin-bottom:4px}
      .engcard small{display:block;color:#6b7280;font-size:11px;line-height:1.45}
      .engcard em{display:block;color:#5b7a95;font-size:10.5px;font-style:normal;margin-top:6px;padding-top:6px;border-top:1px solid #eef2f6}
      .ratebar{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;background:#f4f8fb;border:1px solid #e5e7eb;border-radius:12px;padding:14px;margin-bottom:16px}
      .ratebar small{display:block;font-size:9.5px;font-weight:900;letter-spacing:.08em;color:#f83a4c;margin-bottom:5px}
      .ratebar input,.ratebar select{border:1px solid #e5e7eb;border-radius:8px;padding:8px;font-size:13px;width:100%;box-sizing:border-box}
      .ratebar b{font-size:20px;color:#002147;display:block}
      .ratebar span{display:block;font-size:10.5px;color:#6b7280;margin-top:4px}
      .costbox{border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;margin-top:16px}
      .crow{display:flex;justify-content:space-between;padding:11px 15px;font-size:13px;border-bottom:1px solid #eef2f6}
      .crow.sub{color:#6b7280;font-size:12.5px;background:#fbfcfd}
      .crow.total{background:#002147;color:#fff;font-size:15px;font-weight:800;border:0}
      .cwords{padding:9px 15px;font-size:11.5px;color:#6b7280;background:#f7fafc}
      .preszone{background:linear-gradient(135deg,#0e3271,#17469E);color:#fff;border-radius:13px;padding:18px 22px;margin-top:14px}
      .preszone small{display:block;font-size:9.5px;letter-spacing:.1em;color:#a8c2e0;font-weight:800}
      .preszone b{display:block;font-size:31px;line-height:1.2;margin:4px 0}
      .preszone span{font-size:12.5px;color:#c9dcf0}
      .tgroup{margin-bottom:12px}
      .tgh{display:flex;justify-content:space-between;align-items:center;font-size:10.5px;font-weight:900;letter-spacing:.06em;text-transform:uppercase;color:#51687d;padding:6px 0;border-bottom:1px solid #e5e7eb;margin-bottom:8px}
      .rowgrid2{display:grid;gap:7px;flex:1;grid-template-columns:repeat(2,1fr)}
      .adtabs{display:flex;gap:7px;margin:12px 0 16px;border-bottom:1px solid #e5e7eb;flex-wrap:wrap}
      .adtabs button{border:0;background:none;padding:9px 13px;font-size:12.5px;font-weight:700;color:#5b7a95;cursor:pointer;border-bottom:2px solid transparent}
      .adtabs button.on{color:#002147;border-bottom-color:#f83a4c}
      .modal.wide{width:min(1000px,100%)}
      .verchip{font-size:9px;font-weight:800;padding:2px 7px;border-radius:999px;letter-spacing:.03em;margin-left:7px}
      .verchip.ok{background:#eaf7f0;color:#187159}
      .verchip.no{background:#fff3e0;color:#8a5a12}
      .verrow{display:grid;grid-template-columns:1.4fr 1fr 1fr 1.2fr;gap:7px;margin-top:8px;align-items:center}
      .verrow input{border:1px solid #e5e7eb;border-radius:8px;padding:7px 9px;font-size:12px}
      .switch{display:flex;align-items:center;gap:9px;font-size:13px;font-weight:700;color:#002147;text-transform:none;letter-spacing:0}
      .switch input{width:auto!important}
      @media(max-width:900px){.verrow{grid-template-columns:1fr}.engrid,.ratebar{grid-template-columns:1fr 1fr}}
      .pp{background:#fff;border:1px solid #e5e7eb;border-radius:16px;padding:24px}
      .pp>h2{margin:0 0 4px;font-size:20px;color:#002147}
      .pp .hint{color:#6b7280;font-size:12px;font-weight:400;text-transform:none;letter-spacing:0}
      .pp>.hint{margin-bottom:16px}
      .stepnav{display:flex;justify-content:space-between;margin-top:14px}
      .presetgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:14px 0}
      .preset{text-align:left;border:1px solid #e5e7eb;background:#fff;border-radius:12px;padding:14px;cursor:pointer;border-top:3px solid #e5e7eb}
      .preset:hover{border-color:#002147}
      .preset.on{border-top-color:#f83a4c;background:#f7fafc}
      .preset b{display:block;color:#002147;font-size:14px;margin-bottom:4px}
      .preset small{color:#6b7280;font-size:11px;line-height:1.5}
      .typegrid{display:grid;grid-template-columns:repeat(4,1fr);gap:9px;margin:14px 0}
      .typecard{text-align:left;border:1px solid #e5e7eb;background:#fff;border-radius:12px;padding:13px;cursor:pointer;border-top:3px solid #e5e7eb}
      .typecard:hover{border-color:#002147}
      .typecard.on{border-top-color:#f83a4c;background:#f7fafc}
      .tct{display:flex;justify-content:space-between;align-items:baseline;gap:6px;margin-bottom:5px}
      .typecard b{color:#002147;font-size:13px}
      .typecard .tag{font-size:9px;background:#eef3f8;color:#5b7a95;padding:2px 6px;border-radius:999px;font-weight:800;white-space:nowrap}
      .typecard small{color:#6b7280;font-size:11.5px;line-height:1.5}
      .modegrid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:12px 0}
      .modecard{text-align:left;border:1px solid #e5e7eb;background:#fff;border-radius:12px;padding:14px;cursor:pointer}
      .modecard.on{border-color:#187159;background:#f6fbf8}
      .modecard b{display:block;color:#002147;font-size:14px}
      .modecard .tag{font-size:9.5px;background:#eef3f8;color:#5b7a95;padding:2px 7px;border-radius:999px;font-weight:800}
      .modecard small{display:block;color:#6b7280;font-size:11.5px;margin:6px 0 4px;line-height:1.5}
      .typebar{display:flex;gap:6px;align-items:center;margin-top:8px}
      .tpill{font-size:10px;font-weight:800;background:#eaf0f7;color:#17469E;padding:4px 9px;border-radius:999px}
      .tpill.alt{background:#f2f7f4;color:#187159}
      .storyq{border-left:3px solid #e5e7eb;padding:0 0 0 14px;margin-bottom:18px}
      .storyq:hover{border-left-color:#f83a4c}
      .sqn{font-size:14px;font-weight:700;color:#002147}
      .sqh{font-size:12px;color:#6b7280;margin:2px 0 7px}
      .formgrid{display:grid;grid-template-columns:1fr 1fr;gap:13px}
      .formrow{margin:12px 0}
      .formrow label{display:block;font-size:10px;text-transform:uppercase;letter-spacing:.07em;color:#51687d;font-weight:900;margin-bottom:5px}
      .formrow input,.formrow select,.formrow textarea{width:100%;border:1px solid #e5e7eb;border-radius:9px;padding:10px;background:#fff;color:#1f2937}
      .formrow textarea{min-height:84px;resize:vertical}
      .tickgrid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:8px}
      .tick{display:flex;gap:9px;align-items:flex-start;border:1px solid #e5e7eb;border-radius:10px;padding:10px 11px;cursor:pointer;background:#fff}
      .tick:hover{border-color:#b9c9da}
      .tick.on{border-color:#187159;background:#f6fbf8}
      .tick input{margin-top:2px;width:auto!important}
      .tick b{font-size:12.5px;color:#1f2937;font-weight:700;display:block}
      .tick small{display:block;color:#6b7280;font-size:11px;margin-top:2px;line-height:1.45}
      .chipwrap{display:flex;flex-wrap:wrap;gap:7px}
      .chip{border:1px solid #e5e7eb;background:#fff;color:#42617a;font-size:11.5px;font-weight:700;padding:7px 11px;border-radius:999px;cursor:pointer}
      .chip.on{background:#002147;border-color:#002147;color:#fff}
      .modalback{position:fixed;inset:0;background:rgba(6,25,46,.55);z-index:150;display:grid;place-items:center;padding:18px}
      .modal{width:min(760px,100%);max-height:92vh;overflow:auto;background:#fff;border-radius:18px;box-shadow:0 30px 90px rgba(0,0,0,.24);padding:24px}
      .modal.wide{width:min(1000px,100%)}
      .modal h2{margin:0 0 5px;font-size:24px}
      .modal p{color:#6b7280;font-size:13px}
      .modalactions{display:flex;justify-content:flex-end;gap:8px;margin-top:18px;padding-top:14px;border-top:1px solid #e5e7eb}
      .btn{border:0;border-radius:9px;padding:9px 12px;font-weight:800}
      .btn.primary{background:#002147;color:#fff}
      .btn.line{background:#fff;color:#002147;border:1px solid #e5e7eb}
      .btn.danger{color:#9d2530;border-color:#f0cdd2}
      .btn.tiny{padding:6px 9px;font-size:11px}
      .btn:disabled{opacity:.4;cursor:not-allowed}
      .readbar{flex:1;height:8px;background:#eef3f8;border-radius:999px;overflow:hidden}
      .readbar.sm{height:6px}
      .readbar span{display:block;height:100%;background:linear-gradient(90deg,#1b7a8c,#187159)}
      .propcard{background:#fff;border:1px solid #e5e7eb;border-radius:15px;padding:18px;border-top:3px solid #002147}
      .propcard-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:9px}
      .propid{color:#f83a4c;font-size:10px;font-weight:900;letter-spacing:.06em}
      .propstatus{font-size:10px;font-weight:800;padding:4px 9px;border-radius:999px;background:#eef3f8;color:#42617a}
      .propstatus.s-won{background:#eaf7f0;color:#187159}
      .propstatus.s-lost{background:#fdeef0;color:#9d2530}
      .propcard h3{margin:0 0 4px;font-size:17px;color:#002147}
      .propclient{font-size:12.5px;color:#41607b;font-weight:700;margin-bottom:9px}
      .propstats{display:flex;gap:6px;flex-wrap:wrap;margin:9px 0 8px}
      .propstats span{font-size:10.5px;background:#f1f6fa;border:1px solid #e5e7eb;padding:5px 8px;border-radius:7px;color:#42617a;font-weight:700}
      .propmeta{font-size:11px;color:#6b7280;margin-bottom:12px}
      .propgrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}
      .proposal-toolbar{display:flex;gap:8px;flex-wrap:wrap;margin:15px 0}
      .unitrow{display:grid;grid-template-columns:1fr 1.3fr;gap:7px}
      .ico{display:inline-grid;place-items:center;width:15px;height:15px;border-radius:999px;background:#dbe6f2;color:#17469E;font-size:9.5px;font-weight:900;font-style:italic;margin-left:6px;cursor:help;position:relative;vertical-align:middle}
      .ico:hover:after{content:attr(data-tip);position:absolute;left:20px;top:-6px;width:290px;background:#0e3271;color:#fff;font-size:11.5px;font-weight:400;font-style:normal;letter-spacing:0;text-transform:none;line-height:1.55;padding:10px 12px;border-radius:8px;z-index:50;box-shadow:0 10px 24px rgba(0,0,0,.2)}
      .adv{border:1px solid #e5e7eb;border-radius:12px;padding:12px 15px;margin-top:16px;background:#fbfcfd}
      .adv summary{font-size:12.5px;font-weight:800;color:#5b7a95;cursor:pointer}
      .fieldnote{font-size:11.5px;color:#6b7280;margin-top:6px}
      .fieldnote b{color:#002147}
      .skipbar{display:flex;justify-content:space-between;align-items:center;gap:12px;background:#f6fbf8;border:1px solid #cfe8db;border-radius:10px;padding:9px 13px;margin:10px 0;font-size:12px;font-weight:700;color:#187159}
      .skipbar.off{background:#f7f8fa;border-color:#e5e7eb;color:#6b7280}
      .bugrid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:12px 0}
      .bucard{text-align:left;border:1px solid #e5e7eb;background:#fff;border-radius:11px;padding:11px;cursor:pointer}
      .bucard.on{border-color:#9d2530;background:#fdf7f7}
      .bucard b{display:block;font-size:12.5px;color:#002147}
      .bucard small{display:block;font-size:10.5px;color:#6b7280;margin-top:2px}
      .bucard .tag{display:inline-block;margin-top:6px;font-size:9.5px;font-weight:800;color:#42617a;background:#f1f6fa;border-radius:999px;padding:2px 7px}
      .npstep{display:flex;align-items:center;gap:9px;margin:18px 0 9px;flex-wrap:wrap}
      .npstep b{font-size:14px;color:#002147}
      .npn{display:grid;place-items:center;width:21px;height:21px;border-radius:999px;background:#002147;color:#fff;font-size:11px;font-weight:900}
      .npreq{font-size:9.5px;background:#fdeef0;color:#9d2530;padding:2px 7px;border-radius:999px;font-weight:800}
      .nphint{font-size:10.5px;color:#6b7280}
      .kicker{font-size:10.5px;letter-spacing:.14em;text-transform:uppercase;color:#9d2530;font-weight:900}
      .proptoast{position:fixed;left:50%;bottom:26px;transform:translateX(-50%);background:#0e3271;color:#fff;font-size:12.5px;font-weight:700;padding:11px 18px;border-radius:999px;box-shadow:0 12px 30px rgba(0,0,0,.22);z-index:200}
      @media(max-width:900px){.bugrid{grid-template-columns:1fr 1fr}}
      .bigvalue{background:#f4f8fb;border-left:3px solid #9d2530;border-radius:0 12px 12px 0;padding:18px;margin:14px 0}
      .bigvalue small{display:block;font-size:10px;letter-spacing:.1em;color:#9d2530;font-weight:900}
      .bigvalue b{display:block;font-size:32px;color:#002147;line-height:1.2}
      .bigvalue span{color:#6b7280;font-size:12.5px}
      .check{display:flex;align-items:flex-start;gap:8px;padding:9px;border:1px solid #e5e7eb;border-radius:9px;font-size:12px}
      .check.ok{border-color:#cfe8db;background:#f6fbf8;color:#187159;font-weight:700}
      .check.miss{color:#6b7280}
      .tickgrid.one{grid-template-columns:1fr}
      .readiness{display:flex;gap:11px;align-items:center;margin:6px 0}
      .readiness b{font-size:12px;color:#002147;white-space:nowrap}
      .revsec{border:1px solid #e5e7eb;border-radius:12px;padding:14px 16px;margin:12px 0;background:#fff}
      .revsec.ok{border-color:#cfe8db;background:#fbfefc}
      .revsec.change{border-color:#f0c9b0;background:#fffbf7}
      .revhead{display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-bottom:9px}
      .revhead b{font-size:13.5px;color:#002147;flex:1}
      .revnum{display:grid;place-items:center;min-width:24px;height:24px;border-radius:7px;background:#eef4fb;color:#17469E;font-size:11px;font-weight:900}
      .revverdict{display:flex;gap:6px}
      .revbody{max-height:260px;overflow:auto;border:1px solid #eef2f6;border-radius:9px;padding:12px 14px;background:#fbfcfd;font-size:12.5px;line-height:1.55}
      .revbody .sech{font-size:15px;font-weight:800;color:#002147;margin:0 0 6px}
      .revbody .eyebrow{font-size:9.5px;letter-spacing:.12em;text-transform:uppercase;color:#9d2530;font-weight:900;margin-bottom:4px}
      .revbody table{width:100%;border-collapse:collapse;margin:8px 0;font-size:11.5px}
      .revbody th,.revbody td{border:1px solid #e5e7eb;padding:5px 7px;text-align:left;vertical-align:top}
      .revbody th{background:#f4f8fb;font-weight:800}
      .revbody ul{margin:6px 0 6px 18px}
      .revbody p{margin:5px 0}
      .revnote{width:100%;margin-top:9px;border:1px solid #e5e7eb;border-radius:9px;padding:9px 11px;font-size:12.5px;font-family:inherit;resize:vertical}
      .revfeed{border-left:3px solid #17469E;background:#f7fafd;border-radius:0 10px 10px 0;padding:10px 14px;margin:9px 0}
      .revfeed.change{border-left-color:#c2410c;background:#fffaf6}
      .revfeed.ok{border-left-color:#187159;background:#f7fcf9}
      .revfeedhead{display:flex;align-items:center;gap:8px}
      .revfeedhead b{font-size:12.5px;color:#002147}
      .revfeed p{font-size:12.5px;margin:5px 0 3px}
      .btn.tiny{padding:3px 9px;font-size:10.5px}
      .optb{display:inline-block;margin-left:6px;font-size:9.5px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:#8a6d1f;background:#fdf6e3;border:1px solid #f0e2b8;border-radius:999px;padding:1px 6px;vertical-align:middle}
      .okBox{border:1px solid #bfe3cd;background:#f2fbf5;color:#1c5c37;border-radius:10px;padding:10px 12px;font-size:12.5px;margin:10px 0}
      .unitrow input,.unitrow select{border:1px solid #e5e7eb;border-radius:9px;padding:9px 10px;font-size:13px;background:#fff;width:100%}
      .rowlist{display:grid;gap:8px;margin-top:8px}
      .rowcard{display:flex;gap:8px;align-items:center}
      .rowgrid3{display:grid;grid-template-columns:repeat(3,1fr);gap:7px;flex:1}
      .rowgrid4{display:grid;grid-template-columns:repeat(4,1fr);gap:7px;flex:1}
      .rowgrid5{display:grid;grid-template-columns:repeat(5,1fr);gap:7px;flex:1}
      .rowcard input,.rowcard select{border:1px solid #e5e7eb;border-radius:9px;padding:9px 10px;font-size:12.5px;background:#fff;width:100%}
      .lx{border:1px solid #e5e7eb;background:#fff;color:#9d2530;border-radius:9px;width:34px;min-width:34px;height:34px;cursor:pointer;font-weight:800}
      .libpick{display:flex;gap:8px;flex-wrap:wrap;margin-top:8px}
      .libpick select,.libpick input{flex:1;min-width:140px;border:1px solid #e5e7eb;border-radius:9px;padding:9px 10px;background:#fff;font-size:12.5px}
      .linelist{display:grid;gap:6px;margin-top:8px}
      .lineitem{display:flex;gap:7px;align-items:center}
      .lineitem .ln{color:#f83a4c;font-weight:900;font-size:11px;min-width:20px}
      .lineitem input{flex:1;border:1px solid #e5e7eb;border-radius:9px;padding:9px 10px;font-size:13px}
      .stagecard{display:flex;gap:11px;align-items:flex-start;border:1px solid #e5e7eb;border-radius:11px;padding:12px 14px;cursor:pointer;background:#fff}
      .stagecard.on{border-color:#187159;background:#f6fbf8}
      .stagecard input{width:auto!important;margin-top:3px}
      .stagecard .sn{color:#f83a4c;font-weight:900;font-size:11px;margin-top:2px}
      .stagecard b{display:block;font-size:13px;color:#002147}
      .stagecard small{display:block;color:#6b7280;font-size:11.5px;line-height:1.5;margin-top:3px}
      .stagepick{display:grid;gap:8px;margin:12px 0}
      .stagerow{display:flex;gap:8px;align-items:center}
      .srn{min-width:24px;height:24px;border-radius:999px;background:#002147;color:#fff;display:grid;place-items:center;font-size:11px;font-weight:900}
      .tgroup{margin-bottom:12px}
      .tgh{display:flex;justify-content:space-between;align-items:center;font-size:10.5px;font-weight:900;letter-spacing:.06em;text-transform:uppercase;color:#51687d;padding:6px 0;border-bottom:1px solid #e5e7eb;margin-bottom:8px}
      .mpick{border:1px solid #e5e7eb;border-radius:12px;overflow:hidden}
      .mptop{display:flex;gap:7px;padding:9px;background:#f7fafc;border-bottom:1px solid #e5e7eb}
      .mpq{flex:1;border:1px solid #e5e7eb;border-radius:8px;padding:8px 10px;font-size:12.5px}
      .mpsel{padding:9px;border-bottom:1px solid #e5e7eb;background:#fbfdfe}
      .mplist{max-height:280px;overflow:auto}
      .mprow{display:flex;gap:9px;align-items:flex-start;padding:9px 11px;border-bottom:1px solid #eef2f6;cursor:pointer}
      .mprow:hover{background:#f7fafc}
      .mprow.on{background:#f6fbf8}
      .mprow input{width:auto!important;margin-top:2px}
      .mprow b{font-size:12.5px;font-weight:700;display:block}
      .mprow small{display:block;color:#6b7280;font-size:11px;margin-top:1px}
      .empty{padding:27px;text-align:center;border:1px dashed #cbd7e1;border-radius:14px;background:#fff;color:#6b7280}
      .noteBox{background:#f4f8fb;border:1px solid #e5e7eb;border-left:3px solid #002147;border-radius:0 10px 10px 0;padding:12px 14px;font-size:12px;margin:14px 0}
      .warnBox{background:#fff8ec;border:1px solid #f0dcb8;border-left:3px solid #d08a1b;border-radius:0 10px 10px 0;padding:12px 14px;font-size:12px;margin:12px 0;color:#7a5510}
      .okBox{background:#f6fbf8;border:1px solid #cfe8db;color:#187159;border-radius:10px;padding:12px 14px;font-size:12px;margin-top:12px}
      .seclist{display:grid;gap:6px;margin-top:10px}
      .secrow{display:flex;gap:10px;align-items:flex-start;border:1px solid #e5e7eb;border-radius:10px;padding:10px 12px;background:#fff;cursor:grab}
      .secrow.off{opacity:.45;background:#fafbfc}
      .secrow .grip{color:#b6c3d1;font-size:14px;line-height:1.2}
      .secrow input{width:auto!important;margin-top:2px}
      .secrow b{display:block;font-size:12.5px;color:#002147}
      .secrow small{display:block;font-size:11px;color:#6b7280;margin-top:1px}
      .optb{font-size:9.5px;background:#eef3f8;color:#5b7a95;padding:2px 6px;border-radius:999px;font-weight:800;letter-spacing:.04em}
      .aibtn{float:right;border:1px solid #cfe0f5;background:#f2f7fd;color:#17469E;font-size:10.5px;font-weight:800;padding:4px 9px;border-radius:999px;cursor:pointer;letter-spacing:0;text-transform:none}
      .aibtn:hover{background:#e4eefb}
      .aiout{margin-top:12px;border:1px solid #e5e7eb;border-radius:10px;padding:12px;background:#fbfdfe;max-height:260px;overflow:auto}
      .aiout pre{white-space:pre-wrap;font-family:inherit;font-size:13px;margin:0}
      .apcard{border:1px solid #e5e7eb;border-radius:12px;padding:14px}
      .apch{display:flex;justify-content:space-between;align-items:center;margin-bottom:9px}
      .apch b{color:#002147;font-size:13px}
      .apst{font-size:10px;font-weight:800;padding:4px 9px;border-radius:999px;background:#eef3f8;color:#42617a}
      .apst.s-approved{background:#eaf7f0;color:#187159}
      .apst.s-changes-requested{background:#e9f2ff;color:#1b4f96}
      .aphist{display:grid;gap:7px}
      .aphrow{border-left:2px solid #e5e7eb;padding:7px 0 7px 12px;font-size:12.5px}
      .aphrow b{color:#002147}
      .aphrow span{color:#42617a;margin-left:7px}
      .aphrow small{display:block;color:#6b7280;font-size:11px}
      .aphrow p{margin:4px 0 0;color:#6b7280;font-size:12px}
      .factblock{display:block;border:1px solid #e5e7eb;border-radius:12px;padding:13px 15px;margin-bottom:10px;cursor:pointer;background:#fff}
      .factblock.on{border-color:#187159;background:#f8fdfa}
      .fbh{display:flex;align-items:center;gap:9px;margin-bottom:8px}
      .fbh input{width:auto!important}
      .fbh b{color:#002147;font-size:13.5px}
      .verchip{font-size:9px;font-weight:800;padding:2px 7px;border-radius:999px;letter-spacing:.03em;margin-left:7px}
      .verchip.ok{background:#eaf7f0;color:#187159}
      .verchip.no{background:#fff3e0;color:#8a5a12}
      button.verchip{border:1px solid #f0d9b0;cursor:pointer;font-family:inherit}
      button.verchip:hover{background:#ffe8c4}
      .factlist{padding-left:26px}
      .factrow{display:flex;gap:10px;align-items:flex-start;padding:4px 0}
      .factrow .flag{font-size:17px;line-height:1.2}
      .factrow b{font-size:12.5px;color:#1f2937;font-weight:700}
      .factrow small{display:block;color:#6b7280;font-size:11.5px;margin-top:1px}
      .engrid{display:grid;grid-template-columns:repeat(4,1fr);gap:9px;margin:12px 0 18px}
      .engcard{text-align:left;border:1px solid #e5e7eb;background:#fff;border-radius:12px;padding:12px;cursor:pointer;border-top:3px solid #e5e7eb}
      .engcard.on{border-top-color:#f83a4c;background:#f7fafc}
      .engcard b{display:block;color:#002147;font-size:12.5px;margin-bottom:4px}
      .engcard small{display:block;color:#6b7280;font-size:11px;line-height:1.45}
      .engcard em{display:block;color:#5b7a95;font-size:10.5px;font-style:normal;margin-top:6px;padding-top:6px;border-top:1px solid #eef2f6}
      .costbox{border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;margin-top:16px}
      .crow{display:flex;justify-content:space-between;padding:11px 15px;font-size:13px;border-bottom:1px solid #eef2f6}
      .crow.sub{color:#6b7280;font-size:12.5px;background:#fbfcfd}
      .crow.total{background:#002147;color:#fff;font-size:15px;font-weight:800;border:0}
      .minitable{width:100%;border-collapse:collapse;margin-top:10px}
      .minitable th,.minitable td{border-bottom:1px solid #e5e7eb;padding:10px 8px;text-align:left;font-size:12.5px;vertical-align:top}
      .minitable th{font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#51687d;font-weight:900}
      .minitable .totalrow td{background:#f4f8fb;border-bottom:0}
      .amtin{width:110px;border:1px solid #e5e7eb;border-radius:8px;padding:7px;text-align:right;font-size:12.5px}
      .ratebar{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;background:#f4f8fb;border:1px solid #e5e7eb;border-radius:12px;padding:14px;margin-bottom:16px}
      .ratebar small{display:block;font-size:9.5px;font-weight:900;letter-spacing:.08em;color:#f83a4c;margin-bottom:5px}
      .ratebar input,.ratebar select{border:1px solid #e5e7eb;border-radius:8px;padding:8px;font-size:13px;width:100%;box-sizing:border-box}
      .ratebar b{font-size:20px;color:#002147;display:block}
      .ratebar span{display:block;font-size:10.5px;color:#6b7280;margin-top:4px}
      .section{display:flex;justify-content:space-between;align-items:center;gap:15px;margin:27px 0 12px}
      .section h2{font-size:20px;margin:0}
      .section span{font-size:11px;color:#6b7280}
      .askback{display:flex;align-items:center;gap:12px;background:#f4f8fb;border-left:3px solid #002147;border-radius:0 10px 10px 0;padding:11px 14px;margin-bottom:14px;flex-wrap:wrap}
      .askback span{font-size:10.5px;font-weight:800;letter-spacing:.06em;color:#5b7a95;text-transform:uppercase}
      .askback b{flex:1;font-size:13px;color:#002147;min-width:220px}
      .propcard2{border:1px solid #e5e7eb;border-radius:11px;padding:12px}
      .propcard2 b{display:block;color:#002147;font-size:13px;margin-bottom:7px}
      .propcard2 textarea{width:100%;border:1px solid #e5e7eb;border-radius:9px;padding:9px;font:inherit;font-size:12.5px;min-height:60px;box-sizing:border-box}
      .switch{display:flex;align-items:center;gap:9px;font-size:13px;font-weight:700;color:#002147;text-transform:none;letter-spacing:0}
      .switch input{width:auto!important}
      @media(max-width:1000px){.studio{grid-template-columns:1fr}.studionav{position:static;display:flex;overflow:auto}.studionav button{min-width:170px;border-bottom:0;border-right:1px solid #e5e7eb}.presetgrid{grid-template-columns:1fr 1fr}.propgrid{grid-template-columns:1fr}.rowgrid4,.rowgrid5,.rowgrid3{grid-template-columns:1fr 1fr}.tickgrid{grid-template-columns:1fr}.formgrid{grid-template-columns:1fr}.typegrid{grid-template-columns:1fr 1fr}.modegrid{grid-template-columns:1fr}.engrid{grid-template-columns:1fr 1fr}.ratebar{grid-template-columns:1fr 1fr}}
      @media(max-width:600px){.studio{grid-template-columns:1fr}.studionav{display:flex;overflow:auto;flex-wrap:nowrap}.studionav button{min-width:120px;padding:8px 10px}.studionav button span{font-size:10px}.pp{padding:16px}}

      /* ==================================================================
         STEP-1 DESIGN LAYER
         Restyles the proposal builder's inner section to the Step-1 design:
         a step rail of separate cards, a boxed action bar, and form fields
         as white cards with underline inputs. Appended last so it overrides
         the older rules above without having to unpick them. The app header
         and footer are deliberately untouched.
         ================================================================== */
      :root{
        --d-primary:#000613; --d-red:#b7102a; --d-line:#c4c6cf;
        --d-muted:#43474e; --d-outline:#74777f;
        --d-low:#f2f4f6; --d-high:#e6e8ea; --d-white:#fff;
        --d-shadow:0 1px 2px rgba(0,6,19,.05);
      }

      /* ---- layout: rail + editor, matching the design's flex row ---- */
      .studio{grid-template-columns:288px minmax(0,1fr);gap:40px}
      .studio>section{max-width:64rem}

      /* ---- step rail: separate cards, not one boxed list ---- */
      .studionav{background:none;border:0;border-radius:0;overflow:visible;display:flex;flex-direction:column;gap:8px}
      .studionav button{background:none;border:0;border-bottom:0;border-radius:12px;padding:16px;gap:16px;position:relative;transition:background-color .15s}
      .studionav button:hover:not(.on):not(:disabled){background:#f2f4f6}
      .studionav button.on{background:#fff;border:1px solid #c4c6cf;box-shadow:0 1px 2px rgba(0,6,19,.05);overflow:hidden}
      .studionav button.on::before{content:'';position:absolute;left:0;top:0;bottom:0;width:4px;background:#b7102a}
      .studionav .n{min-width:24px;height:24px;background:#e6e8ea;color:#43474e;font-size:12px;margin-top:2px}
      .studionav button.on .n{background:#b7102a;color:#fff}
      .studionav button b{font-size:14px;font-weight:600;color:#43474e;margin-bottom:4px}
      .studionav button.on b{font-weight:700;color:#000613}
      .studionav button small{font-size:12px;color:#74777f;line-height:1.6}
      .studionav button.on small{color:#43474e}

      /* ---- page header ---- */
      .studiotop{align-items:flex-end;gap:24px;margin-bottom:40px;padding-bottom:0;border-bottom:0}
      .studiotop h1{font-family:'Hanken Grotesk','Segoe UI',sans-serif;font-size:36px;font-weight:800;color:#000613;letter-spacing:-.02em;margin:12px 0 12px}
      .studiotop>div>span{font-size:14px;font-weight:500;color:#43474e}
      .backbtn{font-size:12px;font-weight:700;color:#b7102a;text-transform:uppercase;letter-spacing:.06em;background:none;border:0;padding:0;cursor:pointer}
      .backbtn:hover{text-decoration:underline}
      .typebar{margin-top:16px;gap:8px}
      .tpill{font-size:12px;font-weight:700;background:#e6e8ea;color:#43474e;padding:6px 12px;border-radius:6px}
      .tpill.alt{background:#e6e8ea;color:#43474e}

      /* the readiness + actions cluster becomes a boxed bar */
      .studiometer{background:#fff;border:1px solid #c4c6cf;border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:12px;gap:16px}
      .studiometer .readbar{min-width:90px}
      .studiometer b{font-size:14px;font-weight:700;color:#000613;padding:0 8px}

      /* ---- the panel: sections on the page, not one bordered card ---- */
      .pp{background:none;border:0;border-radius:0;padding:0;box-shadow:none;display:flex;flex-direction:column;gap:32px}
      .pp>h2{font-family:'Hanken Grotesk','Segoe UI',sans-serif;font-size:24px;font-weight:700;color:#000613;margin:0 0 8px}
      .pp>.hint{font-size:14px;color:#43474e;margin:0}
      .pp>h2+.hint{margin-top:-24px}
      .section{margin:0;padding-top:8px;border-top:1px solid #c4c6cf;align-items:baseline}
      .section h2{font-family:'Hanken Grotesk','Segoe UI',sans-serif;font-size:20px;font-weight:700;color:#000613;padding-top:16px}
      .section span{font-size:12px;color:#43474e;font-weight:500}

      /* ---- every field is a card with an underline input ---- */
      .formgrid{grid-template-columns:repeat(2,minmax(0,1fr));gap:24px}
      .formrow{background:#fff;border:1px solid #c4c6cf;border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:20px;margin:0}
      .formrow label{display:flex;align-items:center;gap:8px;font-size:12px;font-weight:700;color:#43474e;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px}
      .formrow input,.formrow select,.formrow textarea,
      .unitrow input,.unitrow select{
        width:100%;background:transparent;border:0;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:#c4c6cf;border-radius:0;
        padding:8px 0;font-size:16px;font-weight:500;color:#000613;transition:border-color .15s}
      .formrow input:focus,.formrow select:focus,.formrow textarea:focus,
      .unitrow input:focus,.unitrow select:focus{outline:0;border-bottom-color:#000613;box-shadow:none}
      .formrow textarea{min-height:76px;resize:vertical;line-height:1.6}
      .formrow input::placeholder,.formrow textarea::placeholder{color:#74777f;font-weight:400}
      .formrow input:disabled,.formrow select:disabled{background:#f2f4f6;color:#74777f}
      .unitrow{gap:16px;align-items:center}
      /* the design shows an info glyph, so hide the literal letter but keep
         it in the DOM for anyone without the icon font */
      .ico{background:transparent;color:#78d1fe;font-style:normal;width:auto;height:auto;font-size:0;line-height:1}
      .ico::before{content:'\\f05a';font-family:'Font Awesome 6 Free';font-weight:900;font-size:11px}

      /* a field that spans the grid */
      .formgrid>.formrow.wide{grid-column:1/-1}

      /* ---- action bars ---- */
      .proposal-toolbar{background:#f2f4f6;border:1px solid #c4c6cf;border-radius:12px;padding:16px;gap:12px;align-items:center;margin:0}
      .proposal-toolbar .hint,.proposal-toolbar+.hint{width:100%;font-size:12px;color:#43474e}

      /* ---- buttons ---- */
      .btn{border-radius:8px;padding:10px 20px;font-size:14px;font-weight:700;letter-spacing:0;text-transform:none;transition:background-color .15s,box-shadow .15s}
      .btn.primary{background:#000613;color:#fff;border:1px solid #000613}
      .btn.primary:hover:not(:disabled){background:#101b2e}
      .btn.line{background:#fff;color:#000613;border:1px solid #c4c6cf}
      .btn.line:hover:not(:disabled){background:#f2f4f6}
      .btn.danger{background:#fff;color:#b7102a;border:1px solid #c4c6cf}
      .btn.danger:hover:not(:disabled){background:#f2f4f6}
      .btn.tiny{padding:5px 12px;font-size:12px}
      .btn:disabled{opacity:.45;cursor:not-allowed}

      /* ---- the Back / Next bar ---- */
      .stepnav{display:flex;justify-content:space-between;align-items:center;padding-top:24px;margin-top:8px;border-top:1px solid #c4c6cf;gap:16px}
      .stepnav .btn.line{border:0;background:none;padding:0;font-size:14px;font-weight:700;color:#000613}
      .stepnav .btn.line:hover:not(:disabled){background:none;text-decoration:underline}
      .stepnav .btn.primary{padding:12px 32px;border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05)}

      /* ---- services as cards ---- */
      /* the section heading: red label left, availability count right */
      .svchead{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;margin-bottom:16px}
      .svclabel{font-size:12px;font-weight:700;color:#b7102a;text-transform:uppercase;letter-spacing:.06em}
      .svccount{font-size:12px;font-weight:500;color:#43474e;white-space:nowrap}

      .svcgrid{display:grid;gap:16px;margin-bottom:24px}
      .svccard{display:flex;gap:16px;align-items:center;background:#fff;border:1px solid #c4c6cf;
        border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:20px;cursor:pointer;position:relative;overflow:hidden;
        transition:box-shadow .15s,border-color .15s,transform .15s}
      .svccard:hover{box-shadow:0 6px 16px rgba(0,6,19,.10);transform:translateY(-1px)}
      /* the border thickens on selection, so trim the padding to keep the row height */
      .svccard.on{border:2px solid #000613;padding:19px}
      .svccard input{position:absolute;opacity:0;pointer-events:none}
      .svccard:focus-within{outline:2px solid #000613;outline-offset:2px}

      .svcmark{width:24px;height:24px;border-radius:999px;border:2px solid #c4c6cf;flex-shrink:0;transition:border-color .15s}
      .svccard:hover .svcmark{border-color:#74777f}
      .svccard.on .svcmark{display:none}
      .svccard.on::after{content:'\\f00c';font-family:'Font Awesome 6 Free';font-weight:900;position:absolute;top:0;right:0;
        width:32px;height:32px;display:grid;place-items:center;background:#000613;color:#fff;border-radius:0 0 0 12px;font-size:13px}

      /* spans are inline by default - without these the service line and the
         title sit side by side instead of stacking */
      .svcbody{display:block;flex:1;min-width:0}
      .svcsub{display:block;font-size:10px;font-weight:700;color:#74777f;text-transform:uppercase;
        letter-spacing:.08em;line-height:1.4;margin-bottom:4px}
      .svctitle{display:block;font-size:18px;font-weight:700;color:#000613;text-transform:uppercase;
        line-height:1.25;letter-spacing:-.01em;overflow-wrap:anywhere}
      .svccard.on .svcbody{padding-right:32px}
      .svcwhat{display:block;font-size:12px;font-weight:400;color:#43474e;line-height:1.55;margin-top:6px;
        text-transform:none;letter-spacing:0}

      /* search field above the service list */
      .svcsearch{position:relative;margin-bottom:24px}
      .svcsearch input{width:100%;padding:12px 40px 12px 16px;border:1px solid #c4c6cf;border-radius:12px;
        background:#fff;box-shadow:0 1px 2px rgba(0,6,19,.05);font-size:14px;font-weight:500;color:#000613}
      .svcsearch input:focus{outline:0;border-color:#000613;box-shadow:0 0 0 3px rgba(0,6,19,.08)}
      .svcsearch::after{content:'\\f002';font-family:'Font Awesome 6 Free';font-weight:900;position:absolute;
        right:16px;top:50%;transform:translateY(-50%);color:#74777f;font-size:13px;pointer-events:none}

      /* ---- notices keep their meaning but match the new surfaces ---- */
      .noteBox,.okBox,.warnBox{border-radius:12px;font-size:14px;padding:14px 16px}
      .adv{border:1px solid #c4c6cf;border-radius:12px;background:#fff;padding:16px 20px}
      .adv summary{font-size:14px;font-weight:700;color:#000613}

      /* ---- Step-2: the story, as a two-up grid of field cards ---- */
      .storygrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:24px}
      .storyq{background:#fff;border:1px solid #c4c6cf;border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);
        padding:20px;margin:0;display:flex;flex-direction:column}
      .sqhead{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:8px}
      .sqn{font-size:12px;font-weight:700;color:#43474e;text-transform:uppercase;letter-spacing:.06em;line-height:1.4}
      .storyq textarea{width:100%;background:transparent;border:0;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:#c4c6cf;border-radius:0;
        padding:8px 0;font-size:14px;font-weight:500;color:#000613;min-height:80px;resize:vertical;line-height:1.6}
      .storyq textarea:focus{outline:0;border-bottom-color:#000613}
      .storyq textarea::placeholder{color:#74777f;font-weight:400}
      .formrow.tall textarea{min-height:120px}

      /* the label row: text left, the AI button right */
      .formrow label{justify-content:space-between}
      .formrow label>span:first-child{display:flex;align-items:center;gap:8px}

      /* ---- the AI button, as the design draws it ---- */
      .aibtn{float:none;background:none;border:0;border-radius:0;padding:0;font-size:10px;font-weight:700;
        color:#000613;text-transform:none;letter-spacing:0;display:inline-flex;align-items:center;gap:4px;
        white-space:nowrap;flex-shrink:0}
      .aibtn:hover{background:none;text-decoration:underline}
      .aibtn::before{content:'\\f72b';font-family:'Font Awesome 6 Free';font-weight:900;font-size:10px}

      /* ---- a lightweight section heading: label left, note right ---- */
      .subhead{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;
        padding-bottom:8px;border-bottom:1px solid #c4c6cf;margin:0}
      .subhead-l{font-size:12px;font-weight:700;color:#000613;text-transform:uppercase;letter-spacing:.06em}
      .subhead-r{font-size:10px;font-weight:500;color:#74777f;text-align:right}
      .subhead+.rowlist,.subhead+.hint{margin-top:16px}


      /* ==================================================================
         STEPS 3-9 — the same language as Step 1 and Step 2, applied to the
         component families those steps use: tick lists, fact blocks, line
         lists, row editors, the pricing tables, stage pickers and the
         generate screen. Cards, uppercase labels, underline inputs.
         ================================================================== */

      /* ---- tick lists (about, value, scope, risks, assumptions, terms) ---- */
      .tgroup{margin-bottom:20px}
      .tgh{display:flex;justify-content:space-between;align-items:center;gap:12px;
        font-size:12px;font-weight:700;color:#000613;text-transform:uppercase;letter-spacing:.06em;
        padding-bottom:8px;margin-bottom:12px;border-bottom:1px solid #c4c6cf}
      .tickgrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
      .tickgrid.one{grid-template-columns:1fr}
      .tick{display:flex;gap:12px;align-items:flex-start;background:#fff;border:1px solid #c4c6cf;
        border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:14px 16px;cursor:pointer;
        transition:border-color .15s,box-shadow .15s}
      .tick:hover{box-shadow:0 4px 12px rgba(0,6,19,.08)}
      .tick.on{border:2px solid #000613;padding:13px 15px;background:#fff}
      .tick input{width:auto!important;margin-top:3px;accent-color:#000613}
      .tick b{display:block;font-size:13.5px;font-weight:600;color:#000613;line-height:1.4}
      .tick.on b{font-weight:700}
      .tick small{display:block;font-size:12px;color:#43474e;line-height:1.55;margin-top:3px}
      .mpq{width:100%;padding:12px 16px;border:1px solid #c4c6cf;border-radius:12px;background:#fff;
        box-shadow:0 1px 2px rgba(0,6,19,.05);font-size:14px;font-weight:500;color:#000613}
      .mpq:focus{outline:0;border-color:#000613}

      /* ---- About SolidPro: the fact blocks ---- */
      .factblock{border:1px solid #c4c6cf;border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);
        padding:18px 20px;margin-bottom:12px;background:#fff}
      .factblock.on{border:2px solid #000613;padding:17px 19px}
      .fbh{gap:10px;margin-bottom:12px;padding-bottom:10px;border-bottom:1px solid #c4c6cf}
      .fbh b{font-size:14px;font-weight:700;color:#000613}
      .fbh input{accent-color:#000613}
      .factlist{display:grid;gap:8px}
      .factrow{display:flex;gap:10px;align-items:flex-start;font-size:13px}
      .factrow b{font-size:13px;font-weight:600;color:#000613}
      .factrow small{display:block;font-size:12px;color:#43474e;margin-top:2px}
      .flag{font-size:15px;line-height:1.2}
      .verchip{font-size:10px;font-weight:700;padding:4px 10px;border-radius:999px;letter-spacing:.04em}

      /* ---- step 4: what they asked, and the per-service note ---- */
      .askback{background:#f2f4f6;border:1px solid #c4c6cf;border-left:4px solid #000613;
        border-radius:12px;padding:16px 20px;gap:12px;margin-bottom:24px}
      .askback span{font-size:10px;font-weight:700;letter-spacing:.08em;color:#74777f}
      .askback b{font-size:14px;font-weight:600;color:#000613}
      .propcard2{background:#fff;border:1px solid #c4c6cf;border-radius:12px;
        box-shadow:0 1px 2px rgba(0,6,19,.05);padding:18px 20px}
      .propcard2 b{font-size:13px;font-weight:700;color:#000613;text-transform:uppercase;
        letter-spacing:.05em;margin-bottom:10px}
      .propcard2 textarea{border:0;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:#c4c6cf;border-radius:0;padding:8px 0;
        font-size:14px;font-weight:500;color:#000613;min-height:64px}
      .propcard2 textarea:focus{outline:0;border-bottom-color:#000613}

      /* ---- numbered line lists (scope, deliverables, annexures) ---- */
      .linelist{display:grid;gap:10px}
      .lineitem{display:flex;gap:12px;align-items:center;background:#fff;border:1px solid #c4c6cf;
        border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:10px 14px}
      .lineitem:hover{border-color:#74777f}
      .ln{min-width:28px;height:28px;display:grid;place-items:center;border-radius:8px;background:#e6e8ea;
        color:#43474e;font-size:11px;font-weight:800;flex-shrink:0}
      /* the same resting underline as every other editable field */
      .lineitem input{flex:1;border:0;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:#c4c6cf;
        border-radius:0;background:transparent;font-size:14px;font-weight:500;color:#000613;padding:4px 0}
      .lineitem input:focus{outline:0;border-bottom-color:#000613}
      .lx{width:32px;min-width:32px;height:32px;border:1px solid #c4c6cf;border-radius:8px;background:#fff;
        color:#b7102a;font-weight:700;cursor:pointer;flex-shrink:0}
      .lx:hover{background:#f2f4f6}

      /* ---- library pickers ---- */
      .libpick{display:flex;gap:12px;flex-wrap:wrap;align-items:center}
      .libpick select,.libpick input{flex:1;min-width:170px;padding:10px 14px;border:1px solid #c4c6cf;
        border-radius:10px;background:#fff;font-size:14px;font-weight:500;color:#000613}
      .libpick select:focus,.libpick input:focus{outline:0;border-color:#000613}

      /* ---- row editors (recipients, team, phases, payments, escalation) ---- */
      .rowlist{display:grid;gap:12px;margin-top:16px}
      .rowcard{display:flex;gap:12px;align-items:center;background:#fff;border:1px solid #c4c6cf;
        border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:14px 16px}
      .rowgrid3,.rowgrid4,.rowgrid5{gap:12px}
      .rowcard input,.rowcard select{border:0;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:#c4c6cf;border-radius:0;background:transparent;
        padding:6px 0;font-size:13.5px;font-weight:500;color:#000613}
      .rowcard input:focus,.rowcard select:focus{outline:0;border-bottom-color:#000613}
      .rowcard input::placeholder{color:#74777f;font-weight:400}

      /* ---- step 5: engagement modes and the rate bar ---- */
      .engrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin:16px 0}
      .engcard{text-align:left;background:#fff;border:1px solid #c4c6cf;border-radius:12px;
        box-shadow:0 1px 2px rgba(0,6,19,.05);padding:16px 18px;cursor:pointer;transition:box-shadow .15s,border-color .15s}
      .engcard:hover{box-shadow:0 4px 12px rgba(0,6,19,.08)}
      .engcard.on{border:2px solid #000613;padding:15px 17px}
      .engcard b{display:block;font-size:14px;font-weight:700;color:#000613;margin-bottom:4px}
      .engcard small{display:block;font-size:12px;color:#43474e;line-height:1.55}
      .engcard em{display:block;font-style:normal;font-size:10px;font-weight:700;text-transform:uppercase;
        letter-spacing:.06em;color:#74777f;margin-top:8px}
      .ratebar{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;background:#fff;
        border:1px solid #c4c6cf;border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:18px 20px;margin:16px 0}
      .ratebar small{display:block;font-size:10px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;
        color:#74777f;margin-bottom:6px}
      .ratebar b{display:block;font-size:22px;font-weight:800;color:#000613;line-height:1.2}
      .ratebar span{display:block;font-size:11px;color:#43474e;margin-top:4px}
      .ratebar input,.ratebar select{border:0;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:#c4c6cf;border-radius:0;background:transparent;
        padding:4px 0;font-size:16px;font-weight:600;color:#000613;width:100%}
      .ratebar input:focus,.ratebar select:focus{outline:0;border-bottom-color:#000613}

      /* ---- pricing tables ---- */
      .minitable{width:100%;border-collapse:separate;border-spacing:0;background:#fff;
        border:1px solid #c4c6cf;border-radius:12px;overflow:hidden;box-shadow:0 1px 2px rgba(0,6,19,.05);margin:16px 0}
      .minitable th{background:#f2f4f6;font-size:10px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;
        color:#43474e;padding:12px 16px;text-align:left;border-bottom:1px solid #c4c6cf}
      .minitable td{padding:12px 16px;font-size:13.5px;color:#000613;border-bottom:1px solid #c4c6cf}
      .minitable tr:last-child td{border-bottom:0}
      .minitable .totalrow td{background:#f2f4f6;font-weight:800}
      .amtin{width:120px;border:0;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:#c4c6cf;border-radius:0;background:transparent;
        padding:4px 0;text-align:right;font-size:14px;font-weight:600;color:#000613}
      .amtin:focus{outline:0;border-bottom-color:#000613}

      /* ---- the cost breakdown and the headline value ---- */
      .costbox{background:#fff;border:1px solid #c4c6cf;border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);
        padding:18px 20px;margin:16px 0}
      .crow{display:flex;justify-content:space-between;gap:16px;padding:9px 0;font-size:13.5px;color:#000613;
        border-bottom:1px solid #c4c6cf}
      .crow:last-child{border-bottom:0}
      .crow.sub{font-size:12.5px;color:#43474e;padding-left:16px}
      .crow.total{font-size:16px;font-weight:800;border-top:2px solid #000613;border-bottom:0;margin-top:6px;padding-top:14px}
      .cwords{font-size:12px;color:#43474e;margin-top:8px}
      .bigvalue{background:#fff;border:1px solid #c4c6cf;border-left:4px solid #b7102a;
        border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:20px 24px;margin:16px 0}
      .bigvalue small{font-size:10px;letter-spacing:.1em;color:#b7102a;font-weight:800}
      .bigvalue b{font-size:34px;color:#000613;line-height:1.15;font-weight:800}
      .bigvalue span{color:#43474e;font-size:13px}
      .preszone{background:#f2f4f6;border:1px solid #c4c6cf;border-radius:12px;padding:16px 20px;margin:16px 0}

      /* ---- step 6: the approach stages ---- */
      .stagepick{display:grid;gap:12px;margin:16px 0}
      .stagecard{display:flex;gap:14px;align-items:flex-start;background:#fff;border:1px solid #c4c6cf;
        border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:16px 18px;cursor:pointer;transition:box-shadow .15s}
      .stagecard:hover{box-shadow:0 4px 12px rgba(0,6,19,.08)}
      .stagecard.on{border:2px solid #000613;padding:15px 17px}
      .stagecard input{width:auto!important;margin-top:3px;accent-color:#000613}
      .stagecard .sn{min-width:28px;height:28px;display:grid;place-items:center;border-radius:8px;
        background:#e6e8ea;color:#43474e;font-size:11px;font-weight:800;flex-shrink:0}
      .stagecard.on .sn{background:#000613;color:#fff}
      .stagecard b{display:block;font-size:13.5px;font-weight:700;color:#000613}
      .stagecard small{display:block;font-size:12px;color:#43474e;line-height:1.55;margin-top:3px}

      /* ---- include / skip control ---- */
      .skipbar{background:#fff;border:1px solid #c4c6cf;border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);
        padding:12px 16px;margin:12px 0;font-size:12.5px;font-weight:600;color:#000613}
      .skipbar.off{background:#f2f4f6;color:#74777f}

      /* ---- step 9: readiness and the section list ---- */
      .readiness{gap:14px;margin:0 0 16px}
      .readbar{height:8px;background:#e6e8ea;border-radius:999px}
      .readbar span{background:#000613}
      .readiness b{font-size:14px;font-weight:700;color:#000613}
      .check{display:flex;gap:10px;align-items:flex-start;background:#fff;border:1px solid #c4c6cf;
        border-radius:10px;padding:11px 14px;font-size:12.5px;box-shadow:0 1px 2px rgba(0,6,19,.05)}
      .check.ok{border-color:#000613;color:#000613;font-weight:600}
      .check.miss{color:#74777f}
      .seclist{display:grid;gap:10px}
      .secrow{display:flex;gap:12px;align-items:center;background:#fff;border:1px solid #c4c6cf;
        border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:12px 16px}
      .secrow.off{background:#f2f4f6;opacity:.65}
      .secrow input{accent-color:#000613}
      .secrow b{font-size:13.5px;font-weight:600;color:#000613}
      .secrow small{display:block;font-size:11.5px;color:#43474e;margin-top:2px}
      .grip{color:#74777f;cursor:grab}
      .optb{margin-left:8px}

      /* ---- modals ---- */
      .modal{border-radius:16px;padding:28px;box-shadow:0 24px 60px rgba(0,6,19,.28)}
      .modal h2{font-family:'Hanken Grotesk','Segoe UI',sans-serif;font-size:24px;font-weight:700;color:#000613}
      .modal p{font-size:14px;color:#43474e}
      .kicker{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#b7102a;font-weight:800}
      .modalactions{display:flex;justify-content:flex-end;gap:12px;margin-top:24px;flex-wrap:wrap}
      .modecard,.bucard,.typecard{background:#fff;border:1px solid #c4c6cf;border-radius:12px;
        box-shadow:0 1px 2px rgba(0,6,19,.05);padding:16px 18px;text-align:left;cursor:pointer}
      .modecard.on,.bucard.on,.typecard.on{border:2px solid #000613;padding:15px 17px;background:#fff}
      .chip{border:1px solid #c4c6cf;border-radius:999px;padding:7px 14px;background:#fff;
        font-size:12.5px;font-weight:600;color:#000613;cursor:pointer}
      .chip.on{background:#000613;color:#fff;border-color:#000613}
      .npn{background:#000613}
      .npreq{background:#fdeef0;color:#b7102a}

      /* ---- the approver's review, kept in the same idiom ---- */
      .revsec{border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:18px 20px}
      .revsec.change{border-color:#b7102a}
      .revnum{border-radius:8px;background:#e6e8ea;color:#43474e}
      .revnote{border:0;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:#c4c6cf;border-radius:0;padding:8px 0}
      .revnote:focus{outline:0;border-bottom-color:#000613}
      .apcard{background:#fff;border:1px solid #c4c6cf;border-radius:12px;box-shadow:0 1px 2px rgba(0,6,19,.05);padding:16px 18px}


      /* ==================================================================
         SELECTION STATES
         Picking something should be obvious at a glance: the box fills, the
         surface tints and the border darkens together. Previously only the
         border moved, so a chosen item barely read as chosen.
         ================================================================== */
      :root{ --d-tint:#eef1f7; --d-tint-line:#b9c4d6; }

      /* ---- the tick box, drawn rather than left to the browser ----
         The tick is a centred background image, not a ::before glyph: a
         pseudo-element on a replaced element like <input> is positioned
         inconsistently and can spill outside the box. A background cannot. */
      .tick input[type="checkbox"],
      .fbh input[type="checkbox"],
      .stagecard input[type="checkbox"],
      .secrow input[type="checkbox"]{
        appearance:none;-webkit-appearance:none;-moz-appearance:none;
        width:20px;height:20px;min-width:20px;max-width:20px;
        margin:0;padding:0;flex:0 0 20px;
        border:2px solid #c4c6cf;border-radius:6px;background-color:#fff;
        background-image:none;background-repeat:no-repeat;background-position:center center;background-size:13px 13px;
        display:inline-block;vertical-align:middle;box-sizing:border-box;
        cursor:pointer;font-size:0;line-height:0;color:transparent;
        transition:background-color .15s,border-color .15s}
      .tick input[type="checkbox"]:checked,
      .fbh input[type="checkbox"]:checked,
      .stagecard input[type="checkbox"]:checked,
      .secrow input[type="checkbox"]:checked{
        background-color:#000613;border-color:#000613;
        background-image:url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAxNiAxNic+PHBhdGggZmlsbD0nbm9uZScgc3Ryb2tlPScjZmZmZmZmJyBzdHJva2Utd2lkdGg9JzIuNScgc3Ryb2tlLWxpbmVjYXA9J3JvdW5kJyBzdHJva2UtbGluZWpvaW49J3JvdW5kJyBkPSdNMy41IDguNGwzLjEgMy4xTDEyLjUgNScvPjwvc3ZnPg==")}
      .tick input[type="checkbox"]:hover:not(:checked),
      .fbh input[type="checkbox"]:hover:not(:checked),
      .stagecard input[type="checkbox"]:hover:not(:checked),
      .secrow input[type="checkbox"]:hover:not(:checked){border-color:#74777f}
      .tick input[type="checkbox"]:focus-visible,
      .fbh input[type="checkbox"]:focus-visible,
      .stagecard input[type="checkbox"]:focus-visible,
      .secrow input[type="checkbox"]:focus-visible{outline:2px solid #000613;outline-offset:2px}
      .tick input[type="checkbox"]:disabled{background:#f2f4f6;border-color:#c4c6cf;cursor:not-allowed}

      /* keep the box on the first line of the label text */
      .tick,.stagecard{align-items:flex-start}
      .tick input[type="checkbox"],
      .stagecard input[type="checkbox"]{margin-top:1px}
      .fbh input[type="checkbox"],
      .secrow input[type="checkbox"]{margin-top:0}
      .fbh,.secrow{align-items:center}

      /* ---- the surface tints with it ---- */
      .tick,.stagecard,.svccard,.factblock,.engcard,.modecard,.bucard,.typecard{
        transition:background-color .15s,border-color .15s,box-shadow .15s,transform .15s}
      .tick.on,
      .stagecard.on,
      .svccard.on,
      .factblock.on,
      .engcard.on,
      .modecard.on,
      .bucard.on,
      .typecard.on{background:#eef1f7;border-color:#000613}
      /* a tinted card needs a slightly stronger inner rule to stay legible */
      .factblock.on .fbh{border-bottom-color:#b9c4d6}
      .tick.on b,.stagecard.on b,.svccard.on .svctitle,.factblock.on .fbh b{color:#000613}
      .tick.on small,.stagecard.on small{color:#43474e}

      /* selected rows lift very slightly, so a long list still scans */
      .tick.on,.stagecard.on{box-shadow:0 2px 8px rgba(0,6,19,.10)}

      /* ---- picked-up-by-click affordance on the whole row ---- */
      .tick,.stagecard,.svccard{user-select:none}
      .tick:hover:not(.on),.stagecard:hover:not(.on){background:#f2f4f6}

      /* ---- the section-list rows on Generate ---- */
      .secrow{transition:background-color .15s,opacity .15s}
      .secrow:has(input:checked){background:#eef1f7;border-color:#b9c4d6}
      .secrow.off{background:#f2f4f6}

      /* ---- chips used as toggles (service lines, practices in the chooser) ---- */
      .chip{transition:background-color .15s,border-color .15s,color .15s}
      .chip:hover:not(.on){background:#f2f4f6;border-color:#74777f}

      /* ==================================================================
         PANEL SURFACE AND CONTROL PADDING
         Each sub-step sits on its own white sheet rather than floating on the
         page tint, and every control has room around its value instead of
         sitting flush against the rule beneath it.
         ================================================================== */

      /* ---- the sheet ---- */
      .substep{background:#fff;border:1px solid #dfe3ea;border-radius:16px;
        box-shadow:0 1px 3px rgba(0,6,19,.06);padding:32px}
      .substep+.substep{margin-top:20px}
      .studio>section>.pp{background:#fff;border:1px solid #dfe3ea;border-radius:16px;
        box-shadow:0 1px 3px rgba(0,6,19,.06);padding:32px}
      /* a .pp already inside a .substep must not draw a second sheet */
      .substep .pp{background:none;border:0;border-radius:0;box-shadow:none;padding:0}

      /* Cards now sit on white, so a white fill would disappear. Give them a
         faint well instead and let the border do the separating; the drop
         shadow goes, because a shadow between two whites reads as grime. */
      .formrow,.storyq,.tick,.svccard,.factblock,.lineitem,.rowcard,.engcard,
      .stagecard,.secrow,.propcard2,.check,.minitable,.costbox,.bigvalue,.apcard,.revsec{
        background-color:#f8fAfc;box-shadow:none}
      .formrow,.storyq,.tick,.svccard,.factblock,.lineitem,.rowcard,.engcard,
      .stagecard,.secrow,.propcard2,.check{border-color:#dfe3ea}
      .tick:hover:not(.on),.stagecard:hover:not(.on){background-color:#f2f5f9}
      .svccard:hover{box-shadow:0 4px 12px rgba(0,6,19,.07)}
      /* selection still has to read clearly against the well */
      .tick.on,.stagecard.on,.svccard.on,.factblock.on,.engcard.on,
      .modecard.on,.bucard.on,.typecard.on{background-color:#e8eef8;border-color:#000613}
      .secrow:has(input:checked){background-color:#e8eef8}

      /* ---- room around every value ---- */
      .formrow input,.formrow select,.formrow textarea,
      .storyq textarea,.unitrow input,.unitrow select,.ratebar input,.ratebar select{
        padding:10px 14px;border-radius:8px 8px 0 0;background-color:#fff}
      .formrow input:focus,.formrow select:focus,.formrow textarea:focus,
      .storyq textarea:focus,.unitrow input:focus,.unitrow select:focus{background-color:#fff}
      .rowcard input,.rowcard select{padding:9px 12px;border-radius:8px 8px 0 0;background-color:#fff}
      .lineitem input{padding:9px 12px;border-radius:8px 8px 0 0;background-color:#fff}
      .amtin{padding:9px 12px;border-radius:8px 8px 0 0;background-color:#fff}

      /* ---- dropdowns: a real chevron, and space so the value clears it ---- */
      .formrow select,.rowcard select,.unitrow select,.ratebar select,.libpick select,.modal select{
        appearance:none;-webkit-appearance:none;-moz-appearance:none;
        background-image:url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAxNiAxNic+PHBhdGggZmlsbD0nbm9uZScgc3Ryb2tlPScjNDM0NzRlJyBzdHJva2Utd2lkdGg9JzEuOCcgc3Ryb2tlLWxpbmVjYXA9J3JvdW5kJyBzdHJva2UtbGluZWpvaW49J3JvdW5kJyBkPSdNNCA2LjVMOCAxMC41TDEyIDYuNScvPjwvc3ZnPg==");
        background-repeat:no-repeat;background-size:15px 15px;
        background-position:right center;background-position:right 12px center;
        padding-right:38px;cursor:pointer}
      .formrow select::-ms-expand,.rowcard select::-ms-expand{display:none}
      /* honoured by Firefox; harmless elsewhere, where the list is drawn by the OS */
      .formrow select option,.rowcard select option,.unitrow select option,
      .ratebar select option,.libpick select option,.modal select option{
        padding:10px 14px;background-color:#fff;color:#000613;font-weight:500}
      .libpick select,.libpick input{padding:11px 14px}
      .libpick select{padding-right:38px}

      /* placeholders sit in from the edge too */
      .formrow input::placeholder,.formrow textarea::placeholder,.storyq textarea::placeholder{opacity:.85}

      @media(max-width:1000px){
        .substep,.studio>section>.pp{padding:20px}
      }
      /* ==================================================================
         SOLUTIONS BROWSE — areas, focus areas, solution and case cards.
         The library is a drill-down, so each level needs a card that reads
         as a destination rather than a form field.
         ================================================================== */
      .pagekicker{font-size:11px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:#b7102a;margin-bottom:8px}
      .crumb{display:flex;gap:8px;align-items:center;flex-wrap:wrap;font-size:12px;color:#43474e;margin-bottom:16px}
      .crumb button{border:0;background:none;padding:0;color:#000613;font-weight:700;cursor:pointer;font-size:12px}
      .crumb button:hover{text-decoration:underline}
      .crumb .sep{color:#74777f}
      .grid2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}

      .domain,.subcard,.solcard{position:relative;display:block;width:100%;text-align:left;background:#fff;
        border:1px solid #dfe3ea;border-radius:16px;padding:22px 24px;cursor:pointer;
        box-shadow:0 1px 3px rgba(0,6,19,.06);transition:box-shadow .15s,border-color .15s,transform .15s}
      .domain:hover,.subcard:hover,.solcard:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(0,6,19,.10);border-color:#000613}
      .domain h3,.subcard h3,.solcard h3{font-family:'Hanken Grotesk','Segoe UI',sans-serif;font-size:18px;font-weight:700;
        color:#000613;margin:0 0 6px;line-height:1.3}
      .domain p,.subcard p,.solcard p{font-size:13px;color:#43474e;line-height:1.6;margin:0}

      .domain .icon{display:inline-grid;place-items:center;min-width:44px;height:32px;padding:0 10px;border-radius:8px;
        background:#e8eef8;color:#000613;font-size:11px;font-weight:900;letter-spacing:.06em;margin-bottom:14px}
      .domain .promise{margin-top:12px;color:#254966;font-weight:700;font-size:12.5px;line-height:1.5}
      .domain .meta{margin-top:14px;padding-top:12px;border-top:1px solid #dfe3ea;font-size:11.5px;font-weight:600;color:#74777f}

      .subcard .tagline{color:#b7102a;font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px}
      .pills{display:flex;gap:6px;flex-wrap:wrap;margin-top:14px}
      .pill{font-size:11px;font-weight:700;background:#f2f4f6;color:#43474e;border:1px solid #dfe3ea;border-radius:999px;padding:4px 11px}

      .solcard .fulltag{position:absolute;right:16px;top:16px;font-size:9.5px;font-weight:800;letter-spacing:.06em;
        text-transform:uppercase;color:#187159;background:#eaf7f0;border-radius:999px;padding:3px 9px}
      .solcard .solmeta{display:flex;gap:6px;flex-wrap:wrap;margin-top:14px}
      .solcard .casetop{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:8px}
      .solcard .caseid{font-size:10px;font-weight:800;letter-spacing:.06em;color:#b7102a}

      /* the Solutions / Case Studies switch inside a focus area */
      .seg{display:inline-flex;padding:4px;background:#e6e8ea;border-radius:12px;margin-bottom:20px;gap:4px}
      .seg button{border:0;background:transparent;padding:9px 16px;border-radius:9px;color:#43474e;
        font-weight:700;font-size:13px;cursor:pointer;transition:background-color .15s,color .15s}
      .seg button.on{background:#fff;color:#000613;box-shadow:0 1px 3px rgba(0,6,19,.10)}
      .seg button:hover:not(.on){color:#000613}

      @media(max-width:900px){.grid2{grid-template-columns:1fr}}
      /* ---- what a case study card leads with ----
         The result line and the measured outcomes are the reason anyone reads
         a case. They were rendering as plain text runs - .metric only existed
         in the stylesheet for the downloadable brief, never in the app. */
      .caseresult{font-size:14px;font-weight:600;color:#000613;line-height:1.55;margin-top:10px;
        padding-left:12px;border-left:3px solid #b7102a}
      .metrics{display:flex;flex-wrap:wrap;gap:8px;margin-top:14px}
      .metric{display:inline-flex;align-items:center;background:#e8eef8;border:1px solid #c8d4e8;
        border-radius:8px;padding:7px 12px;font-size:12.5px;font-weight:700;color:#000613;line-height:1.35}
      /* a metric that reads as a movement gets the accent */
      .metric.shift{background:#fdeef0;border-color:#f3c9cf;color:#8f0d21}

      /* ---- the bar at the top of a detail page ---- */
      .detailnav{display:flex;align-items:center;gap:16px;flex-wrap:wrap;margin-bottom:20px}
      .detailback{display:inline-flex;align-items:center;gap:6px;background:#fff;border:1px solid #dfe3ea;
        border-radius:10px;padding:8px 16px;font-size:13px;font-weight:700;color:#000613;cursor:pointer;
        box-shadow:0 1px 3px rgba(0,6,19,.06);transition:background-color .15s,border-color .15s}
      .detailback:hover{background:#f2f4f6;border-color:#000613}
      .detailback .material-symbols-outlined{font-size:18px}
      .detailnav .crumb{margin:0;flex:1;min-width:0}
      /* the last crumb is the page itself - let it shrink rather than wrap oddly */
      .detailnav .crumb>span:last-child{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#43474e}
      @media(max-width:700px){.detailnav .crumb{width:100%}}
      /* ---- the library filter bar: search on top, options beneath ---- */
      .filterbar{background:#fff;border:1px solid #dfe3ea;border-radius:16px;box-shadow:0 1px 3px rgba(0,6,19,.06);
        padding:20px 24px;margin-bottom:32px}
      .filtersearch{position:relative;margin-bottom:16px}
      .filtersearch input{width:100%;padding:14px 44px 14px 18px;border:1px solid #dfe3ea;border-radius:12px;
        background:#f8fafc;font-size:15px;font-weight:500;color:#000613}
      .filtersearch input:focus{outline:0;border-color:#000613;background:#fff;box-shadow:0 0 0 3px rgba(0,6,19,.06)}
      .filtersearch input::placeholder{color:#74777f;font-weight:400}
      .filtersearch::after{content:'\f002';font-family:'Font Awesome 6 Free';font-weight:900;position:absolute;
        right:18px;top:50%;transform:translateY(-50%);color:#74777f;font-size:14px;pointer-events:none}
      .filterrow{display:grid;grid-template-columns:repeat(4,minmax(0,1fr)) auto;gap:14px;align-items:end}
      .filterrow .ff label{display:block;font-size:11px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;
        color:#43474e;margin-bottom:6px}
      .filterrow .ff select{width:100%;padding:11px 38px 11px 14px;border:1px solid #dfe3ea;border-radius:10px;
        background-color:#fff;font-size:14px;font-weight:500;color:#000613;cursor:pointer}
      .filterrow .ff select:focus{outline:0;border-color:#000613}
      .filterclear{white-space:nowrap;padding:11px 20px}
      .filterchips{display:flex;flex-wrap:wrap;gap:8px;margin-top:16px;padding-top:16px;border-top:1px solid #dfe3ea}
      .activechip{font-size:12px;font-weight:700;background:#e8eef8;color:#000613;border:1px solid #c8d4e8;
        border-radius:999px;padding:5px 12px}
      /* three options instead of four, on the capabilities screen */
      .filterrow-3{grid-template-columns:repeat(3,minmax(0,1fr)) auto}
      .filtercount{font-size:12.5px;font-weight:600;color:#43474e;margin-right:auto}
      .filterchips{align-items:center}
      @media(max-width:900px){.filterrow,.filterrow-3{grid-template-columns:1fr 1fr}.filterclear{grid-column:1/-1}}
      @media(max-width:900px){
        .tickgrid,.engrid{grid-template-columns:1fr}
        .ratebar{grid-template-columns:repeat(2,minmax(0,1fr))}
      }
      @media(max-width:1000px){
        .storygrid{grid-template-columns:1fr}
        .studio{grid-template-columns:1fr;gap:24px}
        .studionav{flex-direction:row;overflow:auto;gap:8px}
        .studionav button{min-width:220px;border-right:0}
        .studiotop h1{font-size:28px}
        .formgrid{grid-template-columns:1fr}
      }
    `;
    head.appendChild(styleTag);
  }
};

// Run injection immediately
injectResources();

// ===== AUTH CONTEXT & API =====
// The compiled-in address can be overridden at runtime from Proposals -> Settings,
// so a deployed build can be pointed at another backend without a rebuild.
// Same-origin in a deployed build (the web server proxies /api through to the
// API service); localhost while developing. REACT_APP_API_URL overrides both.
const isLocalHost = () => typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
const DEFAULT_API_BASE = process.env.REACT_APP_API_URL ||
  (isLocalHost() ? 'http://localhost:5000/api' : '/api');
const API_BASE_KEY = 'solidpro-api-base';
const validApiBase = (v) => {
  if (!v) return '';
  // a same-origin path such as /api is valid and is what a deployed build uses
  if (v.startsWith('/')) return v.replace(/\/$/, '');
  try {
    const u = new URL(v);
    return (u.protocol === 'http:' || u.protocol === 'https:') ? v.replace(/\/$/, '') : '';
  } catch (e) { return ''; }
};
const storedApiBase = () => {
  try {
    const raw = localStorage.getItem(API_BASE_KEY) || '';
    if (raw && !validApiBase(raw)) {
      // Drop it rather than leaving the app pointed at an unusable address.
      console.warn('Ignoring an invalid stored API base:', raw);
      localStorage.removeItem(API_BASE_KEY);
      return '';
    }
    return validApiBase(raw);
  } catch (e) { return ''; }
};
const setApiBase = (v) => {
  const clean = validApiBase(v);
  try { clean ? localStorage.setItem(API_BASE_KEY, clean) : localStorage.removeItem(API_BASE_KEY); } catch (e) {}
  api.defaults.baseURL = clean || DEFAULT_API_BASE;
  return clean;
};

const api = axios.create({ 
  baseURL: storedApiBase() || DEFAULT_API_BASE,
  timeout: 10000
});

api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error.response?.status, error.response?.data);
    // A 401 here always means the stored token is missing/invalid/expired
    // (see authenticate() in server.js - it never uses 401 for anything
    // else). The backend is fine in that case; the fix is to sign in again,
    // not to chase a "server is down" message. Bounce straight to login
    // with a clean slate, unless we're already there (e.g. a bad-password
    // response from /auth/login itself, which also comes back as 401).
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (e) {
        console.error('Failed to parse user data:', e);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(user);
    return user;
  };

  const register = async (email, password) => {
    const res = await api.post('/auth/register', { email, password });
    const { token, user } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/library" />;
  return children;
};

// ===== CONSTANTS =====
const DUR_UNITS = ['days', 'weeks', 'months', 'years'];
const START_MODES = [
  'As soon as the order is placed',
  'Within a set time of the purchase order',
  'On a specific date',
  'After a preceding phase completes',
  'To be agreed at kick-off',
];
const EFFORT_TYPES = [
  ['People', 'engineers, specialists or consultants assigned', ['people', 'engineers', 'specialists', 'analysts']],
  ['People-time', 'how much of their time the work needs', ['person-days', 'person-weeks', 'person-months', 'engineer-months']],
  ['On-site presence', 'days our team spends at your plant or office', ['site days', 'site visits', 'shifts observed', 'weeks on site']],
  ['Workshops & sessions', 'facilitated sessions with your team', ['workshops', 'interviews', 'training sessions', 'review meetings']],
  ['Coverage', 'how much of the operation is in scope', ['lines', 'cells', 'machines', 'plants', 'products', 'suppliers', 'processes', 'drawings', 'parts']],
  ['Data & samples', 'what we need from you to do the work', ['months of data', 'sample parts', 'drawings', 'data sets', 'system extracts']],
  ['Space & facilities', 'what you provide on site', ['workstations', 'seats', 'meeting room days', 'network logins']],
  ['Equipment & instruments', 'measuring or test equipment used', ['instruments', 'loggers', 'licences', 'test rigs']],
  ['Software & licences', 'tools required for the engagement', ['licences', 'user seats', 'environments']],
  ['Travel', 'journeys included in the price', ['trips', 'travel days']],
];

const VALUE_LIB = [
  ['Money', 'Lower cost per unit produced', 'Same output, less spent making it.'],
  ['Money', 'Lower cost per tonne, litre or batch', 'For process and continuous operations.'],
  ['Money', 'Lower energy and utility bills', 'Usually the fastest saving to prove.'],
  ['Money', 'Less material wasted, scrapped or reworked', 'Yield and scrap go straight to margin.'],
  ['Money', 'A capital purchase avoided or deferred', 'The strongest number in most proposals.'],
  ['Money', 'Lower cost of buying the same part or service', 'Sourcing, localisation and supplier work.'],
  ['Money', 'Working capital released from inventory', 'Cash freed without borrowing it.'],
  ['Money', 'Lower cost of poor quality', 'Rejects, returns, warranty and recovery cost.'],
  ['Capacity & speed', 'More output from equipment you already own', 'Capacity without capital.'],
  ['Capacity & speed', 'Shorter time from order to despatch', 'Customers feel this one directly.'],
  ['Capacity & speed', 'Shorter time from idea to production', 'Faster launch, earlier revenue.'],
  ['Capacity & speed', 'Faster design cycles and quicker quotations', 'More bids answered with the same team.'],
  ['Capacity & speed', 'Bottlenecks removed so the line runs to plan', 'Predictability, not just speed.'],
  ['Capacity & speed', 'Less time lost to changeovers and breakdowns', 'Recovering hours already paid for.'],
  ['Certainty', 'A decision made on measured evidence', 'Replaces opinion in the boardroom.'],
  ['Certainty', 'Knowing before you spend whether it is justified', 'Avoids the wrong investment entirely.'],
  ['Certainty', 'Fewer surprises late, when changes cost most', 'Problems found on screen, not on site.'],
  ['Certainty', 'A plan the finance team can approve', 'Costed, phased and defensible.'],
  ['Certainty', 'One accountable partner, not several vendors', 'No gaps between suppliers to fall into.'],
  ['Quality & risk', 'Fewer defects reaching your customer', 'Protects the relationship, not just the cost.'],
  ['Quality & risk', 'Problems traced to the real cause', 'Stops the same failure returning.'],
  ['Quality & risk', 'Design proven against its duty before build', 'Analysis before metal is cut.'],
  ['Quality & risk', 'Safer, more compliant equipment and processes', 'Reduces exposure as well as incidents.'],
  ['Quality & risk', 'Less dependence on one or two key people', 'Knowledge in the system, not in heads.'],
  ['Compliance & trust', 'Evidence that stands up to an audit', 'Locked baseline and stated assumptions.'],
  ['Compliance & trust', 'Customer or regulatory requirements demonstrably met', 'Provable, not asserted.'],
  ['Compliance & trust', 'Documentation customers ask for during qualification', 'Opens doors with large OEMs.'],
  ['Compliance & trust', 'Emissions and ESG numbers you can publish', 'Survives assurance and scrutiny.'],
  ['Your team', 'Your people trained to run it without us', 'Capability stays after we leave.'],
  ['Your team', 'Knowledge written down, not held in heads', 'Survives resignations.'],
  ['Your team', 'Engineering capacity added without headcount', 'Scale up and down without hiring risk.'],
  ['Your team', 'Your engineers freed from repetitive work', 'Senior people back on real problems.'],
  ['Growth', 'A stronger technical case when bidding', 'Wins work, not just saves cost.'],
  ['Growth', 'A product ready for a new market requirement', 'Access you did not have before.'],
  ['Growth', 'Faster response to enquiries and RFQs', 'More quotes out, more orders in.'],
];

const WHY_LIB = [
  ['How we work', 'We deliver what we recommend', 'The team that writes the recommendation engineers it and measures it. Most advisors stop at the report; we do not.'],
  ['How we work', 'Evidence before investment', 'We test the assumption before money is committed - and we say plainly when an investment is not yet justified.'],
  ['How we work', 'We start small and prove value', 'Engagements can open with a short diagnostic so you see real insight before any major commitment.'],
  ['How we work', 'We work on your floor, not from a template', 'Findings come from your data, your line and your people, not from a benchmark library.'],
  ['How we work', 'You can stop at any stage', 'Each stage has a defined output and a decision point. You continue only where the evidence says it is worth it.'],
  ['Commercial', 'We speak finance, not only engineering', 'Every recommendation carries cost, effort and payback, so your CFO can act on it.'],
  ['Commercial', 'Fixed scope, fixed price where it matters', 'Where the work can be defined, it is priced as a fixed fee so there is no open-ended exposure.'],
  ['Commercial', 'Plans that pay for themselves', 'The cheapest improvements are often net-negative cost, so the programme funds its later stages.'],
  ['Commercial', 'No vendor bias', 'We are not reselling equipment or software, so the recommendation follows the evidence.'],
  ['Capability', '25+ years of engineering depth', 'A 200+ engineer team across India, the US, UK and Ireland, with a record of cutting real cost from real operations.'],
  ['Capability', 'One partner across disciplines', 'Mechanical, electronics, embedded, software, manufacturing, digital and sustainability under one execution model.'],
  ['Capability', 'Engineers who have run production', 'Our people have operated the processes they are improving, not only studied them.'],
  ['Capability', 'Global delivery, local presence', 'Onsite where the work needs hands, offshore where it does not - priced accordingly.'],
  ['Capability', 'Regulated-industry experience', 'Medical, pharma, automotive and process environments where documentation and traceability are not optional.'],
  ['Proof & governance', 'Numbers that survive audit', 'Locked baselines and stated assumptions mean every claimed result has an evidence trail.'],
  ['Proof & governance', 'Named team, named owners', 'You know who is doing the work, their experience, and how much of their time you have.'],
  ['Proof & governance', 'Weekly visibility, no surprises', 'Written status every week, a live risk log, and escalation paths agreed before they are needed.'],
  ['Proof & governance', 'We measure whether it worked', 'Results are verified against the baseline after implementation, not asserted at handover.'],
  ['Aftercare', 'Capability that stays with you', 'Documentation, training and handover so your team runs it without us.'],
  ['Aftercare', 'We are still here afterwards', 'A defined support window after handover, and a long-term relationship rather than a one-off engagement.'],
];

const SCOPE_LIB = [
  ['Understand the current situation', [
    'Kick-off meeting and confirm exactly what is in scope',
    'Meet the people who run the process day to day',
    'Observe the site, shopfloor or current way of working',
    'Collect and review the available data, drawings and documents',
    'Agree the current position and starting numbers in writing',
    'Compare where you are today against the target',
    'Rank the improvement opportunities by value and effort',
    'Agree how often we report, and who to escalate to',
  ]],
  ['Measure and find the real problem', [
    'Measure how the operation actually performs today',
    'Measure work time and establish realistic standard times',
    'Find what is genuinely limiting output, cost or quality',
    'Separate the symptoms from the underlying cause',
    'Put a value in money or time on the size of the opportunity',
    'Report honestly where data is missing or unreliable',
  ]],
  ['Engineering & design work', [
    'Turn your requirement into a specification that can be tested',
    'Develop and compare two or three practical design options',
    'Recommend one option, with the reasoning written down',
    'Create the detailed design and 3D models',
    'Prepare manufacturing drawings with tolerances (GD&T)',
    'Prepare the bill of materials (BOM) ready for purchasing',
    'Review the design for easier manufacture and assembly (DFM/DFA)',
    'Support the prototype or pilot build',
    'Update the design based on review or test findings',
  ]],
  ['Prove the design before it is built', [
    'Check the design against the load, pressure or duty it must survive',
    'Run simulation for strength, heat, flow, motion or vibration (FEA/CFD)',
    'Identify weak points and recommend the design change needed',
    'Report the analysis with every assumption stated',
    'Support physical testing and compare it against the predictions',
  ]],
  ['Factory improvement (TAKTIX)', [
    'Study how material moves through the factory and where it waits',
    'Study the layout and how effectively the space is used',
    'Find bottlenecks, capacity limits and line imbalance',
    'Build a factory model that matches your current performance',
    'Test improvement scenarios virtually before changing anything physical',
    'Compare every option on the same basis, including doing nothing',
    'Check whether the proposed equipment investment (CAPEX) is really needed',
    'Produce a decision summary your leadership can act on',
  ]],
  ['Automation & special-purpose machines', [
    'Assess whether automation is justified for this operation',
    'Define what the machine or cell must do, and at what rate',
    'Develop the concept and layout of the machine or cell',
    'Detailed mechanical, electrical and control design',
    'Support build, assembly and commissioning',
    'Prove the machine meets the agreed rate and quality',
  ]],
  ['Digital systems & automation', [
    'Map how the process works today and where it breaks down',
    'Define how the solution should work and what data it needs',
    'Convert your business and engineering rules into system logic',
    'Build the application, automation or integration',
    'Connect systems and move or clean the required data',
    'Test with your users and fix issues before go-live',
    'Deploy the solution and support the initial go-live period',
    'Set up the dashboards and reports your team will use',
    'Train your administrators so your team can change it themselves',
  ]],
  ['Sustainability, energy & carbon', [
    'Agree which operations, period and data are included',
    'Build the Scope 1, 2 and material Scope 3 starting position',
    'Identify reduction actions and rank them by cost and impact (MACC)',
    'Set targets and build a phased action plan with owners and budget',
    'Map the plan to available incentives, credits and green finance',
    'Engage suppliers and collect the required evidence',
    'Engineer and implement the priority reductions',
    'Track, measure and verify progress against the agreed baseline',
    'Prepare the evidence your auditor or assurance provider will accept',
  ]],
  ['Suppliers & supply chain', [
    'Bring supplier, spend and risk information into one view',
    'Segment suppliers so effort goes where it matters most',
    'Assess selected suppliers against the requirement, with evidence',
    'Run workshops with suppliers who need help to meet the requirement',
    'Engineer improvements inside the supplier\'s own process',
    'Connect the findings to your qualification and sourcing decisions',
    'Set up the review cycle that keeps it going after we leave',
  ]],
  ['Quality & regulatory work', [
    'Review where the process can fail, and facilitate it with your team (PFMEA)',
    'Define the quality checks and the control plan',
    'Find the real root cause and define corrective action',
    'Plan and carry out verification and validation (V&V)',
    'Prepare the required documentation and evidence package',
    'Review readiness before an audit, assessment or customer qualification',
    'Check the design or process against the applicable standards',
  ]],
  ['Documentation & technical content', [
    'Review the technical content you publish today',
    'Restructure content so one change updates everywhere',
    'Produce illustrations, manuals and instructions for use (IFU)',
    'Produce labelling and packaging artwork to market requirements',
    'Check content against the requirements of each destination market',
    'Publish in the print and digital formats you need',
  ]],
  ['Project delivery & handover', [
    'Define the SolidPro team and clear work packages',
    'Run a weekly progress review with actions and owners',
    'Run a monthly leadership or steering review where needed',
    'Maintain a simple, live risk and issue log',
    'Document the methods, assumptions and results as we go',
    'Train and hand over to your team',
    'Provide the agreed support period after completion',
  ]],
];

const DELIV_LIB = [
  ['Reports & findings', [
    'Current-state assessment report',
    'Findings and recommendations report',
    'Agreed baseline report, with data sources and assumptions stated',
    'Gap analysis against the agreed requirement',
    'Prioritised improvement list with value and effort',
    'Root-cause investigation report',
    'Comparison against similar operations',
    'Photographic and observation record from site',
  ]],
  ['Business case & decisions', [
    'Business case showing cost, benefit and payback',
    'Comparison of the options on a common basis',
    'Leadership decision summary',
    'Investment recommendation with assumptions stated',
    'Cost model showing what changes if the assumptions change',
    'Make-versus-buy assessment',
    'Board or steering-committee presentation pack',
  ]],
  ['Engineering outputs', [
    '3D models and assemblies',
    'Manufacturing drawings with tolerances (GD&T)',
    'General arrangement drawings',
    'Bill of materials (BOM) ready for purchasing',
    'Material and component specifications',
    'Design calculations with assumptions stated',
    'Concept options pack with our recommendation',
    'Design for manufacture and assembly review (DFM/DFA)',
    'Design-change and release documents',
    'Technical data pack for your suppliers',
  ]],
  ['Analysis & simulation outputs', [
    'Strength / stress analysis report (FEA)',
    'Heat or flow analysis report (CFD)',
    'Fatigue or durability assessment',
    'Tolerance stack-up analysis',
    'Validated model of the current operation',
    'Comparison of improvement scenarios',
    'Record showing the model matches actual performance',
    'Comparison of test results against the predictions',
  ]],
  ['Factory / operational outputs', [
    'Factory layout drawings',
    'Material flow and movement study',
    'Work-time study and standard times',
    'Capacity and line-balance analysis',
    'Bottleneck and constraint map',
    'Manpower and shift plan',
    'Standard work and operator instructions',
    'Changeover reduction plan',
    'Space utilisation study',
    'Internal logistics and buffer concept',
  ]],
  ['Digital outputs', [
    'Solution design / architecture document',
    'Data model and dictionary',
    'Configured and tested application',
    'System interfaces and data mapping',
    'Data migration record showing everything arrived correctly',
    'Test scripts and test results',
    'User acceptance sign-off record',
    'Dashboards and standard reports',
    'Deployment and cutover plan',
    'Administration and configuration guide',
    'Source code and build files where included',
  ]],
  ['Sustainability outputs', [
    'Emissions or energy baseline with the boundary defined',
    'Reduction actions ranked by cost and impact (MACC)',
    'Phased reduction plan with owners and budget',
    'Funding and incentive map',
    'Measurement and verification plan',
    'Savings verification report against the baseline',
    'Reporting-ready evidence pack',
    'Supplier sustainability assessment summary',
  ]],
  ['Quality & regulatory outputs', [
    'Process failure-mode review (PFMEA)',
    'Control plan and inspection definition',
    'Verification and validation plan (V&V)',
    'Verification and validation report',
    'Corrective and preventive action record (CAPA)',
    'Technical documentation file',
    'Audit readiness assessment',
    'Compliance checklist against the applicable standard',
    'Calibration and measurement system record',
  ]],
  ['Documentation & content outputs', [
    'Technical manual or instructions for use (IFU)',
    'Illustrated parts and service documentation',
    'Labelling and packaging artwork',
    'Content structure and reuse model',
    'Market-by-market compliance check record',
    'Published print and digital output files',
  ]],
  ['Project & handover outputs', [
    'Project plan with milestones and owners',
    'Weekly written progress updates',
    'Risk and issue log maintained through the engagement',
    'Assumption log',
    'Change request log and approved changes',
    'Minutes and decisions from stage reviews',
    'Final handover pack with supporting evidence',
    'Training material and training session',
    'Post-completion review report',
    'Lessons-learned summary',
  ]],
];

const OUTSCOPE_LIB = [
  'Buying, supplying or commissioning any equipment',
  'Physically implementing the recommended changes',
  'Software licences, subscriptions and cloud charges',
  'Civil, electrical or mechanical site works',
  'Statutory approvals, permits and third-party certification',
  'Third-party testing or laboratory charges',
  'Travel, boarding and lodging (charged at actuals)',
  'Running the operation or providing manpower after handover',
  'Creating data where none exists today',
  'Work at any site or on any line not named in this proposal',
  'Anything not expressly listed in the scope of work',
];

const ASSUMP_LIB = [
  'The past data we need is available and usable',
  'Your nominated people are available as per the agreed plan',
  'We have access to the site and systems during working hours',
  'Existing drawings and documents are shared in editable format',
  'Product mix, volume and process stay broadly the same during the engagement',
  'Reviews and approvals are completed within five working days',
  'Work is carried out during normal business hours unless agreed otherwise',
  'Workspace and network access are provided for on-site personnel',
  'Existing equipment is working normally during the study',
  'Any third party involved responds within the agreed timeframe',
];

const DEP_LIB = [
  'One named contact on your side who can take decisions',
  'Access to the plant, systems or data as required',
  'The input documents listed in the annexure, given to us on time',
  'Process owners and experts available to meet us',
  'Approval of the baseline before the next stage begins',
  'Licences for any of your software or platforms we need to use',
  'Safety induction and site access arranged for our team',
  'Timely feedback on draft deliverables',
];

const RISK_LIB = [
  ['Input data is incomplete or unreliable', 'High', 'Medium', 'Baseline validated with your team before use; any gap recorded as a stated assumption rather than filled in.'],
  ['Key people are unavailable during the study', 'Medium', 'Medium', 'Interview schedule fixed at kick-off; escalated to the sponsor if slippage exceeds one week.'],
  ['The work grows beyond what was agreed', 'High', 'Medium', 'Formal change control - anything additional is priced and approved in writing before work starts.'],
  ['Site access is restricted or production is stopped', 'Medium', 'Low', 'Alternative observation windows agreed in advance; remote data collection used where possible.'],
  ['Approvals are delayed and the plan slips', 'Medium', 'Medium', 'Five-working-day review commitment agreed in the plan; delays logged and the timeline re-baselined.'],
  ['Key people change on either side during the engagement', 'Medium', 'Low', 'Everything documented as we go, so knowledge is not held by individuals.'],
  ['A third-party vendor delays the work', 'Medium', 'Medium', 'Dependencies identified at planning with named owners and dates.'],
  ['Technology or platform changes mid-engagement', 'Medium', 'Low', 'Architecture reviewed at each stage gate before further build.'],
  ['Product mix or volume changes during the study', 'Medium', 'Medium', 'Scenarios tested across a volume range rather than a single point.'],
  ['Measured results differ from the model', 'Medium', 'Low', 'Model validated against actual output before use, and re-validated after any change.'],
  ['Safety incident during site work', 'High', 'Low', 'Site induction, PPE and permit-to-work followed; any incident reported immediately.'],
  ['Confidential information handled incorrectly', 'High', 'Low', 'NDA in force, least-privilege access, encrypted transfer and access revoked on release.'],
];

const METRIC_LIB = [
  ['Output & capacity', ['Units produced per day', 'Units produced per shift', 'Output as a percentage of line capacity', 'Overall equipment effectiveness (OEE)', 'Machine availability', 'Takt time achieved']],
  ['Time', ['Order-to-despatch lead time', 'Design cycle time', 'Quotation turnaround time', 'Changeover time', 'Downtime hours per month', 'Time to first article']],
  ['Cost', ['Cost per unit produced', 'Cost per tonne or per batch', 'Energy cost per unit of output', 'Material cost per unit', 'Cost of poor quality', 'Capital expenditure avoided']],
  ['Quality', ['First-pass yield', 'Scrap rate', 'Rework hours per month', 'Customer complaints per month', 'Defects per million opportunities', 'Audit findings closed']],
  ['Inventory & flow', ['Work in progress value', 'Inventory turns', 'Material travel distance per batch', 'Buffer level at the constraint']],
  ['Sustainability', ['Scope 1 and 2 emissions (tCO2e)', 'Emissions intensity per unit', 'Energy consumed per unit', 'Water consumed per unit', 'Waste diverted from landfill', 'Verified reduction against baseline']],
  ['Delivery & compliance', ['On-time delivery percentage', 'Milestones met against the baseline plan', 'Documents released on schedule', 'Regulatory submissions accepted first time']],
];

const COMPANY_FACTS = {
  intro: { label: 'About SolidPro - the four lines', always: true,
    text: 'SolidPro is an engineering outcomes partner. Mechanical, electronics, embedded, software, manufacturing, digital transformation and sustainability capability sit under one execution model.\nWe are engineers first, so the team that writes a recommendation is the team that delivers it and measures whether it worked.\nWe work across the full lifecycle - from concept and design through industrialisation, digital enablement and sustainability compliance.\nWhat we build stays with you: documentation, training and handover are part of every engagement, not an afterthought.' },
  presence: { label: 'Where we are',
    items: [['🇮🇳', 'India', 'Chennai (headquarters) · engineering delivery centres'],
            ['🇺🇸', 'United States', 'Sandy, Utah'],
            ['🇬🇧', 'United Kingdom', ''],
            ['🇮🇪', 'Ireland', '']] },
  offices: { label: 'Office locations',
    items: [['Chennai, India', 'Headquarters and principal delivery centre'],
            ['Sandy, Utah, USA', 'North America operations'],
            ['United Kingdom', 'UK and European customers'],
            ['Ireland', 'European delivery and life-sciences customers']] },
  team: { label: 'Team',
    items: [['200+', 'engineers across four countries'],
            ['25+', 'years of engineering depth'],
            ['12', 'business units under one delivery model']] },
  facilities: { label: 'Facilities & tooling',
    items: [['Engineering delivery centres', 'CAD, CAE, embedded and software engineering under one roof'],
            ['Simulation and analysis capability', 'Structural, thermal, flow and discrete-event simulation'],
            ['Factory performance lab (TAKTIX)', 'Model and validate factory decisions before capital is committed'],
            ['Sustainability tooling', 'SimaPro, GaBi/Sphera and OpenLCA licences in-house, with the ecoinvent database'],
            ['Certified LCA practitioners', 'In-house, for ISO 14040/14044 and ISO 14067 work']] },
  clients: { label: 'Who we work with',
    items: [['Automotive & mobility', 'Tier-1 and OEM programmes'],
            ['Industrial equipment & machinery', 'Product development through industrialisation'],
            ['Healthcare, medtech & pharma', 'Regulated device and pharma engineering'],
            ['Process & plant', 'Plant design, safety and utilities'],
            ['Energy & utilities', 'Structure engineering and bid support'],
            ['Consumer products & appliances', 'Design through manufacture']] },
  certs: { label: 'Certifications & standards', note: 'CONFIRM against the current certificate register before sending.',
    items: [['ISO 9001:2015', 'Quality management'],
            ['ISO 27001', 'Information security'],
            ['ISO 13485', 'Medical devices'],
            ['IATF 16949', 'Automotive quality'],
            ['ISO 14001', 'Environmental management']] },
  specialities: { label: 'What we are known for',
    items: [['Engineering-led business transformation', 'BTOS-6D - know which problem to solve before spending on solving it'],
            ['Factory performance engineering', 'TAKTIX - measure, model and test factory decisions virtually first'],
            ['Sustainability transformation', 'NetZeroHub - measure, reduce, verify and disclose'],
            ['Healthcare & lifesciences engineering', 'HeLiXA - regulated device and pharma programmes'],
            ['Digital transformation', 'From CPQ and CAD automation through to data, cloud and managed services'],
            ['Supply chain & sourcing engineering', 'Supplier qualification, localisation and engineered supplier improvement']] },
  achievements: { label: 'Track record', note: 'CONFIRM each figure before it appears in a customer document.',
    items: [['Cost taken out of live operations', 'Improvements engineered and verified against a locked baseline, not modelled and filed'],
            ['Capital deferred by testing first', 'Equipment purchases avoided where the evidence did not support them'],
            ['Regulated submissions supported', 'Device and pharma documentation built to withstand assessor scrutiny'],
            ['Long-term customer relationships', 'Multi-year engagements across four countries']] },
  brands: { label: 'Brands and platforms under SolidPro',
    items: [['TAKTIX', 'Factory performance engineering'],
            ['NetZeroHub', 'Sustainability transformation and value-chain enablement'],
            ['HeLiXA', 'Healthcare & lifesciences engineering'],
            ['BTOS-6D', 'Engineering-led business transformation framework'],
            ['KARMA OS', 'Programme management and data infrastructure platform'],
            ['SATTOS', 'Sustainability assessment and tracking'],
            ['Open Engineering', 'Energy & utilities engineering platform'],
            ['Inno2Market', 'Innovation to market framework'],
            ['StartSmart', 'The universal entry point across every SolidPro vertical']] }
};

const ENGAGEMENT_MODES = [
  { id: 'fixed', label: 'Fixed price', line: 'One agreed price for an agreed scope.',
    best: 'Scope can be defined now. The customer wants certainty and no open-ended exposure.',
    ask: 'people-days' },
  { id: 'retainer', label: 'Monthly retainer', line: 'A fixed monthly fee for ongoing availability and advice.',
    best: 'Ongoing support, governance or advisory where the work varies month to month.', ask: 'people-months' },
  { id: 'billable', label: 'Time & material', line: 'Charged against hours actually worked, at an agreed rate.',
    best: 'Scope genuinely cannot be fixed yet, and the customer accepts a variable bill.', ask: 'hours' },
  { id: 'pod', label: 'Dedicated team (per engineer per month)', line: 'Named engineers assigned for a period, billed monthly.',
    best: 'The customer wants capacity they direct themselves, not a finished deliverable.', ask: 'people-months' },
  { id: 'milestone', label: 'Milestone-based', line: 'The total is fixed, released against defined milestones.',
    best: 'Longer programmes where the customer wants payment tied to visible progress.', ask: 'people-days' },
  { id: 'outcome', label: 'Outcome-linked', line: 'Part of the fee depends on the result being achieved.',
    best: 'You control the levers and both sides agree how the result is measured.', ask: 'people-days' },
  { id: 'subscription', label: 'Subscription / licence', line: 'Recurring fee for access to a platform or service.',
    best: 'KARMA OS, SATTOS, monitoring or any hosted capability.', ask: 'subscription' },
  { id: 'hybrid', label: 'Hybrid', line: 'A fixed element plus a variable or outcome element.',
    best: 'A fixed diagnostic followed by variable delivery, or a base fee plus gain-share.', ask: 'people-days' }
];

const EXTRA_COSTS = [
  ['tools', 'Tools, software or licences purchased for this engagement'],
  ['equipment', 'Instruments, loggers or test equipment'],
  ['travel', 'Travel, boarding and lodging'],
  ['thirdparty', 'Third-party testing, verification or certification'],
  ['materials', 'Materials, samples or prototype build cost'],
  ['training', 'Training delivery and materials'],
  ['other', 'Anything else']
];

const SECTION_DEFS = [
  ['control', 'Document control & distribution', 'Version, classification, who prepared it and who it goes to.', 0],
  ['toc', 'Contents', 'Auto-generated contents page.', 0],
  ['summary', 'Executive summary', 'The one page a CXO reads.', 0],
  ['understanding', 'Understanding your requirement', 'Their business, their ask, the current situation.', 0],
  ['approach', 'Our approach & methodology', 'How the work will be done, stage by stage.', 0],
  ['solutions', 'What we propose to do', 'The SolidPro services proposed.', 0],
  ['capabilities', 'Expertise we will use', 'Tools, disciplines and methods applied.', 1],
  ['cases', 'Relevant experience', 'Comparable engagements delivered.', 1],
  ['scope', 'Scope of work', 'Everything included in the price.', 0],
  ['deliverables', 'What you receive', 'Deliverables and how they are accepted.', 0],
  ['plan', 'Timeline & milestones', 'Phases, durations and milestone outputs.', 0],
  ['team', 'Who will do the work', 'Named people, designations, roles and allocation.', 0],
  ['governance', 'How the project will be managed', 'Review cadence, decision rights, escalation.', 0],
  ['risks', 'Risks and how we manage them', 'Risks, impact, likelihood and mitigation.', 1],
  ['quality', 'Quality & standards', 'Standards followed and how quality is assured.', 1],
  ['security', 'Confidentiality and who owns the work', 'Data handling, IP ownership, NDA position.', 1],
  ['compliance', 'SolidPro company details', 'GST, PAN, MSME, insurance, registrations.', 1],
  ['commercials', 'Commercials', 'Price, basis, taxes.', 0],
  ['payment', 'Payment schedule & terms', 'Milestone-linked payment plan.', 0],
  ['assumptions', 'What this proposal assumes', 'Assumptions, your inputs, and what is excluded.', 0],
  ['change', 'If the scope changes', 'How changes are priced and approved.', 1],
  ['sla', 'Service commitments', 'Measurable commitments.', 1],
  ['warranty', 'Warranty & support after handover', 'Support window and terms.', 1],
  ['hse', 'Safety on your site', 'Applies when our team works at your premises.', 1],
  ['esg', 'Sustainability & ESG', 'Increasingly requested by large-corporate procurement.', 1],
  ['why', 'Why SolidPro', 'Differentiators selected for this customer.', 0],
  ['next', 'Next steps', 'How we move from proposal to engagement.', 0],
  ['accept', 'Acceptance & signature', 'Signature block for both parties.', 0],
  ['annex', 'Annexures', 'Anything attached at the back.', 1]
];

const SEC_LABEL = id => (SECTION_DEFS.find(s => s[0] === id) || [, id])[1];
const SEC_OPTIONAL = id => ((SECTION_DEFS.find(s => s[0] === id) || [])[3] === 1);
const IP_OPTIONS = ['All deliverables and new IP belong to the customer on full payment', 'New IP belongs to the customer; SolidPro\'s existing methods, tools and know-how remain ours', 'Joint ownership as defined in the master services agreement', 'As per the master services agreement already signed between us'];
const SEC_CLAUSES = ['All our people are covered by confidentiality agreements in their employment terms', 'A mutual NDA is in place, or will be signed before any data is shared', 'Your data is held only on controlled, access-restricted SolidPro systems', 'Data is transferred over encrypted channels only', 'No customer data is retained after closure except where the contract requires it', 'Access is granted by name, on a least-privilege basis, and removed when someone leaves the project', 'We can work entirely inside your own environment or VDI if you prefer', 'Access is reviewed periodically for the duration of the engagement', 'Any security incident is reported to you within 24 hours of detection'];
const STD_LIB = ['ISO 9001:2015 Quality Management', 'ISO 27001 Information Security', 'ISO 14001 Environmental Management', 'ISO 45001 Occupational Health & Safety', 'ISO 13485 Medical Devices', 'IATF 16949 Automotive Quality', 'ISO 12100 Machinery Safety', 'ASME / API design codes', 'IPMVP measurement & verification', 'GHG Protocol', 'BRSR / CBAM / CCTS reporting', 'Your own engineering standards and specifications'];
const SLA_LIB = [['Progress reporting', 'Written status report every week'], ['Query response', 'Acknowledged within 1 working day, answered or planned within 3'], ['Milestone adherence', 'Milestones met against the approved baseline plan'], ['Deliverable rework', 'Your review comments addressed within 5 working days'], ['Escalation response', 'Level 1 within 24 hours, Level 2 within 48 hours'], ['Team availability', 'Agreed allocation of the named team maintained throughout'], ['Defect resolution (software)', 'Priority 1 within 8 working hours, Priority 2 within 2 working days'], ['On-site attendance', 'Agreed site days delivered or rescheduled within the same month']];
const PAY_TEMPLATES = {
  'Standard 30/40/30': [['On issue of purchase order', 30, 'Advance against PO'], ['On acceptance of the mid-point milestone', 40, 'Milestone acceptance'], ['On submission of final deliverables', 30, 'Final acceptance']],
  'Milestone 20/30/30/20': [['On issue of purchase order', 20, 'Advance against PO'], ['On baseline sign-off', 30, 'Stage 1 acceptance'], ['On draft deliverable submission', 30, 'Stage 2 acceptance'], ['On final acceptance', 20, 'Closure']],
  'Monthly in arrears': [['Month 1', '', 'Invoiced monthly in arrears'], ['Month 2', '', 'Invoiced monthly in arrears'], ['Month 3', '', 'Invoiced monthly in arrears']],
  '50/50': [['On issue of purchase order', 50, 'Advance against PO'], ['On final acceptance', 50, 'Final acceptance']]
};
const ROLE_LIB = ['Engagement Sponsor', 'Engagement Manager', 'Project Manager', 'Delivery Lead', 'Technical Lead', 'Lead Engineer', 'Senior Engineer', 'Engineer', 'Simulation Specialist', 'Industrial Engineer', 'Quality Engineer', 'Solution Architect', 'Data Engineer', 'Sustainability Consultant', 'Business Analyst', 'QA Lead'];
const DESIG_LIB = ['Managing Director', 'Vice President', 'Assistant Vice President', 'General Manager', 'Head of Practice', 'Senior Manager', 'Manager', 'Assistant Manager', 'Principal Engineer', 'Senior Engineer', 'Engineer', 'Consultant', 'Senior Consultant'];
const CUST_DESIG_LIB = ['Chairman / Managing Director', 'Chief Executive Officer', 'Chief Operating Officer', 'Chief Financial Officer', 'Chief Technology Officer', 'President / Business Head', 'Vice President – Operations', 'Vice President – Engineering', 'Plant Head', 'General Manager – Manufacturing', 'Head – Procurement', 'Head – Sustainability / ESG', 'Head – Quality', 'Head – Digital / IT', 'Project Manager', 'Purchase Manager', 'Other'];
const ENG_MODELS = ['Project-Based (fixed scope)', 'Diagnostic / Pilot', 'Engineering POD (dedicated team)', 'Specialist Capacity (single engineer)', 'Managed Service', 'Advisory Retainer', 'Implementation Programme', 'Delivery Partnership', 'Enablement / Training Programme', 'Hybrid / Phased', 'Other'];
const COM_MODELS = ['Fixed Fee', 'Milestone-Based', 'Monthly Retainer', 'Per Engineer / Month', 'Time & Material', 'Outcome-Linked', 'Subscription / Licence', 'Hybrid', 'Other'];
const PRACTICES = ['SolidPro', 'Helixa', 'NetZeroHub', 'TAKTIX'];
const PROP_STATUS = ['Draft', 'Internal review', 'Sent to customer', 'Under negotiation', 'Won', 'Lost', 'On hold'];
const CLASSIFICATIONS = ['Commercial in Confidence', 'Confidential', 'Strictly Private & Confidential', 'Internal Use Only'];

// --- PRESETS ---
const PRESETS = {
  'Diagnostic / Pilot': { off: ['sla', 'warranty', 'hse', 'change', 'esg', 'annex', 'compliance'], eng: 'Diagnostic / Pilot', com: 'Fixed Fee', dur: { n: 3, unit: 'weeks' }, pay: '50/50' },
  'Project (fixed scope)': { off: ['sla', 'esg', 'annex'], eng: 'Project-Based (fixed scope)', com: 'Fixed Fee', dur: { n: 10, unit: 'weeks' }, pay: 'Standard 30/40/30' },
  'Engineering POD / staffing': { off: ['risks', 'hse', 'esg', 'annex'], eng: 'Engineering POD (dedicated team)', com: 'Per Engineer / Month', dur: { n: 12, unit: 'months' }, pay: 'Monthly in arrears' },
  'Managed service / retainer': { off: ['hse', 'annex'], eng: 'Managed Service', com: 'Monthly Retainer', dur: { n: 12, unit: 'months' }, pay: 'Monthly in arrears' },
  'Transformation programme': { off: ['annex'], eng: 'Implementation Programme', com: 'Milestone-Based', dur: { n: 9, unit: 'months' }, pay: 'Milestone 20/30/30/20' },
  'Enterprise RFP response': { off: [], eng: 'Implementation Programme', com: 'Milestone-Based', dur: { n: 12, unit: 'months' }, pay: 'Milestone 20/30/30/20' }
};

// ===== PROPOSAL TYPES & MODES =====
const PROPOSAL_TYPES = {
  quick: {
    label: 'Quick Quote',
    tag: '1–4 pages',
    line: 'A short priced offer for a request you already understand.',
    when: 'A known ask, a small value, a customer who wants a number this week.',
    eng: 'Diagnostic / Pilot',
    com: 'Fixed Fee',
    mode: 'fast',
    on: ['summary', 'understanding', 'solutions', 'scope', 'deliverables', 'plan', 'commercials', 'payment', 'assumptions', 'next', 'accept'],
    order: ['summary', 'understanding', 'solutions', 'scope', 'deliverables', 'plan', 'commercials', 'payment', 'assumptions', 'next', 'accept']
  },
  service: {
    label: 'Service Proposal',
    tag: '8–15 pages',
    line: 'We will do defined work, to a defined scope, for a defined price.',
    when: 'The customer knows what they want done. You are competing on scope, team and price.',
    eng: 'Project-Based (fixed scope)',
    com: 'Fixed Fee',
    mode: 'guided',
    on: ['control', 'toc', 'summary', 'understanding', 'approach', 'solutions', 'capabilities', 'cases', 'scope', 'deliverables', 'plan', 'team', 'governance', 'risks', 'commercials', 'payment', 'assumptions', 'why', 'next', 'accept'],
    order: ['control', 'toc', 'summary', 'understanding', 'approach', 'solutions', 'capabilities', 'cases', 'scope', 'deliverables', 'plan', 'team', 'governance', 'risks', 'commercials', 'payment', 'assumptions', 'why', 'next', 'accept']
  },
  outcome: {
    label: 'Outcome Proposal',
    tag: '10–18 pages',
    line: 'We commit to a result, not to a list of activities.',
    when: 'The customer cares about the number moving. Scope exists to serve the promise.',
    eng: 'Outcome-Linked',
    com: 'Outcome-Linked',
    mode: 'guided',
    on: ['control', 'toc', 'summary', 'understanding', 'outcome', 'approach', 'solutions', 'cases', 'scope', 'deliverables', 'plan', 'team', 'governance', 'risks', 'commercials', 'payment', 'assumptions', 'why', 'next', 'accept'],
    order: ['control', 'toc', 'summary', 'understanding', 'outcome', 'approach', 'solutions', 'cases', 'scope', 'deliverables', 'plan', 'team', 'governance', 'risks', 'commercials', 'payment', 'assumptions', 'why', 'next', 'accept']
  },
  transformation: {
    label: 'Transformation Programme',
    tag: '18–30 pages',
    line: 'A staged programme over months or years, with a gate at every stage.',
    when: 'Large, multi-phase change. The customer approves one stage at a time.',
    eng: 'Implementation Programme',
    com: 'Milestone-Based',
    mode: 'full',
    on: ['control', 'toc', 'summary', 'understanding', 'approach', 'solutions', 'capabilities', 'cases', 'scope', 'deliverables', 'plan', 'stages', 'team', 'governance', 'risks', 'quality', 'security', 'commercials', 'payment', 'assumptions', 'change', 'sla', 'why', 'next', 'accept', 'annex'],
    order: ['control', 'toc', 'summary', 'understanding', 'approach', 'solutions', 'capabilities', 'cases', 'scope', 'deliverables', 'stages', 'plan', 'team', 'governance', 'risks', 'quality', 'security', 'commercials', 'payment', 'assumptions', 'change', 'sla', 'why', 'next', 'accept', 'annex']
  },
  venture: {
    label: 'Business Case',
    tag: '18–25 pages',
    line: 'We are proposing the customer builds something - and that we help them build it.',
    when: 'You are not selling a service. You are arguing that an opportunity exists and the customer is the right one to take it.',
    eng: 'Delivery Partnership',
    com: 'Milestone-Based',
    mode: 'full',
    on: ['control', 'toc', 'ataglance', 'ask', 'market', 'revenue', 'roadmap', 'havegap', 'horizon', 'contribution', 'parties', 'stages', 'commercials', 'governance', 'handover', 'next', 'accept'],
    order: ['control', 'toc', 'ataglance', 'ask', 'market', 'revenue', 'roadmap', 'havegap', 'horizon', 'contribution', 'parties', 'stages', 'commercials', 'governance', 'handover', 'next', 'accept']
  },
  rfp: {
    label: 'RFP / Tender Response',
    tag: '25–40 pages',
    line: 'A formal answer that mirrors the customer\'s own document.',
    when: 'They issued an RFP with a required structure, mandatory annexures and an evaluation matrix.',
    eng: 'Implementation Programme',
    com: 'Milestone-Based',
    mode: 'full',
    on: SECTION_DEFS.map(s => s[0]),
    order: SECTION_DEFS.map(s => s[0])
  },
  staffing: {
    label: 'Team / POD Proposal',
    tag: '6–12 pages',
    line: 'We are supplying capability by the month, not a finished deliverable.',
    when: 'The customer wants engineers, not an outcome. They manage the work.',
    eng: 'Engineering POD (dedicated team)',
    com: 'Per Engineer / Month',
    mode: 'guided',
    on: ['control', 'toc', 'summary', 'understanding', 'solutions', 'capabilities', 'scope', 'plan', 'team', 'governance', 'sla', 'commercials', 'payment', 'assumptions', 'security', 'why', 'next', 'accept'],
    order: ['control', 'toc', 'summary', 'understanding', 'solutions', 'capabilities', 'team', 'scope', 'plan', 'governance', 'sla', 'commercials', 'payment', 'assumptions', 'security', 'why', 'next', 'accept']
  },
  credentials: {
    label: 'Capability Document',
    tag: '6–12 pages',
    line: 'An introduction to SolidPro. No scope, no price.',
    when: 'Vendor registration, empanelment, or a first meeting.',
    eng: 'Project-Based (fixed scope)',
    com: 'Fixed Fee',
    mode: 'fast',
    on: ['toc', 'understanding', 'solutions', 'capabilities', 'cases', 'team', 'quality', 'security', 'compliance', 'why', 'next'],
    order: ['toc', 'understanding', 'solutions', 'capabilities', 'cases', 'team', 'quality', 'security', 'compliance', 'why', 'next']
  }
};

const MODES = {
  fast: { label: 'Fast Track', line: 'Four screens. Everything sensible is filled in for you.', note: 'Best when the ask is clear and you want a document today.' },
  guided: { label: 'Guided', line: 'Nine screens that walk the customer\'s story first, then the detail.', note: 'Best for most proposals. Enterprise detail sits in one optional screen.' },
  full: { label: 'Full Control', line: 'Every screen, every field, nothing hidden.', note: 'Best for tenders, programmes and anything a procurement team will pick apart.' }
};

// --- METHODS ---
const METHODS = {
  BTOS: { label: 'Business Transformation - BTOS-6D', stages: [
    ['Discover', 'Establish what is actually happening', 'We assess the business area by area and record how confident we are in each finding. You get a business health map, not a single score that hides the detail.'],
    ['Diagnose', 'Test why it is happening, and rule causes out', 'Every plausible cause is tested against supporting and refuting evidence. Most are eliminated. What survives is a validated cause, not a hypothesis.'],
    ['Design', 'Decide what should change, and in what order', 'The target state, alternatives, readiness, dependencies and business case - sequenced against what your organisation can realistically absorb.'],
    ['Deliver', 'Implement against the design that was approved', 'Execution governed against the approved design, so what gets built is what was signed off.'],
    ['Demonstrate', 'Prove the value actually arrived', 'Benefits measured against the baseline that was locked at the start. Claimed and realised are not the same thing.'],
    ['Differentiate', 'Leave capability behind', 'Knowledge transfer, capability building and continuous improvement, so the gain does not leave when we do.']] },
  ENG: { label: 'Engineering & Product Development', stages: [
    ['Define', 'Agree what the product or system must do', 'We turn the requirement into testable numbers - duty, load, life, cost and compliance - so success is measurable rather than a matter of opinion.'],
    ['Concept', 'Put up options before committing to one', 'Two or three workable concepts are developed and compared on cost, risk, manufacturability and lead time, with a recommendation and the reasoning behind it.'],
    ['Design', 'Engineer the chosen concept in detail', 'Full 3D models, drawings with tolerances, and a bill of materials built for the way it will actually be made and assembled.'],
    ['Prove', 'Test it before it is built', 'Analysis and simulation against the governing load case, so weak points are found on screen rather than on the shop floor.'],
    ['Release', 'Hand over a manufacturable pack', 'Drawings, BOM and specifications released in your system, in a state a supplier can quote and build from without coming back with questions.'],
    ['Support', 'Stay with it into production', 'Design changes arising from first builds, supplier queries and validation findings are handled by the same team.']] },
  MFG: { label: 'Manufacturing, Factory & Industrialisation', stages: [
    ['Observe', 'See what actually happens on the floor', 'We walk the process, time the work and watch a full cycle. Estimates and spreadsheets are set aside until we have measured the operation ourselves.'],
    ['Measure', 'Establish a baseline nobody can argue with', 'Cycle times, changeovers, breakdowns, manning and flow are recorded and agreed with your team, so every later number is compared against something solid.'],
    ['Find the constraint', 'Identify what is really limiting output', 'The station that looks busiest is rarely the one holding the line back. We test which constraint the evidence supports before anyone proposes a fix.'],
    ['Model', 'Build a working model of the operation', 'A model of the line is built and validated until it reproduces your actual output - only then is it used to answer questions.'],
    ['Test the options', 'Try the changes virtually first', 'Manning, balancing, buffers, shifts, layout and new equipment are each tested in the model and compared on the same basis, at no risk to production.'],
    ['Recommend', 'Give leadership one clear comparison', 'A single decision sheet: what each option delivers, what it costs, what it assumes - including the option of doing nothing.'],
    ['Implement & verify', 'Make the change and prove it worked', 'Where you want us to, our engineers deliver the change and measure the result against the original baseline.']] },
  DT: { label: 'Digital Transformation', stages: [
    ['Understand the process', 'Start from the business problem, not the software', 'We map how the work is done today, where it stalls and what it costs - before any tool is discussed.'],
    ['Design the solution', 'Decide what should change and what should not', 'Architecture, data model and rules are defined around your process. Automating an unstable process only makes its errors faster.'],
    ['Build', 'Configure and develop in short cycles', 'Working software is shown to your users at regular intervals so direction is corrected early, not at the end.'],
    ['Integrate', 'Connect it to the systems you already run', 'Interfaces, data migration and cutover planned so the new tool sits inside your landscape rather than beside it.'],
    ['Prove', 'Test with your people and your data', 'Structured test cycles and user acceptance with real scenarios, so go-live is a formality rather than an event.'],
    ['Go live & support', 'Deploy, then stay close', 'Cutover, hypercare and a defined support window while your users settle in.'],
    ['Enable', 'Hand over ownership', 'Documentation, admin training and knowledge transfer so your team can change it themselves.']] },
  ESG: { label: 'Sustainability & Compliance', stages: [
    ['Set the boundary', 'Agree what is being counted', 'Base year, organisational boundary and which scopes are included - fixed in writing, because everything downstream depends on it.'],
    ['Measure', 'Build a baseline that survives assurance', 'Actual consumption and activity data rather than estimates, with gaps recorded honestly instead of filled in.'],
    ['Find the levers', 'Identify every practical way to reduce', 'Efficiency, fuel switching, electrification, renewables, process change and material substitution - assessed on your site, not from a generic list.'],
    ['Cost it', 'Rank the options cheapest first', 'Every lever carries a cost per tonne and a payback. Many pay for themselves; those are done first.'],
    ['Sequence', 'Turn it into a phased, funded plan', 'Phases with owners, budgets and expected impact, mapped to incentives, credits and green finance.'],
    ['Deliver', 'Engineer the reductions', 'Our engineers implement the priority levers - the step a pure consultancy cannot offer.'],
    ['Verify & disclose', 'Prove the reduction happened', 'Measurement and verification against the locked baseline, and disclosure your assurance provider will accept.']] },
  MED: { label: 'Healthcare, MedTech & Pharma', stages: [
    ['Requirements & risk', 'Start from intended use and patient risk', 'User needs, intended use and risk analysis established first, because every downstream document traces back to them.'],
    ['Design', 'Develop under design control', 'Design inputs and outputs kept traceable throughout, so the file tells a complete story rather than being assembled afterwards.'],
    ['Verify', 'Prove the design meets the input', 'Verification planned and executed against defined acceptance criteria, with objective evidence retained.'],
    ['Validate', 'Prove it works in real use', 'Validation against intended use and user needs, in conditions representative of actual practice.'],
    ['Document', 'Build the file the assessor will read', 'Technical documentation, DHF and evidence assembled to withstand notified-body or regulator scrutiny.'],
    ['Transfer', 'Move it into manufacture', 'Process transfer, process validation and controls so what is produced matches what was approved.'],
    ['Sustain', 'Keep it compliant as it changes', 'Change control, post-market inputs and periodic review handled with the same traceability.']] },
  SCM: { label: 'Supply Chain & Sourcing', stages: [
    ['Map', 'See what is actually in your supply base', 'Suppliers, spend, parts, locations, criticality and existing evidence brought together - starting from what you already hold.'],
    ['Prioritise', 'Focus where it matters', 'Not every supplier needs the same attention. Segmentation by importance, dependency and exposure so effort goes where it changes something.'],
    ['Assess', 'Get reliable information, not just questionnaire answers', 'Focused assessment with evidence, coverage and gaps made visible, so an incomplete picture never looks complete.'],
    ['Engage', 'Bring suppliers into the work', 'Workshops and improvement discussions matched to each supplier\'s maturity, including support for those who do not yet understand the requirement.'],
    ['Improve', 'Turn findings into engineered change', 'Where a gap needs practical intervention, our engineers work on the supplier\'s process, material, energy or logistics - not just score it.'],
    ['Integrate', 'Connect it to procurement decisions', 'Qualification, RFQ evaluation, reviews and supplier development informed by what has been found.'],
    ['Sustain', 'Make it continuous', 'Ownership, review cycles, dashboards and development plans so it continues beyond the first assessment.']] },
  INN: { label: 'Innovation & New Concepts', stages: [
    ['Frame', 'Define the opportunity precisely', 'What problem, for whom, and what would make it worth pursuing - agreed before effort is spent exploring.'],
    ['Explore', 'Scan what already exists', 'Technology and market scouting so you build what is missing rather than what has already been solved elsewhere.'],
    ['Concept', 'Generate and screen options', 'Multiple concepts developed and screened against feasibility, cost and time to market.'],
    ['Prototype', 'Make something you can react to', 'Rapid prototypes built to answer the specific question that most threatens the idea.'],
    ['Test', 'Put it in front of reality', 'Structured testing with users or in operating conditions, and an honest read of the result.'],
    ['Decide', 'Recommend build, park or stop', 'A clear recommendation with the reasoning - including when the answer is not to proceed.']] },
  'P&E': { label: 'Process & Plant Engineering', stages: [
    ['Survey', 'Establish what is really installed', 'Existing plant captured as it is today - drawings are often out of date, so the current state is verified rather than assumed.'],
    ['Basis of design', 'Agree duty, codes and constraints', 'Process requirements, applicable codes and site constraints fixed before layout work begins.'],
    ['Layout & routing', 'Design for operation and maintenance', 'Equipment, piping and access designed around how the plant will be run and maintained, not only how it fits.'],
    ['Detail', 'Produce construction-ready output', 'Isometrics, supports, fabrication drawings and BOM in a state a fabricator can build from.'],
    ['Check', 'Review before steel is cut', 'Clash detection, constructability and safety review so problems are found in the model rather than on site.'],
    ['As-built', 'Leave you with the truth', 'Documentation updated to reflect what was actually installed, so the next project starts from reality.']] },
  PUB: { label: 'Packaging, Labelling & Technical Content', stages: [
    ['Audit', 'Review what you publish today', 'Existing content, structure, artwork and compliance position assessed against the markets you sell into.'],
    ['Structure', 'Define how content should be built', 'Modular, reusable structure so one change updates everywhere instead of being repeated by hand.'],
    ['Produce', 'Create the content and artwork', 'Illustration, authoring, labelling and packaging produced to the agreed structure and standards.'],
    ['Comply', 'Check against market requirements', 'Language, symbols, UDI and regulatory content verified for each destination market.'],
    ['Publish', 'Release in every format needed', 'Print, digital and eIFU outputs generated from a single source.'],
    ['Maintain', 'Keep it current as the product changes', 'Change process so revisions flow through without the whole pack being rebuilt.']] },
  GEN: { label: 'General engagement', stages: [
    ['Understand', 'Agree the objective and the boundary', 'What a good outcome looks like, in writing, before work starts.'],
    ['Measure', 'Collect actual data', 'Real operating data rather than estimates, with weak data recorded as such.'],
    ['Analyse', 'Establish the current state and test the causes', 'Candidate explanations tested against supporting and refuting evidence before one is accepted.'],
    ['Design', 'Develop and compare options', 'Options compared on a common basis with a recommendation and its reasoning stated.'],
    ['Deliver', 'Execute with named owners', 'Agreed work delivered with weekly governance and a controlled change process.'],
    ['Verify', 'Prove the result', 'Outcome measured against the baseline so it is demonstrated, not asserted.'],
    ['Hand over', 'Leave the capability behind', 'Documentation, training and knowledge transfer.']] }
};

// --- Utility functions ---
const uniq = (a) => [...new Set((a || []).filter(Boolean))];
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[c]));
// Lower-case last: matching case-sensitively first turned every capital into a
// separator, so "SP-PROP-20260820-01" came out as "20260820-01".
const safe = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const lower = (s) => s ? String(s).charAt(0).toLowerCase() + String(s).slice(1) : '';
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch (e) { return ''; } };
const durText = (d) => { if (!d) return ''; if (typeof d === 'string') return d; return d.n ? `${d.n} ${d.unit || 'weeks'}` : ''; };
const startText = (s) => {
  if (!s) return '';
  if (typeof s === 'string') return s;
  if (s.mode === 'On a specific date') return s.date ? ('On ' + fmtDate(s.date)) : 'On a date to be confirmed';
  if (s.mode === 'Within a set time of the purchase order') return s.n ? `Within ${s.n} ${s.unit || 'weeks'} of the purchase order` : 'Shortly after the purchase order';
  return s.mode || 'To be agreed at kick-off';
};
const effortText = (e) => (e || []).filter(x => x.qty).map(x => `${x.qty} ${x.unit}`).join(' · ');
const money = (cur, amt) => {
  if (amt === '' || amt == null) return 'To be finalised';
  const n = Number(String(amt).replace(/[, ]/g, ''));
  if (Number.isFinite(n) && String(amt).trim() !== '') return (cur === 'USD' ? '$' : '₹') + n.toLocaleString(cur === 'USD' ? 'en-US' : 'en-IN');
  return (cur === 'USD' ? '$' : '₹') + amt;
};
const numOf = (v) => { const n = Number(String(v ?? '').replace(/[, ]/g, '')); return Number.isFinite(n) ? n : 0; };
const inWords = (cur, amt) => {
  const n = numOf(amt);
  if (!n) return '';
  if (cur === 'USD') return n >= 1e6 ? (n / 1e6).toFixed(2).replace(/\.00$/, '') + ' million USD' : '';
  return n >= 1e7 ? (n / 1e7).toFixed(2).replace(/\.00$/, '') + ' crore' : n >= 1e5 ? (n / 1e5).toFixed(2).replace(/\.00$/, '') + ' lakh' : '';
};
const linesToList = (s) => String(s || '').split(/\n|;/).map(x => x.trim()).filter(Boolean);
const getPath = (obj, path) => path.split('.').reduce((a, k) => a?.[k], obj) ?? '';
const setPath = (obj, path, val) => {
  const parts = path.split('.');
  let o = obj;
  for (let i = 0; i < parts.length - 1; i++) { o[parts[i]] = o[parts[i]] || {}; o = o[parts[i]]; }
  o[parts[parts.length - 1]] = val;
};

// --- Service Master ---
const BU_BASE_HOURS = { 'Engineering Services': 160, 'Manufacturing & Industrialisation': 160, 'Digital Transformation': 200, 'NetZeroHub': 140, 'Helixa': 200, 'Innovation': 120, 'Process Engineering': 160, 'Energy & Utilities': 160, 'Structural Engineering': 140, 'BFSI': 180 };
const BU_METHOD = { 'Engineering Services': 'ENG', 'Manufacturing & Industrialisation': 'MFG', 'Digital Transformation': 'DT', 'NetZeroHub': 'ESG', 'Helixa': 'MED', 'Innovation': 'INN', 'Process Engineering': 'P&E', 'Energy & Utilities': 'P&E', 'Structural Engineering': 'ENG', 'BFSI': 'DT' };
const CAT_METHOD = { ENG: 'ENG', DIG: 'DT', INN: 'INN', MFG: 'MFG', SCM: 'SCM', SUS: 'ESG', PKG: 'PUB', MED: 'MED', PROC: 'P&E', EU: 'P&E', BEI: 'ENG', BFSI: 'DT' };
const BU_ENGAGEMENT = { 'Engineering Services': 'fixed', 'Manufacturing & Industrialisation': 'fixed', 'Digital Transformation': 'milestone', 'NetZeroHub': 'fixed', 'Helixa': 'fixed', 'Innovation': 'fixed', 'Process Engineering': 'fixed', 'Energy & Utilities': 'fixed', 'Structural Engineering': 'pod', 'BFSI': 'fixed' };
const BU_METRICS = {
  'Engineering Services': ['Design cycle time', 'Time to first article', 'Drawings released on schedule', 'Design changes after release', 'Cost per unit produced'],
  'Manufacturing & Industrialisation': ['Units produced per day', 'Overall equipment effectiveness (OEE)', 'Cycle time at the constraint', 'Changeover time', 'Scrap and rework rate', 'Capital expenditure avoided'],
  'Digital Transformation': ['Process cycle time', 'Manual touches removed', 'Quotation turnaround time', 'Data accuracy at source', 'User adoption after go-live'],
  'NetZeroHub': ['Scope 1 and 2 emissions (tCO2e)', 'Emissions intensity per unit', 'Energy consumed per unit', 'Verified reduction against baseline', 'Cost per tonne abated'],
  'Helixa': ['Regulatory submissions accepted first time', 'Audit findings raised', 'Design history file completeness', 'Time to submission', 'Batch release cycle time'],
  'Innovation': ['Concepts screened', 'Time from idea to prototype', 'Technical risks retired', 'Go / no-go decisions supported'],
  'Process Engineering': ['Plant availability', 'Utility consumption per unit', 'Safety findings closed', 'As-built accuracy'],
  'Energy & Utilities': ['Design output per week', 'Bid response time', 'Rework on submitted designs', 'Structures delivered on schedule'],
  'Structural Engineering': ['Drawings issued per week', 'Model clash count', 'Rework after review', 'Delivery against programme'],
  'BFSI': ['Process cycle time', 'Straight-through processing rate', 'Exceptions per thousand transactions', 'Turnaround time to customer']
};

let _SM = null;
let _SM_SRC = null;
const serviceMaster = (allSols) => {
  // Defaults to the loaded catalogue. Caching on the source array's identity
  // means the map is built once per catalogue load and rebuilt when it reloads -
  // caching on "was an argument passed" used to freeze an empty map in place.
  const sols = allSols || window._SOLS || [];
  if (_SM && _SM_SRC === sols) return _SM;
  _SM = {};
  _SM_SRC = sols;
  sols.forEach(s => {
    const eff = (() => {
      const base = BU_BASE_HOURS[s.bu] || 160;
      const n = (s.get || []).length;
      const h = Math.round((base * (0.7 + 0.1 * Math.min(n, 6))) / 10) * 10;
      return { hours: h, low: Math.round(h * 0.7 / 10) * 10, high: Math.round(h * 1.4 / 10) * 10 };
    })();
    const acts = uniq([
      'Kick-off and confirm exactly what is in scope',
      'Collect the data, drawings and documents needed to start',
      'Agree the current position in writing before analysis begins',
      ...(s.get || []).map(d => {
        const l = d.toLowerCase();
        if (/requirements? (specification|definition)/i.test(l)) return 'Define the requirements as testable, measurable numbers';
        if (/specification/i.test(l)) return 'Define and document the ' + lower(d);
        if (/(risk register|risk assessment)/i.test(l)) return 'Identify the technical risks and record them with the hardest named first';
        if (/(architecture|subsystem split)/i.test(l)) return 'Develop the ' + lower(d);
        if (/concept options?/i.test(l)) return 'Develop alternative concepts and compare them on a common basis';
        if (/recommendation/i.test(l)) return 'Recommend a preferred direction, with the reasoning stated';
        if (/(cost|effort) estimate/i.test(l)) return 'Estimate the cost and engineering effort';
        if (/(business case|payback)/i.test(l)) return 'Build the business case with cost, benefit and payback';
        if (/^(current|as-is).*(review|state)/i.test(l)) return 'Review and record the ' + lower(d);
        if (/(observation|walk-?through|survey)/i.test(l)) return 'Observe and record the ' + lower(d);
        if (/(study|analysis|assessment)/i.test(l)) return 'Carry out the ' + lower(d);
        if (/(review)$/i.test(l)) return 'Carry out the ' + lower(d);
        if (/(baseline)/i.test(l)) return 'Establish the ' + lower(d) + ' and agree it in writing';
        if (/(model|simulation)/i.test(l)) return 'Build and validate the ' + lower(d);
        if (/(drawing|3d|cad|layout|isometric)/i.test(l)) return 'Produce the ' + lower(d);
        if (/(bom|bill of material)/i.test(l)) return 'Prepare the bill of materials ready for purchasing';
        if (/(test|validation|verification)/i.test(l)) return 'Plan and carry out the ' + lower(d);
        if (/(documentation|dossier|file|pack|report)/i.test(l)) return 'Prepare the ' + lower(d);
        if (/(training|workshop)/i.test(l)) return 'Deliver the ' + lower(d);
        if (/(dashboard|reporting)/i.test(l)) return 'Configure the ' + lower(d);
        if (/(integration|interface|migration)/i.test(l)) return 'Build and test the ' + lower(d);
        if (/(plan|roadmap|pathway)/i.test(l)) return 'Develop the ' + lower(d) + ' with owners and dates';
        if (/(opportunit|improvement)/i.test(l)) return 'Identify and prioritise the ' + lower(d);
        if (/(audit|compliance|regulatory)/i.test(l)) return 'Check and evidence the ' + lower(d);
        if (/(monitoring|measurement|mrv)/i.test(l)) return 'Set up the ' + lower(d);
        if (/(supplier|vendor)/i.test(l)) return 'Assess and work with the ' + lower(d);
        return 'Produce the ' + lower(d);
      }),
      'Review progress with you and record decisions as we go',
      'Hand over the results and walk your team through them'
    ]);
    _SM[s.id] = {
      id: s.id,
      bu: s.bu || 'Unassigned',
      line: s.sub,
      title: s.title,
      description: s.what || '',
      scope: acts,
      deliverables: (s.get || []).slice(),
      capabilities: (s.cap || []).slice(),
      standards: (s.std || []).slice(),
      metrics: (BU_METRICS[s.bu] || BU_METRICS['Engineering Services']).slice(0, 5),
      caseIds: (window._CASES || []).filter(c => (c.sols || []).includes(s.id)).map(c => c.id),
      methodology: BU_METHOD[s.bu] || CAT_METHOD[s.catId] || 'GEN',
      hours: eff.hours,
      hoursLow: eff.low,
      hoursHigh: eff.high,
      engagement: BU_ENGAGEMENT[s.bu] || 'fixed',
      payTemplate: (BU_ENGAGEMENT[s.bu] === 'pod') ? 'Monthly in arrears' : 'Standard 30/40/30',
      brand: s.bu === 'NetZeroHub' ? 'NetZeroHub' : s.bu === 'Helixa' ? 'Helixa' : (s.sub || '').includes('TAKTIX') ? 'TAKTIX' : 'SolidPro'
    };
  });
  return _SM;
};
const sm = (id, allSols) => serviceMaster(allSols)[id];

// --- Business Unit Master ---
function buMaster() {
  const m = {};
  (window._SOLS || []).forEach(s => {
    const b = s.bu || 'Unassigned';
    m[b] = m[b] || { name: b, lines: {}, count: 0 };
    m[b].lines[s.sub] = (m[b].lines[s.sub] || 0) + 1;
    m[b].count++;
  });
  return Object.keys(m).sort().map(k => ({ name: k, lines: Object.keys(m[k].lines).sort(), count: m[k].count }));
}

function linesFor(bu) {
  const b = buMaster().find(x => x.name === bu);
  return b ? b.lines : [];
}

function servicesFor(bu, line) {
  // Must bucket exactly as buMaster does. It groups an absent business unit
  // under 'Unassigned', so comparing the raw field here would offer a unit in
  // the picker that then resolves to no services at all.
  return (window._SOLS || []).filter(s => (!bu || (s.bu || 'Unassigned') === bu) && (!line || s.sub === line));
}

// --- Admin ---
const ADMIN_KEY = 'solidpro-admin-v15';
function blankAdmin() {
  return {
    company: { legalName: 'SolidPro Engineering Support Pvt. Ltd.', address: 'Chennai 600032, India', gst: '', pan: '', cin: '', msme: '', insurance: 'Professional indemnity and workmen compensation cover in force',
      email: 'connect@solidpro-es.com', phone: '+91 63853 36852', web: 'solidpro-es.com', bank: '' },
    wording: { confidentiality: '', ip: IP_OPTIONS[1], quality: '', warranty: '', hse: '', payment: 'Payment within 30 days of invoice date.', tax: 'All amounts are exclusive of applicable taxes. Travel, boarding and lodging are charged at actuals where site work is required.' },
    people: [], rates: [],
    finance: { usdInr: '', updated: '', updatedBy: '', maxAgeDays: 30 },
    verified: {}
  };
}

function loadAdmin() {
  try {
    const a = JSON.parse(localStorage.getItem(ADMIN_KEY) || 'null');
    if (!a) return blankAdmin();
    const b = blankAdmin();
    return { company: Object.assign(b.company, a.company || {}), wording: Object.assign(b.wording, a.wording || {}), people: a.people || [], rates: a.rates || [], finance: Object.assign(b.finance, a.finance || {}), verified: a.verified || {} };
  } catch (e) { return blankAdmin(); }
}

let ADMIN = loadAdmin();

function saveAdmin() { localStorage.setItem(ADMIN_KEY, JSON.stringify(ADMIN)); }

function applyAdmin(p) {
  const c = ADMIN.company, w = ADMIN.wording;
  p.compliance = Object.assign(p.compliance, { gst: c.gst, pan: c.pan, cin: c.cin, msme: c.msme, insurance: c.insurance });
  p.doc.preparedEmail = c.email || p.doc.preparedEmail;
  p.doc.preparedPhone = c.phone || p.doc.preparedPhone;
  if (w.ip) p.ip = w.ip;
  if (w.quality) p.qualityNote = w.quality;
  if (w.warranty) p.warranty = w.warranty;
  if (w.hse) p.hse = w.hse;
  if (w.payment) p.paymentTerms = w.payment;
  if (w.tax) p.taxNote = w.tax;
  return p;
}

// ===== COSTING ENGINE =====
// One rule: hours x effective rate. The engagement model changes only how the
// number is PRESENTED to the customer, never how it is calculated.

const RATE_DEFAULTS = { base: 25, currency: 'USD', discount: 0, hoursPerWeek: 40, fx: 84 };
const POD_HOURS_PER_MONTH = 160;

const rateCfg = (p) => Object.assign({}, RATE_DEFAULTS, p?.rate || {});
const effRate = (p) => { const r = rateCfg(p); return r.base * (1 - (Number(r.discount) || 0) / 100); };

// Finance-approved USD->INR only. There is deliberately no default: an assumed
// exchange rate has no place in a priced document.
const fxRate = () => Number((ADMIN.finance || {}).usdInr) || 0;
const fxOk = () => {
  const f = ADMIN.finance || {};
  if (!Number(f.usdInr)) return { ok: false, why: 'No finance-approved USD to INR rate has been entered.' };
  if (!f.updated) return { ok: false, why: 'The USD to INR rate has no "last updated" date.' };
  const days = Math.floor((Date.now() - new Date(f.updated).getTime()) / 864e5);
  if (days > Number(f.maxAgeDays || 30)) return { ok: false, why: `The USD to INR rate was last updated ${days} days ago, beyond the ${f.maxAgeDays || 30}-day limit.` };
  return { ok: true, days };
};
const toCurrency = (p, usd) => {
  if (p.currency !== 'INR') return Math.round(usd);
  const fx = fxRate();
  return fx ? Math.round(usd * fx) : null;
};

const hoursFor = (people, n, unit, hpw) => {
  const w = unit === 'days' ? (Number(n) / 5) : unit === 'months' ? (Number(n) * 4.33) : unit === 'years' ? (Number(n) * 52) : Number(n);
  return (Number(people) || 0) * (w || 0) * (Number(hpw) || 40);
};
const priceFor = (p, hours) => {
  const usd = (Number(hours) || 0) * effRate(p);
  return { hours: Number(hours) || 0, usd: Math.round(usd), inr: fxRate() ? Math.round(usd * fxRate()) : null };
};
const totalHours = (p) => (p.items || []).reduce((a, i) => a + (Number(i.hours) || 0), 0);
const extrasTotal = (p) => { const c = p.calc || {}; return EXTRA_COSTS.reduce((a, e) => a + numOf((c.extras || {})[e[0]]), 0); };

const costBreakdown = (p) => {
  const hours = totalHours(p), rate = effRate(p);
  const labourUSD = hours * rate;
  const labour = toCurrency(p, labourUSD);
  const extras = extrasTotal(p);
  const contPct = Number((p.calc || {}).contingency) || 0;
  const sub = labour === null ? null : labour + extras;
  const cont = sub === null ? null : Math.round(sub * contPct / 100);
  const total = sub === null ? null : sub + cont;
  return { hours, rate, labourUSD, labour, extras, contPct, cont, total, fx: fxRate(), blocked: labour === null };
};

// How the same number is presented under each engagement model.
const presentPrice = (p) => {
  const b = costBreakdown(p);
  if (b.blocked) return null;
  const m = p.engMode || 'fixed';
  const c = p.calc || {};
  if (m === 'pod') {
    const months = Number(c.months) || 0, people = Number(c.people) || 0;
    const perEngMonth = toCurrency(p, POD_HOURS_PER_MONTH * b.rate);
    return { headline: perEngMonth, unit: 'per engineer per month',
      note: people && months ? `${people} engineers x ${months} months` : '',
      total: people && months ? perEngMonth * people * months : b.total };
  }
  if (m === 'retainer') {
    const months = Number(c.months) || 1;
    return { headline: Math.round(b.total / Math.max(months, 1)), unit: 'per month', note: `over ${months} months`, total: b.total };
  }
  if (m === 'billable') return { headline: toCurrency(p, b.rate), unit: 'per hour', note: `estimated ${b.hours.toLocaleString()} hours`, total: b.total };
  const hourly = p.currency === 'USD' ? '$' + b.rate.toFixed(2) : (b.fx ? '₹' + Math.round(b.rate * b.fx).toLocaleString('en-IN') : '—');
  return { headline: b.total, unit: 'fixed price', note: `${b.hours.toLocaleString()} hours at ${hourly} per hour`, total: b.total };
};

const calcTotal = (p) => {
  const mode = ENGAGEMENT_MODES.find(m => m.id === p.engMode);
  if (!mode) return costBreakdown(p).total || 0;
  const r = rateCfg(p), eff = effRate(p), c = p.calc || {}, cur = p.currency;
  const conv = u => cur === 'USD' ? u : u * (Number(r.fx) || 84);
  let usd = 0;
  if (mode.ask === 'people-days') usd = (Number(c.people) || 0) * (Number(c.hoursPerDay) || 0) * eff * (Number(c.days) || 0);
  else if (mode.ask === 'people-months') usd = (Number(c.people) || 0) * (Number(c.hoursPerDay) || 0) * 21.7 * eff * (Number(c.months) || 0);
  else if (mode.ask === 'hours') usd = (Number(c.hours) || 0) * eff;
  else if (mode.ask === 'subscription') return Math.round((numOf(c.monthly) * (Number(c.months) || 0)) * (1 + (Number(c.contingency) || 0) / 100) + extrasTotal(p));
  if (!usd) { const b = costBreakdown(p); return b.total || 0; }
  const sub = Math.round(conv(usd)) + extrasTotal(p);
  return Math.round(sub * (1 + (Number(c.contingency) || 0) / 100));
};

// ===== DERIVATION ENGINE =====
// s.get is what the customer RECEIVES. Scope must say what SolidPro DOES.
// These rules convert one into the other; every line stays editable.

const lcFirst = (s) => String(s).charAt(0).toLowerCase() + String(s).slice(1);

const ACTIVITY_RULES = [
  [/^requirements? (specification|definition)/i, () => 'Define the requirements as testable, measurable numbers'],
  [/specification/i, d => 'Define and document the ' + lcFirst(d)],
  [/(risk register|risk assessment)/i, () => 'Identify the technical risks and record them with the hardest named first'],
  [/(architecture|subsystem split)/i, d => 'Develop the ' + lcFirst(d)],
  [/concept options?/i, () => 'Develop alternative concepts and compare them on a common basis'],
  [/recommendation/i, () => 'Recommend a preferred direction, with the reasoning stated'],
  [/(cost|effort) estimate/i, () => 'Estimate the cost and engineering effort'],
  [/(business case|payback)/i, () => 'Build the business case with cost, benefit and payback'],
  [/^(current|as-is).*(review|state)/i, d => 'Review and record the ' + lcFirst(d)],
  [/(observation|walk-?through|survey)/i, d => 'Observe and record the ' + lcFirst(d)],
  [/(study|analysis|assessment)/i, d => 'Carry out the ' + lcFirst(d)],
  [/(review)$/i, d => 'Carry out the ' + lcFirst(d)],
  [/(baseline)/i, d => 'Establish the ' + lcFirst(d) + ' and agree it in writing'],
  [/(model|simulation)/i, d => 'Build and validate the ' + lcFirst(d)],
  [/(drawing|3d|cad|layout|isometric)/i, d => 'Produce the ' + lcFirst(d)],
  [/(bom|bill of material)/i, () => 'Prepare the bill of materials ready for purchasing'],
  [/(test|validation|verification)/i, d => 'Plan and carry out the ' + lcFirst(d)],
  [/(documentation|dossier|file|pack|report)/i, d => 'Prepare the ' + lcFirst(d)],
  [/(training|workshop)/i, d => 'Deliver the ' + lcFirst(d)],
  [/(dashboard|reporting)/i, d => 'Configure the ' + lcFirst(d)],
  [/(integration|interface|migration)/i, d => 'Build and test the ' + lcFirst(d)],
  [/(plan|roadmap|pathway)/i, d => 'Develop the ' + lcFirst(d) + ' with owners and dates'],
  [/(opportunit|improvement)/i, d => 'Identify and prioritise the ' + lcFirst(d)],
  [/(audit|compliance|regulatory)/i, d => 'Check and evidence the ' + lcFirst(d)],
  [/(monitoring|measurement|mrv)/i, d => 'Set up the ' + lcFirst(d)],
  [/(supplier|vendor)/i, d => 'Assess and work with the ' + lcFirst(d)]
];

const activityFor = (deliverable) => {
  for (const [re, out] of ACTIVITY_RULES) { if (re.test(deliverable)) return out(deliverable); }
  return 'Produce the ' + lcFirst(deliverable);
};

// Every engagement does these, whatever the service.
const COMMON_ACTIVITIES = [
  'Kick-off and confirm exactly what is in scope',
  'Collect the data, drawings and documents needed to start',
  'Agree the current position in writing before analysis begins',
  'Review progress with you and record decisions as we go',
  'Hand over the results and walk your team through them'
];

// Hours are a starting estimate by business unit, adjusted for how much the
// service delivers. Always editable, shown as a range so nobody mistakes them
// for a quotation.
const defaultHours = (s) => {
  const base = BU_BASE_HOURS[s.bu] || 160;
  const n = (s.get || []).length;
  const h = Math.round((base * (0.7 + 0.1 * Math.min(n, 6))) / 10) * 10;
  return { hours: h, low: Math.round(h * 0.7 / 10) * 10, high: Math.round(h * 1.4 / 10) * 10 };
};

const methodStages = (p) => { const k = detectMethod(p); return (METHODS[k] || METHODS.GEN).stages; };

const detectMethod = (p) => {
  if (p && p.methodKey && p.methodKey !== 'AUTO' && METHODS[p.methodKey]) return p.methodKey;
  if (p && p.derivedMethod && METHODS[p.derivedMethod]) return p.derivedMethod;
  const counts = {};
  const all = window._SOLS || [];
  ((p && p.solutions) || []).forEach(id => {
    const s = all.find(x => x.id === id);
    if (!s) return;
    const m = BU_METHOD[s.bu] || CAT_METHOD[s.catId];
    if (m) counts[m] = (counts[m] || 0) + 1;
  });
  const best = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  return METHODS[best] ? best : 'GEN';
};

// Auto content is recalculated in full whenever the service selection changes.
// Manual additions are held separately and always survive - this is what stops
// a case study from a service you removed staying in the proposal.
const recomputeAuto = (p) => {
  const all = window._SOLS || [];
  const cases = window._CASES || [];
  const recs = (p.solutions || []).map(id => {
    const s = all.find(x => x.id === id);
    if (!s) return null;
    const eff = defaultHours(s);
    return {
      id: s.id, bu: s.bu || 'Unassigned', line: s.sub, title: s.title, description: s.what || '',
      scope: uniq([...COMMON_ACTIVITIES.slice(0, 3), ...(s.get || []).map(activityFor), ...COMMON_ACTIVITIES.slice(3)]),
      deliverables: (s.get || []).slice(),
      capabilities: (s.cap || []).slice(),
      standards: (s.std || []).slice(),
      metrics: (BU_METRICS[s.bu] || BU_METRICS['Engineering Services'] || []).slice(0, 5),
      caseIds: cases.filter(c => (c.sols || []).includes(s.id)).map(c => c.id),
      methodology: BU_METHOD[s.bu] || CAT_METHOD[s.catId] || 'GEN',
      hours: eff.hours, hoursLow: eff.low, hoursHigh: eff.high,
      engagement: BU_ENGAGEMENT[s.bu] || 'fixed'
    };
  }).filter(Boolean);

  p.autoScope = uniq(recs.flatMap(r => r.scope));
  p.autoDeliverables = uniq(recs.flatMap(r => r.deliverables));
  p.autoCapabilities = uniq(recs.flatMap(r => r.capabilities));
  p.autoCases = uniq(recs.flatMap(r => r.caseIds));
  p.autoMetrics = uniq(recs.flatMap(r => r.metrics));
  p.autoStandards = uniq(recs.flatMap(r => r.standards));
  p.manualScope = p.manualScope || [];
  p.manualDeliverables = p.manualDeliverables || [];
  p.manualCapabilities = p.manualCapabilities || [];
  p.manualCases = p.manualCases || [];
  p.manualMetrics = p.manualMetrics || [];
  p.manualStandards = p.manualStandards || [];

  // The effective lists the rest of the tool reads.
  p.scopeItems = uniq([...p.autoScope, ...p.manualScope]);
  p.deliverables = uniq([...p.autoDeliverables, ...p.manualDeliverables]);
  p.capabilities = uniq([...p.autoCapabilities, ...p.manualCapabilities]);
  p.cases = uniq([...p.autoCases, ...p.manualCases]);
  p.metrics = uniq([...p.autoMetrics, ...p.manualMetrics]);
  p.standards = uniq([...p.autoStandards, ...p.manualStandards]);

  if (!p.methodKey || p.methodKey === 'AUTO') {
    const counts = {};
    recs.forEach(r => counts[r.methodology] = (counts[r.methodology] || 0) + 1);
    p.derivedMethod = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || 'GEN';
  }

  // Default effort per service, so a price exists before anyone types.
  p.items = (p.items || []).filter(i => (p.solutions || []).includes(i.solutionId));
  recs.forEach(r => {
    let it = p.items.find(x => x.solutionId === r.id);
    if (!it) { it = { solutionId: r.id, title: r.title }; p.items.push(it); }
    if (it.hours === undefined || it.hours === '') it.hours = r.hours;
    it.hoursLow = r.hoursLow;
    it.hoursHigh = r.hoursHigh;
    if (!it.engagementModel) it.engagementModel = ENGAGEMENT_MODES.find(m => m.id === r.engagement)?.label || p.engagementModel;
  });
  if (!p.engMode && recs.length) p.engMode = recs[0].engagement;
  return p;
};

// ===== PROPOSAL API FUNCTIONS (DB only) =====

const blankProposal = (seq) => ({
  uid: 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
  id: 'SP-PROP-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-' + String(seq || 1).padStart(2, '0'),
  title: 'Engineering & Transformation Proposal',
  status: 'Draft',
  preset: '',
  type: 'service',
  mode: 'guided',
  format: 'standard',
  buId: '',
  serviceLine: '',
  metrics: [],
  metricNote: '',
  closingNote: '',
  about: [],
  autoScope: [],
  manualScope: [],
  autoDeliverables: [],
  manualDeliverables: [],
  autoCapabilities: [],
  manualCapabilities: [],
  autoCases: [],
  manualCases: [],
  autoMetrics: [],
  manualMetrics: [],
  autoStandards: [],
  manualStandards: [],
  derivedMethod: '',
  engMode: '',
  calc: { people: '', hoursPerDay: 8, days: '', months: '', hours: '', monthly: '', extras: {}, contingency: 0 },
  solNotes: {},
  rate: { base: 25, currency: 'USD', fx: 84, discount: 0, hoursPerWeek: 40 },
  theyHave: '',
  whatsMissing: '',
  whatDifferent: '',
  whyNow: '',
  approveNow: '',
  stageAsk: '',
  outcome: { metric: '', baseline: '', target: '', by: '', method: '', conditions: '', ifnot: '' },
  stages: [],
  venture: {},
  clientName: '',
  clientIndustry: '',
  clientLocation: '',
  clientRevenue: '',
  clientLogo: '',
  rfpRef: '',
  recipients: [],
  team: [],
  doc: {
    version: '1.0',
    classification: 'Commercial in Confidence',
    issueDate: new Date().toISOString().slice(0, 10),
    preparedName: '',
    preparedDesig: '',
    preparedEmail: 'connect@solidpro-es.com',
    preparedPhone: '+91 63853 36852',
    reviewedName: '',
    reviewedDesig: '',
    approvedName: '',
    approvedDesig: ''
  },
  customerAbout: '',
  customerAsk: '',
  currentSituation: '',
  objectives: '',
  valueAdds: [],
  valueCustom: '',
  executiveSummary: '',
  methodKey: 'AUTO',
  approach: [],
  approachCustom: '',
  theme: 'SolidPro',
  practices: ['SolidPro'],
  whyUs: ['We deliver what we recommend', 'Evidence before investment', 'One partner across disciplines'],
  whyCustom: '',
  solutions: [],
  cases: [],
  capabilities: [],
  items: [],
  scopeItems: [],
  deliverables: [],
  acceptance: 'Each deliverable is reviewed by your nominated representative and treated as accepted if no written comments are received within five working days of submission.',
  outScope: [],
  assumptions: [],
  dependencies: [],
  risks: [],
  phases: [],
  efforts: [],
  governance: 'A written progress report every week with actions and owners, a stage review before each phase begins, and a live risk log maintained throughout.',
  escalation: [
    ['Level 1', 'Project Manager (SolidPro) ↔ Project Manager (Customer)', 'Within 24 hours'],
    ['Level 2', 'Delivery Head (SolidPro) ↔ Functional Head (Customer)', 'Within 48 hours'],
    ['Level 3', 'Business Head (SolidPro) ↔ Sponsor (Customer)', 'Within 5 working days']
  ],
  standards: ['ISO 9001:2015 Quality Management'],
  qualityNote: '',
  security: [],
  ip: IP_OPTIONS[1],
  compliance: {
    gst: '',
    pan: '',
    cin: '',
    msme: '',
    insurance: 'Professional indemnity and workmen compensation cover in force',
    vendorCode: ''
  },
  currency: 'INR',
  commercialModel: 'Fixed Fee',
  commercialAmount: '',
  taxNote: 'All amounts are exclusive of applicable taxes. Travel, boarding and lodging are charged at actuals where site work is required.',
  payTemplate: 'Standard 30/40/30',
  payments: [],
  paymentTerms: 'Payment within 30 days of invoice date.',
  commercialTerms: '',
  notes: '',
  engagementModel: 'Project-Based (fixed scope)',
  duration: { n: '', unit: 'weeks' },
  start: { mode: 'Within a set time of the purchase order', n: 2, unit: 'weeks', date: '' },
  validity: { n: 30, unit: 'days' },
  changeControl: 'Any change to the agreed scope, timeline or deliverables is raised as a written change request, assessed for effort and cost, and carried out only after both sides approve it in writing.',
  sla: [],
  warranty: 'We warrant that the deliverables meet the agreed specification. Anything reported within 30 days of acceptance that is attributable to our work is corrected at no additional cost.',
  hse: 'Our people follow all your site safety rules, use the prescribed PPE, complete site induction before entry and observe permit-to-work requirements. Any incident or near miss is reported to your HSE representative immediately.',
  esg: 'SolidPro operates an environmental management approach aligned to ISO 14001 principles. Through our NetZeroHub practice we support customers with measuring, reducing and disclosing emissions, and we raise sustainability opportunities wherever the engagement can influence energy, material or waste outcomes.',
  nextSteps: 'Confirm the scope and priorities → agree commercials and start date → issue the purchase order → kick-off and data collection → execution against the approved plan.',
  annexures: [],
  approval: {
    status: 'Not sent',
    levels: [
      { name: '', email: '', designation: '', status: 'Pending', comment: '', at: '' },
      { name: '', email: '', designation: '', status: 'Pending', comment: '', at: '' }
    ],
    twoLevel: false,
    history: [],
    token: ''
  },
  approvalStatus: 'Draft',
  approverId: null,
  approverEmail: '',
  approvalComment: '',
  approvalMessage: '',
  approvalSentAt: null,
  approvedAt: null,
  approvedBy: null,
  lockedForEditing: false,
  createdBy: null,
  _search: '',
  _libGroup: 'Understand the current situation',
  _libItem: '',
  _delivGroup: 'Reports & findings',
  _delivItem: '',
  sectionsOn: Object.fromEntries(SECTION_DEFS.map(s => [s[0], s[3] !== 1])),
  sectionOrder: SECTION_DEFS.map(s => s[0]),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

// The proposals table keeps scalar columns alongside a `data` JSONB blob, and
// the API returns the row as-is. Anything that consumes a proposal has to fold
// the blob back over a blank first, or it gets a record with no doc, no id and
// snake_case fields.
const hydrateProposal = (row) => {
  if (!row) return null;
  const blank = blankProposal(0);
  const d = row.data || {};
  return {
    ...blank,
    ...row,
    ...d,
    doc: { ...blank.doc, ...(d.doc || {}), ...(row.doc || {}) },
    id: d.id || row.id || blank.id,
    title: row.title || d.title || blank.title,
    approverId: row.approver_id ?? row.approverId ?? null,
    approvalStatus: row.approval_status || row.approvalStatus || 'Draft',
    approverEmail: row.approver_email || row.approverEmail || '',
    approvalComment: row.approval_comment || row.approvalComment || '',
    approvalMessage: row.approval_message || row.approvalMessage || '',
    approvalSentAt: row.approval_sent_at || row.approvalSentAt || null,
    approvedAt: row.approved_at || row.approvedAt || null,
    approvedBy: row.approved_by || row.approvedBy || null,
    lockedForEditing: row.locked_for_editing !== undefined ? row.locked_for_editing : (row.lockedForEditing || false)
  };
};

// ---- API-based proposal functions ----
const fetchProposals = async () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token');
  const res = await api.get('/proposals', {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data || [];
};

const fetchProposal = async (uid) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token');
  const res = await api.get(`/proposals/${uid}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};


const duplicateProposal = async (uid) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token');
  const res = await api.post(`/proposals/${uid}/duplicate`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// Applies a proposal type: engagement defaults, which sections are on, and the
// order they print in. Ported from the HTML build.
const applyProposalType = (p, key, mode) => {
  const t = PROPOSAL_TYPES[key];
  if (!t) return p;
  p.type = key;
  p.mode = mode || t.mode;
  p.engagementModel = t.eng;
  p.commercialModel = t.com;
  p.sectionsOn = Object.fromEntries(SECTION_DEFS.map(s => [s[0], t.on.includes(s[0])]));
  p.sectionOrder = t.order.concat(SECTION_DEFS.map(s => s[0]).filter(x => !t.order.includes(x)));
  if (!p.title || p.title === 'Engineering & Transformation Proposal') {
    p.title = key === 'venture' ? 'Business Case & Commercial Proposal'
      : key === 'credentials' ? 'SolidPro Capability Document'
      : key === 'staffing' ? 'Engineering Team Proposal'
      : key === 'outcome' ? 'Outcome-Based Proposal'
      : key === 'transformation' ? 'Transformation Programme Proposal'
      : key === 'quick' ? 'Proposal'
      : 'Engineering Services Proposal';
  }
  return p;
};

// Persists a proposal that has already been shaped client-side, rather than
// creating a blank one and patching it with a second round-trip.
const createProposalWith = async (proposal) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No token');
  const { _search, _libGroup, _libItem, _delivGroup, _delivItem, ...payload } = proposal;
  const res = await api.post('/proposals', payload, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
};

// ===== NEW PROPOSAL CHOOSER =====
// Five answers. Everything else - sections, scope, capability, case studies,
// methodology and the first price - is derived from the services picked here.
const NewProposalModal = ({ onClose, onCreated }) => {
  const [depth, setDepth] = useState('detailed');
  const [bu, setBu] = useState('');
  const [line, setLine] = useState('');
  const [svc, setSvc] = useState([]);
  const [client, setClient] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const BUs = buMaster();
  const lines = bu ? linesFor(bu) : [];
  const svcs = bu ? servicesFor(bu, line) : [];
  const ready = !!bu && svc.length > 0 && client.trim().length > 0;
  const master = serviceMaster();
  const hoursOf = id => (master[id] || {}).hours || 0;
  const totalHours = svc.reduce((a, id) => a + hoursOf(id), 0);

  const pickBu = (name) => { setBu(name); setLine(''); setSvc([]); };
  const toggleSvc = (id) => setSvc(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);

  const create = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setErr('');
    try {
      const p = blankProposal(1);
      p.clientName = client.trim();
      p.buId = bu;
      p.serviceLine = line;
      p.solutions = svc.slice();

      // The structure follows from the answers - the user is never asked for it.
      const first = master[svc[0]];
      applyProposalType(p, depth === 'quick' ? 'quick' : 'service', depth === 'quick' ? 'fast' : 'full');
      p.format = 'standard';
      p.mode = depth === 'quick' ? 'fast' : 'full';
      p.title = (first ? first.bu : 'Engineering') + ' Proposal';
      if (first) p.engMode = first.engagement;
      recomputeAuto(p);

      const saved = await createProposalWith(p);
      onCreated(saved, (p.autoScope || []).length);
    } catch (e) {
      console.error('Create failed:', e);
      setErr('The proposal could not be created. Check the connection and try again.');
      setBusy(false);
    }
  };

  return (
    <div className="modalback" onClick={onClose}>
      <div className="modal wide" onClick={e => e.stopPropagation()}>
        <div className="kicker">New proposal</div>
        <h2>Create a proposal</h2>
        <p>Five answers. Everything else – the sections, the scope, the capability, the case studies, the methodology and the first price – is derived from the services you pick.</p>

        <div className="npstep"><span className="npn">1</span><b>How much detail?</b></div>
        <div className="modegrid">
          <button className={`modecard ${depth === 'quick' ? 'on' : ''}`} onClick={() => setDepth('quick')}>
            <b>Quick Proposal</b><small>A short priced offer for a request you already understand. Four screens, 1–4 pages.</small>
          </button>
          <button className={`modecard ${depth === 'detailed' ? 'on' : ''}`} onClick={() => setDepth('detailed')}>
            <b>Detailed Proposal</b><small>The full document a large-corporate procurement team expects. Nine screens.</small>
          </button>
        </div>

        <div className="npstep"><span className="npn">2</span><b>Which SolidPro business unit?</b><span className="npreq">required</span></div>
        <div className="bugrid">
          {BUs.map(b => (
            <button key={b.name} className={`bucard ${bu === b.name ? 'on' : ''}`} onClick={() => pickBu(b.name)}>
              <b>{b.name}</b><small>{b.lines.slice(0, 3).join(' · ')}</small><span className="tag">{b.count} services</span>
            </button>
          ))}
        </div>

        {bu ? (
          <>
            <div className="npstep"><span className="npn">3</span><b>Service line</b><span className="nphint">optional – narrows the list</span></div>
            <div className="chipwrap">
              <button className={`chip ${!line ? 'on' : ''}`} onClick={() => setLine('')}>All lines</button>
              {lines.map(l => <button key={l} className={`chip ${line === l ? 'on' : ''}`} onClick={() => setLine(l)}>{l}</button>)}
            </div>

            <div className="npstep"><span className="npn">4</span><b>Which services?</b><span className="npreq">at least one</span><span className="nphint">{svc.length} selected</span></div>
            <div className="mplist" style={{ border: '1px solid #e5e7eb', borderRadius: 12, maxHeight: 230 }}>
              {svcs.length ? svcs.map(s => (
                <label key={s.id} className={`mprow ${svc.includes(s.id) ? 'on' : ''}`}>
                  <input type="checkbox" checked={svc.includes(s.id)} onChange={() => toggleSvc(s.id)} />
                  <span><b>{s.title}</b><small>{s.sub} – about {hoursOf(s.id) || '-'} hours as a starting estimate</small></span>
                </label>
              )) : <div className="empty">No services here.</div>}
            </div>
          </>
        ) : <div className="noteBox">Choose a business unit to see its service lines.</div>}

        <div className="npstep"><span className="npn">5</span><b>Customer</b><span className="npreq">required</span></div>
        <div className="formrow">
          <input
            value={client}
            onChange={e => setClient(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') create(); }}
            placeholder="Customer company name"
          />
        </div>

        {svc.length > 0 && (
          <div className="okBox">
            <b>Ready.</b> {svc.length} service{svc.length > 1 ? 's' : ''} selected – about {totalHours.toLocaleString()} hours of effort as a starting estimate, which you can change at any point.
          </div>
        )}
        {err ? <div className="warnBox">{err}</div> : null}

        <div className="modalactions">
          <button className="btn line" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={create} disabled={!ready || busy}>
            {busy ? 'Creating…' : 'Create proposal draft →'}
          </button>
        </div>
        <div className="hint" style={{ textAlign: 'right', marginTop: 8 }}>
          The proposal structure is chosen for you. Change it later under <b>Advanced → Change proposal structure</b>.
        </div>
      </div>
    </div>
  );
};

// ===== SETTINGS =====
// The React build ships with a compiled-in API address; this lets an operator
// point a deployed copy at a different backend without a rebuild.
const SettingsModal = ({ onClose }) => {
  const [url, setUrl] = useState(storedApiBase());
  const [ai, setAi] = useState(storedAiBase());
  const [out, setOut] = useState(null);

  const test = async () => {
    setOut({ kind: 'note', text: 'Testing…' });
    try {
      const base = (url || DEFAULT_API_BASE).replace(/\/$/, '');
      const res = await axios.get(base + '/health', { timeout: 8000 });
      const d = res.data || {};
      // The drafting service reports what it can actually do; the data API
      // only reports that it is up.
      const bits = [];
      if (d.provider) bits.push('provider ' + d.provider);
      if (d.model) bits.push('model ' + d.model);
      if (d.ai !== undefined) bits.push(d.ai ? 'AI key configured' : 'no AI key set');
      if (d.email !== undefined) bits.push(d.email ? 'email configured' : 'no email configured');
      setOut({ kind: d.ai === false ? 'warn' : 'ok', text: 'Connected.' + (bits.length ? ' ' + bits.join(' · ') + '.' : '') });
    } catch (e) {
      setOut({ kind: 'warn', text: 'Could not reach that address. Check the URL and that the server allows this origin (CORS).' });
    }
  };

  return (
    <div className="modalback" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="kicker">Settings</div>
        <h2>Backend connection</h2>
        <p>Where this app looks for the proposals API. Leave it blank to use the address this build was compiled with.</p>
        <div className="formrow">
          <label>Backend base URL</label>
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder={DEFAULT_API_BASE} />
        </div>
        <div className="noteBox">Default: <b>{DEFAULT_API_BASE}</b>. Include the <b>/api</b> path. Changing this signs you out of the current server's session.</div>
        <div className="section"><h2>AI drafting</h2></div>
        <p>Where the <b>✨ Write with AI</b> buttons send their request. It must expose <b>POST /api/ai/write</b> taking <code>{'{ field, tone, task, context }'}</code> and returning <code>{'{ text }'}</code>. The provider API key lives on that server, never in this app.</p>
        <div className="formrow">
          <label>Drafting service base URL</label>
          <input value={ai} onChange={e => setAi(e.target.value)} placeholder={DEFAULT_AI_BASE} />
        </div>
        <div className="noteBox">
          Default: <b>{DEFAULT_AI_BASE}</b> – the drafting service that ships with this project (<code>ai-server</code>).
          Leave blank to use it, or type <b>off</b> to disable AI drafting and use the offline composer only.
          Either way the ✨ buttons keep working; only the quality of the draft changes.
        </div>
        {out ? <div className={out.kind === 'ok' ? 'okBox' : out.kind === 'warn' ? 'warnBox' : 'hint'}>{out.text}</div> : null}
        <div className="modalactions">
          <button className="btn line" onClick={onClose}>Cancel</button>
          <button className="btn line" onClick={test}>Test connection</button>
          <button className="btn primary" onClick={() => { setApiBase(url.trim()); setAiBase(ai.trim()); onClose(); }}>Save</button>
        </div>
      </div>
    </div>
  );
};

// ===== TYPE SWITCHER COMPONENT =====
const TypeSwitcher = ({ p, onClose, onApply }) => {
  const [npType, setNpType] = useState(p?.type || 'service');
  const [npMode, setNpMode] = useState(p?.mode || 'guided');

  return (
    <div className="modalback" onClick={onClose}>
      <div className="modal wide" onClick={e => e.stopPropagation()}>
        <div className="kicker">Change type or mode</div>
        <h2>{p?.id || 'SP-PROP-20260819-02'}</h2>
        <p>Changing the type resets which sections are switched on and reorders the document. Your content is kept - only the structure changes.</p>
        
        <div className="typegrid">
          {Object.keys(PROPOSAL_TYPES).map(k => {
            const t = PROPOSAL_TYPES[k];
            return (
              <button 
                key={k} 
                className={`typecard ${npType === k ? 'on' : ''}`} 
                onClick={() => setNpType(k)}
              >
                <div className="tct">
                  <b>{t.label}</b>
                  <span className="tag">{t.tag}</span>
                </div>
                <small>{t.line}</small>
              </button>
            );
          })}
        </div>

        <div className="modegrid" style={{ marginTop: '16px' }}>
          {Object.keys(MODES).map(k => {
            const m = MODES[k];
            const screens = k === 'fast' ? 4 : k === 'guided' ? 9 : 12;
            return (
              <button 
                key={k} 
                className={`modecard ${npMode === k ? 'on' : ''}`} 
                onClick={() => setNpMode(k)}
              >
                <b>{m.label}</b>
                <span className="tag">{screens} screens</span>
                <small>{m.line}</small>
              </button>
            );
          })}
        </div>

        <div className="modalactions">
          <button className="btn line" onClick={onClose}>Cancel</button>
          <button className="btn line" onClick={() => {
            const newP = { ...p, mode: npMode };
            onApply(newP);
          }}>Change mode only</button>
          <button className="btn primary" onClick={() => {
            const t = PROPOSAL_TYPES[npType];
            const newP = { ...p, type: npType, mode: npMode };
            if (t) {
              SECTION_DEFS.forEach(s => {
                newP.sectionsOn[s[0]] = t.on.includes(s[0]);
              });
              newP.sectionOrder = t.order.concat(
                SECTION_DEFS.map(s => s[0]).filter(x => !t.order.includes(x))
              );
              if (!newP.title || newP.title === 'Engineering & Transformation Proposal') {
                newP.title = npType === 'venture' ? 'Business Case & Commercial Proposal'
                  : npType === 'credentials' ? 'SolidPro Capability Document'
                  : npType === 'staffing' ? 'Engineering Team Proposal'
                  : npType === 'outcome' ? 'Outcome-Based Proposal'
                  : npType === 'transformation' ? 'Transformation Programme Proposal'
                  : npType === 'quick' ? 'Proposal'
                  : 'Engineering Services Proposal';
              }
            }
            onApply(newP);
          }}>Apply</button>
        </div>
      </div>
    </div>
  );
};

// ===== STEP SETS =====
// ===== STEP SETS =====
// Ported verbatim from capabilities-library-v18.html. Labels carry their own
// "1 · " prefixes exactly as the HTML defines them, so the sidebar numbering
// matches the source app.

const STANDARD_STEP_SET = [
  ['charter', '1 · Proposal setup', 'Business unit, services and the document details.', ['charter']],
  ['customer', '2 · Customer', 'Who they are, what they asked, and who receives it.', ['basics', 'story', 'people']],
  ['about', '3 · About SolidPro', 'Capability, experience and why us - the standing material.', ['about']],
  ['propose', '4 · What we propose', 'What we recommend, the scope, what they receive and how success is measured.', ['propose', 'scope', 'deliver', 'metrics']],
  ['money', '5 · Engagement & commercials', 'The engagement model, what it will cost, and payment terms.', ['engagement', 'money']],
  ['process', '6 · Process & next actions', 'Method, timeline, governance and what happens next.', ['method', 'plan', 'govern']],
  ['conditions', '7 · Conditions', 'Assumptions, what is not included, terms and compliance.', ['terms']],
  ['approval', '8 · Approval & closing', 'Prepared, reviewed, approved - and the closing note.', ['closing']],
  ['generate', '9 · Generate', 'Sections, order and the finished document.', ['sections', 'review']]
];

const BTOS_STEP_SET = [
  ['charter', '1 · Programme setup', 'Business unit, services and the document details.', ['charter']],
  ['customer', '2 · Customer & the problem', 'What is happening, and why it matters now.', ['basics', 'story', 'people']],
  ['about', '3 · About SolidPro', 'Capability, experience and why us.', ['about']],
  ['sixd', '4 · The 6D journey', 'Discover → Diagnose → Design → Deliver → Demonstrate → Differentiate.', ['method', 'stages']],
  ['propose', '5 · Scope & deliverables', 'What each stage covers and what it produces.', ['propose', 'scope', 'deliver', 'metrics']],
  ['money', '6 · Commercials per stage', 'Each stage priced, with a gate at the end.', ['engagement', 'money']],
  ['process', '7 · Governance & plan', 'How the programme is run.', ['plan', 'govern']],
  ['conditions', '8 · Conditions', 'Assumptions, what is not included, terms.', ['terms']],
  ['approval', '9 · Approval & closing', 'Sign-off flow and closing note.', ['closing']],
  ['generate', '10 · Generate', 'Sections, order and the finished document.', ['sections', 'review']]
];

const VENTURE_STEP_SET = [
  ['charter', '1 · Proposal charter', 'Business unit, services and the document details.', ['charter']],
  ['customer', '2 · Customer & the ask', 'Who they are and what they asked for.', ['basics', 'story', 'people']],
  ['case', '3 · The business case', 'Opportunity, market, why them, revenue, roadmap, parties.', ['venture']],
  ['about', '4 · About SolidPro', 'Capability, experience and why us.', ['about']],
  ['propose', '5 · What SolidPro contributes', 'Services, scope, deliverables and metrics.', ['propose', 'scope', 'deliver', 'metrics']],
  ['stages', '6 · Stages & gates', 'Each stage priced, with a decision at the end.', ['stages']],
  ['money', '7 · Commercials', 'How the customer is charged.', ['engagement', 'money']],
  ['process', '8 · Delivery & governance', 'Method, plan and how it is run.', ['method', 'plan', 'govern']],
  ['conditions', '9 · Conditions', 'Assumptions, exclusions and terms.', ['terms']],
  ['approval', '10 · Approval & closing', 'Sign-off flow and closing note.', ['closing']],
  ['generate', '11 · Generate', 'Sections, order and the finished document.', ['sections', 'review']]
];

const STEP_SETS = {
  fast: [
    ['ft1', 'Project & client', 'Who it is for, and who is involved.', ['basics', 'people']],
    ['ft2', 'Solution & scope', 'What we will do and what they receive.', ['content', 'scope', 'deliver']],
    ['ft3', 'Timeline & price', 'How long, and how much.', ['plan', 'money']],
    ['ft4', 'Review & send', 'Check it, generate it, send it for approval.', ['sections', 'review']]
  ],
  guided: [
    ['g1', 'Customer & opportunity', 'Who they are and what they are trying to achieve.', ['basics']],
    ['g2', 'The story', 'The eight questions that make a proposal persuasive.', ['story']],
    ['g3', 'What we recommend', 'Services, expertise, experience.', ['content']],
    ['g4', 'Scope & roadmap', 'What we do, what they get, and when.', ['scope', 'deliver', 'plan']],
    ['g5', 'Price', 'How the customer will be charged.', ['money']],
    ['g6', 'Enterprise details', 'Only needed for larger customers - skip it otherwise.', ['people', 'govern', 'terms', 'sections']],
    ['g7', 'Generate & approval', 'Produce the document and send it for approval.', ['review']]
  ],
  full: [
    ['start', 'Quick start', 'Type, mode and presets.', ['start']],
    ['basics', 'Customer & document', 'Who it is for, and the document details.', ['basics']],
    ['people', 'People', 'Your team and their recipients.', ['people']],
    ['content', 'What we propose', 'Services, expertise, experience and value.', ['content']],
    ['scope', 'Scope: what we will do', 'Everything included in the price.', ['scope']],
    ['deliver', 'What the customer receives', 'Deliverables and how they are approved.', ['deliver']],
    ['plan', 'Timeline & effort', 'How long it takes and what it uses.', ['plan']],
    ['govern', 'Managing it & risk', 'How the project is run, and what could go wrong.', ['govern']],
    ['terms', 'Terms & compliance', 'Quality, confidentiality, statutory, service levels.', ['terms']],
    ['money', 'Price', 'How the customer is charged.', ['money']],
    ['sections', 'Sections & order', 'Drag to reorder, tick to include.', ['sections']],
    ['review', 'Generate & approval', 'Produce the document and send it for approval.', ['review']]
  ]
};

const typeOf = (p) => PROPOSAL_TYPES[p?.type] || PROPOSAL_TYPES.service;

// Resolves which step set a proposal uses. Mirrors curSteps() in the HTML,
// including its precedence: p.format wins over p.mode, and proposal-type
// extras are spliced into mode-driven sets only.
const curSteps = (p) => {
  if (!p) return STEP_SETS.full;

  // Format-driven sets take priority and are used as-is.
  if (p.format === 'btos') return BTOS_STEP_SET;
  if (p.format === 'detailed') return VENTURE_STEP_SET;
  if (p.format === 'standard') return STANDARD_STEP_SET;

  // Otherwise fall back to the mode-driven sets.
  let set = p.mode === 'fast' ? STEP_SETS.fast : p.mode === 'guided' ? STEP_SETS.guided : STEP_SETS.full;
  const t = typeOf(p);

  if (t.extra === 'venture') {
    set = [set[0], ['venture', 'The business case', 'Market, revenue, roadmap and the parties.', ['venture']], ...set.slice(1)];
  }
  if (t.extra === 'outcome') {
    set = [...set.slice(0, 2), ['outcome', 'The outcome', 'What we commit to, and how it is measured.', ['outcome']], ...set.slice(2)];
  }
  if (t.extra === 'stages') {
    set = [...set.slice(0, 4), ['stages', 'Stages & gates', 'Each stage priced, with a decision at the end.', ['stages']], ...set.slice(4)];
  }
  if (p.mode !== 'full') set = set.filter(s => s[0] !== 'start');

  return set;
};

// The first step of whatever set applies - used on load and after a type switch.
const firstStepOf = (p) => (curSteps(p)[0] || ['charter'])[0];

// ===== OFFLINE DRAFTING =====
// Composes a usable draft from what the user has already entered. No network
// call, no API key: the same role the HTML build's localDraft plays when no
// backend is configured, but covering every field that carries an AI button so
// Regenerate always produces something.
//
// The tone is applied afterwards by toneShift(), so pressing Regenerate with a
// different tone visibly changes the wording rather than returning the same
// sentence each time.

const draftBody = (field, p) => {
  const all = window._SOLS || [];
  const sols = (p.solutions || []).map(id => all.find(s => s.id === id)).filter(Boolean);
  const who = p.clientName || 'The customer';
  const ask = lower(p.customerAsk || 'address a defined engineering requirement');
  const askS = /[.!?]$/.test(ask) ? ask : ask + '.';
  const titles = sols.slice(0, 3).map(s => s.title).join(', ');
  const dur = durText(p.duration);
  const total = p.commercialAmount || (p.items || []).reduce((a, i) => a + numOf(i.amount), 0);
  const V = p.venture || {};
  const O = p.outcome || {};

  switch (field) {
    /* --- the story --- */
    case 'customerAbout':
      return `${who} operates in ${p.clientIndustry || 'its sector'}${p.clientLocation ? ' from ' + p.clientLocation : ''}. This engagement concerns the part of that operation covered by the scope set out in this proposal.`;
    case 'customerAsk':
      return `${who} has asked SolidPro to ${askS}`;
    case 'currentSituation':
      return `Today, ${p.clientName || 'the operation'} works to figures that have not been independently measured. This proposal begins by establishing that baseline so every later comparison is made against something agreed.`;
    case 'theyHave':
      return `${who} already has the operation, the people and the records this work depends on${p.clientLocation ? ' at ' + p.clientLocation : ''}. That is the foundation the engagement builds on rather than replaces.`;
    case 'whatsMissing':
      return `What is missing is an agreed, measured view of the current position and a comparison of the available options on a common basis. Without it, decisions rest on opinion rather than evidence.`;
    case 'whatDifferent':
      return `At the end of this work ${lower(who)} will hold a measured baseline, a recommendation supported by evidence, and a decision that can be defended to the board${dur ? ', reached within ' + dur : ''}.`;
    case 'whyNow':
      return `The cost of the current position grows for as long as it is left unmeasured, and the window to act before the next planning cycle is limited. Starting now keeps the decision ahead of the commitment.`;
    case 'approveNow':
      // 'the 10 weeks Line Balancing' does not read; name the stage, then the length.
      return sols.length
        ? `Approve the first stage - ${sols[0].title}${dur ? ', ' + dur : ''} - so the baseline can be established and the options costed.`
        : `Approve the ${dur || 'initial'} diagnostic so the baseline can be established and the options costed.`;
    case 'objectives':
      return ['Establish an agreed, measured view of the current position',
        'Identify what is genuinely limiting the outcome',
        'Compare the available options on a common basis',
        'Give leadership a decision they can defend'].join('\n');
    case 'executiveSummary':
      return `${who} has asked SolidPro to ${askS} We propose a ${lower(p.engagementModel || 'project-based')} engagement`
        + (sols.length ? ' combining ' + titles : '')
        + (dur ? ', delivered over ' + dur : '')
        + `. The work starts from a measured view of the current position, moves into the deliverables listed in this proposal, and closes with the result verified against that starting point.`
        + (total ? ` The indicative value is ${money(p.currency, total)}.` : '');

    /* --- value and differentiators --- */
    case 'valueCustom':
      return VALUE_LIB.slice(0, 5).map(v => v[1]).join('\n');
    case 'whyCustom':
      return WHY_LIB.slice(0, 3).map(w => w[1]).join('\n');
    case 'approachCustom':
      return `The approach is adapted for ${p.clientIndustry || 'this sector'}: ${sols.length ? lower(sols[0].title) + ' leads the sequence' : 'the first stage establishes the baseline'}, and each stage closes with a written review before the next begins.`;

    /* --- delivery and governance --- */
    case 'governance':
      return `A written progress report every week with actions and owners, a stage review before each phase begins, and a live risk log maintained throughout. Decisions are recorded in the report and escalated on the matrix below if they cannot be closed within a week.`;
    case 'acceptance':
      return `Each deliverable is submitted to your nominated representative for review. It is treated as accepted if no written comments are received within five working days of submission, and any comments raised are addressed before the next deliverable is issued.`;
    case 'qualityNote':
      return `Every deliverable is peer-reviewed inside SolidPro before submission, assumptions are recorded alongside results, and each stage is signed off before the next begins${(p.standards || []).length ? ', in line with ' + p.standards.slice(0, 2).join(' and ') : ''}.`;
    case 'nextSteps':
      return 'Confirm the scope and priorities → agree commercials and start date → issue the purchase order → kick-off and data collection → execution against the approved plan.';
    case 'commercialTerms':
      return `Prices are held for ${durText(p.validity) || '30 days'} from the date of issue and are quoted in ${p.currency || 'INR'}. Any change to the agreed scope is handled through written change control. Liability is capped at the total fee paid under this engagement. Either side may cancel with 30 days' written notice, with work completed to that date payable in full.`;

    /* --- the business case --- */
    case 'venture.opportunity':
      return `${who} is positioned to serve a demand its current operation cannot yet meet in full. The opportunity is to close that gap with a defined, staged commitment rather than a single large investment.`;
    case 'venture.whyNow':
      return `The window is open while the current cost base and market position still allow a staged entry. It closes as competitors commit capacity and the cost of catching up rises.`;
    case 'venture.assets':
      return `${who} already holds the operating knowledge, the customer relationships and the physical base that a competitor would need years to build. That advantage is what this venture is built on.`;
    case 'venture.market':
      return `State the size, the structure and who actually buys, using only figures ${lower(who)} has verified. Where a number is an estimate, say so and name the source.`;
    case 'venture.willnot':
      return `SolidPro will not take an equity position, will not carry market risk, and will not operate the business day to day. Our contribution is the engineering and the evidence behind the decisions.`;
    case 'venture.handover':
      return `Ownership transfers progressively: SolidPro leads the build, runs it jointly through the proving period, then hands over to your named team with documentation, training and a defined support tail.`;

    /* --- outcome-based commitments --- */
    case 'outcome.method':
      return `${O.metric || 'The agreed measure'} is taken from ${lower(p.clientName || 'your')} own source records, measured by a jointly nominated owner${O.by ? ' by ' + O.by : ''}, and reported monthly against the baseline agreed at kick-off.`;
    case 'outcome.conditions':
      return `The commitment assumes the baseline is agreed in writing before work begins, that access to data, people and the site is provided as scheduled, and that no change is made to the operation outside this scope that affects the measure.`;
    case 'outcome.ifnot':
      return `If the agreed outcome is not reached and the conditions above were met, SolidPro continues at its own cost until it is, or the outcome-linked element of the fee is not payable.`;

    default:
      return '';
  }
};

// Reshapes a composed draft to the requested tone. Offline, so this is wording
// rather than rewriting - but it makes Regenerate produce a visibly different
// answer instead of the same sentence every time.
const toneShift = (text, tone, p) => {
  if (!text) return text;
  const t = String(tone || '');
  const sentences = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  const multiline = text.includes('\n');

  if (/Shorter and sharper/i.test(t) && !multiline) {
    return sentences.slice(0, Math.max(1, sentences.length - 1)).join(' ');
  }
  if (/More formal/i.test(t) && !multiline) {
    return `For the avoidance of doubt: ${lower(text)}`;
  }
  if (/More detailed/i.test(t) && !multiline) {
    return text + ` The detail behind this sits in the scope and deliverables sections of this proposal, and each point is owned by a named member of the engagement team.`;
  }
  if (/Commercially persuasive/i.test(t) && !multiline) {
    const total = p && (p.commercialAmount || (p.items || []).reduce((a, i) => a + numOf(i.amount), 0));
    return text + ` The cost of leaving this unresolved compounds every quarter${total ? `, against an indicative investment of ${money(p.currency, total)}` : ''}.`;
  }
  return text;
};

const localDraft = (field, p, tone) => toneShift(draftBody(field, p), tone, p);

// ===== AI DRAFTING =====
// Per-field brief: [panel heading, the instruction sent to the writer].
// A field only shows a "Write with AI" button if it appears here.
const AI_PRESETS = {
  theyHave: ['Describe what they already have', 'Two or three sentences on the assets, data, people or market position this customer can build on. Be specific.'],
  whatsMissing: ['Describe the gap', 'Two or three sentences naming honestly what is missing today. Only name gaps that can be filled.'],
  whatDifferent: ['Describe the end state', 'Two or three sentences on how things will be different once the work is done. Describe the situation, not the deliverables.'],
  whyNow: ['Explain why now', 'Two sentences on why this matters this quarter rather than next year.'],
  approveNow: ['Draft the ask', 'One sentence naming the single smallest next commitment the customer should approve. Keep it small and specific.'],
  'venture.opportunity': ['Describe the opportunity', 'Two or three sentences a board member would repeat to someone else.'],
  'venture.whyNow': ['Explain the window', 'Two sentences on why the opportunity is open now and what closes it.'],
  'venture.assets': ['Describe their advantage', 'Three sentences on what this customer has that a competitor would need years to build.'],
  'venture.market': ['Describe the market', 'Three sentences on size, structure and who buys. Use only facts supplied.'],
  'venture.willnot': ['Draft the limits', 'Two or three sentences stating plainly what SolidPro will not do, and why.'],
  'venture.handover': ['Draft the handover', 'Two or three sentences on how the business becomes fully the customer’s.'],
  'outcome.method': ['Draft the measurement method', 'Two sentences naming who measures, how often and from which source record.'],
  'outcome.conditions': ['Draft the conditions', 'Two sentences on what the commitment depends on and is outside our control.'],
  'outcome.ifnot': ['Draft the consequence', 'Two sentences stating what happens if the outcome is not achieved.'],
  executiveSummary: ['Write the executive summary', 'Three or four sentences: what they need, what we recommend, what they end up with.'],
  customerAbout: ['Describe the customer', 'Two or three factual sentences about their business and operation.'],
  currentSituation: ['Describe the current situation', 'What is happening today, factually, in a way they would recognise as accurate.'],
  objectives: ['List the objectives', 'Four short objectives, one per line, no bullets.'],
  customerAsk: ['Sharpen their ask', 'One sentence stating precisely what they have asked us to do.'],
  governance: ['Draft the governance model', 'Two or three sentences on cadence, ownership and escalation.'],
  nextSteps: ['Draft the next steps', 'One sentence describing the sequence from agreement to kick-off.'],
  acceptance: ['Draft acceptance criteria', 'Two sentences on how deliverables are reviewed and accepted.'],
  qualityNote: ['Draft the quality note', 'Two or three sentences on how quality is assured on this engagement.'],
  commercialTerms: ['Draft commercial terms', 'Short paragraph covering escalation, currency, cancellation and liability.'],
  valueCustom: ['Suggest extra value points', 'Five short value points specific to this customer, one per line, no bullets.'],
  whyCustom: ['Suggest extra differentiators', 'Three short differentiators specific to this customer, one per line.'],
  approachCustom: ['Add a stage note', 'Two sentences describing any stage specific to this engagement.']
};

const AI_TONES = ['Plain and factual', 'More formal / enterprise', 'Shorter and sharper', 'More detailed', 'Commercially persuasive'];

// The drafting endpoint is separate from the data API and off by default: no
// request leaves the browser until someone sets it in Settings.
const AI_ENDPOINT_KEY = 'solidpro-ai-endpoint';
const DEFAULT_AI_BASE = process.env.REACT_APP_AI_URL ||
  (isLocalHost() ? 'http://localhost:8787' : '/ai');
const AI_OFF = 'off';
const storedAiBase = () => {
  try {
    const raw = (localStorage.getItem(AI_ENDPOINT_KEY) || '').trim();
    if (raw === AI_OFF) return '';
    if (!raw) return DEFAULT_AI_BASE;
    return validApiBase(raw) || DEFAULT_AI_BASE;
  } catch (e) { return DEFAULT_AI_BASE; }
};
// Blank restores the default; 'off' turns drafting off and leaves the offline
// composer in place.
const setAiBase = (v) => {
  const clean = (v || '').trim();
  try {
    if (!clean || clean === DEFAULT_AI_BASE) localStorage.removeItem(AI_ENDPOINT_KEY);
    else localStorage.setItem(AI_ENDPOINT_KEY, clean.toLowerCase() === AI_OFF ? AI_OFF : (validApiBase(clean) || ''));
  } catch (e) {}
};

// Everything the writer is told about this proposal. Plain text on purpose -
// it is a prompt, not a payload.
const aiCtx = (p) => {
  const sols = (p.solutions || []).map(id => (window._SOLS || []).find(s => s.id === id)).filter(Boolean);
  return [
    `Customer: ${p.clientName || '(not set)'} | Industry: ${p.clientIndustry || '(not set)'} | Scale: ${p.clientRevenue || '(not set)'}`,
    `Their ask: ${p.customerAsk || '(not set)'}`,
    `Current situation: ${p.currentSituation || '(not set)'}`,
    `About them: ${p.customerAbout || '(not set)'}`,
    `Objectives: ${linesToList(p.objectives).join('; ') || '(not set)'}`,
    `Proposed services: ${sols.map(s => s.title + ' - ' + s.what).join(' | ') || '(none)'}`,
    `Scope lines: ${(p.scopeItems || []).slice(0, 12).join('; ') || '(none)'}`,
    `Engagement: ${p.engagementModel}, ${durText(p.duration) || 'duration TBC'}, value ${money(p.currency, p.commercialAmount || '')}`
  ].join('\n');
};

// Calls the drafting service when one is configured, otherwise composes the
// offline draft from what has already been entered.
const aiWrite = async (field, tone, p) => {
  const base = storedAiBase();
  const task = (AI_PRESETS[field] || ['Write this section', 'Two or three sentences.'])[1];
  const offline = () => ({ text: localDraft(field, p, tone), source: 'offline' });
  if (!base) return offline();

  try {
    const res = await fetch(base.replace(/\/$/, '') + '/api/ai/write', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ field, tone: tone || 'Plain and factual', task, context: aiCtx(p) })
    });
    if (!res.ok) {
      return { ...offline(), warning: `The drafting service returned ${res.status}, so this is an offline draft.` };
    }
    const d = await res.json();
    const text = (d.text || '').trim();
    if (!text) return { ...offline(), warning: 'The drafting service returned nothing, so this is an offline draft.' };
    return { text, source: 'service' };
  } catch (e) {
    return { ...offline(), warning: `Could not reach the drafting service (${e.message}), so this is an offline draft.` };
  }
};

const AiPanel = ({ field, p, onApply, onClose }) => {
  const [tone, setTone] = useState(AI_TONES[0]);
  const [hint, setHint] = useState('');
  const [text, setText] = useState('');
  const [state, setState] = useState('idle');
  const [err, setErr] = useState('');
  const heading = (AI_PRESETS[field] || ['Draft this'])[0];
  const configured = !!storedAiBase();

  const run = async () => {
    setState('running');
    setErr('');
    try {
      const r = await aiWrite(field, tone + (hint ? ' - ' + hint : ''), p);
      setText(r.text || '');
      setErr(r.warning || '');
      setState('done');
    } catch (e) {
      // Nothing above should throw, but a draft is better than a dead end.
      setText(localDraft(field, p, tone) || '');
      setErr(e.message);
      setState('done');
    }
  };

  // Draft on open, the way the HTML build does. Deliberately runs once:
  // re-running on every 'run' identity change would redraft on each keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);

  return (
    <div className="modalback" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="kicker">AI assist</div>
        <h2>{heading}</h2>
        <p>Drafted from what you have already entered. Nothing is applied until you press Use this.</p>
        <div className="formgrid">
          <div className="formrow">
            <label>Tone</label>
            <select value={tone} onChange={e => setTone(e.target.value)}>
              {AI_TONES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="formrow">
            <label>Extra instruction (optional)</label>
            <input value={hint} onChange={e => setHint(e.target.value)} placeholder="e.g. mention the 1,300 unit target" />
          </div>
        </div>
        <div className="aiout">
          {state === 'running' ? <div className="hint">Drafting…</div>
            : text ? <pre>{text}</pre>
            : <div className="warnBox">
                No draft could be produced for this field. Write it yourself, or set a drafting endpoint under Proposals → Settings.
              </div>}
        </div>
        {err ? <div className="noteBox">{err}</div> : null}
        {!configured && text ? <div className="fieldnote">Composed offline from what you have already entered. Set a drafting endpoint in Settings for full AI drafting.</div> : null}
        <div className="modalactions">
          <button className="btn line" onClick={onClose}>Cancel</button>
          <button className="btn line" onClick={run} disabled={state === 'running'}>{text ? 'Regenerate' : 'Generate'}</button>
          {text ? <button className="btn primary" onClick={() => { onApply(text); onClose(); }}>Use this</button> : null}
        </div>
      </div>
    </div>
  );
};

// The ✨ button itself. Self-contained so any field can carry one without the
// parent step having to hold panel state.
const AiButton = ({ field, p, onApply }) => {
  const [open, setOpen] = useState(false);
  if (!AI_PRESETS[field]) return null;
  return (
    <>
      <button type="button" className="aibtn" title="Draft this with AI" onClick={() => setOpen(true)}>✨ Write with AI</button>
      {open && <AiPanel field={field} p={p} onApply={onApply} onClose={() => setOpen(false)} />}
    </>
  );
};

// Practice blurbs used by the generated document.
const practiceBlurb = (x) => x === 'SolidPro'
  ? 'SolidPro is an engineering outcomes partner. Mechanical, electronics, embedded, software, manufacturing, digital transformation and sustainability capability sit under one execution model, so the team that writes the recommendation is the team that delivers it.'
  : x === 'Helixa'
  ? 'HeLiXA is SolidPro\u2019s Healthcare & Lifesciences engineering practice, supporting product development, quality, regulatory, manufacturing and digital workflows across the device and pharma lifecycle.'
  : x === 'NetZeroHub'
  ? 'NetZeroHub is SolidPro\u2019s sustainability practice - roadmap, decarbonization strategy, supplier sustainability, energy and carbon compliance, and the SATTOS / KARMA OS digital layer that keeps evidence and progress organised.'
  : 'TAKTIX is SolidPro\u2019s factory performance engineering capability: measure the current operation, build and validate a model of it, test alternatives virtually, and recommend the operating or investment scenario the evidence supports.';

// ===== STEP RENDERER FUNCTIONS =====

// --- Charter (was stepStart) ---
// The Back/Next bar. A composed step passes handlers only to its last
// sub-module, so the earlier ones render no bar at all.
const StepNav = ({ onBack, onNext, nextLabel }) => {
  if (!onBack && !onNext) return null;
  return (
    <div className="stepnav">
      <button className="btn line" onClick={onBack} disabled={!onBack}>‹ Back</button>
      <button className="btn primary" onClick={onNext} disabled={!onNext}>{nextLabel || 'Next ›'}</button>
    </div>
  );
};

// ===== SHARED FIELD HELPERS (mirror fInput/fSelect/fDur/info in the HTML build) =====
// Free text commits on blur rather than on every keystroke: updateProposal
// writes straight through to the API, so per-character saves would hammer it.
const Info = ({ tip }) => tip ? <span className="ico" data-tip={tip}>i</span> : null;

const FInput = ({ p, path, label, ph, type, tip, onCommit }) => {
  const val = getPath(p, path);
  const [draft, setDraft] = useState(val);
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(val); }, [val]);
  const commit = () => { focused.current = false; if (String(draft) !== String(val)) onCommit(path, draft); };
  return (
    <div className="formrow">
      <label>{label}<Info tip={tip} /></label>
      <input
        type={type || 'text'}
        value={draft ?? ''}
        placeholder={ph || ''}
        onFocus={() => { focused.current = true; }}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      />
    </div>
  );
};

const FSelect = ({ p, path, label, opts, tip, onCommit, className }) => (
  <div className={`formrow ${className || ''}`}>
    <label>{label}<Info tip={tip} /></label>
    <select value={getPath(p, path) || ''} onChange={e => onCommit(path, e.target.value)}>
      {opts.map(x => <option key={x} value={x}>{x}</option>)}
    </select>
  </div>
);

const FDur = ({ p, path, label, tip, onCommit, className }) => {
  const d = getPath(p, path) || {};
  return (
    <div className={`formrow ${className || ''}`}>
      <label>{label}<Info tip={tip} /></label>
      <div className="unitrow">
        <input type="number" min="0" placeholder="0" value={d.n ?? ''} onChange={e => onCommit(path + '.n', e.target.value)} />
        <select value={d.unit || 'days'} onChange={e => onCommit(path + '.unit', e.target.value)}>
          {DUR_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
    </div>
  );
};

// Multi-line free text. Same blur-commit rule as FInput, plus the offline
// draft button wherever localDraft() knows how to write the field.
const FText = ({ p, path, label, ph, tip, rows, onCommit, draftable, className }) => {
  const val = getPath(p, path);
  const [draft, setDraft] = useState(val);
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(val); }, [val]);
  const commit = () => { focused.current = false; if (String(draft) !== String(val)) onCommit(path, draft); };
  const apply = (text) => { setDraft(text); onCommit(path, text); };
  return (
    <div className={`formrow ${className || ''}`}>
      <label>
        <span>{label}<Info tip={tip} /></span>
        {draftable !== false ? <AiButton field={path} p={p} onApply={apply} /> : null}
      </label>
      <textarea
        rows={rows || 3}
        value={draft ?? ''}
        placeholder={ph || ''}
        onFocus={() => { focused.current = true; }}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
      />
    </div>
  );
};

// Grouped tick boxes with a search box and a per-group "select all".
// lib rows are either [group, value, description] or [value].
const TickGroups = ({ p, field, lib, label, tip, onChange }) => {
  const [q, setQ] = useState('');
  const cur = p[field] || [];
  const groups = {};
  lib.forEach(x => {
    const g = x.length > 2 ? x[0] : 'All';
    const v = x.length > 2 ? x[1] : x[0];
    const d = x.length > 2 ? x[2] : (x[1] || '');
    (groups[g] = groups[g] || []).push([v, d]);
  });
  const needle = q.trim().toLowerCase();
  const toggle = (v) => onChange(cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]);
  const names = Object.keys(groups);
  return (
    <div className="formrow">
      <label>{label}<Info tip={tip} /> <span className="hint">{cur.length} selected</span></label>
      <input className="mpq" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" style={{ marginBottom: 8 }} />
      {names.map(g => {
        const rows = groups[g].filter(r => !needle || `${r[0]} ${r[1]}`.toLowerCase().includes(needle));
        if (!rows.length) return null;
        return (
          <div className="tgroup" key={g}>
            {names.length > 1 || g !== 'All' ? (
              <div className="tgh">
                {g}
                <button type="button" className="btn line tiny" onClick={() => onChange(uniq([...cur, ...rows.map(r => r[0])]))}>Select all</button>
              </div>
            ) : null}
            <div className="tickgrid">
              {rows.map(r => (
                <label key={r[0]} className={`tick ${cur.includes(r[0]) ? 'on' : ''}`}>
                  <input type="checkbox" checked={cur.includes(r[0])} onChange={() => toggle(r[0])} />
                  <span><b>{r[0]}</b>{r[1] ? <small>{r[1]}</small> : null}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Numbered, editable, reorderable list of plain strings.
const LineList = ({ items, empty, onChange }) => {
  const list = items || [];
  const set = (i, v) => { const n = [...list]; n[i] = v; onChange(n); };
  const up = (i) => { if (!i) return; const n = [...list]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; onChange(n); };
  const del = (i) => { const n = [...list]; n.splice(i, 1); onChange(n); };
  if (!list.length) return <div className="hint">{empty}</div>;
  return (
    <div className="linelist">
      {list.map((x, i) => (
        <div key={i} className="lineitem">
          <span className="ln">{String(i + 1).padStart(2, '0')}</span>
          <input value={x} onChange={e => set(i, e.target.value)} />
          <button className="lx" title="Move up" onClick={() => up(i)}>↑</button>
          <button className="lx" onClick={() => del(i)}>✕</button>
        </div>
      ))}
    </div>
  );
};

// Group → item → add, with "add every line in this group" and a free-text row.
// lib rows are [groupName, [items…]].
const LibPick = ({ lib, label, tip, groupAllLabel, newPlaceholder, existing, onAdd }) => {
  const [group, setGroup] = useState(lib[0]?.[0] || '');
  const [item, setItem] = useState('');
  const [own, setOwn] = useState('');
  const items = (lib.find(g => g[0] === group) || ['', []])[1];
  const add = (vals) => { const fresh = vals.filter(v => v && !(existing || []).includes(v)); if (fresh.length) onAdd(fresh); };
  return (
    <>
      <div className="formrow">
        <label>{label}<Info tip={tip} /></label>
        <div className="libpick">
          <select value={group} onChange={e => { setGroup(e.target.value); setItem(''); }}>
            {lib.map(g => <option key={g[0]} value={g[0]}>{g[0]}</option>)}
          </select>
          <select value={item} onChange={e => setItem(e.target.value)}>
            <option value="">Select an item…</option>
            {items.map(x => <option key={x} value={x}>{x}</option>)}
          </select>
          <button className="btn line" onClick={() => { add([item]); setItem(''); }}>＋ Add</button>
        </div>
        <div className="proposal-toolbar">
          <button className="btn line tiny" onClick={() => add(items)}>{groupAllLabel}</button>
        </div>
      </div>
      <div className="libpick" style={{ marginTop: 8 }}>
        <input value={own} onChange={e => setOwn(e.target.value)} placeholder={newPlaceholder}
          onKeyDown={e => { if (e.key === 'Enter' && own.trim()) { add([own.trim()]); setOwn(''); } }} />
        <button className="btn line" onClick={() => { if (own.trim()) { add([own.trim()]); setOwn(''); } }}>＋ Add</button>
      </div>
    </>
  );
};

// When we can start - the extra inputs appear only for the mode that needs them.
const FStart = ({ p, tip, onCommit }) => {
  const s = p.start || {};
  return (
    <div className="formrow">
      <label>When we can start<Info tip={tip} /></label>
      <select value={s.mode || ''} onChange={e => onCommit('start.mode', e.target.value)}>
        {START_MODES.map(x => <option key={x} value={x}>{x}</option>)}
      </select>
      {s.mode === 'Within a set time of the purchase order' && (
        <div className="unitrow" style={{ marginTop: 7 }}>
          <input type="number" min="0" value={s.n ?? ''} placeholder="2" onChange={e => onCommit('start.n', e.target.value)} />
          <select value={s.unit || 'weeks'} onChange={e => onCommit('start.unit', e.target.value)}>
            {DUR_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      )}
      {s.mode === 'On a specific date' && (
        <input type="date" style={{ marginTop: 7 }} value={s.date || ''} onChange={e => onCommit('start.date', e.target.value)} />
      )}
      <div className="fieldnote">Reads as: <b>{startText(s)}</b></div>
    </div>
  );
};

// What the engagement consumes - people, site days, workshops, data.
const EffortBlock = ({ p, onChange }) => {
  const list = p.efforts || [];
  const set = (i, f, v) => {
    const n = list.map((e, j) => j === i ? { ...e, [f]: v } : e);
    // Changing the type changes which units are legal, so reset to the first.
    if (f === 'type') n[i].unit = (EFFORT_TYPES.find(x => x[0] === v) || EFFORT_TYPES[0])[2][0];
    onChange(n);
  };
  return (
    <div className="formrow">
      <label>Effort and what the engagement uses<Info tip="Say what the work consumes - people, their time, days on your site, workshops, how much of the operation is covered, data and space. This is what stops a bare number like “4” meaning nothing." /></label>
      <div className="rowlist">
        {list.length ? list.map((e, i) => {
          const t = EFFORT_TYPES.find(x => x[0] === e.type) || EFFORT_TYPES[0];
          return (
            <div key={i} className="rowcard">
              <div className="rowgrid3">
                <select value={e.type || t[0]} onChange={ev => set(i, 'type', ev.target.value)}>
                  {EFFORT_TYPES.map(x => <option key={x[0]} value={x[0]}>{x[0]}</option>)}
                </select>
                <input type="number" min="0" value={e.qty ?? ''} placeholder="Quantity" onChange={ev => set(i, 'qty', ev.target.value)} />
                <select value={e.unit || t[2][0]} onChange={ev => set(i, 'unit', ev.target.value)}>
                  {t[2].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <button className="lx" onClick={() => onChange(list.filter((_, j) => j !== i))}>✕</button>
            </div>
          );
        }) : <div className="hint">Nothing added yet. Add a line for each thing the engagement consumes.</div>}
      </div>
      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => onChange([...list, { type: EFFORT_TYPES[0][0], qty: '', unit: EFFORT_TYPES[0][2][0] }])}>＋ Add effort line</button>
      </div>
      {list.length ? <div className="fieldnote">Reads as: <b>{effortText(list)}</b></div> : null}
    </div>
  );
};

// Per-section include/skip control, so an irrelevant block can be left out
// without deleting what has been typed into it.
const SkipBar = ({ p, secId, onToggle }) => {
  const on = p.sectionsOn?.[secId] !== false;
  return (
    <div className={`skipbar ${on ? '' : 'off'}`}>
      <span>{on ? '✔ This section will appear in the proposal' : '✕ Skipped - this will not appear in the proposal'}</span>
      <button className={`btn ${on ? 'line' : 'primary'} tiny`} onClick={() => onToggle(secId, !on)}>
        {on ? 'Skip this from proposal' : 'Activate in proposal'}
      </button>
    </div>
  );
};

// Recipient / team row editors - the same shape is needed in two steps.
const PeopleRows = ({ rows, fields, empty, onChange }) => (
  <div className="rowlist">
    {(rows || []).length ? rows.map((r, i) => (
      <div key={i} className="rowcard">
        <div className={`rowgrid${fields.length}`}>
          {fields.map(f => f.opts
            ? (
              <select key={f.k} value={r[f.k] || ''} onChange={e => onChange(rows.map((x, j) => j === i ? { ...x, [f.k]: e.target.value } : x))}>
                <option value="">{f.ph}</option>
                {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            )
            : <input key={f.k} value={r[f.k] || ''} placeholder={f.ph} onChange={e => onChange(rows.map((x, j) => j === i ? { ...x, [f.k]: e.target.value } : x))} />
          )}
        </div>
        <button className="lx" onClick={() => onChange(rows.filter((_, j) => j !== i))}>✕</button>
      </div>
    )) : <div className="hint">{empty}</div>}
  </div>
);

const STD_TEAM = [
  { name: '', designation: 'Manager', role: 'Engagement Manager', exp: '', alloc: '20%' },
  { name: '', designation: 'Senior Engineer', role: 'Project Manager', exp: '', alloc: '50%' },
  { name: '', designation: 'Principal Engineer', role: 'Technical Lead', exp: '', alloc: '40%' },
  { name: '', designation: 'Engineer', role: 'Engineer', exp: '', alloc: '100%' }
];

// Seeds the terms, payment schedule and section list from a preset. Anything
// the user has already filled in is left alone.
const applyPresetTo = (p, k, pr) => {
  const np = { ...p, preset: k, engagementModel: pr.eng, commercialModel: pr.com, payTemplate: pr.pay };
  if (!p.duration?.n) np.duration = { ...pr.dur };
  np.sectionsOn = { ...p.sectionsOn };
  SECTION_DEFS.forEach(s => { np.sectionsOn[s[0]] = !pr.off.includes(s[0]); });
  if (!p.payments?.length) np.payments = (PAY_TEMPLATES[pr.pay] || []).map(x => ({ milestone: x[0], percent: x[1] || '', amount: '', trigger: x[2] }));
  if (!p.risks?.length) np.risks = RISK_LIB.slice(0, 5).map(r => r.slice());
  if (!p.assumptions?.length) np.assumptions = ASSUMP_LIB.slice(0, 5);
  if (!p.dependencies?.length) np.dependencies = DEP_LIB.slice(0, 4);
  if (!p.outScope?.length) np.outScope = OUTSCOPE_LIB.slice(0, 6);
  if (!p.security?.length) np.security = SEC_CLAUSES.slice(0, 6);
  return np;
};

const PresetGrid = ({ p, onPick }) => (
  <div className="presetgrid">
    {Object.keys(PRESETS).map(k => {
      const pr = PRESETS[k];
      return (
        <button key={k} className={`preset ${p.preset === k ? 'on' : ''}`} onClick={() => onPick(k, pr)}>
          <b>{k}</b>
          <small>{pr.eng} · {pr.com} · {durText(pr.dur)}</small>
        </button>
      );
    })}
  </div>
);

// --- Quick start (presets) - only appears in full mode, as in the HTML build ---
const StepStart = ({ p, setP, onBack, onNext }) => (
  <div className="pp">
    <h2>Quick start</h2>
    <div className="hint">Pick the type of proposal. This sets the sections, engagement model, payment template and default terms – all changeable afterwards.</div>
    <PresetGrid p={p} onPick={(k, pr) => setP(applyPresetTo(p, k, pr))} />
    <div className="noteBox"><b>How this works.</b> The steps down the left are mostly tick boxes. What you choose in <b>Proposal setup</b> narrows what the later steps offer, so the further in you go the less there is to decide.</div>
    <StepNav onBack={onBack} onNext={onNext} />
  </div>
);
const stepStart = (p, setP, onBack, onNext) => <StepStart p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- 1 · Proposal setup (the charter) ---
// Business unit, service line and the services themselves. Everything the rest
// of the builder offers - scope, capability, case studies, methodology, price -
// is derived from what is ticked here.
const StepCharter = ({ p, setP, onBack, onNext }) => {
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState('');

  const BUs = buMaster();
  const bu = BUs.find(b => b.name === p.buId);
  const lines = bu ? bu.lines : [];
  const available = servicesFor(p.buId, p.serviceLine);
  const picked = p.solutions || [];
  const q = filter.trim().toLowerCase();
  const shown = q ? available.filter(s => `${s.title} ${s.sub}`.toLowerCase().includes(q)) : available;

  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };

  // A different unit means a different service list, so the service line has to
  // reset or it silently filters everything out.
  const changeBu = (v) => { setP({ ...p, buId: v, serviceLine: '' }); setNotice(''); };

  const toggleService = (id) => {
    setP({ ...p, solutions: picked.includes(id) ? picked.filter(x => x !== id) : [...picked, id] });
    setNotice('');
  };

  const selectAllShown = () => { setP({ ...p, solutions: uniq([...picked, ...shown.map(s => s.id)]) }); setNotice(''); };
  const clearServices = () => { setP({ ...p, solutions: [] }); setNotice(''); };

  const derive = () => {
    if (!picked.length) { setNotice('Select at least one service first.'); return; }
    const np = recomputeAuto(JSON.parse(JSON.stringify(p)));
    setP(np);
    setNotice(`Derived ${np.scopeItems.length} scope lines, ${np.capabilities.length} capabilities, ${np.deliverables.length} deliverables and ${np.cases.length} case studies.`);
  };

  return (
    <div className="pp">
      <h2>Proposal setup</h2>
      <div className="hint">Set this up once and the rest of the builder narrows to match. Services are the heart of it – capability, scope, case studies and price are all derived from what you pick here.</div>

      <div className="formgrid">
        <div className="formrow">
          <label>Business unit<Info tip="Which SolidPro business unit is sending this proposal. It filters the services list and sets the methodology." /></label>
          <select value={p.buId || ''} onChange={e => changeBu(e.target.value)}>
            <option value="">Select a business unit…</option>
            {BUs.map(b => <option key={b.name} value={b.name}>{b.name} ({b.count})</option>)}
          </select>
        </div>
        <div className="formrow">
          <label>Service line<Info tip="The practice within that business unit. Leave blank to see everything in the unit." /></label>
          <select value={p.serviceLine || ''} onChange={e => setField('serviceLine', e.target.value)} disabled={!p.buId}>
            <option value="">All service lines</option>
            {lines.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>

      {p.buId ? (
        <section>
          <div className="svchead">
            <span className="svclabel">Services in this proposal</span>
            <span className="svccount">{available.length} available · {picked.length} selected</span>
          </div>
          <div className="svcsearch">
            <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter services..." />
          </div>
          <div className="svcgrid">
            {shown.length ? shown.map(s => {
              const on = picked.includes(s.id);
              return (
                <label key={s.id} className={`svccard ${on ? 'on' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleService(s.id)} />
                  {!on && <span className="svcmark" />}
                  <span className="svcbody">
                    <span className="svcsub">{s.sub}</span>
                    <span className="svctitle">{s.title}</span>
                  </span>
                </label>
              );
            }) : <div className="empty">No services in this selection.</div>}
          </div>
          <div className="proposal-toolbar">
            <button className="btn primary" onClick={derive}>Derive scope, capability and experience from these services</button>
            {shown.length ? <button className="btn line" onClick={selectAllShown}>Select all shown</button> : null}
            {picked.length ? <button className="btn danger" onClick={clearServices}>Clear</button> : null}
            <p className="hint">This fills the capability list, the scope of work, the relevant case studies and the methodology from the services you ticked. You then edit rather than assemble.</p>
          </div>
          {notice ? <div className="okBox">{notice}</div> : null}
        </section>
      ) : (
        <div className="noteBox">Pick a business unit to see its services.</div>
      )}

      <div className="section"><h2>Document details</h2></div>
      <div className="formgrid">
        <FInput p={p} path="id" label="Proposal reference" onCommit={setField} />
        <FInput p={p} path="doc.version" label="Version" ph="1.0" onCommit={setField} />
        <FSelect p={p} path="doc.classification" label="Confidentiality marking" opts={CLASSIFICATIONS} onCommit={setField} />
        <FInput p={p} path="doc.issueDate" label="Date of issue" type="date" onCommit={setField} />
        <FSelect
          p={p}
          path="status"
          className="wide"
          label="Where this proposal stands (internal only)"
          opts={PROP_STATUS}
          tip="Your own pipeline tracking — Draft, Sent to customer, Won, Lost. It shows on the proposals list so you can see what is live. It is never printed in the document the customer receives."
          onCommit={setField}
        />
      </div>
      <div className="formgrid">
        <FDur p={p} path="validity" label="How long the offer stays valid" className="wide" onCommit={setField} />
      </div>

      <details className="adv">
        <summary>Advanced – start from a preset</summary>
        <div className="hint" style={{ margin: '10px 0' }}>Sets the engagement model, payment template, default terms and which sections are switched on. Anything you have already filled in is left alone.</div>
        <PresetGrid p={p} onPick={(k, pr) => { setP(applyPresetTo(p, k, pr)); setNotice(`Preset applied: ${k}.`); }} />
      </details>

      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};
const stepCharter = (p, setP, onBack, onNext) => <StepCharter p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- Customer (was stepBasics + stepPeople) ---
// --- 2 · Customer (stepBasics + stepPeople from the HTML build) ---
const CUST_SCALE = ['', 'Under ₹50 crore', '₹50–250 crore', '₹250–1,000 crore', '₹1,000–5,000 crore', '₹5,000–10,000 crore', 'Above ₹10,000 crore'];

const StepCustomer = ({ p, setP, onBack, onNext }) => {
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };

  return (
    <div className="pp">
      <h2>Who this proposal is for</h2>
      <div className="hint">The customer's own details. Document control and sign-off are handled in the setup and closing steps, so they are not repeated here.</div>

      <div className="formgrid">
        <FInput p={p} path="clientName" label="Customer company name" ph="e.g. Bharat Precision Industries Ltd" onCommit={setField} />
        <FInput p={p} path="clientIndustry" label="Industry / sector" ph="e.g. Automotive & Mobility" onCommit={setField} />
        <FInput p={p} path="clientLocation" label="Location / plant" ph="e.g. Chakan, Pune" onCommit={setField} />
        <FSelect p={p} path="clientRevenue" label="Customer scale" opts={CUST_SCALE}
          tip="Larger customers expect more of the optional sections — compliance, service levels, change control and security."
          onCommit={setField} />
        <FInput p={p} path="rfpRef" label="Their RFP / enquiry reference" ph="e.g. RFP/ENGG/2026/014" onCommit={setField} />
        <FInput p={p} path="title" label="Proposal title" ph="e.g. Factory Performance Improvement Programme" onCommit={setField} />
      </div>

      <div className="subhead">
        <span className="subhead-l">Who this proposal goes to</span>
        <span className="subhead-r">the email addresses are also used when you send it for approval</span>
      </div>
      <PeopleRows
        rows={p.recipients || []}
        empty="No recipients added."
        fields={[
          { k: 'name', ph: 'Name' },
          { k: 'designation', ph: 'Designation', opts: CUST_DESIG_LIB },
          { k: 'dept', ph: 'Function' },
          { k: 'email', ph: 'Email' }
        ]}
        onChange={rows => setP({ ...p, recipients: rows })}
      />
      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => setP({ ...p, recipients: [...(p.recipients || []), { name: '', designation: '', dept: '', email: '' }] })}>＋ Add recipient</button>
      </div>

      <div className="subhead">
        <span className="subhead-l">SolidPro engagement team</span>
        <span className="subhead-r">named people, designation, role and how much of their time you get</span>
      </div>
      <PeopleRows
        rows={p.team || []}
        empty="No team members added."
        fields={[
          { k: 'name', ph: 'Name' },
          { k: 'designation', ph: 'Designation', opts: DESIG_LIB },
          { k: 'role', ph: 'Role', opts: ROLE_LIB },
          { k: 'exp', ph: 'Experience e.g. 14 yrs' },
          { k: 'alloc', ph: 'Allocation e.g. 50%' }
        ]}
        onChange={rows => setP({ ...p, team: rows })}
      />
      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => setP({ ...p, team: [...(p.team || []), { name: '', designation: '', role: '', exp: '', alloc: '' }] })}>＋ Add team member</button>
        <button className="btn line" onClick={() => setP({ ...p, team: [...(p.team || []), ...STD_TEAM.map(t => ({ ...t }))] })}>Use a standard project team</button>
        {(ADMIN.people || []).length ? (
          <button className="btn line" onClick={() => setP({ ...p, team: [...(p.team || []), ...ADMIN.people.map(t => ({ ...t }))] })}>From the team library</button>
        ) : null}
      </div>

      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};
const stepCustomer = (p, setP, onBack, onNext) => <StepCustomer p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- 3 · About SolidPro ---
// Standing company material. A block can only be ticked once someone has
// marked it verified in Proposal admin, so an unchecked claim cannot reach a
// customer document.
const ABOUT_BLOCKS = ['intro', 'presence', 'offices', 'team', 'facilities', 'clients', 'certs', 'specialities', 'achievements', 'brands'];
const ABOUT_CORE = ['intro', 'presence', 'team', 'certs'];

const StepAbout = ({ p, setP, onBack, onNext }) => {
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };
  const [showAdmin, setShowAdmin] = useState(false);
  // ADMIN is module state, not React state, so a verification recorded here or
  // in the admin panel needs an explicit nudge to repaint.
  const [, bump] = useState(0);
  const refresh = () => bump(x => x + 1);
  const F = (ADMIN.facts && Object.keys(ADMIN.facts).length) ? ADMIN.facts : COMPANY_FACTS;
  const on = k => (p.about || []).includes(k);
  const isVerified = k => !!((ADMIN.verified || {})[k] || {}).ok;
  const verifiedOf = (keys) => keys.filter(isVerified);

  const toggleBlock = (k) => {
    const cur = p.about || [];
    setP({ ...p, about: cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k] });
  };

  const unverified = ABOUT_BLOCKS.filter(k => F[k] && !isVerified(k));

  // Records who confirmed the block and when, the same shape Proposal admin writes.
  const markVerified = (k) => {
    ADMIN.verified = { ...(ADMIN.verified || {}), [k]: { ok: true, by: 'Checked in the proposal builder', at: new Date().toISOString() } };
    saveAdmin();
    refresh();
  };
  const verifyAll = () => {
    const v = { ...(ADMIN.verified || {}) };
    ABOUT_BLOCKS.filter(k => F[k]).forEach(k => { v[k] = v[k] && v[k].ok ? v[k] : { ok: true, by: 'Checked in the proposal builder', at: new Date().toISOString() }; });
    ADMIN.verified = v;
    saveAdmin();
    refresh();
  };

  return (
    <div className="pp">
      <h2>About SolidPro</h2>
      <div className="hint">Tick a block and it is written into the proposal, formatted, with no typing. Everything here is editable once in <b>Proposal admin</b> – change it there and every future proposal picks it up.</div>

      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => setP({ ...p, about: verifiedOf(ABOUT_BLOCKS.filter(k => F[k])) })}>Select all</button>
        <button className="btn line" onClick={() => setP({ ...p, about: verifiedOf(ABOUT_CORE.filter(k => F[k])) })}>Core set only</button>
        <button className="btn line" onClick={() => setP({ ...p, about: [] })}>Clear</button>
        <button className="btn line" onClick={() => setShowAdmin(true)}>Edit these details</button>
        {unverified.length ? <button className="btn line" onClick={verifyAll}>Mark all as verified</button> : null}
      </div>

      {ABOUT_BLOCKS.map(k => {
        const b = F[k];
        if (!b) return null;
        const ver = isVerified(k);
        return (
          <label key={k} className={`factblock ${on(k) ? 'on' : ''}`}>
            <div className="fbh">
              <input type="checkbox" checked={on(k)} disabled={!ver} onChange={() => toggleBlock(k)} />
              <b>{b.label}</b>
              {ver
                ? <span className="verchip ok">✓ Verified</span>
                : <button type="button" className="verchip no" title="Confirm this is accurate, then it can be used"
                    onClick={e => { e.preventDefault(); markVerified(k); }}>⚠ Verify before use</button>}
            </div>
            <div className="factlist">
              {(b.items && b.items.length)
                ? b.items.map((it, i) => it.length === 3
                  ? <div className="factrow" key={i}><span className="flag">{it[0]}</span><div><b>{it[1]}</b>{it[2] ? <small>{it[2]}</small> : null}</div></div>
                  : <div className="factrow" key={i}><div><b>{it[0]}</b>{it[1] ? <small>{it[1]}</small> : null}</div></div>)
                : <p style={{ fontSize: '12.5px', color: '#6b7280', whiteSpace: 'pre-line', margin: 0 }}>{b.text || ''}</p>}
            </div>
          </label>
        );
      })}

      {unverified.length ? (
        <div className="warnBox">
          <b>Only verified facts can go into a proposal.</b> {unverified.length} block{unverified.length === 1 ? '' : 's'} still marked <b>⚠ Verify before use</b> cannot be ticked – click that chip to confirm the block is accurate, or set it in <b>Proposal admin → About SolidPro</b> where the check is recorded against a source. This matters most for certifications, team size, office count and years of experience.
        </div>
      ) : null}

      <div className="section"><h2>Why SolidPro – for this customer</h2></div>
      <TickGroups p={p} field="whyUs" lib={WHY_LIB}
        label="Pick four to six" tip="Twenty differentiators reads as none."
        onChange={v => setP({ ...p, whyUs: v })} />
      <FText p={p} path="whyCustom" label="Anything else specific to this customer" ph="One per line." draftable onCommit={setField} />

      <details className="adv">
        <summary>Advanced – name the individual people on this engagement</summary>
        <div className="hint" style={{ margin: '10px 0' }}>Most proposals do not need this. Large customers and tenders usually do.</div>
        <PeopleRows
          rows={p.team || []}
          empty="Nobody named – the proposal will simply not include this section."
          fields={[
            { k: 'name', ph: 'Name' },
            { k: 'designation', ph: 'Designation', opts: DESIG_LIB },
            { k: 'role', ph: 'Role', opts: ROLE_LIB },
            { k: 'exp', ph: 'Experience' },
            { k: 'alloc', ph: 'Allocation' }
          ]}
          onChange={rows => setP({ ...p, team: rows })}
        />
        <div className="proposal-toolbar">
          <button className="btn line" onClick={() => setP({ ...p, team: [...(p.team || []), { name: '', designation: '', role: '', exp: '', alloc: '' }] })}>＋ Add person</button>
          <button className="btn line" onClick={() => setP({ ...p, team: [...(p.team || []), ...STD_TEAM.map(t => ({ ...t }))] })}>Standard project team</button>
          {(ADMIN.people || []).length ? (
            <button className="btn line" onClick={() => setP({ ...p, team: [...(p.team || []), ...ADMIN.people.map(t => ({ ...t }))] })}>From the team library</button>
          ) : null}
        </div>
      </details>

      <StepNav onBack={onBack} onNext={onNext} />
      {showAdmin && <ProposalAdmin onClose={() => { setShowAdmin(false); refresh(); }} />}
    </div>
  );
};
const stepAbout = (p, setP, onBack, onNext) => <StepAbout p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- 4 · What we propose (stepPropose + stepScope + stepDeliver) ---
const StepPropose = ({ p, setP, onBack, onNext }) => {
  const [q, setQ] = useState('');
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };

  const allSols = window._SOLS || [];
  const sel = (p.solutions || []).map(id => allSols.find(s => s.id === id)).filter(Boolean);
  // The setup step has already narrowed the catalogue; only fall back to
  // everything when no business unit has been chosen.
  const pool = p.buId ? servicesFor(p.buId, p.serviceLine) : allSols;
  const needle = q.trim().toLowerCase();
  const list = needle ? pool.filter(s => [s.title, s.cat, s.sub, s.what].join(' ').toLowerCase().includes(needle)) : pool;

  const toggleService = (id) => {
    const cur = p.solutions || [];
    const np = { ...p, solutions: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    setP(np);
  };

  // Anything typed or picked by hand is recorded as manual, so a later re-derive
  // does not throw it away.
  const addManual = (autoKey, manualKey, effectiveKey, vals) => {
    const np = { ...p };
    np[manualKey] = uniq([...(p[manualKey] || []), ...vals]);
    np[effectiveKey] = uniq([...(p[autoKey] || []), ...np[manualKey]]);
    setP(np);
  };
  const editList = (autoKey, manualKey, effectiveKey, next) => {
    const np = { ...p };
    np[effectiveKey] = next;
    np[manualKey] = next.filter(x => !(p[autoKey] || []).includes(x));
    setP(np);
  };

  const scopeCount = (p.scopeItems || []).length;
  const delivCount = (p.deliverables || []).length;

  return (
    <div className="pp">
      <h2>What we propose</h2>
      {p.customerAsk
        ? <div className="askback"><span>They asked us to</span><b>{p.customerAsk}</b><button className="btn line tiny" onClick={onBack}>Edit in the customer step</button></div>
        : <div className="warnBox">You have not captured what the customer asked for yet. Go back to the customer step – everything in this section should answer it.</div>}

      <div className="section">
        <h2>The services we recommend</h2>
        <span>{sel.length} selected{p.buId ? ' · from ' + p.buId : ''}</span>
      </div>
      <div className="svcsearch"><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search services..." /></div>
      <div className="svcgrid">
        {list.length ? list.map(s => {
          const on = (p.solutions || []).includes(s.id);
          return (
            <label key={s.id} className={`svccard ${on ? 'on' : ''}`}>
              <input type="checkbox" checked={on} onChange={() => toggleService(s.id)} />
              {!on && <span className="svcmark" />}
              <span className="svcbody">
                <span className="svcsub">{s.sub}</span>
                <span className="svctitle">{s.title}</span>
                {s.what ? <span className="svcwhat">{s.what}</span> : null}
              </span>
            </label>
          );
        }) : <div className="empty">No match.</div>}
      </div>
      <div className="proposal-toolbar">
        <button className="btn primary" onClick={() => setP(recomputeAuto(JSON.parse(JSON.stringify(p))))}>
          Derive scope, capability and experience from these
        </button>
      </div>
      {(p.autoScope || []).length > 0 && (
        <div className="okBox">
          Derived <b>{(p.autoScope || []).length}</b> scope lines, <b>{(p.autoDeliverables || []).length}</b> deliverables,{' '}
          <b>{(p.autoCapabilities || []).length}</b> capabilities and <b>{(p.autoCases || []).length}</b> related case studies.
          {(p.manualScope || []).length > 0 && <> Your <b>{(p.manualScope || []).length}</b> manual scope lines are kept.</>}
        </div>
      )}

      {sel.length > 0 && (
        <>
          <div className="section"><h2>What each service will do here</h2><span>edit the wording the customer reads</span></div>
          <div className="rowlist">
            {sel.map(s => (
              <div key={s.id} className="propcard2">
                <b>{s.title}</b>
                <textarea
                  rows="2"
                  value={(p.solNotes || {})[s.id] || ''}
                  placeholder="Why this service, for this customer. Leave blank to use the standard description."
                  onChange={e => setP({ ...p, solNotes: { ...(p.solNotes || {}), [s.id]: e.target.value } })}
                />
              </div>
            ))}
          </div>
        </>
      )}

      <div className="section"><h2>The result we are aiming at</h2></div>
      <FText p={p} path="whatDifferent" label="What should be different at the end"
        ph="The situation after the work is done - not the deliverables."
        tip="This is the sentence the customer repeats to their own board." draftable onCommit={setField} />
      <TickGroups p={p} field="valueAdds" lib={VALUE_LIB}
        label="What this is worth to them"
        tip="Tick the outcomes this customer actually cares about. They print as numbered cards in the executive summary - so four strong ones beat twelve weak ones."
        onChange={v => setP({ ...p, valueAdds: v })} />
      <FText p={p} path="valueCustom" label="Anything else specific to this customer" ph="One per line." draftable onCommit={setField} />

      <div className="section"><h2>Scope of work</h2><span>{scopeCount} lines</span></div>
      <div className="noteBox">
        <b>What this section is.</b> Everything the price covers, written the way the customer would describe it – not in SolidPro shorthand. Anything not listed here is not included, so a line that is vague costs money later. Deriving from the services above fills this automatically; add anything else from the library below.
      </div>
      <LibPick
        lib={SCOPE_LIB}
        label="Add from the scope library"
        tip="Grouped by the kind of work rather than by internal service name, so you can find the right line quickly."
        groupAllLabel="Add every line in this group"
        newPlaceholder="Or write your own line, in the customer’s language"
        existing={p.scopeItems}
        onAdd={vals => addManual('autoScope', 'manualScope', 'scopeItems', vals)}
      />
      <LineList items={p.scopeItems} empty="No scope lines yet."
        onChange={next => editList('autoScope', 'manualScope', 'scopeItems', next)} />

      <div className="section"><h2>What you receive</h2><span>{delivCount} deliverables</span></div>
      <div className="noteBox">
        <b>Scope versus deliverables.</b> Scope is the <b>work we do</b>. Deliverables are the <b>things you are handed</b> at the end. Procurement teams check that every scope line produces something tangible – so if a scope line has no matching deliverable, add one.
      </div>
      <LibPick
        lib={DELIV_LIB}
        label="Add from the deliverables library"
        tip="Grouped by output type - assessment, decision, design, analysis, factory, digital, sustainability, quality, documentation and governance."
        groupAllLabel="Add every item in this group"
        newPlaceholder="Or write your own deliverable"
        existing={p.deliverables}
        onAdd={vals => addManual('autoDeliverables', 'manualDeliverables', 'deliverables', vals)}
      />
      <LineList items={p.deliverables} empty="Nothing listed yet."
        onChange={next => editList('autoDeliverables', 'manualDeliverables', 'deliverables', next)} />
      {scopeCount > 0 && delivCount === 0 && (
        <div className="warnBox">You have scope lines but no deliverables. A proposal that says what we will do but not what you receive will come back with questions.</div>
      )}
      <FText p={p} path="acceptance" label="How the customer approves the deliverables"
        ph="Who reviews them, and what happens if there is no response."
        tip="Without this, “accepted” is a matter of opinion and the final payment stalls." onCommit={setField} />

      <div className="section"><h2>What is not included</h2></div>
      <TickGroups p={p} field="outScope" lib={OUTSCOPE_LIB.map(x => [x])}
        label="What is not included"
        tip="Stating this clearly protects both sides. It is the single most common cause of dispute later."
        onChange={v => setP({ ...p, outScope: v })} />

      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};
const stepPropose = (p, setP, onBack, onNext) => <StepPropose p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- Money (was stepMoney) ---
// --- The story: the questions that make a proposal persuasive ---
const STORY_FIELDS = [
  ['customerAbout', 'About the customer', 'What kind of business are they, and what matters to them?', 'Two or three factual sentences.'],
  ['customerAsk', 'What have they asked us for?', 'In their own words. Everything later in the proposal answers this.', '"Establish whether the line can reach 1,300 units a day without another machine."'],
  ['currentSituation', 'What is happening today?', 'The situation they would recognise as accurate.', 'Only what they already know to be true — one wrong detail costs credibility for the whole document.'],
  ['theyHave', 'What do they already have?', 'The assets, data, people or position they can build on.', 'Be specific.'],
  ['whatsMissing', 'What is missing today?', 'Name the gap honestly.', 'Only name gaps that can be filled.'],
  ['whatDifferent', 'What should be different at the end?', 'The situation after the work is done — not the deliverables.', 'This is the sentence the customer repeats to their own board.'],
  ['whyNow', 'Why does this matter now?', 'A deadline, a window, or a cost that grows.', 'Optional, but it is often what moves a proposal off the pile.'],
  ['approveNow', 'What should they approve now?', 'The single smallest next commitment.', '"Approve the 3-week diagnostic." Not "approve a five-year programme."']
];

// One story question: the prompt, the hint, the box, and its AI button.
// Commits on blur like every other free-text field, so typing does not fire a
// save per keystroke.
const StoryBox = ({ p, field, prompt, hint, ph, onCommit }) => {
  const val = p[field] || '';
  const [draft, setDraft] = useState(val);
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(val); }, [val]);
  const commit = () => { focused.current = false; if (draft !== val) onCommit(field, draft); };
  return (
    <div className="storyq">
      <div className="sqhead">
        <span className="sqn">{prompt}</span>
        <AiButton field={field} p={p} onApply={t => { setDraft(t); onCommit(field, t); }} />
      </div>
      <textarea
        value={draft}
        placeholder={hint || ph}
        title={hint}
        onFocus={() => { focused.current = true; }}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
      />
    </div>
  );
};

const StepStory = ({ p, setP, onBack, onNext }) => {
  const set = (k, v) => setP({ ...p, [k]: v });
  return (
    <div className="pp">
      <h2>The story</h2>
      <div className="hint">
        Answer these and the proposal argues itself – the executive summary, the opening section and the closing ask are all written from them.
        Every box has a <b>✨ Write with AI</b> button.
      </div>
      <div className="storygrid">
        {STORY_FIELDS.map(f => (
          <StoryBox key={f[0]} p={p} field={f[0]} prompt={f[1]} hint={f[2]} ph={f[3]} onCommit={set} />
        ))}
      </div>
      <FText p={p} path="executiveSummary" label="Executive summary" className="tall"
        ph="Three or four sentences: what they need, what we recommend, what they end up with."
        rows={5} onCommit={set} />
      <FText p={p} path="objectives" label="Objectives of this engagement" className="tall"
        ph="One per line." rows={5} onCommit={set} />
      <div className="noteBox"><b>Why the last question matters most.</b> A proposal that asks for a large commitment gets deferred. A proposal that asks for one small, well-defined next step gets signed.</div>
      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};
const stepStory = (p, setP, onBack, onNext) => <StepStory p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- The outcome we commit to ---
const stepOutcome = (p, setP, onBack, onNext) => {
  const o = p.outcome || {};
  const set = (k, v) => setP({ ...p, outcome: { ...o, [k]: v } });
  return (
    <div className="pp">
      <h2>The outcome we commit to</h2>
      <div className="hint">An outcome proposal lives or dies on three numbers: where they are now, where they will be, and how both are measured.</div>
      <div className="formgrid">
        <div className="formrow"><label>What is being measured</label><input value={o.metric || ''} onChange={e => set('metric', e.target.value)} placeholder="e.g. Units per day" /></div>
        <div className="formrow"><label>Where they are today</label><input value={o.baseline || ''} onChange={e => set('baseline', e.target.value)} placeholder="e.g. 1,000 units/day" /></div>
        <div className="formrow"><label>Where they will be</label><input value={o.target || ''} onChange={e => set('target', e.target.value)} placeholder="e.g. 1,300 units/day" /></div>
        <div className="formrow"><label>By when</label><input value={o.by || ''} onChange={e => set('by', e.target.value)} placeholder="e.g. Within 6 months of go-live" /></div>
      </div>
      <div className="formrow"><label>How it will be measured</label><textarea value={o.method || ''} onChange={e => set('method', e.target.value)} placeholder="Who measures it, how often, and using what source data." /></div>
      <div className="formrow"><label>What the outcome depends on</label><textarea value={o.conditions || ''} onChange={e => set('conditions', e.target.value)} placeholder="Volume, product mix, shift pattern — anything outside our control." /></div>
      <div className="formrow"><label>What happens if it is not achieved</label><textarea value={o.ifnot || ''} onChange={e => set('ifnot', e.target.value)} placeholder="Be explicit. A vague answer here is read as no commitment at all." /></div>
      <div className="noteBox"><b>A caution worth taking seriously.</b> Only commit to an outcome you control the levers for. If the customer controls volume, manning or investment approval, the commitment must be conditional on those — and stated as such here.</div>
      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};

// --- Stages and gates ---
const stepStages = (p, setP, onBack, onNext) => {
  const st = p.stages || [];
  const upd = (i, f, v) => { const s = [...st]; s[i] = { ...s[i], [f]: v }; setP({ ...p, stages: s }); };
  const BTOS_STAGES = [
    { name: 'D1 · Discover', delivers: 'Business health map — what is actually happening', durN: 3, durU: 'weeks', fee: '', gate: 'Continue to diagnosis?' },
    { name: 'D2 · Diagnose', delivers: 'Validated causes, with the wrong ones ruled out', durN: 4, durU: 'weeks', fee: '', gate: 'Is the cause worth solving?' },
    { name: 'D3 · Design', delivers: 'Transformation blueprint, sequenced to what you can absorb', durN: 6, durU: 'weeks', fee: '', gate: 'Approve the investment?' },
    { name: 'D4 · Deliver', delivers: 'Implementation governed against the approved design', durN: 6, durU: 'months', fee: '', gate: 'Has the change landed?' },
    { name: 'D5 · Demonstrate', delivers: 'Benefit measured against the baseline', durN: 2, durU: 'months', fee: '', gate: 'Did the value arrive?' },
    { name: 'D6 · Differentiate', delivers: 'Capability that stays after we leave', durN: 2, durU: 'months', fee: '', gate: 'Handover complete' }
  ];
  return (
    <div className="pp">
      <h2>Stages and gates</h2>
      <div className="hint">A staged proposal asks for one small approval, not the whole programme. Each stage carries its own price and ends in a decision — including the decision to stop.</div>
      <div className="rowlist">
        {st.map((s, i) => (
          <div className="stagerow" key={i}>
            <div className="srn">{i + 1}</div>
            <div className="rowgrid5">
              <input value={s.name || ''} onChange={e => upd(i, 'name', e.target.value)} placeholder="Stage name" />
              <input value={s.delivers || ''} onChange={e => upd(i, 'delivers', e.target.value)} placeholder="What it delivers" />
              <div className="unitrow">
                <input type="number" min="0" value={s.durN || ''} onChange={e => upd(i, 'durN', e.target.value)} />
                <select value={s.durU || 'weeks'} onChange={e => upd(i, 'durU', e.target.value)}>{DUR_UNITS.map(u => <option key={u}>{u}</option>)}</select>
              </div>
              <input value={s.fee || ''} onChange={e => upd(i, 'fee', e.target.value)} placeholder="Fee" />
              <input value={s.gate || ''} onChange={e => upd(i, 'gate', e.target.value)} placeholder="Decision at the end" />
            </div>
            <button className="lx" onClick={() => { const s2 = [...st]; s2.splice(i, 1); setP({ ...p, stages: s2 }); }}>✕</button>
          </div>
        ))}
        {!st.length && <div className="hint">No stages yet.</div>}
      </div>
      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => setP({ ...p, stages: [...st, { name: '', delivers: '', durN: '', durU: 'weeks', fee: '', gate: '' }] })}>＋ Add stage</button>
        <button className="btn line" onClick={() => setP({ ...p, stages: BTOS_STAGES })}>Use the BTOS-6D stages</button>
      </div>
      <div className="formrow"><label>Which stage are you asking them to approve today?</label><input value={p.stageAsk || ''} onChange={e => setP({ ...p, stageAsk: e.target.value })} placeholder="e.g. Stage 1 only — 8 weeks, fixed fee" /></div>
      <div className="noteBox"><b>The point of gates.</b> The customer never commits beyond the next gate. That is what makes a large programme signable — they are approving one stage and a decision point, not the whole thing.</div>
      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};

// --- The business case (venture) ---
const stepVenture = (p, setP, onBack, onNext) => {
  const v = p.venture || {};
  const setV = (k, val) => setP({ ...p, venture: { ...v, [k]: val } });
  const rowUpd = (key, i, f, val) => { const arr = [...(v[key] || [])]; arr[i] = { ...arr[i], [f]: val }; setV(key, arr); };
  const rows = (key, cols, empty) => (
    <>
      <div className="rowlist">
        {(v[key] || []).map((r, i) => (
          <div className="rowcard" key={i}>
            <div className={`rowgrid${cols.length}`}>
              {cols.map(c => <input key={c[0]} value={r[c[0]] || ''} onChange={e => rowUpd(key, i, c[0], e.target.value)} placeholder={c[1]} />)}
            </div>
            <button className="lx" onClick={() => { const arr = [...(v[key] || [])]; arr.splice(i, 1); setV(key, arr); }}>✕</button>
          </div>
        ))}
        {!(v[key] || []).length && <div className="hint">{empty}</div>}
      </div>
      <div className="proposal-toolbar"><button className="btn line" onClick={() => setV(key, [...(v[key] || []), {}])}>＋ Add row</button></div>
    </>
  );
  return (
    <div className="pp">
      <h2>The business case</h2>
      <div className="noteBox"><b>This is a different kind of proposal.</b> You are not selling a service — you are arguing that an opportunity exists, that this customer is unusually well placed to take it, and that SolidPro is the right partner to build it with them.</div>
      <div className="section"><h2>The opportunity</h2></div>
      <div className="formrow"><label>What is the opportunity?</label><textarea value={v.opportunity || ''} onChange={e => setV('opportunity', e.target.value)} placeholder="In two or three sentences a board member would repeat to someone else." /></div>
      <div className="formrow"><label>Why now?</label><textarea value={v.whyNow || ''} onChange={e => setV('whyNow', e.target.value)} placeholder="The window — a new regulation, a new market, an unoccupied position." /></div>
      <div className="formrow"><label>What does the market look like?</label><textarea value={v.market || ''} onChange={e => setV('market', e.target.value)} placeholder="Size, structure, who buys, what they pay. Only state what you have verified." /></div>
      <div className="section"><h2>Why this customer</h2></div>
      <div className="formrow"><label>What do they already have that others do not?</label><textarea value={v.assets || ''} onChange={e => setV('assets', e.target.value)} placeholder="Operations, relationships, data, balance sheet, position." /></div>
      <div className="formrow"><label>What has to be added</label>{rows('gaps', [['missing', 'What is missing'], ['why', 'Why it is needed'], ['how', 'How it gets filled']], 'No gaps listed yet.')}</div>
      <div className="section"><h2>How it earns</h2></div>
      <div className="formrow"><label>Revenue streams</label>{rows('streams', [['name', 'Stream'], ['how', 'How it works'], ['risk', 'Risk'], ['when', 'Opens']], 'No revenue streams yet.')}</div>
      <div className="section"><h2>The roadmap</h2></div>
      <div className="formrow"><label>Where they stand over time</label>{rows('horizon', [['year', 'Year'], ['position', 'Their position'], ['streams', 'What is earning']], 'No horizon yet.')}</div>
      <div className="formrow"><label>What is the long-term position?</label><textarea value={v.longterm || ''} onChange={e => setV('longterm', e.target.value)} placeholder="Where this ends up if it works." /></div>
      <div className="section"><h2>Who does what</h2></div>
      <div className="formrow"><label>The parties</label>{rows('parties', [['party', 'Party'], ['role', 'What they are responsible for']], 'No parties listed yet.')}</div>
      <div className="formrow"><label>What SolidPro will not do</label><textarea value={v.willnot || ''} onChange={e => setV('willnot', e.target.value)} placeholder="The limits — regulatory, ethical and practical." /></div>
      <div className="section"><h2>Handover</h2></div>
      <div className="formrow"><label>How does it become fully theirs?</label><textarea value={v.handover || ''} onChange={e => setV('handover', e.target.value)} placeholder="Who leads at each phase, and what they keep in every scenario." /></div>
      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};

// --- Engagement & costing (ported from stepEngagement) ---
// One costing engine. Every price is hours x rate; the engagement model changes
// only how that number is presented to the customer.
const stepEngagement = (p, setP, onBack, onNext) => {
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };
  const r = rateCfg(p), eff = effRate(p), fx = fxOk(), b = costBreakdown(p), pres = presentPrice(p);
  const cur = p.currency, c = p.calc || {};
  const hourly = cur === 'USD' ? ('$' + b.rate.toFixed(2)) : (b.fx ? ('₹' + Math.round(b.rate * b.fx).toLocaleString('en-IN')) : '—');

  const setCalc = (k, v) => { const np = { ...p, calc: { ...(p.calc || {}), [k]: v } }; setP(np); };
  const setExtra = (k, v) => { const np = { ...p, calc: { ...(p.calc || {}), extras: { ...((p.calc || {}).extras || {}), [k]: v } } }; setP(np); };
  const setHours = (id, v) => { const items = (p.items || []).map(i => i.solutionId === id ? { ...i, hours: v } : i); setP({ ...p, items }); };

  const pickMode = (id) => {
    const m = ENGAGEMENT_MODES.find(x => x.id === id);
    const comMap = { fixed: 'Fixed Fee', retainer: 'Monthly Retainer', billable: 'Time & Material', pod: 'Per Engineer / Month', milestone: 'Milestone-Based', outcome: 'Outcome-Linked', subscription: 'Subscription / Licence', hybrid: 'Hybrid' };
    setP({ ...p, engMode: id, engagementModel: m ? m.label : p.engagementModel, commercialModel: comMap[id] || p.commercialModel });
  };

  return (
    <div className="pp">
      <h2>How SolidPro will work with you, and what it costs</h2>
      <div className="noteBox"><b>One costing engine.</b> Every price is hours × rate. The engagement model below changes only how that number is <i>presented</i> to the customer — never how it is calculated.</div>

      <div className="engrid">
        {ENGAGEMENT_MODES.map(m => (
          <button key={m.id} className={`engcard ${p.engMode === m.id ? 'on' : ''}`} onClick={() => pickMode(m.id)}>
            <b>{m.label}</b><small>{m.line}</small><em>{m.best}</em>
          </button>
        ))}
      </div>

      <div className="ratebar">
        <div><small>BILLABLE RATE</small>
          <div className="unitrow">
            <input type="number" value={r.base} onChange={e => setField('rate.base', e.target.value)} />
            <select value={cur} onChange={e => setField('currency', e.target.value)}>
              <option value="INR">INR</option><option value="USD">USD</option>
            </select>
          </div>
          <span>US dollars per hour</span>
        </div>
        <div><small>ADJUST</small><input type="number" value={r.discount} onChange={e => setField('rate.discount', e.target.value)} /><span>% — positive discounts, negative marks up</span></div>
        <div><small>EFFECTIVE RATE</small><b>{'$' + eff.toFixed(2)}</b><span>per hour</span></div>
        <div><small>USD TO INR</small><b>{fx.ok ? fxRate() : '—'}</b><span>{fx.ok ? 'finance-approved · ' + ((ADMIN.finance || {}).updated || '') : 'not set'}</span></div>
      </div>

      {cur === 'INR' && !fx.ok && (
        <div className="warnBox">
          <b>Cannot price in rupees yet.</b> {fx.why} A proposal must not use an assumed exchange rate. Enter the finance-approved rate under <b>Admin → Finance</b>, or switch this proposal to USD.
        </div>
      )}

      <div className="section"><h2>Effort by service</h2><span>each service arrives with a starting estimate</span></div>
      {(p.items || []).length > 0 ? (
        <table className="minitable">
          <thead><tr><th>Service</th><th>Hours</th><th>Typical range</th><th style={{ textAlign: 'right' }}>Value</th></tr></thead>
          <tbody>
            {p.items.map(i => {
              const q = priceFor(p, i.hours);
              const v = cur === 'USD' ? q.usd : q.inr;
              return (
                <tr key={i.solutionId}>
                  <td><b>{i.title}</b></td>
                  <td><input className="amtin" style={{ width: 82 }} type="number" min="0" step="10" value={i.hours ?? 0} onChange={e => setHours(i.solutionId, e.target.value)} /></td>
                  <td><small>{i.hoursLow || '—'}–{i.hoursHigh || '—'} h</small></td>
                  <td style={{ textAlign: 'right' }}><b>{v === null ? '—' : money(cur, v)}</b></td>
                </tr>
              );
            })}
            <tr className="totalrow">
              <td><b>Total effort</b></td>
              <td><b>{b.hours.toLocaleString()} h</b></td>
              <td></td>
              <td style={{ textAlign: 'right' }}><b>{b.labour === null ? '—' : money(cur, b.labour)}</b></td>
            </tr>
          </tbody>
        </table>
      ) : <div className="empty">No services selected. Add them in the setup step.</div>}

      <div className="section"><h2>Anything bought or spent on top</h2><span>leave blank and it will not appear</span></div>
      <div className="formgrid">
        {EXTRA_COSTS.map(e => (
          <div className="formrow" key={e[0]}>
            <label>{e[1]}</label>
            <input type="number" min="0" value={(c.extras || {})[e[0]] || ''} onChange={ev => setExtra(e[0], ev.target.value)} placeholder="0" />
          </div>
        ))}
      </div>

      <div className="formgrid">
        <div className="formrow"><label>Contingency %</label><input type="number" min="0" max="50" value={c.contingency || 0} onChange={e => setCalc('contingency', e.target.value)} /></div>
        {(p.engMode === 'pod' || p.engMode === 'retainer') && (
          <>
            <div className="formrow"><label>Number of people</label><input type="number" min="0" value={c.people || ''} onChange={e => setCalc('people', e.target.value)} /></div>
            <div className="formrow"><label>Number of months</label><input type="number" min="0" value={c.months || ''} onChange={e => setCalc('months', e.target.value)} /></div>
          </>
        )}
      </div>

      {!b.blocked && (
        <>
          <div className="costbox">
            <div className="crow"><span>Effort — {b.hours.toLocaleString()} hours at {hourly} per hour</span><b>{money(cur, b.labour)}</b></div>
            {EXTRA_COSTS.filter(e => numOf((c.extras || {})[e[0]])).map(e => (
              <div className="crow sub" key={e[0]}><span>{e[1]}</span><b>{money(cur, numOf(c.extras[e[0]]))}</b></div>
            ))}
            {b.cont ? <div className="crow sub"><span>Contingency at {b.contPct}%</span><b>{money(cur, b.cont)}</b></div> : null}
            <div className="crow total"><span>Total</span><b>{money(cur, b.total)}</b></div>
            {inWords(cur, b.total) ? <div className="cwords">{inWords(cur, b.total)}</div> : null}
          </div>
          {pres && (
            <div className="preszone">
              <small>THE CUSTOMER SEES THIS AS</small>
              <b>{money(cur, pres.headline)}</b>
              <span>{pres.unit}{pres.note ? ' · ' + pres.note : ''}</span>
            </div>
          )}
          <div className="proposal-toolbar">
            <button className="btn primary" onClick={() => setP({ ...p, commercialAmount: String(calcTotal(p)) })}>Use this as the proposal value</button>
            <button className="btn line" onClick={() => {
              const t = calcTotal(p);
              const mag = t > 1e7 ? 1e5 : t > 1e6 ? 1e4 : t > 1e5 ? 1e3 : 100;
              setP({ ...p, commercialAmount: String(Math.round(t / mag) * mag) });
            }}>Round to a clean number</button>
          </div>
        </>
      )}
      <div className="hint">Current proposal value: <b>{money(cur, p.commercialAmount || 0)}</b></div>

      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};

// --- Metrics: how success will be measured ---
const StepMetrics = ({ p, setP, onBack, onNext }) => {
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };
  return (
    <div className="pp">
      <h2>How success will be measured</h2>
      <div className="noteBox"><b>Why this matters.</b> A proposal that names the number it will move is far harder to argue with than one that lists activities. Pick the measures this engagement will actually affect – and only those.</div>
      <TickGroups p={p} field="metrics" lib={METRIC_LIB.flatMap(g => g[1].map(x => [g[0], x, '']))}
        label="Measures this engagement will move"
        onChange={v => setP({ ...p, metrics: v })} />
      <FText p={p} path="metricNote" label="How and when these will be measured" ph="Who measures, from which record, and how often." onCommit={setField} />
      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};
const stepMetrics = (p, setP, onBack, onNext) => <StepMetrics p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- 5b · Commercials (stepMoney) ---
const StepMoney = ({ p, setP, onBack, onNext }) => {
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };
  const items = p.items || [];
  const sum = items.reduce((a, i) => a + numOf(i.amount), 0);
  const total = p.commercialAmount || sum;
  const payments = p.payments || [];

  const setPayment = (i, f, v) => setP({ ...p, payments: payments.map((x, j) => j === i ? { ...x, [f]: v } : x) });

  // Splits the proposal value across the milestones by their stated percentage.
  const recalcFromPercent = () => {
    const base = numOf(total);
    if (!base) return;
    setP({ ...p, payments: payments.map(m => ({ ...m, amount: m.percent ? String(Math.round(base * numOf(m.percent) / 100)) : m.amount })) });
  };

  return (
    <div className="pp">
      <h2>Commercials</h2>
      <div className="hint">Line items come from the services you priced in the engagement step. Edit any value here.</div>

      {items.length ? (
        <>
          <table className="minitable">
            <thead><tr><th>Item</th><th>Engagement</th><th>Uses</th><th style={{ textAlign: 'right' }}>Value</th></tr></thead>
            <tbody>
              {items.map(i => (
                <tr key={i.solutionId}>
                  <td><b>{i.title || ''}</b>{durText(i.duration) ? <><br /><small>{durText(i.duration)}</small></> : null}</td>
                  <td>{i.engagementModel || '—'}</td>
                  <td><small>{effortText(i.efforts) || '-'}</small></td>
                  <td style={{ textAlign: 'right' }}>
                    <input className="amtin" value={i.amount || ''} placeholder="-"
                      onChange={e => setP({ ...p, items: items.map(x => x.solutionId === i.solutionId ? { ...x, amount: e.target.value } : x) })} />
                  </td>
                </tr>
              ))}
              <tr className="totalrow">
                <td colSpan="3"><b>Line-item total</b></td>
                <td style={{ textAlign: 'right' }}><b>{money(p.currency, sum || '')}</b></td>
              </tr>
            </tbody>
          </table>
          <div className="proposal-toolbar">
            <button className="btn line" onClick={() => setField('commercialAmount', String(sum))}>Use as proposal value</button>
          </div>
        </>
      ) : <div className="empty">No priced items yet – price a service in the engagement step.</div>}

      <div className="formgrid">
        <FSelect p={p} path="commercialModel" label="How the customer will be charged" opts={COM_MODELS} onCommit={setField} />
        <FSelect p={p} path="currency" label="Currency" opts={['INR', 'USD']} onCommit={setField} />
        <FInput p={p} path="commercialAmount" label="Total proposal value" ph="e.g. 2450000" onCommit={setField} />
      </div>

      {total ? (
        <div className="bigvalue">
          <small>PROPOSAL VALUE</small>
          <b>{money(p.currency, total)}</b>
          {inWords(p.currency, total) ? <span>{inWords(p.currency, total)}</span> : null}
        </div>
      ) : null}

      <div className="section"><h2>Payment schedule</h2><span>{payments.length} milestones</span></div>
      <div className="libpick">
        <select value={p.payTemplate || ''} onChange={e => setField('payTemplate', e.target.value)}>
          {Object.keys(PAY_TEMPLATES).map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <button className="btn line" onClick={() => setP({
          ...p,
          payments: (PAY_TEMPLATES[p.payTemplate] || []).map(x => ({ milestone: x[0], percent: x[1] || '', amount: '', trigger: x[2] }))
        })}>Apply template</button>
      </div>
      <div className="rowlist">
        {payments.length ? payments.map((m, i) => (
          <div key={i} className="rowcard">
            <div className="rowgrid4">
              <input value={m.milestone || ''} placeholder="Milestone" onChange={e => setPayment(i, 'milestone', e.target.value)} />
              <input value={m.percent || ''} placeholder="%" onChange={e => setPayment(i, 'percent', e.target.value)} />
              <input value={m.amount || ''} placeholder="Amount" onChange={e => setPayment(i, 'amount', e.target.value)} />
              <input value={m.trigger || ''} placeholder="What triggers it" onChange={e => setPayment(i, 'trigger', e.target.value)} />
            </div>
            <button className="lx" onClick={() => setP({ ...p, payments: payments.filter((_, j) => j !== i) })}>✕</button>
          </div>
        )) : <div className="hint">No milestones yet.</div>}
      </div>
      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => setP({ ...p, payments: [...payments, { milestone: '', percent: '', amount: '', trigger: '' }] })}>＋ Add milestone</button>
        <button className="btn line" onClick={recalcFromPercent}>Recalculate amounts from %</button>
      </div>
      {payments.length && payments.reduce((a, m) => a + numOf(m.percent), 0) !== 100 && payments.some(m => m.percent) ? (
        <div className="warnBox">The milestone percentages add up to {payments.reduce((a, m) => a + numOf(m.percent), 0)}%, not 100%.</div>
      ) : null}

      <FText p={p} path="paymentTerms" label="Payment terms" onCommit={setField} />
      <FText p={p} path="taxNote" label="Taxes & expenses" onCommit={setField} />
      <FText p={p} path="commercialTerms" label="Other commercial terms" ph="Escalation, currency, cancellation, liability cap." onCommit={setField} />
      <FText p={p} path="notes" label="Commercial notes" onCommit={setField} />

      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};
const stepMoney = (p, setP, onBack, onNext) => <StepMoney p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- 6 · Process & next actions (stepMethod + stepPlan + stepGovern) ---
const StepProcess = ({ p, setP, onBack, onNext }) => {
  const [newRisk, setNewRisk] = useState('');
  const [newMit, setNewMit] = useState('');
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };
  const toggleSection = (secId, on) => setP({ ...p, sectionsOn: { ...p.sectionsOn, [secId]: on } });

  const method = detectMethod(p);
  const stages = methodStages(p);
  // An empty approach list means "every stage", which is how a fresh proposal
  // starts; the first untick has to materialise the full list.
  const stageOn = (id) => (p.approach || []).length ? p.approach.includes(id) : true;
  const toggleStage = (id) => {
    const cur = (p.approach || []).length ? p.approach : stages.map(s => s[0]);
    setP({ ...p, approach: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] });
  };

  const phases = p.phases || [];
  const setPhase = (i, f, v) => setP({ ...p, phases: phases.map((x, j) => j === i ? { ...x, [f]: v } : x) });
  const escalation = p.escalation || [];
  const risks = p.risks || [];

  return (
    <div className="pp">
      <h2>Our approach</h2>
      <div className="hint">Set to <b>{METHODS[method].label}</b> from the services you selected. Change it if this engagement is really something else.</div>
      <div className="formrow">
        <label>Methodology<Info tip="Each SolidPro business unit works differently. A factory study starts by observing the floor; a transformation programme starts with BTOS Discover. Picking the right one makes the proposal read as though it was written for them." /></label>
        <select value={p.methodKey || 'AUTO'} onChange={e => setField('methodKey', e.target.value)}>
          <option value="AUTO">Automatic – follow the services selected ({METHODS[method].label})</option>
          {Object.keys(METHODS).map(k => <option key={k} value={k}>{METHODS[k].label}</option>)}
        </select>
      </div>
      <div className="stagepick">
        {stages.map((s, i) => (
          <label key={s[0]} className={`stagecard ${stageOn(s[0]) ? 'on' : ''}`}>
            <input type="checkbox" checked={stageOn(s[0])} onChange={() => toggleStage(s[0])} />
            <span className="sn">{String(i + 1).padStart(2, '0')}</span>
            <div><b>{s[0]} - {s[1]}</b><small>{s[2]}</small></div>
          </label>
        ))}
      </div>
      <FText p={p} path="approachCustom" label="Anything to add about the approach" ph="Optional – a stage or emphasis specific to this engagement." onCommit={setField} />

      <div className="section"><h2>Timeline</h2></div>
      <div className="formgrid">
        <FSelect p={p} path="engagementModel" label="How SolidPro will work with the customer" opts={ENG_MODELS} tip="How the work is contracted." onCommit={setField} />
        <FDur p={p} path="duration" label="How long the whole engagement takes" tip="Pick the number and the unit – “10” on its own means nothing on a customer’s desk." onCommit={setField} />
      </div>
      <FStart p={p} tip="Most customers want to know how quickly you can start, tied to their PO rather than to a fixed date." onCommit={setField} />
      <EffortBlock p={p} onChange={v => setP({ ...p, efforts: v })} />

      <div className="section"><h2>Phases and milestones</h2><span>{phases.length} phases</span></div>
      <div className="rowlist">
        {phases.length ? phases.map((f, i) => (
          <div key={i} className="rowcard">
            <div className="rowgrid4">
              <input value={f.name || ''} placeholder="Phase name" onChange={e => setPhase(i, 'name', e.target.value)} />
              <div className="unitrow">
                <input type="number" min="0" value={f.durN ?? ''} placeholder="0" onChange={e => setPhase(i, 'durN', e.target.value)} />
                <select value={f.durU || 'weeks'} onChange={e => setPhase(i, 'durU', e.target.value)}>
                  {DUR_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <input value={f.output || ''} placeholder="Milestone output" onChange={e => setPhase(i, 'output', e.target.value)} />
              <input value={f.owner || ''} placeholder="Owner" onChange={e => setPhase(i, 'owner', e.target.value)} />
            </div>
            <button className="lx" onClick={() => setP({ ...p, phases: phases.filter((_, j) => j !== i) })}>✕</button>
          </div>
        )) : <div className="hint">No phases yet.</div>}
      </div>
      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => setP({ ...p, phases: [...phases, { name: '', durN: '', durU: 'weeks', output: '', owner: '' }] })}>＋ Add phase</button>
        <button className="btn line" onClick={() => setP({
          ...p,
          phases: stages.filter(s => stageOn(s[0])).map(s => ({ name: `${s[0]} - ${s[1]}`, durN: '', durU: 'weeks', output: '', owner: '' }))
        })}>Build phases from the approach stages</button>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>Building from the approach stages keeps the plan and the methodology telling the same story – reviewers notice when they do not.</div>

      <div className="section"><h2>How we will run it</h2></div>
      <FText p={p} path="governance" label="Governance" ph="Review cadence, decision rights, reporting."
        tip="Large customers look for a named rhythm – weekly written report, stage reviews, a live risk log." onCommit={setField} />

      <div className="section"><h2>Escalation</h2><span>who talks to whom when something is stuck</span></div>
      <div className="rowlist">
        {escalation.map((e, i) => (
          <div key={i} className="rowcard">
            <div className="rowgrid3">
              {[0, 1, 2].map(idx => (
                <input key={idx} value={e[idx] || ''} placeholder={['Level', 'Who ↔ who', 'Response time'][idx]}
                  onChange={ev => setP({ ...p, escalation: escalation.map((r, j) => j === i ? r.map((c, k) => k === idx ? ev.target.value : c) : r) })} />
              ))}
            </div>
            <button className="lx" onClick={() => setP({ ...p, escalation: escalation.filter((_, j) => j !== i) })}>✕</button>
          </div>
        ))}
      </div>
      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => setP({ ...p, escalation: [...escalation, ['', '', '']] })}>＋ Add level</button>
      </div>

      <div className="section"><h2>Risks and how we manage them</h2><span>{risks.length} selected</span></div>
      <SkipBar p={p} secId="risks" onToggle={toggleSection} />
      <div className="noteBox">Naming risks does not weaken a proposal – it is what separates a considered submission from an optimistic one. Each risk below comes with a mitigation already written.</div>
      <div className="tickgrid one">
        {RISK_LIB.map(r => {
          const on = risks.some(x => x[0] === r[0]);
          return (
            <label key={r[0]} className={`tick ${on ? 'on' : ''}`}>
              <input type="checkbox" checked={on} onChange={() => setP({ ...p, risks: on ? risks.filter(x => x[0] !== r[0]) : [...risks, r.slice()] })} />
              <span><b>{r[0]}</b><small>Impact {r[1]} · Likelihood {r[2]} - {r[3]}</small></span>
            </label>
          );
        })}
      </div>
      <div className="libpick" style={{ marginTop: 10 }}>
        <input value={newRisk} onChange={e => setNewRisk(e.target.value)} placeholder="Add your own risk" />
        <input value={newMit} onChange={e => setNewMit(e.target.value)} placeholder="How we will manage it" />
        <button className="btn line" onClick={() => {
          if (!newRisk.trim()) return;
          setP({ ...p, risks: [...risks, [newRisk.trim(), 'Medium', 'Medium', newMit.trim()]] });
          setNewRisk(''); setNewMit('');
        }}>＋ Add</button>
      </div>

      <div className="section"><h2>What this proposal assumes</h2></div>
      <TickGroups p={p} field="assumptions" lib={ASSUMP_LIB.map(x => [x])}
        label="Assumptions" tip="If an assumption turns out to be wrong, this is what justifies a change request."
        onChange={v => setP({ ...p, assumptions: v })} />
      <TickGroups p={p} field="dependencies" lib={DEP_LIB.map(x => [x])}
        label="What we need from the customer" tip="Being explicit here prevents the engagement stalling in week two."
        onChange={v => setP({ ...p, dependencies: v })} />

      <FText p={p} path="nextSteps" label="Next steps" draftable onCommit={setField} />

      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};
const stepProcess = (p, setP, onBack, onNext) => <StepProcess p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- 7 · Terms & compliance (stepTerms) ---
// Each block carries its own skip control: an irrelevant section can be left
// out of the document without losing what has been typed into it.
const StepConditions = ({ p, setP, onBack, onNext }) => {
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };
  const toggleSection = (secId, on) => setP({ ...p, sectionsOn: { ...p.sectionsOn, [secId]: on } });

  return (
    <div className="pp">
      <h2>Terms & compliance</h2>
      <div className="hint">Each block below can be skipped if it is not relevant to this customer.</div>

      <div className="section"><h2>Quality & standards</h2></div>
      <SkipBar p={p} secId="quality" onToggle={toggleSection} />
      <TickGroups p={p} field="standards" lib={STD_LIB.map(x => [x])} label="Standards we will follow"
        onChange={v => setP({ ...p, standards: v })} />
      <FText p={p} path="qualityNote" label="How quality is assured" ph="Reviews, checks and sign-offs on this engagement." onCommit={setField} />

      <div className="section"><h2>Confidentiality, data & IP</h2></div>
      <SkipBar p={p} secId="security" onToggle={toggleSection} />
      <TickGroups p={p} field="security" lib={SEC_CLAUSES.map(x => [x])} label="What we commit to"
        tip="Enterprise procurement almost always asks about this. Ticking it up front avoids a round of questions."
        onChange={v => setP({ ...p, security: v })} />
      <FSelect p={p} path="ip" label="Who owns what is produced" opts={IP_OPTIONS}
        tip="The single question customers most often send back. Answer it in the proposal." onCommit={setField} />

      <div className="section"><h2>Company & statutory details</h2></div>
      <SkipBar p={p} secId="compliance" onToggle={toggleSection} />
      <div className="formgrid">
        <FInput p={p} path="compliance.gst" label="GSTIN" onCommit={setField} />
        <FInput p={p} path="compliance.pan" label="PAN" onCommit={setField} />
        <FInput p={p} path="compliance.cin" label="CIN" onCommit={setField} />
        <FInput p={p} path="compliance.msme" label="MSME / Udyam registration" onCommit={setField} />
        <FInput p={p} path="compliance.vendorCode" label="Their vendor code for us" onCommit={setField} />
        <FInput p={p} path="compliance.insurance" label="Insurance cover" onCommit={setField} />
      </div>

      <div className="section"><h2>Service levels</h2></div>
      <SkipBar p={p} secId="sla" onToggle={toggleSection} />
      <TickGroups p={p} field="sla" lib={SLA_LIB} label="Performance commitments"
        onChange={v => setP({ ...p, sla: v })} />

      <div className="section"><h2>Other terms</h2></div>
      <SkipBar p={p} secId="change" onToggle={toggleSection} />
      <FText p={p} path="changeControl" label="If the scope changes" onCommit={setField} />
      <SkipBar p={p} secId="warranty" onToggle={toggleSection} />
      <FText p={p} path="warranty" label="Warranty & support after handover" onCommit={setField} />
      <SkipBar p={p} secId="hse" onToggle={toggleSection} />
      <FText p={p} path="hse" label="Safety on your site" tip="Only relevant where our people work at their premises." onCommit={setField} />
      <SkipBar p={p} secId="esg" onToggle={toggleSection} />
      <FText p={p} path="esg" label="Sustainability & ESG" onCommit={setField} />

      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};
const stepConditions = (p, setP, onBack, onNext) => <StepConditions p={p} setP={setP} onBack={onBack} onNext={onNext} />;

// --- Approval (was stepReview but without sections) ---
const stepApproval = (p, setP, onBack, onNext, currentUser, showApprovalModal, setShowApprovalModal, approverEmail, setApproverEmail, approvalComment, setApprovalComment, approvalStatus, setApprovalStatus, handleSendForApproval, handleApprove, showMail, setShowMail) => {
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };
  
  const isApprover = p.approverId === currentUser?.id && p.approvalStatus === 'Pending';
  const isCreator = p.createdBy === currentUser?.id;
  const isAdmin = currentUser?.role === 'admin';
  const isReadOnly = p.lockedForEditing && p.approvalStatus === 'Pending' && !isApprover && !isAdmin;

  const toggleApprovalModal = (show) => setShowApprovalModal(show);

  return (
    <div className="pp">
      <h2>Approval & closing</h2>
      <div className="hint">Prepared, reviewed, approved – and the closing note.</div>
      
      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="modalback" onClick={() => toggleApprovalModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{p.approvalStatus === 'Pending' ? 'Review & Approve' : 'Send for Approval'}</h2>
            
            {p.approvalStatus !== 'Pending' ? (
              <>
                <div className="formrow">
                  <label>Approver Email <span className="text-red-500">*</span></label>
                  <input 
                    value={approverEmail} 
                    onChange={e => setApproverEmail(e.target.value)} 
                    placeholder="approver@solidpro-es.com"
                  />
                </div>
                <div className="formrow">
                  <label>Message (optional)</label>
                  <textarea 
                    rows="3" 
                    value={approvalComment} 
                    onChange={e => setApprovalComment(e.target.value)} 
                    placeholder="Any additional notes for the approver..."
                  />
                </div>
                <div className="modalactions">
                  <button className="btn line" onClick={() => toggleApprovalModal(false)}>Cancel</button>
                  <button className="btn primary" onClick={handleSendForApproval}>
                    Send for Approval
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="formrow">
                  <label>Your Decision</label>
                  <div className="flex flex-wrap gap-4">
                    <button 
                      className={`btn ${approvalStatus === 'Approved' ? 'primary' : 'line'}`}
                      onClick={() => setApprovalStatus('Approved')}
                    >
                      ✅ Approve
                    </button>
                    <button 
                      className={`btn ${approvalStatus === 'Changes Requested' ? 'primary' : 'line'}`}
                      onClick={() => setApprovalStatus('Changes Requested')}
                    >
                      🔄 Request Changes
                    </button>
                    <button 
                      className={`btn ${approvalStatus === 'Rejected' ? 'danger' : 'line'}`}
                      onClick={() => setApprovalStatus('Rejected')}
                    >
                      ❌ Reject
                    </button>
                  </div>
                </div>
                <div className="formrow">
                  <label>Comment</label>
                  <textarea 
                    rows="3" 
                    value={approvalComment} 
                    onChange={e => setApprovalComment(e.target.value)} 
                    placeholder="Please provide feedback..."
                  />
                </div>
                <div className="modalactions">
                  <button className="btn line" onClick={() => toggleApprovalModal(false)}>Cancel</button>
                  <button 
                    className="btn primary" 
                    onClick={() => handleApprove(approvalStatus)} 
                    disabled={!approvalStatus}
                  >
                    Submit Decision
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Status Banner */}
      {p.approvalStatus === 'Pending' && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <p className="text-yellow-700">
            <strong>⏳ Pending Approval</strong> 
            {isApprover && ' You are the approver. Please review and approve/reject this proposal.'}
            {isCreator && !isApprover && ` This proposal has been sent to ${p.approverEmail || 'the approver'} for approval.`}
            {isReadOnly && ' You can view but not edit this proposal while it is pending approval.'}
          </p>
        </div>
      )}

      {p.approvalStatus === 'Approved' && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
          <p className="text-green-700">
            <strong>✅ Approved</strong> 
            This proposal has been approved.
            {p.approvalComment && <span className="block mt-2">Comment: {p.approvalComment}</span>}
          </p>
        </div>
      )}

      {p.approvalStatus === 'Rejected' && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <p className="text-red-700">
            <strong>❌ Rejected</strong> 
            This proposal has been rejected.
            {p.approvalComment && <span className="block mt-2">Reason: {p.approvalComment}</span>}
          </p>
        </div>
      )}

      {p.approvalStatus === 'Changes Requested' && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <p className="text-blue-700">
            <strong>🔄 Changes Requested</strong> 
            Please make the requested changes and resend for approval.
            {p.approvalComment && <span className="block mt-2">Feedback: {p.approvalComment}</span>}
          </p>
        </div>
      )}

      <div className="formgrid">
        <div className="formrow">
          <label>Prepared by – name</label>
          <input value={p.doc.preparedName} onChange={e => setField('doc.preparedName', e.target.value)} placeholder="Your name" disabled={isReadOnly} />
        </div>
        <div className="formrow">
          <label>Designation</label>
          <select value={p.doc.preparedDesig || ''} onChange={e => setField('doc.preparedDesig', e.target.value)} disabled={isReadOnly}>
            <option value="">Select designation</option>
            {DESIG_LIB.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="formrow">
          <label>Email</label>
          <input value={p.doc.preparedEmail || ''} onChange={e => setField('doc.preparedEmail', e.target.value)} placeholder="name@solidpro-es.com" disabled={isReadOnly} />
        </div>
        <div className="formrow">
          <label>Phone</label>
          <input value={p.doc.preparedPhone || ''} onChange={e => setField('doc.preparedPhone', e.target.value)} placeholder="+91 …" disabled={isReadOnly} />
        </div>
        <div className="formrow">
          <label>Reviewed by – name</label>
          <input value={p.doc.reviewedName} onChange={e => setField('doc.reviewedName', e.target.value)} placeholder="Reviewer name" disabled={isReadOnly} />
        </div>
        <div className="formrow">
          <label>Designation</label>
          <select value={p.doc.reviewedDesig || ''} onChange={e => setField('doc.reviewedDesig', e.target.value)} disabled={isReadOnly}>
            <option value="">Select designation</option>
            {DESIG_LIB.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="formrow">
          <label>Approved by – name</label>
          <input value={p.doc.approvedName} onChange={e => setField('doc.approvedName', e.target.value)} placeholder="Approver name" disabled={isReadOnly} />
        </div>
        <div className="formrow">
          <label>Designation</label>
          <select value={p.doc.approvedDesig || ''} onChange={e => setField('doc.approvedDesig', e.target.value)} disabled={isReadOnly}>
            <option value="">Select designation</option>
            {DESIG_LIB.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>
      
      <div className="formrow">
        <label>Closing note</label>
        <textarea 
          rows="3" 
          value={p.closingNote} 
          onChange={e => setField('closingNote', e.target.value)} 
          placeholder="The last thing they read. One short paragraph – why this matters, and what you would like to happen next."
          disabled={isReadOnly}
        />
      </div>

      <ReviewFeedback p={p} />

      <div className="section">
        <h2>Approval status</h2>
        <span className={`px-3 py-1 rounded-full text-sm font-bold ${
          p.approvalStatus === 'Approved' ? 'bg-green-100 text-green-700' :
          p.approvalStatus === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
          p.approvalStatus === 'Rejected' ? 'bg-red-100 text-red-700' :
          p.approvalStatus === 'Changes Requested' ? 'bg-blue-100 text-blue-700' :
          'bg-gray-100 text-gray-600'
        }`}>
          {p.approvalStatus || 'Draft'}
        </span>
      </div>

      <div className="formrow">
        <label>Approver email</label>
        <input 
          value={p.approverEmail || ''} 
          onChange={e => setField('approverEmail', e.target.value)} 
          placeholder="approver@solidpro-es.com"
          disabled={isReadOnly || p.approvalStatus === 'Pending'}
        />
      </div>

      {p.approvalStatus === 'Pending' && isApprover && (
        <div className="formrow flex flex-wrap gap-4">
          <button 
            className="btn primary" 
            onClick={() => {
              setApprovalStatus('Approved');
              const comment = prompt('Optional comment:');
              if (comment !== null) {
                setApprovalComment(comment || '');
                handleApprove('Approved');
              }
            }}
          >
            ✅ Approve
          </button>
          <button 
            className="btn line" 
            onClick={() => {
              setApprovalStatus('Changes Requested');
              const comment = prompt('Please provide feedback on what changes are needed:');
              if (comment !== null && comment.trim()) {
                setApprovalComment(comment);
                handleApprove('Changes Requested');
              } else if (comment !== null) {
                alert('Please provide feedback when requesting changes.');
              }
            }}
          >
            🔄 Request Changes
          </button>
          <button 
            className="btn danger" 
            onClick={() => {
              setApprovalStatus('Rejected');
              const comment = prompt('Reason for rejection:');
              if (comment !== null && comment.trim()) {
                setApprovalComment(comment);
                handleApprove('Rejected');
              } else if (comment !== null) {
                alert('Please provide a reason for rejection.');
              }
            }}
          >
            ❌ Reject
          </button>
        </div>
      )}

      {(p.approvalStatus === 'Draft' || p.approvalStatus === 'Changes Requested') && !isReadOnly && (
        <div className="proposal-toolbar">
          <button className="btn primary" onClick={() => toggleApprovalModal(true)}>
            Send for approval
          </button>
          <button className="btn line" onClick={() => setShowMail(true)}>
            Preview the email
          </button>
        </div>
      )}

      {showMail && <MailPreview p={p} toEmail={p.approverEmail} onClose={() => setShowMail(false)} />}

      {isReadOnly && (
        <div className="formrow">
          <p className="text-sm text-gray-500">🔒 This proposal is locked for editing while pending approval.</p>
        </div>
      )}

      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
};

// --- Generate (was stepSections + generation) ---
// --- 9 · Generate (stepSections + stepReview) ---
const READY_CHECKS = (p, lineTotal) => [
  ['Customer named', !!p.clientName],
  ['What they asked for captured', !!p.customerAsk],
  ['Services selected', (p.solutions || []).length > 0],
  ['Scope defined', (p.scopeItems || []).length > 0],
  ['Deliverables listed', (p.deliverables || []).length > 0],
  ['Success measures chosen', (p.metrics || []).length > 0],
  ['Timeline set', !!(p.duration || {}).n],
  ['Team named', (p.team || []).length > 0],
  ['Commercials entered', !!p.commercialAmount || lineTotal > 0],
  ['Payment schedule set', (p.payments || []).length > 0],
  ['Assumptions stated', (p.assumptions || []).length > 0],
  ['Prepared-by filled in', !!p.doc?.preparedName]
];

const StepGenerate = ({ p, setP, onBack, onNext }) => {
  const [annex, setAnnex] = useState('');
  const setField = (path, val) => { const np = { ...p }; setPath(np, path, val); setP(np); };

  const lineTotal = (p.items || []).reduce((a, i) => a + numOf(i.amount), 0);
  const checks = READY_CHECKS(p, lineTotal);
  const pct = Math.round(checks.filter(c => c[1]).length / checks.length * 100);

  const setSections = (fn) => {
    const on = { ...p.sectionsOn };
    SECTION_DEFS.forEach(s => { on[s[0]] = fn(s); });
    setP({ ...p, sectionsOn: on });
  };
  const reorder = (from, to) => {
    const order = [...p.sectionOrder];
    const [moved] = order.splice(from, 1);
    order.splice(to, 0, moved);
    setP({ ...p, sectionOrder: order });
  };

  const openProposal = () => {
    const html = generateProposalHTML(p);
    const win = window.open('', '_blank');
    if (!win) { window.alert('Please allow pop-ups to generate the proposal.'); return; }
    win.document.write(html);
    win.document.close();
  };
  const downloadHTML = () => {
    const b = new Blob([generateProposalHTML(p)], { type: 'text/html;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b);
    a.download = `${safe(p.id)}-${safe(p.title)}.html`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const exportData = () => {
    const b = new Blob([JSON.stringify(p, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b);
    a.download = `${safe(p.id)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const readLogo = (file) => {
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => setField('clientLogo', fr.result);
    fr.readAsDataURL(file);
  };

  const annexures = p.annexures || [];

  return (
    <div className="pp">
      <h2>Generate</h2>
      <div className="readiness">
        <div className="readbar"><span style={{ width: pct + '%' }}></span></div>
        <b>{pct}% ready</b>
      </div>
      <div className="tickgrid" style={{ margin: '12px 0 18px' }}>
        {checks.map(([label, ok]) => (
          <div key={label} className={`check ${ok ? 'ok' : 'miss'}`}><span>{ok ? '✔' : '○'}</span><span>{label}</span></div>
        ))}
      </div>
      <div className="proposal-toolbar">
        <button className="btn primary" onClick={openProposal}>Open the proposal</button>
        <button className="btn line" onClick={downloadHTML}>Download HTML</button>
        <button className="btn line" onClick={exportData}>Export data</button>
      </div>
      <div className="noteBox"><b>Printing:</b> choose A4, margins <b>None</b>, and tick <b>Background graphics</b>.</div>

      <div className="section"><h2>Sections & order</h2><span>untick to leave a section out</span></div>
      <div className="hint">Sections marked <b>optional</b> are the ones smaller customers rarely need.</div>
      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => setSections(() => true)}>Include all</button>
        <button className="btn line" onClick={() => setSections(s => !SEC_OPTIONAL(s[0]))}>Core only</button>
        <button className="btn line" onClick={() => setSections(() => true)}>Full enterprise set</button>
        <button className="btn line" onClick={() => setP({ ...p, sectionOrder: SECTION_DEFS.map(s => s[0]) })}>Reset order</button>
      </div>
      <div className="seclist">
        {(p.sectionOrder || SECTION_DEFS.map(d => d[0])).map((id, idx) => {
          const def = SECTION_DEFS.find(s => s[0] === id);
          if (!def) return null;
          return (
            <div key={id} className={`secrow ${p.sectionsOn[id] === false ? 'off' : ''}`}>
              <span className="grip">⠿</span>
              <input type="checkbox" checked={p.sectionsOn[id] !== false}
                onChange={() => setP({ ...p, sectionsOn: { ...p.sectionsOn, [id]: p.sectionsOn[id] === false } })} />
              <div>
                <b>{def[1]}{SEC_OPTIONAL(id) ? <span className="optb">optional</span> : null}</b>
                <small>{def[2]}</small>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                <button className="lx" onClick={() => { if (idx > 0) reorder(idx, idx - 1); }}>↑</button>
                <button className="lx" onClick={() => { if (idx < (p.sectionOrder || []).length - 1) reorder(idx, idx + 1); }}>↓</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="section"><h2>Cover & branding</h2></div>
      <div className="formgrid">
        <FSelect p={p} path="theme" label="Cover theme" opts={PRACTICES} onCommit={setField} />
      </div>
      <div className="formrow">
        <label>Practice introductions to include</label>
        <div className="tickgrid">
          {PRACTICES.map(x => (
            <label key={x} className={`tick ${(p.practices || []).includes(x) ? 'on' : ''}`}>
              <input type="checkbox" checked={(p.practices || []).includes(x)} onChange={() => {
                const cur = p.practices || [];
                setP({ ...p, practices: cur.includes(x) ? cur.filter(y => y !== x) : [...cur, x] });
              }} />
              <span><b>{x}</b></span>
            </label>
          ))}
        </div>
      </div>
      <div className="formrow">
        <label>Customer logo (optional)</label>
        <input type="file" accept="image/*" onChange={e => readLogo(e.target.files?.[0])} />
        {p.clientLogo ? (
          <div style={{ marginTop: 8 }}>
            <img src={p.clientLogo} alt="Customer logo" style={{ maxHeight: 50, border: '1px solid #e5e7eb', borderRadius: 8, padding: 5 }} />{' '}
            <button className="btn line tiny" onClick={() => setField('clientLogo', '')}>Remove</button>
          </div>
        ) : null}
      </div>

      <div className="section"><h2>Annexures</h2></div>
      <div className="linelist">
        {annexures.length ? annexures.map((x, i) => (
          <div key={i} className="lineitem">
            <span className="ln">{String.fromCharCode(65 + i)}</span>
            <input value={x} onChange={e => setP({ ...p, annexures: annexures.map((y, j) => j === i ? e.target.value : y) })} />
            <button className="lx" onClick={() => setP({ ...p, annexures: annexures.filter((_, j) => j !== i) })}>✕</button>
          </div>
        )) : <div className="hint">None.</div>}
      </div>
      <div className="libpick" style={{ marginTop: 8 }}>
        <input value={annex} onChange={e => setAnnex(e.target.value)} placeholder="e.g. CVs of the engagement team"
          onKeyDown={e => { if (e.key === 'Enter' && annex.trim()) { setP({ ...p, annexures: [...annexures, annex.trim()] }); setAnnex(''); } }} />
        <button className="btn line" onClick={() => { if (annex.trim()) { setP({ ...p, annexures: [...annexures, annex.trim()] }); setAnnex(''); } }}>＋ Add</button>
      </div>

      <StepNav onBack={onBack} onNext={openProposal} nextLabel="Open the proposal" />
    </div>
  );
};
const stepGenerate = (p, setP, onBack, onNext) => <StepGenerate p={p} setP={setP} onBack={onBack} onNext={onNext} />;

const DOC_LOGO = '/logo.svg';
const BOOK_URL = 'https://www.solidpro-es.com/contact';
// The cover QR points at the contact page; the app ships the image in public/.
const QR = '/qrcode.png';

// Opening paragraph of the executive summary when the author has not written one.
const autoSummary = (p, sols) => {
  const ask = lower(p.customerAsk || 'address a defined engineering requirement');
  const askS = /[.!?]$/.test(ask) ? ask : ask + '.';
  const who = p.clientName || 'The customer';
  if (!sols.length) {
    return `${who} has asked SolidPro to ${askS} SolidPro proposes a focused engagement to confirm the current state and deliver the agreed outcome with named ownership, defined deliverables and measurable acceptance.`;
  }
  const value = money(p.currency, p.commercialAmount || (p.items || []).reduce((a, i) => a + numOf(i.amount), 0) || '');
  const titles = sols.slice(0, 3).map(s => s.title).join(', ');
  const more = sols.length > 3 ? ' and related capabilities' : '';
  const over = durText(p.duration) || 'an agreed period';
  return `${who} has asked SolidPro to ${askS} SolidPro recommends a ${lower(p.engagementModel)} engagement combining ${titles}${more}, delivered over ${over} by a named team. The work moves from a confirmed current-state baseline into defined deliverables, governed execution and verified results, at an indicative value of ${value}.`;
};

// ===== GENERATED PROPOSAL DOCUMENT =====
// Ported from the HTML build's proposalHTML(): same A4 sheet layout, same
// stylesheet, same section vocabulary and the same client-side pagination, so
// the printed PDF is identical.
const generateProposalHTML = (p) => {
  if (!p) return '<p>No proposal selected.</p>';
  const bySol = (id) => (window._SOLS || []).find(s => s.id === id);
  const byCase = (id) => (window._CASES || []).find(c => c.id === id);
 const on=id=>(p.sectionsOn || {})[id] !== false;
 const sols=(p.solutions||[]).map(bySol).filter(Boolean);
 const cases=(p.cases||[]).map(byCase).filter(Boolean);
 const caps=p.capabilities||[],items=p.items||[];
 const theme=p.theme||'SolidPro';
 const accent=theme==='NetZeroHub'?'#1d7a45':theme==='TAKTIX'?'#0d5f86':theme==='Helixa'?'#6a3fa0':'#17469E';
 const lineTotal=items.reduce((a,i)=>a+numOf(i.amount),0);
 const total=p.commercialAmount||lineTotal||'';
 const issue=fmtDate(p.doc.issueDate)||fmtDate(new Date().toISOString());
 const values=uniq([...(p.valueAdds||[]),...linesToList(p.valueCustom)]);
 const whys=WHY_LIB.filter(w=>(p.whyUs||[]).includes(w[0]));
 const _st=methodStages(p);const appr=_st.filter(a=>!(p.approach||[]).length||p.approach.includes(a[0]));

 const sheets=[],secs=[];
 const eyebrow=t=>`<div class="eyebrow">${esc(t)}</div>`;
 const H=t=>`<h1 class="sech">${esc(t)}</h1>`;
 const add=(kick,title,inner,solo,secId)=>{if(inner)secs.push(`<article class="sec" data-solo="${solo?1:0}"${secId?` data-sec="${secId}"`:''}>${eyebrow(kick)}${H(title)}${inner}</article>`)};
 const table=(head,rows,cls)=>`<table class="dtable ${cls||''}"><tr>${head.map(h=>`<th>${esc(h)}</th>`).join('')}</tr>${rows.map(r=>`<tr>${r.map(c=>`<td>${c}</td>`).join('')}</tr>`).join('')}</table>`;
 const ticks=arr=>arr.length?`<ul class="tick splitlist">${arr.map(x=>`<li>${esc(x)}</li>`).join('')}</ul>`:'';

 /* ---------- COVER ---------- */
 sheets.push(`<section class="sheet cover"><div class="rule"><i></i><b></b></div>
  <header class="shead"><img class="mark" src="${DOC_LOGO}" alt="SolidPro"><div class="shead-r"><span class="pill">${esc((p.doc.classification || '').toUpperCase())}</span></div></header>
  <div class="tagline">${esc(theme==='SolidPro'?'ENGINEERING-LED OUTCOMES':theme==='NetZeroHub'?'SUSTAINABILITY TRANSFORMATION · ENGINEERING IMPROVEMENT':theme==='TAKTIX'?'FACTORY PERFORMANCE ENGINEERING':'HEALTHCARE & LIFESCIENCES ENGINEERING')}</div>
  <div class="coverbody">
   ${eyebrow('PROPOSAL'+(p.rfpRef?' · IN RESPONSE TO '+p.rfpRef.toUpperCase():''))}
   <h1 class="covertitle">${esc(p.title)}</h1>
   <div class="coverfor">Prepared for<b>${esc(p.clientName||'-')}</b><span>${esc([p.clientIndustry,p.clientLocation].filter(Boolean).join(' · '))}</span></div>
   <p class="coverlead">${esc(p.executiveSummary||autoSummary(p,sols))}</p>
   <div class="stats">
    <div class="stat"><b>${sols.length||'-'}</b><span>solutions proposed</span></div>
    <div class="stat"><b>${esc(durText(p.duration)||durText(items.find(i=>i.duration)?.duration)||'TBC')}</b><span>duration</span></div>
    <div class="stat"><b>${(p.team||[]).length||'-'}</b><span>named team</span></div>
    <div class="stat"><b>${esc(money(p.currency,total))}</b><span>indicative value</span></div>
   </div>
   <div class="coverspacer"></div>
   <div class="covermeta">
    <div><small>PROPOSAL REF</small><b>${esc(p.id)}</b><span>Version ${esc(p.doc.version)}</span></div>
    <div><small>DATE OF ISSUE</small><b>${esc(issue)}</b><span>Valid for ${esc(p.validity)}</span></div>
    <div><small>PREPARED BY</small><b>${esc(p.doc.preparedName||'SolidPro Business Team')}</b><span>${esc(p.doc.preparedDesig||'')}</span></div>
    <div><small>CONTACT</small><b>${esc(p.doc.preparedEmail)}</b><span>${esc(p.doc.preparedPhone)}</span></div>
   </div>
   ${p.clientLogo?`<div class="clientlogo"><img src="${p.clientLogo}"></div>`:''}
  </div></section>`);

 /* ---------- section builders ---------- */
 const B={};
 B.control=()=>{
  const rows=[['Proposal reference',esc(p.id)],['Version',esc(p.doc.version)],['Date of issue',esc(issue)],['Classification',esc(p.doc.classification)],['Offer validity',esc(durText(p.validity))+' from date of issue'],['Customer reference',esc(p.rfpRef||'Not applicable')],['Prepared by',esc(p.doc.preparedName||'-')+(p.doc.preparedDesig?', '+esc(p.doc.preparedDesig):'')],['Reviewed by',esc(p.doc.reviewedName||'-')+(p.doc.reviewedDesig?', '+esc(p.doc.reviewedDesig):'')],['Approved by',esc(p.doc.approvedName||'-')+(p.doc.approvedDesig?', '+esc(p.doc.approvedDesig):'')]];
  return table(['Document control',''],rows,'splitlist')
   +((p.recipients||[]).length?`<h2 class="subh">Distribution</h2>`+table(['Name','Designation','Function','Email'],p.recipients.map(r=>[esc(r.name||'-'),esc(r.designation||''),esc(r.dept||''),esc(r.email||'')]),'striped splitlist'):'')
   +`<div class="panel"><b>Confidentiality.</b> This document is submitted in confidence to ${esc(p.clientName||'the recipient')} and contains commercial and technical information proprietary to SolidPro. It may not be disclosed, copied or circulated beyond the named distribution list without written consent. All content remains valid for ${esc(durText(p.validity))} from the date of issue.</div>`};
 B.summary=()=>`<p class="lead">${esc(p.executiveSummary||autoSummary(p,sols))}</p>`
  +(linesToList(p.objectives).length?`<h2 class="subh">Objectives of this engagement</h2>${ticks(linesToList(p.objectives))}`:'')
  +(values.length?`<h2 class="subh">The value this creates</h2><div class="cards2 splitlist">${values.map((v,i)=>`<div class="numcard"><span class="num">${String(i+1).padStart(2,'0')}</span><div><b>${esc(v)}</b></div></div>`).join('')}</div>`:'')
  +`<h2 class="subh">At a glance</h2>`+table(['',''],[['Recommended',esc(sols.map(s=>s.title).join(' · ')||'To be confirmed')],['How SolidPro will work with you',esc(p.engagementModel)],['Duration',esc(durText(p.duration)||'To be confirmed')],['Indicative value',esc(money(p.currency,total))+(inWords(p.currency,total)?' ('+esc(inWords(p.currency,total))+')':'')],['Offer validity',esc(durText(p.validity))]]);
 B.understanding=()=>`<div class="cards2 splitlist">
   <div class="infocard red"><b>About ${esc(p.clientName||'the customer')}</b><p>${esc(p.customerAbout||'To be confirmed during discovery.')}</p></div>
   <div class="infocard"><b>What you have asked for</b><p>${esc(p.customerAsk||'To be confirmed.')}</p></div>
   <div class="infocard"><b>The current situation</b><p>${esc(p.currentSituation||'To be established during the discovery stage.')}</p></div>
   ${p.whatDifferent?`<div class="infocard"><b>What should be different at the end</b><p>${esc(p.whatDifferent)}</p></div>`:''}${p.whyNow?`<div class="infocard"><b>Why this matters now</b><p>${esc(p.whyNow)}</p></div>`:''}<div class="infocard"><b>What success looks like</b><p>${esc(linesToList(p.objectives).join(' ')||'Success criteria to be agreed at kick-off so both sides measure the outcome the same way.')}</p></div></div>`
   +((p.practices||[]).length?`<h2 class="subh">The practice delivering this</h2>${(p.practices||[]).map(x=>`<div class="prow"><b>${esc(x==='Helixa'?'HeLiXA':x)}</b><p>${esc(practiceBlurb(x))}</p></div>`).join('')}`:'');
 B.approach=()=>appr.length?`<p class="lead">${esc(METHODS[detectMethod(p)].label)} engagements follow the sequence below. Each stage produces something you can review before the next begins.</p><div class="stagegrid splitlist">${appr.map((a,i)=>`<div class="stage"><span class="snum">${String(i+1).padStart(2,'0')}</span><b>${esc(a[0])} - ${esc(a[1])}</b><p>${esc(a[2]||'')}</p></div>`).join('')}</div>`:'';
 B.solutions=()=>sols.length?`<p class="lead">Each solution below is a defined SolidPro service, selected because it addresses a specific part of your requirement.</p><div class="solblocks splitlist">${sols.map((s,i)=>{const it=items.find(x=>x.solutionId===s.id);return `<div class="solblock"><div class="soltop"><span class="solnum">${String(i+1).padStart(2,'0')}</span><div><b>${esc(s.title)}</b><small>${esc(s.cat)} · ${esc(s.sub)}</small></div></div><p>${esc(s.what)}</p>${(s.get||[]).length?`<ul class="tick">${s.get.slice(0,5).map(x=>`<li>${esc(x)}</li>`).join('')}</ul>`:''}${it?`<div class="solfoot">${esc(it.engagementModel)}${it.duration?' · '+esc(it.duration):''}${it.qty?' · '+esc(it.qty):''}${it.amount?' · '+esc(money(it.currency||p.currency,it.amount)):''}</div>`:''}</div>`}).join('')}</div>`:'';
 B.capabilities=()=>caps.length?`<p class="lead">The specific tools, methods and disciplines applied on this engagement.</p><div class="tagcloud">${caps.map(x=>`<span class="dtag">${esc(x)}</span>`).join('')}</div><div class="panel"><b>Why this matters.</b> A capability list only helps if the people who hold it are the people doing the work. Every capability named here sits inside the team proposed in this document.</div>`:'';
 B.cases=()=>cases.length?cases.map(e=>`<div class="casebox"><div class="casetop"><span class="cid">${esc(e.caseId)}</span><span class="cpill">${esc(e.pillar||'')}</span></div><b>${esc(e.title)}</b><div class="cprofile">${esc(e.customerProfile||e.client||'')}</div><div class="ctwo"><div><small>THE CHALLENGE</small><p>${esc(e.problem||'')}</p></div><div><small>WHAT WE DID</small><p>${esc(e.approach||'')}</p></div></div>${(e.result||[]).length?`<div class="cresult"><small>OUTCOME</small><ul>${e.result.slice(0,3).map(x=>`<li>${esc(x)}</li>`).join('')}</ul></div>`:''}${(e.metrics||[]).length?`<div class="cmetrics">${e.metrics.slice(0,4).map(x=>`<span>${esc(x)}</span>`).join('')}</div>`:''}</div>`).join(''):'';
 B.scope=()=>(p.scopeItems||[]).length?`<p class="lead">The following work is included in the price quoted in this proposal.</p><div class="scopegrid splitlist">${p.scopeItems.map((x,i)=>`<div class="scoperow"><span>${String(i+1).padStart(2,'0')}</span><p>${esc(x)}</p></div>`).join('')}</div>`:'';
 B.deliverables=()=>(p.deliverables||[]).length?table(['#','Deliverable','Format'],p.deliverables.map((d,i)=>[String(i+1).padStart(2,'0'),'<b>'+esc(d)+'</b>','Document / data pack']),'striped splitlist')+(p.acceptance?`<h2 class="subh">Acceptance</h2><div class="panel">${esc(p.acceptance)}</div>`:''):'';
 B.plan=()=>(p.phases||[]).length?table(['Phase','Duration','Milestone output','Owner'],p.phases.map((f,i)=>['<b>'+String(i+1).padStart(2,'0')+' '+esc(f.name||'')+'</b>',esc(f.durN?f.durN+' '+(f.durU||'weeks'):(f.duration||'')),esc(f.output||''),esc(f.owner||'')]),'striped splitlist')+`${(p.efforts||[]).filter(e=>e.qty).length?'<h2 class="subh">What the engagement uses</h2>'+table(['What','Quantity'],p.efforts.filter(e=>e.qty).map(e=>[esc(e.type),'<b>'+esc(e.qty+' '+e.unit)+'</b>']),'splitlist'):''}<div class="panel"><b>Start:</b> ${esc(startText(p.start)||'To be mutually agreed')} &nbsp;·&nbsp; <b>Total duration:</b> ${esc(durText(p.duration)||'To be confirmed')} &nbsp;·&nbsp; <b>Engagement model:</b> ${esc(p.engagementModel)}</div>`:'';
 B.team=()=>(p.team||[]).length?`<p class="lead">The engagement is delivered by the named individuals below. Any substitution is notified in advance and matched on equivalent experience.</p>`+table(['Name','Designation','Role on this engagement','Experience','Allocation'],p.team.map(t=>['<b>'+esc(t.name||'To be named')+'</b>',esc(t.designation||''),esc(t.role||''),esc(t.exp||''),esc(t.alloc||'')]),'striped splitlist'):'';
 B.governance=()=>(p.governance?`<div class="panel">${esc(p.governance)}</div>`:'')
  +((p.escalation||[]).length?`<h2 class="subh">Escalation matrix</h2>`+table(['Level','Escalation path','Response'],p.escalation.map(e=>['<b>'+esc(e[0])+'</b>',esc(e[1]),esc(e[2])]),'striped splitlist'):'')
  +`<h2 class="subh">Responsibilities</h2>`+table(['Activity','SolidPro','Customer'],[['Project management and reporting','Responsible','Consulted'],['Provision of data, access and SMEs','Consulted','Responsible'],['Technical execution of the agreed scope','Responsible','Informed'],['Review and acceptance of deliverables','Consulted','Accountable'],['Change requests and approvals','Consulted','Accountable'],['Implementation of recommendations','As separately agreed','Accountable']],'striped splitlist');
 B.risks=()=>(p.risks||[]).length?table(['Risk','Impact','Likelihood','Mitigation'],p.risks.map(r=>['<b>'+esc(r[0])+'</b>',esc(r[1]),esc(r[2]),esc(r[3])]),'striped splitlist'):'';
 B.quality=()=>((p.standards||[]).length?`<p class="lead">Work is carried out in line with the following standards and practices.</p>${ticks(p.standards)}`:'')+(p.qualityNote?`<div class="panel">${esc(p.qualityNote)}</div>`:`<div class="panel"><b>How quality is assured.</b> Every deliverable is peer-reviewed inside SolidPro before submission, assumptions are recorded alongside results, and each stage is signed off before the next begins.</div>`);
 B.security=()=>((p.security||[]).length?ticks(p.security):'')+`<h2 class="subh">Intellectual property</h2><div class="panel">${esc(p.ip)}</div>`;
 B.compliance=()=>{const c=p.compliance||{};const rows=[['GSTIN',c.gst],['PAN',c.pan],['CIN',c.cin],['MSME / Udyam registration',c.msme],['Customer vendor code',c.vendorCode],['Insurance',c.insurance]].filter(r=>r[1]);
  return rows.length?table(['Particulars','Details'],rows.map(r=>[esc(r[0]),'<b>'+esc(r[1])+'</b>'])) +`<div class="note">Supporting certificates are available on request and can be submitted directly to your vendor-registration team.</div>`:''};
 B.commercials=()=>(items.length?table(['Item','Engagement','Basis','Value'],items.map(i=>['<b>'+esc(i.title||'')+'</b>'+(i.qty?'<br><small>'+esc(i.qty)+'</small>':'')+(durText(i.duration)?'<br><small>'+esc(durText(i.duration))+'</small>':'')+(effortText(i.efforts)?'<br><small>'+esc(effortText(i.efforts))+'</small>':''),esc(i.engagementModel),esc(i.commercialModel),'<div style="text-align:right">'+esc(i.amount?money(i.currency||p.currency,i.amount):'-')+'</div>']).concat([['<b>Total</b>','','','<div style="text-align:right"><b>'+esc(money(p.currency,total))+'</b></div>']]),'striped splitlist')
  :`<div class="bignum"><small>TOTAL PROPOSAL VALUE</small><b>${esc(money(p.currency,total))}</b><span>${esc(p.commercialModel)} · ${esc(p.currency)}</span></div>`)
  +(inWords(p.currency,total)?`<div class="panel"><b>In words:</b> ${esc(inWords(p.currency,total))} ${p.currency==='INR'?'rupees':''} only, exclusive of taxes.</div>`:'')
  +(p.taxNote?`<div class="note">${esc(p.taxNote)}</div>`:'');
 B.payment=()=>((p.payments||[]).length?table(['Milestone','%','Amount','Trigger / evidence'],p.payments.map(m=>['<b>'+esc(m.milestone||'')+'</b>',esc(m.percent?m.percent+'%':'-'),esc(m.amount?money(p.currency,m.amount):'-'),esc(m.trigger||'')]),'striped splitlist'):'')
  +(p.paymentTerms?`<h2 class="subh">Payment terms</h2><div class="panel">${esc(p.paymentTerms)}</div>`:'')
  +(p.commercialTerms?`<h2 class="subh">Other commercial terms</h2><p>${esc(p.commercialTerms)}</p>`:'')
  +(p.notes?`<p>${esc(p.notes)}</p>`:'');
 B.assumptions=()=>((p.assumptions||[]).length?`<h2 class="subh">Assumptions</h2>${ticks(p.assumptions)}`:'')
  +((p.dependencies||[]).length?`<h2 class="subh">Customer dependencies</h2>${ticks(p.dependencies)}`:'')
  +((p.outScope||[]).length?`<h2 class="subh">Out of scope</h2><div class="panel dark"><ul>${p.outScope.map(x=>`<li>${esc(x)}</li>`).join('')}</ul></div>`:'');
 B.change=()=>p.changeControl?`<div class="panel">${esc(p.changeControl)}</div>`+table(['Step','Action','Owner'],[['1','Change identified and logged','Either party'],['2','Impact assessed on scope, effort, cost and timeline','SolidPro'],['3','Change request issued in writing','SolidPro'],['4','Written approval or rejection','Customer'],['5','Baseline updated and work commences','SolidPro']],'striped'):'';
 B.sla=()=>(p.sla||[]).length?table(['Measure','Commitment'],p.sla.map(s=>{const f=SLA_LIB.find(x=>x[0]===s);return ['<b>'+esc(s)+'</b>',esc(f?f[1]:'')]}),'striped splitlist'):'';
 B.warranty=()=>p.warranty?`<div class="panel">${esc(p.warranty)}</div>`:'';
 B.hse=()=>p.hse?`<div class="panel">${esc(p.hse)}</div>`:'';
 B.esg=()=>p.esg?`<div class="panel">${esc(p.esg)}</div>`:'';
 B.why=()=>whys.length?`<div class="cards2 splitlist">${whys.map(w=>`<div class="infocard"><b>${esc(w[0])}</b><p>${esc(w[1])}</p></div>`).join('')}</div>`:'';
 B.next=()=>`${p.approveNow?`<div class="bignum"><small>WHAT WE ARE ASKING YOU TO APPROVE</small><b style="font-size:15pt;line-height:1.35">${esc(p.approveNow)}</b></div>`:''}<p class="lead">${esc(p.nextSteps||'')}</p><div class="ctabox"><div><div class="ctakick">READY TO PROCEED?</div><h3>Let’s confirm the scope and start.</h3><p>${esc(p.doc.preparedName||'SolidPro Business Team')}${p.doc.preparedDesig?' · '+esc(p.doc.preparedDesig):''}</p><p>${esc(p.doc.preparedEmail)} · ${esc(p.doc.preparedPhone)}</p><div class="ctaurl">${esc(BOOK_URL)}</div></div><img src="${QR}" alt="QR"></div>`;
 B.accept=()=>`<p class="lead">Acceptance of this proposal may be confirmed by signing below or by issuing a purchase order referencing ${esc(p.id)} version ${esc(p.doc.version)}.</p>`
  +table(['',''],[['Proposal reference',esc(p.id)+' · v'+esc(p.doc.version)],['Total value',esc(money(p.currency,total))+' '+(p.currency==='INR'?'(exclusive of taxes)':'')],['Offer valid until',esc(durText(p.validity))+' from '+esc(issue)]])
  +`<div class="signblock"><div><small>FOR ${esc((p.clientName||'CUSTOMER').toUpperCase())}</small><div class="sigline"></div><span>Name · Designation · Date · Company seal</span></div><div><small>FOR SOLIDPRO</small><div class="sigline"></div><span>${esc(p.doc.approvedName||p.doc.preparedName||'Authorised signatory')}${p.doc.approvedDesig?' · '+esc(p.doc.approvedDesig):''} · Date</span></div></div>`
  +`<div class="note">This document is a commercial proposal and not a contract. Scope, timeline and commercials become binding only on issue of a purchase order or a signed work order referencing this proposal.</div>`;
 B.annex=()=>(p.annexures||[]).length?table(['Annexure','Description'],p.annexures.map((a,i)=>['<b>'+String.fromCharCode(65+i)+'</b>',esc(a)]),'striped'):'';


 /* ---- v15 section builders ---- */
 const v=p.venture||{};
 const vrows=(k,cols)=>((v[k]||[]).length?table(cols.map(c=>c[1]),v[k].map(r=>cols.map((c,i)=>i===0?'<b>'+esc(r[c[0]]||'')+'</b>':esc(r[c[0]]||''))),'striped splitlist'):'');
 B.ataglance=()=>{
  const bits=[['THE ASK',p.customerAsk],['WHAT THEY HAVE',p.theyHave||v.assets],['THE OPPORTUNITY',v.opportunity],['WHY NOW',v.whyNow||p.whyNow],['WHAT CHANGES',p.whatDifferent],['THE DECISION TODAY',p.approveNow||p.stageAsk]].filter(x=>x[1]);
  if(!bits.length)return '';
  return '<div class="cards2 splitlist">'+bits.map(b=>'<div class="infocard'+(b[0]==='THE DECISION TODAY'?' red':'')+'"><b>'+esc(b[0])+'</b><p>'+esc(b[1])+'</p></div>').join('')+'</div>'
   +((p.stages||[]).length?'<h2 class="subh">The stages</h2>'+table(['Stage','Delivers','Time','Fee'],p.stages.map(s=>['<b>'+esc(s.name||'')+'</b>',esc(s.delivers||''),esc(s.durN?s.durN+' '+(s.durU||''):''),esc(s.fee||'')]),'striped splitlist'):'');
 };
 B.ask=()=>p.customerAsk?'<p class="lead">'+esc(p.customerAsk)+'</p>'
   +(p.approveNow?'<div class="panel"><b>The short answer, up front.</b> '+esc(p.approveNow)+'</div>':''):'';
 B.market=()=>[['What the opportunity is',v.opportunity],['Why now',v.whyNow||p.whyNow],['What the market looks like',v.market]].filter(x=>x[1])
   .map(x=>'<h2 class="subh">'+esc(x[0])+'</h2><p>'+esc(x[1])+'</p>').join('');
 B.revenue=()=>vrows('streams',[['name','Stream'],['how','How it works'],['risk','Risk'],['when','Opens']]);
 B.roadmap=()=>vrows('horizon',[['year','Year'],['position','Position'],['streams','Earning']])
   +(v.longterm?'<div class="panel"><b>The long-term position.</b> '+esc(v.longterm)+'</div>':'');
 B.havegap=()=>(v.assets||p.theyHave?'<h2 class="subh">What you already have</h2><p>'+esc(v.assets||p.theyHave)+'</p>':'')
   +vrows('gaps',[['missing','What is missing'],['why','Why it is needed'],['how','How it gets filled']])
   +(p.whatsMissing?'<div class="panel">'+esc(p.whatsMissing)+'</div>':'');
 B.horizon=()=>vrows('horizon',[['year','Year'],['position','Position'],['streams','Earning']]);
 B.contribution=()=>(sols.length?B.solutions():'')
   +(v.willnot?'<div class="panel dark"><b>What SolidPro will not do.</b> '+esc(v.willnot)+'</div>':'');
 B.parties=()=>vrows('parties',[['party','Party'],['role','Responsibility']]);
 B.stages=()=>(p.stages||[]).length?table(['Stage','What it delivers','Time','Fee','Decision at the end'],
   p.stages.map(s=>['<b>'+esc(s.name||'')+'</b>',esc(s.delivers||''),esc(s.durN?s.durN+' '+(s.durU||''):''),esc(s.fee||''),esc(s.gate||'')]),'striped splitlist')
   +(p.stageAsk?'<div class="panel"><b>What we are asking you to approve today.</b> '+esc(p.stageAsk)+'</div>':''):'';
 B.handover=()=>v.handover?'<div class="panel">'+esc(v.handover)+'</div>':'';
 B.outcome=()=>{const O=p.outcome||{};if(!O.metric&&!O.target)return '';
  return table(['',''],[['What is measured','<b>'+esc(O.metric||'')+'</b>'],['Where you are today',esc(O.baseline||'')],['Where you will be','<b>'+esc(O.target||'')+'</b>'],['By when',esc(O.by||'')]])
   +(O.method?'<h2 class="subh">How it will be measured</h2><p>'+esc(O.method)+'</p>':'')
   +(O.conditions?'<h2 class="subh">What the commitment depends on</h2><p>'+esc(O.conditions)+'</p>':'')
   +(O.ifnot?'<div class="panel red"><b>If the outcome is not achieved.</b> '+esc(O.ifnot)+'</div>':'');
 };

 /* ---------- assemble in the user's order ---------- */
 const order=(p.sectionOrder||SECTION_DEFS.map(s=>s[0])).filter(id=>on(id)&&id!=='toc');
 const built=order.map(id=>({id,label:SEC_LABEL(id),html:B[id]?B[id]():''})).filter(s=>s.html);
 if(on('toc')&&built.length)add('CONTENTS','What this proposal covers',
  `<div class="toclist splitlist">${built.map((s,i)=>`<div class="tocrow"><span class="tocnum">${String(i+1).padStart(2,'0')}</span><div><b>${esc(s.label)}</b><span>${esc((SECTION_DEFS.find(d=>d[0]===s.id)||[,,''])[2])}</span></div></div>`).join('')}</div>
   <div class="panel"><b>How to read this proposal.</b> The opening sections state what we understand and what we recommend. The middle sections define exactly what will be done, by whom and by when. The closing sections cover commercials, terms and acceptance. Nothing commits either party until confirmed in writing.</div>`);
 built.forEach((s,i)=>add(String(i+1).padStart(2,'0')+' · '+s.label.toUpperCase(),s.label,s.html,s.id==='accept',s.id));

 const SHEET_HEAD=`<div class="rule"><i></i><b></b></div><header class="shead"><img class="mark" src="${DOC_LOGO}" alt="SolidPro"><div class="shead-r"><span class="pill">${esc(p.id)} · v${esc(p.doc.version)}</span></div></header><div class="tagline">${esc((p.title||'PROPOSAL').toUpperCase())}${p.clientName?' &nbsp;·&nbsp; '+esc(p.clientName.toUpperCase()):''}</div><div class="sbody"></div>`;
 const FOOT_LEFT=`<b>${esc(p.doc.classification)}</b> · ${esc(p.doc.preparedEmail)} · solidpro-es.com`;

 return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${esc(p.id)} · ${esc(p.title)}</title><style>
@page{size:A4;margin:0}
:root{--blue:${accent};--deep:#0e3271;--red:#E21A22;--ink:#2b3340;--mute:#5b6675;--line:#dfe6f2;--tint:#f5f8fc}
*{box-sizing:border-box}
body{margin:0;background:#e8edf3;color:var(--ink);font-family:'Segoe UI',Inter,-apple-system,Arial,sans-serif;font-size:10.5pt;line-height:1.55;-webkit-font-smoothing:antialiased}
.sheet{position:relative;width:210mm;min-height:297mm;background:#fff;margin:0 auto 8mm;padding:0 16mm 22mm;overflow:hidden}
.rule{display:flex;height:4px;margin:0 -16mm}.rule i{width:23%;background:var(--red)}.rule b{flex:1;background:var(--blue)}
.shead{display:flex;justify-content:space-between;align-items:center;padding:9mm 0 0}
.mark{width:120px;height:34px;object-fit:contain;object-position:left}
.pill{background:var(--blue);color:#fff;font-size:7.5pt;font-weight:700;letter-spacing:.08em;padding:6px 12px;border-radius:4px}
.tagline{font-size:7.5pt;letter-spacing:.14em;color:var(--blue);font-weight:700;margin-top:5mm;padding-bottom:4mm;border-bottom:2px solid var(--line)}
.sbody{padding-top:8mm}
.secgap{margin-top:11mm;padding-top:9mm;border-top:2px solid var(--line)}
.eyebrow{color:var(--red);font-size:8pt;font-weight:800;letter-spacing:.13em;margin-bottom:3mm}
.sech{color:var(--deep);font-size:21pt;line-height:1.16;font-weight:700;margin:0 0 6mm;letter-spacing:-.01em;max-width:150mm}
.contd{color:var(--deep);font-size:13pt;font-weight:700;margin:0 0 5mm;padding-bottom:3mm;border-bottom:1px solid var(--line)}
.subh{color:var(--deep);font-size:12.5pt;font-weight:700;margin:8mm 0 3mm}
.lead{font-size:11pt;color:#3a4657;margin:0 0 6mm;max-width:165mm}
p{margin:0 0 3mm}
.cover .coverbody{padding-top:10mm;display:flex;flex-direction:column;min-height:228mm}
.covertitle{color:var(--deep);font-size:30pt;line-height:1.1;font-weight:700;letter-spacing:-.015em;margin:0 0 6mm;max-width:160mm}
.coverfor{border-left:3px solid var(--red);padding:1mm 0 1mm 5mm;margin-bottom:6mm;font-size:8pt;letter-spacing:.1em;color:var(--mute);text-transform:uppercase}
.coverfor b{display:block;font-size:15pt;color:var(--deep);letter-spacing:0;text-transform:none;margin-top:1mm}
.coverfor span{font-size:9.5pt;color:var(--mute);letter-spacing:0;text-transform:none}
.coverlead{font-size:10.5pt;color:#3a4657;max-width:160mm;margin-bottom:9mm}
.stats{display:grid;grid-template-columns:repeat(4,1fr);gap:4mm}
.stat{background:var(--blue);color:#fff;border-radius:5px;padding:6mm 4mm;text-align:center}
.stat b{display:block;font-size:15pt;font-weight:700;line-height:1.1;word-break:break-word}
.stat span{font-size:7.5pt;opacity:.9;display:block;margin-top:2mm}
.coverspacer{flex:1;min-height:6mm}
.covermeta{display:grid;grid-template-columns:repeat(4,1fr);gap:5mm;border-top:2px solid var(--line);padding-top:6mm}
.covermeta small{display:block;font-size:7pt;letter-spacing:.1em;color:var(--red);font-weight:800;margin-bottom:2mm}
.covermeta b{display:block;color:var(--deep);font-size:9.5pt}
.covermeta span{font-size:8.5pt;color:var(--mute)}
.clientlogo{margin-top:6mm}.clientlogo img{max-height:16mm;max-width:55mm;object-fit:contain}
.toclist{border-top:1px solid var(--line);column-count:2;column-gap:9mm}
.tocrow{break-inside:avoid}
.tocrow{display:flex;gap:6mm;padding:3.2mm 0;border-bottom:1px solid var(--line)}
.tocnum{color:var(--red);font-weight:800;font-size:9pt;padding-top:.6mm}
.tocrow b{display:block;color:var(--deep);font-size:10.5pt}
.tocrow span{color:var(--mute);font-size:8.5pt}
.panel{background:var(--tint);border-left:3px solid var(--blue);border-radius:0 5px 5px 0;padding:5mm 6mm;margin:5mm 0;font-size:9.5pt}
.panel.dark{background:var(--deep);color:#fff;border-left-color:var(--red)}
.panel.dark ul{margin:0;padding-left:5mm}
.cards2{display:grid;grid-template-columns:1fr 1fr;gap:4mm}
.infocard{background:var(--tint);border-left:3px solid var(--blue);border-radius:0 5px 5px 0;padding:5mm;page-break-inside:avoid}
.infocard.red{border-left-color:var(--red)}
.infocard b{display:block;color:var(--deep);font-size:10.5pt;margin-bottom:2mm}
.infocard p{font-size:9.5pt;color:var(--mute);margin:0}
.numcard{display:flex;gap:4mm;align-items:flex-start;border:1px solid var(--line);border-radius:5px;padding:4mm;page-break-inside:avoid}
.numcard .num{background:var(--red);color:#fff;font-size:8pt;font-weight:800;padding:2px 6px;border-radius:3px}
.numcard b{color:var(--deep);font-size:10pt;font-weight:600}
.prow{border-top:1px solid var(--line);padding:4mm 0}
.prow b{color:var(--blue);font-size:10.5pt}
.prow p{font-size:9.5pt;color:var(--mute);margin:1mm 0 0}
.stagegrid{display:grid;grid-template-columns:repeat(2,1fr);gap:4mm}
.stage{border-top:3px solid var(--blue);background:var(--tint);padding:4mm;border-radius:0 0 4px 4px;page-break-inside:avoid}
.snum{color:var(--red);font-weight:800;font-size:8.5pt}
.stage b{display:block;color:var(--deep);font-size:11pt;margin:1mm 0}
.stage p{font-size:9pt;color:var(--mute);margin:0}
.solblocks{display:grid;gap:4mm}
.solblock{border:1px solid var(--line);border-top:3px solid var(--blue);border-radius:5px;padding:5mm;page-break-inside:avoid}
.soltop{display:flex;gap:4mm;align-items:baseline;margin-bottom:2mm}
.solnum{color:var(--red);font-weight:800;font-size:11pt}
.soltop b{color:var(--deep);font-size:12pt;display:block}
.soltop small{color:var(--mute);font-size:8.5pt}
.solblock p{font-size:9.5pt;color:var(--mute)}
.solfoot{margin-top:3mm;padding-top:3mm;border-top:1px solid var(--line);font-size:8.5pt;color:var(--blue);font-weight:700}
ul.tick{list-style:none;padding:0;margin:3mm 0 0}
ul.tick li{position:relative;padding-left:6mm;margin:1.6mm 0;font-size:9.5pt}
ul.tick li:before{content:'';position:absolute;left:0;top:.55em;width:2.4mm;height:2.4mm;background:var(--blue);border-radius:1px}
.tagcloud{display:flex;flex-wrap:wrap;gap:2mm}
.dtag{border:1px solid var(--line);background:var(--tint);color:var(--deep);font-size:8.5pt;font-weight:600;padding:2mm 3mm;border-radius:4px}
.casebox{border:1px solid var(--line);border-radius:5px;padding:5mm;margin-bottom:4mm;page-break-inside:avoid}
.casetop{display:flex;justify-content:space-between;margin-bottom:2mm}
.cid{color:var(--red);font-size:8pt;font-weight:800;letter-spacing:.06em}
.cpill{background:var(--tint);border:1px solid var(--line);color:var(--blue);font-size:7.5pt;font-weight:700;padding:1mm 3mm;border-radius:3px}
.casebox>b{color:var(--deep);font-size:11.5pt;display:block}
.cprofile{color:var(--mute);font-size:9pt;font-style:italic;margin:1mm 0 3mm}
.ctwo{display:grid;grid-template-columns:1fr 1fr;gap:5mm}
.ctwo small,.cresult small{display:block;font-size:7pt;letter-spacing:.1em;color:var(--red);font-weight:800;margin-bottom:1mm}
.ctwo p{font-size:9pt;color:var(--mute);margin:0}
.cresult{margin-top:3mm;padding-top:3mm;border-top:1px solid var(--line)}
.cresult ul{margin:0;padding-left:5mm;font-size:9pt;color:var(--mute)}
.cmetrics{display:flex;flex-wrap:wrap;gap:2mm;margin-top:3mm}
.cmetrics span{background:var(--blue);color:#fff;font-size:8pt;font-weight:700;padding:1.5mm 3mm;border-radius:3px}
.scopegrid{border-top:1px solid var(--line)}
.scoperow{display:flex;gap:5mm;padding:3mm 0;border-bottom:1px solid var(--line);page-break-inside:avoid}
.scoperow span{color:var(--red);font-weight:800;font-size:8.5pt;padding-top:.4mm}
.scoperow p{margin:0;font-size:9.5pt}
.dtable{width:100%;border-collapse:collapse;margin-top:2mm}
.dtable th,.dtable td{border-bottom:1px solid var(--line);padding:3.4mm 3mm;text-align:left;vertical-align:top;font-size:9.5pt}
.dtable th{color:var(--deep);font-size:8pt;letter-spacing:.06em;text-transform:uppercase;font-weight:800;width:32%}
.dtable.striped th{background:var(--blue);color:#fff;width:auto}
.dtable.striped tr:nth-child(even) td{background:#fafcfe}
.dtable small{color:var(--mute)}
.bignum{background:var(--tint);border-left:3px solid var(--red);padding:7mm;border-radius:0 5px 5px 0}
.bignum small{display:block;font-size:7.5pt;letter-spacing:.1em;color:var(--red);font-weight:800}
.bignum b{display:block;font-size:26pt;color:var(--deep);line-height:1.2}
.bignum span{color:var(--mute);font-size:9.5pt}
.ctabox{background:var(--deep);color:#fff;border-radius:6px;padding:7mm;display:grid;grid-template-columns:1fr 28mm;gap:6mm;align-items:center;margin:6mm 0}
.ctakick{color:#ff9ba0;font-size:7.5pt;letter-spacing:.12em;font-weight:800}
.ctabox h3{margin:2mm 0;font-size:15pt;font-weight:700}
.ctabox p{font-size:9.5pt;margin:0}
.ctaurl{font-size:8pt;color:#b9cde8;margin-top:2mm}
.ctabox img{width:28mm;background:#fff;padding:2mm;border-radius:4px}
.signblock{display:grid;grid-template-columns:1fr 1fr;gap:10mm;margin-top:10mm}
.signblock small{font-size:7.5pt;letter-spacing:.08em;color:var(--red);font-weight:800}
.sigline{border-bottom:1px solid #9aa7b8;height:16mm}
.signblock span{font-size:8pt;color:var(--mute)}
.note{font-size:8pt;color:var(--mute);border-top:1px solid var(--line);padding-top:3mm;margin-top:6mm}
.sfoot{position:absolute;left:16mm;right:16mm;bottom:9mm;border-top:1px solid var(--line);padding-top:3mm;display:flex;justify-content:space-between;font-size:7.5pt;color:var(--mute)}
.sfoot b{color:var(--deep)}
.tight .sech{font-size:19pt;margin-bottom:4mm}.tight .lead{margin-bottom:4mm}.tight .subh{margin:5mm 0 2mm}
.tight .tocrow{padding:2.6mm 0}.tight .panel{padding:4mm 5mm;margin:4mm 0}
.tight .casebox,.tight .infocard,.tight .solblock,.tight .stage{padding:4mm;margin-bottom:3mm}
.tight .scoperow{padding:2.4mm 0}.tight .dtable th,.tight .dtable td{padding:2.6mm 3mm}
.tighter .sech{font-size:17pt}.tighter .sbody{padding-top:5mm}.tighter .tocrow{padding:2.1mm 0}
.tighter ul.tick li{margin:1.1mm 0}.tighter .dtable th,.tighter .dtable td{padding:2.1mm 2.4mm;font-size:9pt}
.tighter .casebox,.tighter .infocard,.tighter .solblock,.tighter .stage{padding:3.4mm;margin-bottom:2.4mm}
.tighter .signblock{margin-top:6mm}.tighter .sigline{height:12mm}
.tightest .sech{font-size:15pt;margin-bottom:3mm}.tightest .sbody{padding-top:4mm}
.tightest .dtable th,.tightest .dtable td{padding:1.7mm 2.2mm;font-size:8.5pt}
.tightest .panel{padding:3mm 4mm;margin:3mm 0;font-size:9pt}
.tightest ul.tick li{margin:.9mm 0;font-size:9pt}
.tightest .lead{font-size:10pt;margin-bottom:3mm}
.bar{position:relative;z-index:9;background:#0e3271;color:#fff;padding:10px 16px;display:flex;gap:8px;justify-content:center}
.bar button{border:0;padding:9px 16px;border-radius:6px;font-weight:700;cursor:pointer;font-size:12px}
.bar .p{background:#fff;color:#0e3271}.bar .l{background:transparent;color:#fff;border:1px solid rgba(255,255,255,.5)}
@media print{body{background:#fff}.bar{display:none}.sheet{margin:0;page-break-after:always;box-shadow:none}.sheet:last-of-type{page-break-after:auto}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
@media screen{.sheet{box-shadow:0 8px 30px rgba(14,50,113,.14)}}
</style></head><body>
<div class="bar"><button class="p" onclick="window.print()">Print / Save as PDF</button><button class="l" onclick="dl()">Download HTML</button></div>
<div id="pages">${sheets.join('')}</div>
<div id="src" style="position:absolute;left:-10000px;top:0;width:178mm">${secs.join('')}</div>
<script>
var SHEET_HEAD=${JSON.stringify(SHEET_HEAD)},FOOT_LEFT=${JSON.stringify(FOOT_LEFT)},DOC_ID=${JSON.stringify(p.id)};
function dl(){var b=new Blob([document.documentElement.outerHTML],{type:'text/html;charset=utf-8'}),a=document.createElement('a');a.href=URL.createObjectURL(b);a.download=${JSON.stringify(safe(p.id)+'-'+safe(p.title)+'.html')};a.click()}
function isHead(el){return !el||el.classList.contains('eyebrow')||el.classList.contains('sech')||el.classList.contains('contd')}
function bodyOf(el){/* a splitlist table splits its rows, not its tbody */
 if(el.tagName==='TABLE')return el.tBodies&&el.tBodies[0]?el.tBodies[0]:null;
 return el}
function splitSection(sec,body,limit){
 var moved=[],wrap=null,guard=0;
 while(body.scrollHeight>limit&&guard++<400){
  var last=sec.lastElementChild;
  if(isHead(last))break;
  var host=last.classList.contains('splitlist')?bodyOf(last):null;
  /* keep at least the header row / first item behind so the page is never empty */
  var floor=(last.tagName==='TABLE')?2:1;
  if(host&&host.childElementCount>floor){
   if(wrap&&wrap.key!==last.className+last.tagName)break;
   if(!wrap)wrap={key:last.className+last.tagName,cls:last.className,tag:last.tagName,table:last.tagName==='TABLE',
                  head:last.tagName==='TABLE'&&host.firstElementChild?host.firstElementChild.outerHTML:'',items:[]};
   wrap.items.unshift(host.lastElementChild);host.removeChild(host.lastElementChild);
  }else{
   if(wrap)break;
   moved.unshift(last);sec.removeChild(last);
  }
 }
 /* if everything was stripped there is nothing left to keep - put it back and accept the overflow */
 var remaining=0;
 [].slice.call(sec.children).forEach(function(c){if(!isHead(c))remaining++});
 if(!remaining&&!wrap){moved.forEach(function(x){sec.appendChild(x)});return null}
 if(!moved.length&&!wrap)return null;
 var eb=sec.querySelector('.eyebrow'),hd=sec.querySelector('.sech');
 var cont=document.createElement('article');cont.className='sec';cont.setAttribute('data-solo','0');
 cont.innerHTML='<div class="eyebrow">'+(eb?eb.textContent:'')+'</div><div class="contd">'+(hd?hd.textContent:'')+' - continued</div>';
 if(wrap&&wrap.items.length){
  var w=document.createElement(wrap.tag);w.className=wrap.cls;
  if(wrap.table){var tb=document.createElement('tbody');
   if(wrap.head)tb.innerHTML=wrap.head;
   wrap.items.forEach(function(x){tb.appendChild(x)});w.appendChild(tb)}
  else wrap.items.forEach(function(x){w.appendChild(x)});
  cont.appendChild(w);
 }
 moved.forEach(function(x){cont.appendChild(x)});
 return cont;
}
function paginate(){
 var host=document.getElementById('pages'),src=document.getElementById('src');
 if(!host||!src)return;
 var secs=[].slice.call(src.children);src.parentNode.removeChild(src);
 var sheet=null,body=null,limit=0;
 function newSheet(){sheet=document.createElement('section');sheet.className='sheet';sheet.innerHTML=SHEET_HEAD;
  host.appendChild(sheet);body=sheet.querySelector('.sbody');
  limit=sheet.getBoundingClientRect().height-(body.getBoundingClientRect().top-sheet.getBoundingClientRect().top)-83}
 newSheet();
 for(var i=0;i<secs.length&&i<400;i++){
  var sec=secs[i],solo=sec.getAttribute('data-solo')==='1',gap=null;
  if(solo&&body.children.length)newSheet();
  if(body.children.length){gap=document.createElement('div');gap.className='secgap';body.appendChild(gap)}
  body.appendChild(sec);
  if(body.scrollHeight>limit&&body.children.length>(gap?2:1)){
   if(gap)body.removeChild(gap);
   body.removeChild(sec);newSheet();body.appendChild(sec);
  }
  if(body.children.length===1&&body.scrollHeight>limit){
   sheet.classList.add('tight');
   if(body.scrollHeight>limit)sheet.classList.add('tighter');
   if(body.scrollHeight>limit){
    var cont=splitSection(sec,body,limit);
    if(cont)secs.splice(i+1,0,cont);
    sheet.classList.remove('tighter');
    if(body.scrollHeight>limit)sheet.classList.add('tighter');
    else{sheet.classList.remove('tight');if(body.scrollHeight>limit)sheet.classList.add('tight')}
    if(body.scrollHeight>limit)sheet.classList.add('tightest');
   }
  }
  if(solo&&i<secs.length-1)newSheet();
 }
 [].slice.call(host.querySelectorAll('.sheet')).forEach(function(s){var b=s.querySelector('.sbody');if(b&&!b.children.length&&!s.classList.contains('cover'))s.parentNode.removeChild(s)});
 /* final safety pass: nothing may exceed one A4 page */
 var A4=Math.round(297*(96/25.4));
 [].slice.call(host.querySelectorAll('.sheet')).forEach(function(s){
  ['tight','tighter','tightest'].forEach(function(c){if(s.getBoundingClientRect().height>A4+1)s.classList.add(c)});
 });
 var all=[].slice.call(host.querySelectorAll('.sheet'));
 all.forEach(function(s,n){var f=s.querySelector('.sfoot');if(f)f.parentNode.removeChild(f);
  var el=document.createElement('footer');el.className='sfoot';
  el.innerHTML='<span>'+FOOT_LEFT+'</span><span>'+DOC_ID+' · Page '+(n+1)+' of '+all.length+'</span>';s.appendChild(el)});
}
if(document.readyState==='complete')paginate();else window.addEventListener('load',paginate);
<\/script>
</body></html>`};

// Splits the generated document into its sections so a reviewer can comment on
// each one. Parsing the real output guarantees the reviewer sees exactly what
// the customer would.
const proposalSections = (p) => {
  try {
    const doc = new DOMParser().parseFromString(generateProposalHTML(p), 'text/html');
    return [...doc.querySelectorAll('article.sec[data-sec]')].map(el => ({
      id: el.getAttribute('data-sec'),
      label: (el.querySelector('.sech') || {}).textContent || SEC_LABEL(el.getAttribute('data-sec')),
      html: el.innerHTML
    }));
  } catch (e) {
    console.error('Could not split the proposal into sections:', e);
    return [];
  }
};

// The message the approver receives. Shown before sending so the author can
// see exactly what goes out, and reused by the backend when email is wired up.
const approvalEmail = (p, toName, toEmail) => {
  const total = p.commercialAmount || (p.items || []).reduce((a, i) => a + numOf(i.amount), 0);
  const words = inWords(p.currency, total);
  const subject = `Approval required: ${p.id} - ${p.title} - ${p.clientName || 'Customer'} - ${money(p.currency, total)}`;
  const body = [
    `Dear ${toName || 'Approver'},`,
    '',
    'A proposal is ready for your approval.',
    '',
    `  Proposal reference : ${p.id} (version ${p.doc?.version || '1.0'})`,
    `  Customer           : ${p.clientName || '-'}${p.clientIndustry ? ' · ' + p.clientIndustry : ''}`,
    `  Title              : ${p.title}`,
    `  Value              : ${money(p.currency, total)}${words ? ' (' + words + ')' : ''}`,
    `  Engagement         : ${p.engagementModel}${durText(p.duration) ? ' · ' + durText(p.duration) : ''}`,
    `  Prepared by        : ${p.doc?.preparedName || '-'}${p.doc?.preparedDesig ? ', ' + p.doc.preparedDesig : ''}`,
    `  Offer valid for    : ${durText(p.validity) || '-'}`,
    '',
    'Open the proposal to read it in full. You can approve it, or return it with',
    'comments against each section - the person who prepared it is notified either way.',
    '',
    `  ${window.location.origin}/proposal/${p.uid}`,
    '',
    `This document is ${p.doc?.classification || 'Commercial in Confidence'}. Please do not forward it`,
    'outside the approval chain.',
    '',
    'SolidPro Engineering Support Pvt. Ltd.'
  ].join('\n');
  return { to: toEmail || '', subject, body };
};

const MailPreview = ({ p, toEmail, onClose }) => {
  const [copied, setCopied] = useState('');
  const mail = approvalEmail(p, '', toEmail);
  const copy = () => {
    const text = mail.subject + '\n\n' + mail.body;
    if (navigator.clipboard) navigator.clipboard.writeText(text).then(() => { setCopied('Copied'); setTimeout(() => setCopied(''), 2000); });
    else setCopied('Select the text and copy it manually');
  };
  return (
    <div className="modalback" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="kicker">Approval email</div>
        <h2>What the approver receives</h2>
        <div className="formrow"><label>To</label><input readOnly value={mail.to || '(no email entered)'} /></div>
        <div className="formrow"><label>Subject</label><input readOnly value={mail.subject} /></div>
        <div className="formrow"><label>Body</label><textarea rows="16" readOnly value={mail.body} /></div>
        <div className="noteBox">Email delivery is not configured on this server yet, so the approver is notified inside the app instead. This is the message that will be sent once SMTP details are added.</div>
        <div className="modalactions">
          <button className="btn line" onClick={copy}>{copied || 'Copy'}</button>
          <button className="btn primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ===== SECTION-BY-SECTION REVIEW =====
// What the approver sees. Each section of the finished document is shown as the
// customer would read it, with a verdict and a comment box beside it. The
// verdicts and comments travel back to the author with the decision.
const saveReviewComments = async (uid, sectionComments) => {
  const token = localStorage.getItem('token');
  const res = await api.put(`/proposals/${uid}/review`, { sectionComments }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

const ReviewPanel = ({ p, currentUser, onSubmitted, onClose }) => {
  const sections = useMemo(() => proposalSections(p), [p]);
  const [notes, setNotes] = useState(() => ({ ...(p.reviewComments || {}) }));
  const [overall, setOverall] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const set = (id, patch) => setNotes(cur => ({ ...cur, [id]: { ...(cur[id] || {}), ...patch } }));
  const withText = Object.values(notes).filter(c => String(c.text || '').trim()).length;
  const changes = Object.values(notes).filter(c => c.status === 'change').length;

  const saveDraft = async () => {
    setBusy('save'); setErr('');
    try { await saveReviewComments(p.uid, notes); setBusy('saved'); setTimeout(() => setBusy(''), 2500); }
    catch (e) { setErr(e.response?.data?.error || e.message); setBusy(''); }
  };

  const submit = async (status) => {
    setBusy(status); setErr('');
    try {
      const token = localStorage.getItem('token');
      await api.post(`/proposals/${p.uid}/approve`,
        { status, comment: overall, sectionComments: notes },
        { headers: { Authorization: `Bearer ${token}` } });
      onSubmitted(status, withText);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setBusy('');
    }
  };

  return (
    <div className="pp">
      <h2>Review this proposal</h2>
      <div className="hint">
        Sent by {p.doc?.preparedName || p.createdByEmail || 'the author'} for your approval.
        Read each section as the customer will see it and leave your comments beside it.
        Everything you write here goes back to the author with your decision.
      </div>

      <div className="propstats">
        <span>{sections.length} sections</span>
        <span>{withText} commented</span>
        <span>{changes} marked for change</span>
      </div>

      <div className="proposal-toolbar">
        <button className="btn line" onClick={() => {
          const w = window.open('', '_blank');
          if (!w) { alert('Please allow pop-ups to open the document.'); return; }
          w.document.write(generateProposalHTML(p)); w.document.close();
        }}>Open the full document</button>
        <button className="btn line" onClick={saveDraft} disabled={busy === 'save'}>
          {busy === 'save' ? 'Saving…' : busy === 'saved' ? '✓ Saved' : 'Save my comments'}
        </button>
      </div>

      {sections.length === 0 && (
        <div className="warnBox">This proposal has no sections switched on yet, so there is nothing to review.</div>
      )}

      {sections.map((s, i) => {
        const c = notes[s.id] || {};
        return (
          <div key={s.id} className={`revsec ${c.status === 'change' ? 'change' : c.status === 'ok' ? 'ok' : ''}`}>
            <div className="revhead">
              <span className="revnum">{String(i + 1).padStart(2, '0')}</span>
              <b>{s.label}</b>
              <div className="revverdict">
                <button className={`btn tiny ${c.status === 'ok' ? 'primary' : 'line'}`}
                  onClick={() => set(s.id, { status: c.status === 'ok' ? '' : 'ok' })}>✓ Fine</button>
                <button className={`btn tiny ${c.status === 'change' ? 'primary' : 'line'}`}
                  onClick={() => set(s.id, { status: c.status === 'change' ? '' : 'change' })}>Needs change</button>
              </div>
            </div>
            {/* The section exactly as the document renders it. */}
            <div className="revbody docsnippet" dangerouslySetInnerHTML={{ __html: s.html }} />
            <textarea
              className="revnote"
              rows="2"
              value={c.text || ''}
              placeholder={`Your comment on "${s.label}" — the author sees this against this section.`}
              onChange={e => set(s.id, { text: e.target.value })}
            />
            {c.by ? <div className="fieldnote">Last saved by {c.by} · {fmtDate(c.at)}</div> : null}
          </div>
        );
      })}

      <div className="section"><h2>Your decision</h2></div>
      <div className="formrow">
        <label>Overall note to the author (optional)</label>
        <textarea rows="3" value={overall} onChange={e => setOverall(e.target.value)}
          placeholder="Anything that applies to the proposal as a whole." />
      </div>
      {err ? <div className="warnBox">{err}</div> : null}
      <div className="proposal-toolbar">
        <button className="btn primary" onClick={() => submit('Approved')} disabled={!!busy}>
          {busy === 'Approved' ? 'Submitting…' : 'Approve'}
        </button>
        <button className="btn line" onClick={() => submit('Changes Requested')} disabled={!!busy}>
          {busy === 'Changes Requested' ? 'Submitting…' : `Return with ${withText} comment${withText === 1 ? '' : 's'}`}
        </button>
        <button className="btn line danger" onClick={() => submit('Rejected')} disabled={!!busy}>
          {busy === 'Rejected' ? 'Submitting…' : 'Reject'}
        </button>
        {onClose ? <button className="btn line" onClick={onClose}>Close</button> : null}
      </div>
      <div className="hint">Approving unlocks the proposal. Returning it sends your comments back to the author to work through.</div>
    </div>
  );
};

// What the author sees when the review comes back: every comment against the
// section it belongs to, so there is no hunting through the document.
const ReviewFeedback = ({ p }) => {
  const comments = p.reviewComments || {};
  const ids = Object.keys(comments).filter(k => String(comments[k].text || '').trim() || comments[k].status);
  if (!ids.length) return null;
  const changes = ids.filter(k => comments[k].status === 'change');
  return (
    <>
      <div className="section">
        <h2>Reviewer comments</h2>
        <span>{ids.length} section{ids.length === 1 ? '' : 's'}{changes.length ? ` · ${changes.length} needing change` : ''}</span>
      </div>
      <div className="hint">
        Returned by {p.reviewedBy || 'the approver'}{p.reviewedAt ? ' on ' + fmtDate(p.reviewedAt) : ''}.
        Each comment is against the section it applies to.
      </div>
      {ids.map(id => {
        const c = comments[id];
        return (
          <div key={id} className={`revfeed ${c.status === 'change' ? 'change' : c.status === 'ok' ? 'ok' : ''}`}>
            <div className="revfeedhead">
              <b>{SEC_LABEL(id)}</b>
              <span className={`verchip ${c.status === 'change' ? 'no' : 'ok'}`}>
                {c.status === 'change' ? 'Needs change' : c.status === 'ok' ? 'Fine' : 'Comment'}
              </span>
            </div>
            {c.text ? <p>{c.text}</p> : null}
            <div className="fieldnote">{c.by}{c.at ? ' · ' + fmtDate(c.at) : ''}</div>
          </div>
        );
      })}
    </>
  );
};

// ===== PROPOSAL MODULE =====
// All variables are now properly scoped inside the component.
const ProposalModule = ({ activeUid, onBack, onProposalUpdated }) => {
  const [p, setP] = useState(null);
  const [step, setStep] = useState('charter');
  const [loading, setLoading] = useState(true);
  const [showTypeSwitcher, setShowTypeSwitcher] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const [pendingNotice, setPendingNotice] = useState('');
  const [approverEmail, setApproverEmail] = useState('');
  const [approvalComment, setApprovalComment] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const { user } = useAuth();

  const isUserUpdateRef = useRef(false);
  const stepRef = useRef(step);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    const loadProposal = async () => {
      if (!activeUid) {
        const blank = blankProposal(0);
        setP(blank);
        setLoading(false);
        return;
      }
      try {
        const normalizedProposal = hydrateProposal(await fetchProposal(activeUid));
        // Anything the user parked with "Add to Proposal" in the library is
        // applied on open, then cleared so it is never applied twice.
        const { p: withPending, added } = applyPending(normalizedProposal);
        setP(withPending);
        if (added) {
          setPendingNotice(`${added} item${added > 1 ? 's' : ''} added from the library.`);
          setTimeout(() => setPendingNotice(''), 5000);
        }
        if (!isUserUpdateRef.current) {
          setStep(firstStepOf(withPending));
        }
      } catch (err) {
        console.error('Failed to fetch proposal:', err);
        if (err.response?.status === 404) {
          alert('This proposal no longer exists or you don\'t have access to it. Returning to proposal list.');
          if (onBack) onBack();
        } else {
          alert('Could not load proposal. Please try again.');
          if (onBack) onBack();
        }
      } finally {
        setLoading(false);
      }
    };
    loadProposal();
    fetchNotifications();
    fetchPendingCount();
  }, [activeUid, onBack]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/notifications/pending-count', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingCount(res.data.count || 0);
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  const saveProposalToAPI = async (proposal) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.put(
        `/proposals/${proposal.uid}`,
        proposal,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Save proposal API error:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  };

  const updateProposal = async (newP) => {
    if (p?.lockedForEditing && p?.approvalStatus === 'Pending') {
      const isApprover = p?.approverId === user?.id;
      const isAdmin = user?.role === 'admin';
      if (!isApprover && !isAdmin) {
        alert('This proposal is pending approval and cannot be edited.');
        return;
      }
    }

    isUserUpdateRef.current = true;
    const currentStep = stepRef.current;
    setP(newP);

    try {
      const updated = await saveProposalToAPI(newP);
      setP(prevP => ({
        ...updated,
        clientName: newP.clientName || prevP?.clientName || updated.clientName,
        ...newP,
        ...updated,
        uid: updated.uid,
        doc: {
          ...updated.doc,
          version: updated.doc?.version || newP.doc?.version || '1.0'
        }
      }));
      if (currentStep) setStep(currentStep);
      if (onProposalUpdated) onProposalUpdated(updated);
      return updated;
    } catch (err) {
      console.error('Update failed:', err);
      alert('Failed to save proposal.');
      return null;
    } finally {
      setTimeout(() => { isUserUpdateRef.current = false; }, 100);
    }
  };

  const handleSendForApproval = async () => {
    if (!approverEmail.trim()) {
      alert('Please enter an approver email.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await api.post(
        `/proposals/${p.uid}/send-approval`,
        { approverEmail: approverEmail.trim(), message: approvalComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const updatedP = {
          ...p,
          approvalStatus: 'Pending',
          approverEmail: approverEmail.trim(),
          approverId: res.data.proposal.approver_id,
          lockedForEditing: true
        };
        setP(updatedP);
        await updateProposal(updatedP);
        setShowApprovalModal(false);
        setApproverEmail('');
        setApprovalComment('');
        alert('Proposal sent for approval successfully!');
        fetchNotifications();
        fetchPendingCount();
      }
    } catch (error) {
      console.error('Error sending for approval:', error);
      alert(error.response?.data?.error || 'Failed to send for approval.');
    }
  };

  const handleApprove = async (status) => {
    if (!status) {
      alert('Please select a decision.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await api.post(
        `/proposals/${p.uid}/approve`,
        { status, comment: approvalComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const updatedP = {
          ...p,
          approvalStatus: status,
          approvalComment: approvalComment,
          lockedForEditing: status === 'Rejected' ? true : false
        };
        setP(updatedP);
        await updateProposal(updatedP);
        setShowApprovalModal(false);
        setApprovalComment('');
        setApprovalStatus('');
        alert(`Proposal ${status.toLowerCase()} successfully!`);
        fetchNotifications();
        fetchPendingCount();
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      alert(error.response?.data?.error || 'Failed to process approval.');
    }
  };

  const go = (s) => setStep(s);
  const toggleTypeSwitcher = () => setShowTypeSwitcher(!showTypeSwitcher);

  const calculateReadiness = (proposal) => {
    if (!proposal) return 0;
    const total = (proposal.items || []).reduce((a, i) => a + numOf(i.amount), 0);
    const checks = [
      ['Customer named', !!proposal.clientName],
      ['Services selected', (proposal.solutions || []).length > 0],
      ['Scope defined', (proposal.scopeItems || []).length > 0],
      ['Team named', (proposal.team || []).length > 0],
      ['Commercials entered', !!proposal.commercialAmount || total > 0]
    ];
    return Math.round(checks.filter(c => c[1]).length / checks.length * 100);
  };

  const readiness = calculateReadiness(p);
  const isReadOnly = p?.lockedForEditing && p?.approvalStatus === 'Pending' && p?.approverId !== user?.id && user?.role !== 'admin';
  // The nominated approver gets the review screen rather than the step editor:
  // their job is to comment on the finished document, not to edit it.
  const isReviewer = p?.approvalStatus === 'Pending' && String(p?.approverId ?? '') === String(user?.id ?? '') && !!user?.id;
  const isApprover = p?.approverId === user?.id && p?.approvalStatus === 'Pending';

  const stepSet = curSteps(p);
  const currentStep = stepSet.find(s => s[0] === step) || stepSet[0];
  const currentIndex = stepSet.findIndex(s => s[0] === step);
  const nextStep = currentIndex < stepSet.length - 1 ? stepSet[currentIndex + 1][0] : null;
  const prevStep = currentIndex > 0 ? stepSet[currentIndex - 1][0] : null;

  if (loading) return <div className="p-8 text-center">Loading proposal...</div>;
  if (!p) return <div className="p-8 text-center">No proposal selected. <button className="btn primary" onClick={onBack}>Back to list</button></div>;

  const statusColors = {
    'Draft': 'bg-gray-100 text-gray-600',
    'Pending': 'bg-yellow-100 text-yellow-700',
    'Approved': 'bg-green-100 text-green-700',
    'Rejected': 'bg-red-100 text-red-700',
    'Changes Requested': 'bg-blue-100 text-blue-700'
  };

  // Each step in a step set declares the sub-modules it is composed of - the
  // HTML build renders all of them in sequence, so a "money" step shows both the
  // engagement calculator and the payment schedule. Sub-modules that share a
  // renderer are de-duplicated, and only the last one keeps its Back/Next bar.
  const renderStep = () => {
    // Null rather than a no-op, so StepNav can show the ends of the sequence
    // as disabled and drop the bar entirely on non-final sub-modules.
    const goBack = prevStep ? () => go(prevStep) : null;
    const goNext = (nextStep && !isReadOnly) ? () => go(nextStep) : null;

    const approvalRenderer = (pp, setPP, b, n) => stepApproval(pp, setPP, b, n, user, showApprovalModal, setShowApprovalModal, approverEmail, setApproverEmail, approvalComment, setApprovalComment, approvalStatus, setApprovalStatus, handleSendForApproval, handleApprove, showMail, setShowMail);

    const SUB = {
      start: stepStart, charter: stepCharter,
      basics: stepCustomer, people: stepCustomer, customer: stepCustomer,
      story: stepStory,
      about: stepAbout,
      propose: stepPropose, content: stepPropose, scope: stepPropose, deliver: stepPropose,
      metrics: stepMetrics,
      engagement: stepEngagement,
      money: stepMoney,
      method: stepProcess, plan: stepProcess, govern: stepProcess, process: stepProcess,
      terms: stepConditions, conditions: stepConditions,
      closing: approvalRenderer, approval: approvalRenderer,
      sections: stepGenerate, review: stepGenerate, generate: stepGenerate,
      venture: stepVenture, case: stepVenture,
      stages: stepStages, sixd: stepStages,
      outcome: stepOutcome
    };

    // Fast-track and guided ids carry their own composition.
    const COMPOSITE = {
      ft1: ['basics', 'people'],
      ft2: ['propose', 'scope', 'deliver'],
      ft3: ['engagement', 'money'],
      ft4: ['sections', 'review'],
      g1: ['basics'], g2: ['story'], g3: ['propose'],
      g4: ['scope', 'deliver', 'plan'], g5: ['engagement', 'money'],
      g6: ['people', 'govern', 'terms', 'sections'], g7: ['review']
    };

    const def = stepSet.find(s => s[0] === step);
    let keys = COMPOSITE[step] || (def && def[3]) || [step];

    // De-duplicate by renderer so 'basics'+'people' does not show the same
    // screen twice, while preserving the declared order.
    const seen = [];
    keys.forEach(k => { const fn = SUB[k]; if (fn && !seen.includes(fn)) seen.push(fn); });
    if (!seen.length) seen.push(stepCharter);

    return (
      <>
        {seen.map((fn, i) => {
          const last = i === seen.length - 1;
          return (
            <div key={i} className={last ? 'substep' : 'substep nonav'}>
              {fn(p, updateProposal, last ? goBack : null, last ? goNext : null)}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="page active">
      {showApprovalModal && (
        <div className="modalback" onClick={() => setShowApprovalModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{p.approvalStatus === 'Pending' ? 'Review & Approve' : 'Send for Approval'}</h2>
            {p.approvalStatus !== 'Pending' ? (
              <>
                <div className="formrow">
                  <label>Approver Email <span className="text-red-500">*</span></label>
                  <input
                    value={approverEmail}
                    onChange={e => setApproverEmail(e.target.value)}
                    placeholder="approver@solidpro-es.com"
                  />
                </div>
                <div className="formrow">
                  <label>Message (optional)</label>
                  <textarea
                    rows="3"
                    value={approvalComment}
                    onChange={e => setApprovalComment(e.target.value)}
                    placeholder="Any additional notes for the approver..."
                  />
                </div>
                <div className="modalactions">
                  <button className="btn line" onClick={() => setShowApprovalModal(false)}>Cancel</button>
                  <button className="btn primary" onClick={handleSendForApproval}>
                    Send for Approval
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="formrow">
                  <label>Your Decision</label>
                  <div className="flex flex-wrap gap-4">
                    <button
                      className={`btn ${approvalStatus === 'Approved' ? 'primary' : 'line'}`}
                      onClick={() => setApprovalStatus('Approved')}
                    >
                      ✅ Approve
                    </button>
                    <button
                      className={`btn ${approvalStatus === 'Changes Requested' ? 'primary' : 'line'}`}
                      onClick={() => setApprovalStatus('Changes Requested')}
                    >
                      🔄 Request Changes
                    </button>
                    <button
                      className={`btn ${approvalStatus === 'Rejected' ? 'danger' : 'line'}`}
                      onClick={() => setApprovalStatus('Rejected')}
                    >
                      ❌ Reject
                    </button>
                  </div>
                </div>
                <div className="formrow">
                  <label>Comment</label>
                  <textarea
                    rows="3"
                    value={approvalComment}
                    onChange={e => setApprovalComment(e.target.value)}
                    placeholder="Please provide feedback..."
                  />
                </div>
                <div className="modalactions">
                  <button className="btn line" onClick={() => setShowApprovalModal(false)}>Cancel</button>
                  <button
                    className="btn primary"
                    onClick={() => handleApprove(approvalStatus)}
                    disabled={!approvalStatus}
                  >
                    Submit Decision
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showTypeSwitcher && (
        <TypeSwitcher
          p={p}
          onClose={toggleTypeSwitcher}
          onApply={(newP) => {
            updateProposal(newP);
            toggleTypeSwitcher();
            setStep(firstStepOf(newP));
          }}
        />
      )}

      {p.approvalStatus && p.approvalStatus !== 'Draft' && (
        <div className={`mb-4 p-4 rounded-lg border ${statusColors[p.approvalStatus] || 'bg-gray-100 text-gray-600'}`}>
          <div className="flex items-center justify-between flex-wrap">
            <div>
              <strong>Status: {p.approvalStatus}</strong>
              {p.approvalComment && <span className="ml-4 text-sm">Comment: {p.approvalComment}</span>}
              {p.approverEmail && <span className="ml-4 text-sm">Approver: {p.approverEmail}</span>}
              {p.approvedAt && <span className="ml-4 text-sm">Date: {new Date(p.approvedAt).toLocaleDateString()}</span>}
            </div>
            {isReadOnly && (
              <span className="text-sm font-bold text-red-600">🔒 Read-Only Mode</span>
            )}
            {isApprover && (
              <button className="btn primary tiny" onClick={() => setShowApprovalModal(true)}>
                Review & Approve
              </button>
            )}
          </div>
        </div>
      )}

      <div className="studiotop">
        <div>
          <button className="backbtn" onClick={onBack}>‹ All proposals</button>
          <h1>{p.title}</h1>
          <span>
            {p.id} · v{p.doc?.version || '1.0'}
            {p.clientName ? ' · ' + p.clientName : ''}
          </span>
          <div className="typebar">
            <span className="tpill">{(PROPOSAL_TYPES[p.type] || PROPOSAL_TYPES.service).label}</span>
            <span className="tpill alt">{(MODES[p.mode] || MODES.guided).label}</span>
            <button className="btn line tiny" onClick={toggleTypeSwitcher}>Change</button>
            {isApprover && (
              <button className="btn primary tiny" onClick={() => setShowApprovalModal(true)}>
                Review & Approve
              </button>
            )}
            {pendingCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {pendingCount} pending
              </span>
            )}
          </div>
        </div>
        <div className="studiometer">
          <div className="readbar"><span style={{ width: readiness + '%' }}></span></div>
          <b>{readiness}% ready</b>
          {!isReadOnly && p.approvalStatus !== 'Pending' && p.approvalStatus !== 'Approved' && (
            <button className="btn primary" onClick={() => setShowApprovalModal(true)}>
              Send for Approval
            </button>
          )}
          {isReadOnly && (
            <span className="text-xs font-bold text-red-600">🔒 Read-Only</span>
          )}
          <button className="btn line" onClick={() => {
            const html = generateProposalHTML(p);
            const win = window.open('', '_blank');
            if (win) { win.document.write(html); win.document.close(); }
          }}>Generate</button>
        </div>
      </div>

      {pendingNotice && (
        <div className="okBox" style={{ marginBottom: 12 }}>{pendingNotice}</div>
      )}

      <div className="studio">
        <aside className="studionav">
          {stepSet.map((s, i) => (
            <button
              key={s[0]}
              className={step === s[0] ? 'on' : ''}
              onClick={() => go(s[0])}
              disabled={isReadOnly && s[0] !== 'review' && s[0] !== 'generate'}
            >
              <span className="n">{i + 1}</span>
              <span>
                <b>{s[1]}</b>
                <small>{s[2]}</small>
                {isReadOnly && <span className="text-xs text-red-500 ml-2">🔒</span>}
              </span>
            </button>
          ))}
        </aside>
        <section>
          {isReviewer ? (
            <ReviewPanel
              p={p}
              currentUser={user}
              onSubmitted={(status, count) => {
                setPendingNotice(
                  status === 'Approved'
                    ? 'Approved. The author has been notified.'
                    : `Returned to the author with ${count} comment${count === 1 ? '' : 's'}.`
                );
                if (onProposalUpdated) onProposalUpdated(p);
                if (onBack) onBack();
              }}
            />
          ) : isReadOnly && p.approvalStatus === 'Pending' ? (
            <div className="pp">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-yellow-700">
                  <strong>🔒 This proposal is pending approval.</strong>
                  You can view all details but cannot make changes.
                  {p.approverEmail && ` It has been sent to ${p.approverEmail} for review.`}
                </p>
              </div>
              <div className="proposal-preview">
                <h2 className="text-xl font-bold mb-4">Proposal Preview</h2>
                <div className="bg-gray-50 p-6 rounded-lg">
                  {step === 'generate' ? stepGenerate(p, updateProposal, go) : renderStep()}
                </div>
              </div>
            </div>
          ) : (
            renderStep()
          )}
        </section>
      </div>
    </div>
  );
};

// ===== NEW CAPABILITIES VIEW COMPONENT =====
// Replaces the previous renderCapabilities function
const CapabilitiesView = ({ data, industries }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [selectedGroup, setSelectedGroup] = useState('All');

  // Define capability types (same as before, but now inside component)
  const capTypes = [
    { 
      id: 'Tools & Technology', 
      desc: 'CAD, CAE, electronics, PLM, CPQ, cloud, data, AI and automation platforms.',
      groups: [
        { name: 'CAD & Mechanical Design', note: '3D modelling, assemblies, drawings and design automation.', items: ['SolidWorks', 'Creo', 'Siemens NX', 'CATIA', 'Autodesk Inventor', 'AutoCAD', 'Solid Edge', 'DraftSight', 'SolidWorks Composer'] },
        { name: 'Simulation & Analysis', note: 'Structural, thermal, flow, motion and durability analysis.', items: ['ANSYS', 'SolidWorks Simulation', 'Altair HyperMesh', 'Abaqus', 'Nastran', 'MSC Adams', 'Ansys Fluent (CFD)'] },
        { name: 'Electronics & PCB', note: 'Schematic capture, layout, simulation and embedded targets.', items: ['Altium Designer', 'Cadence OrCAD', 'Cadence Allegro', 'Mentor PADS', 'LTspice', 'STM32'] },
        { name: 'PLM, PDM & Engineering Data', note: 'Product data, BOM structure, change control and release.', items: ['Teamcenter', 'PTC Windchill', 'SolidWorks PDM', 'DB Works PDM', 'Aras Innovator', 'ENOVIA'] },
        { name: 'Automation & Controls', note: 'PLC, HMI, SCADA and machine control engineering.', items: ['Siemens TIA Portal', 'Rockwell Studio 5000', 'FactoryTalk', 'Qt / C++ HMI'] },
        { name: 'Application & Platform Development', note: 'Enterprise applications, portals, mobile and services.', items: ['.NET Core', 'React', 'Node.js', 'Flutter', 'Python', 'C++', 'FastAPI', 'PostgreSQL'] },
        { name: 'Data, Cloud & Intelligence', note: 'Data platforms, analytics, dashboards and AI models.', items: ['Power BI', 'SQL Server', 'Azure', 'AWS', 'Python / ML stack'] },
        { name: 'Enterprise Systems Integrations', note: 'Systems SolidPro connects engineering data into.', items: ['SAP', 'Oracle', 'Dynamics 365', 'Zoho CRM', 'Salesforce', 'Epicor', 'Infor', 'IFS'] },
        { name: 'Structural & Plant Engineering', note: 'Structures, detailing, plant modelling and coordination.', items: ['STAAD.Pro', 'Tekla Structures', 'Revit', 'Navisworks', 'AutoCAD Plant 3D', 'Point cloud / scan-to-BIM'] },
        { name: 'SolidPro Platforms', note: 'Software SolidPro has built and runs.', items: ['TRACE eQMS', 'NetZeroHub Enterprise', 'Open Engineering', 'BTOS-Alpha', 'ConfiguraX', 'one.solidpro.app'] },
        { name: 'SolidPro Sustainability Platforms', note: 'SolidPro-owned / SolidPro-led transformation systems referenced in current sustainability material.', items: ['NetZeroHub', 'SATTOS', 'KARMA OS', 'NetZeroHub Pathway', 'NetZero500'] }
      ]
    },
    { 
      id: 'Engineering Disciplines', 
      desc: 'The engineering and domain skills SolidPro teams can execute.',
      groups: [
        { name: 'Mechanical & Product Engineering', items: ['Concept and feasibility engineering', 'Detailed mechanical design', 'Plastics, sheet metal, castings and machined parts', 'GD&T and tolerance stack-up', 'DFM and DFA', 'Prototype engineering', 'Reverse engineering', 'Design automation'] },
        { name: 'Simulation & Analysis', items: ['Structural FEA', 'Thermal analysis', 'Computational fluid dynamics', 'Vibration and NVH', 'Fatigue and durability', 'Drop and impact', 'Motion and kinematics', 'Design optimisation'] },
        { name: 'Electronics & Embedded', items: ['Electronics system architecture', 'PCB design and multilayer layout', 'Board bring-up', 'Firmware and device drivers', 'Component engineering and obsolescence', 'IoT connectivity and telemetry', 'FMEA and MTBF', 'EMC, EMI and ESD pre-compliance'] },
        { name: 'Software Engineering', items: ['Embedded and real-time software', 'HMI and operator interfaces', 'Full-stack product portals', 'IIoT platforms', 'Cloud services', 'Test automation', 'CI/CD pipelines', 'Application modernisation'] },
        { name: 'Testing & Validation', items: ['Requirements-based test planning', 'Requirements-to-test traceability', 'System and integration testing', 'Custom test rigs and simulators', 'Environmental and life testing', 'Reliability testing', 'Certification readiness', 'Audit-ready evidence packages'] },
        { name: 'Structural Engineering', items: ['Transmission and distribution structures', 'Substation structures', 'Telecom monopoles and towers', 'Wind tower structures', 'Foundation design', 'Connection design', 'Capacity assessment', 'Multi-standard engineering'] },
        { name: 'Manufacturing Engineering', items: ['Special purpose machine design', 'Fixture, tooling and gauge design', 'Process and line engineering', 'Line balancing', 'Quality engineering and SPC', 'Shopfloor digitisation', 'OEE and value stream', 'Pilot run and ramp-up'] },
        { name: 'Quality & Regulatory', items: ['eQMS design and implementation', 'Design controls and DHF', 'Risk management to ISO 14971', 'CAPA and complaint handling', 'Supplier quality', 'Mock audit and inspection readiness', '483 remediation', 'EU MDR technical files'] },
        { name: 'Sustainability Engineering', items: ['GHG accounting across Scope 1, 2 and 3', 'MRV setup and verified reporting', 'Marginal abatement cost curves', 'Energy audit and efficiency engineering', 'Carbon intensity reduction', 'Life cycle assessment', 'Measurement and verification', 'ESG disclosure readiness'] },
        { name: 'Digital Engineering', items: ['CPQ and product rules modelling', 'CAD and BOM automation', 'PLM implementation', 'Enterprise systems integration', 'Data platforms and analytics', 'Workflow and RPA automation', 'Predictive analytics', 'Decision intelligence'] },
        { name: 'Technical Documentation', items: ['Manuals, IFUs and service guides', 'Structured authoring and single-source', 'Technical illustration and exploded views', 'Multilingual localisation', 'Regulatory documentation', 'Labelling and UDI', 'Documentation change control', 'Spares and parts catalogues'] },
        { name: 'Supply Chain Engineering', items: ['Vendor identification and qualification', 'Should-cost modelling', 'Import substitution and localisation', 'BOM risk review', 'Inventory and ABC policy', 'Demand and supply planning', 'On-time delivery recovery', 'Working capital reduction'] },
        { name: 'Factory Performance Engineering', items: ['Material flow & internal logistics', 'Factory layout & space optimisation', 'Capacity & throughput modelling', 'Line balancing & bottleneck analysis', 'Work measurement & standard time', 'Ergonomics & operator movement', 'Manufacturing quality optimisation', 'Automation feasibility', 'Discrete-event factory simulation', 'Brownfield / greenfield factory planning', 'CAPEX validation'] },
        { name: 'Sustainability Transformation', items: ['Net-zero readiness & roadmap', 'Decarbonization strategy & MACC', 'Board & workforce enablement', 'Supplier sustainability & development', 'Sustainable procurement', 'Sustainability transformation governance', 'Energy & carbon compliance', 'Product sustainability & circularity'] }
      ]
    },
    { 
      id: 'Standards & Compliance', 
      desc: 'Engineering, regulatory and sustainability standards supported by the work.',
      groups: [
        { name: 'Quality & Product', items: ['ISO 9001:2015', 'CE marking', 'FCC', 'EMC / EMI', 'RoHS', 'REACH', 'WEEE', 'BIS'] },
        { name: 'Medical Devices', items: ['ISO 13485', 'ISO 14971', 'IEC 62366', 'IEC 60601', 'ISO 11607', 'ASTM D4169', 'ASTM F1980', '21 CFR 820 / QMSR', '21 CFR 820.30', '21 CFR 820.45', '21 CFR Part 11', 'EU MDR 2017/745', 'UKCA', 'CDSCO MDR 2017', 'MDSAP', 'GS1 UDI'] },
        { name: 'Energy, Utilities & Structures', items: ['ASCE 48', 'ASCE 7-22', 'NESC', 'AISC', 'IS 802', 'IS 875', 'TIA-222', 'AWS D1.1', 'IEC'] },
        { name: 'Machinery & Safety', items: ['ISO 12100', 'ANSI B11', 'CE marking', 'OSHA'] },
        { name: 'Sustainability & Carbon', items: ['CCTS', 'CBAM', 'BRSR', 'PAT', 'Energy Conservation Act', 'IPMVP', 'GHG Protocol', 'ISO 14040', 'ISO 14044', 'IPCC 2006 Tier 1', 'CEA grid factors'] },
        { name: 'Documentation', items: ['S1000D', 'Structured authoring standards'] }
      ]
    },
    { 
      id: 'SolidPro Frameworks', 
      desc: 'Structured SolidPro methods used to discover, validate, execute and scale.',
      groups: [
        { name: 'Discover & Validate', items: ['StartSmart™ - A free scoping conversation that ends in a straight answer about whether SolidPro can help.', 'ProofX™ - A short piece of real engineering on your own work, so capability is judged on output.', 'DM5 - Digital maturity scored across five dimensions with a costed roadmap.', 'BTOS-6D - The Business Transformation Operating System - Discover, Diagnose, Design, Deliver, Demonstrate, Differentiate.'] },
        { name: 'Design & Execute', items: ['Inno2Market™ - Structured innovation from problem framing to market-ready output.', 'DesignOps™ - Standardised engineering workflows for consistent output across programmes.', 'VE-Optimizer - The value engineering framework behind should-costing and validated redesign.', 'ConfiguraX™ - CPQ framework for configurable equipment with automated CAD and pricing output.', '3-Concept Method - Three engineered options compared with analysis before one is carried forward.'] },
        { name: 'Scale & Sustain', items: ['Engineering POD™ - A dedicated cross-functional team operating as extended R&D.', 'SOMF™ - The offering management framework that structures a solution to business goals.', 'GlocalEdge™ - Multi-geography delivery with local code compliance from one integrated team.', 'NetZeroEdge™ - Sustainability intelligence, energy monitoring and ESG automation.', 'Managed Services - Ongoing sustaining, documentation and monitoring.'] },
        { name: 'Factory & Sustainability Transformation', items: ['TAKTIX Factory Diagnostic - Focused 2–3 week factory diagnostic for one line, shop or bay before deeper modelling.', 'TAKTIX Brownfield - Improve an existing factory through flow, space, capacity, work and simulation.', 'TAKTIX Greenfield - Plan and prove a new factory, line or warehouse before build.', 'TAKTIX CAPEX Check - Test machine, line, automation or facility investment assumptions before approval.', 'NetZero500 - Sustainability transformation journey from opportunity discovery to action, capability and measured progress.', 'SATTOS - Sustainability Action & Transformation Operating System for roadmap, projects, owners, budgets, milestones and evidence.', 'KARMA OS - Supply Chain Sustainability & Intelligence Operating System for supplier maturity, risk, evidence, improvement and sourcing decisions.'] }
      ]
    },
    { 
      id: 'Labs, Infrastructure & Delivery', 
      desc: 'Labs, prototyping, delivery footprint, governance and security capability.',
      groups: [
        { name: 'Engineering Infrastructure', items: ['25,000+ sq.ft engineering workspace', 'Crescent Innovation Centre', 'Rapid prototyping capability', 'Cross-domain R&D'] },
        { name: 'Testing & Lab Capability', items: ['In-house electronics laboratory', 'Reliability laboratory', 'Design verification and pre-compliance testing', 'Custom test rig and simulator development', 'Material and consumable validation, including ink and fluid testing', 'Environmental and life testing'] },
        { name: 'Delivery Footprint', items: ['Chennai, India - primary engineering centre', 'Sandy, Utah, USA', 'United Kingdom', 'Dublin, Ireland', 'Follow-the-sun delivery across US and India hours'] },
        { name: 'Governance & Security', items: ['ISO 9001:2015 certified processes', 'Segregated project environments per client', 'Least-privilege access control', 'Encrypted transfer over approved systems only', 'Defined review gates and design verification', 'Client IP ownership on all work product', 'NDA-governed from first conversation', 'Structured handover so no programme depends on one person'] }
      ]
    },
    { 
      id: 'Industry Depth', 
      desc: 'Industry-specific capability areas that make the engineering context relevant.',
      groups: [
        { name: 'Energy & Utilities', items: ['Mechanical Engineering', 'Integrated New Product Development', 'Sustenance & Value Engineering', 'Engineering Capacity', 'Electronics & Embedded Systems', 'Software Engineering', 'FEA', 'Python', 'APIs', 'React', 'Structural analysis', 'V&V', 'Node.js', 'ERP integration', 'Workflow automation', 'Audit trail'] },
        { name: 'Industrial Equipment & Machinery', items: ['Mechanical Engineering', 'Integrated New Product Development', 'Sustenance & Value Engineering', 'Engineering Capacity', 'Electronics & Embedded Systems', 'Software Engineering', 'Python', 'APIs', 'Inno2Market', 'React', 'Cycle-time analysis', 'DFM/DFA', 'FEA', 'V&V', 'Node.js', 'Simulation'] },
        { name: 'Consumer Products & Appliances', items: ['Mechanical Engineering', 'Integrated New Product Development', 'Sustenance & Value Engineering', 'Engineering Capacity', 'Electronics & Embedded Systems', 'Software Engineering', 'Python', 'APIs', 'Inno2Market', 'React', 'Cycle-time analysis', 'DFM/DFA', 'FEA', 'V&V', 'Node.js', 'Simulation'] },
        { name: 'Healthcare & Medical Devices', items: ['Mechanical Engineering', 'Integrated New Product Development', 'Sustenance & Value Engineering', 'Engineering Capacity', 'Electronics & Embedded Systems', 'Software Engineering', 'Python', 'FEA', 'APIs', 'Inno2Market', 'V&V', 'React', 'Cycle-time analysis', '3-Concept Method', 'DFM/DFA', 'VAVE'] },
        { name: 'Pharma & Life Sciences', items: ['Quoting & Configuration', 'Systems & Data', 'Digital Strategy', 'Digital Consulting', 'Automation as a Service', 'Data, Cloud & Analytics', 'Python', 'Master data management', 'Workflow automation', 'APIs', 'Audit trail', 'React', 'Eco-design', 'Process mapping', 'Cycle-time analysis', 'Cost modelling'] },
        { name: 'Process Industries', items: ['Quoting & Configuration', 'Systems & Data', 'Digital Strategy', 'Digital Consulting', 'Automation as a Service', 'Data, Cloud & Analytics', 'Python', 'Cycle-time analysis', 'Workflow automation', 'APIs', 'Audit trail', 'React', 'Cloud', 'Eco-design', 'BIM', 'Process mapping'] },
        { name: 'Structural & Built Environment', items: ['Quoting & Configuration', 'Systems & Data', 'Digital Strategy', 'Digital Consulting', 'Automation as a Service', 'Data, Cloud & Analytics', 'Python', 'FEA', 'Structural analysis', 'ERP integration', 'Workflow automation', 'APIs', 'Audit trail', 'React', 'Cloud', 'Eco-design'] },
        { name: 'Automotive & Mobility', items: ['Mechanical Engineering', 'Integrated New Product Development', 'Sustenance & Value Engineering', 'Engineering Capacity', 'Electronics & Embedded Systems', 'Software Engineering', 'Python', 'APIs', 'Inno2Market', 'React', 'Cycle-time analysis', 'DFM/DFA', 'FEA', 'V&V', 'Node.js', 'Simulation'] },
        { name: 'Plastics & Packaging', items: ['Mechanical Engineering', 'Integrated New Product Development', 'Sustenance & Value Engineering', 'Engineering Capacity', 'Electronics & Embedded Systems', 'Software Engineering', 'Python', 'APIs', 'Inno2Market', 'React', 'DFM/DFA', 'FEA', 'V&V', 'Node.js', 'Simulation', 'Cycle-time analysis'] },
        { name: 'Banking & Financial Services', items: ['Quoting & Configuration', 'Systems & Data', 'Digital Strategy', 'Digital Consulting', 'Automation as a Service', 'Data, Cloud & Analytics', 'Python', 'Workflow automation', 'APIs', 'Audit trail', 'React', 'Power BI', 'SQL', 'RPA', 'Node.js', 'Cloud'] }
      ]
    }
  ];

  // Compute counts from capTypes
  const counts = {
    disc: capTypes.find(t => t.id === 'Engineering Disciplines')?.groups.reduce((acc, g) => acc + g.items.length, 0) || 0,
    tools: capTypes.find(t => t.id === 'Tools & Technology')?.groups.reduce((acc, g) => acc + g.items.length, 0) || 0,
    std: capTypes.find(t => t.id === 'Standards & Compliance')?.groups.reduce((acc, g) => acc + g.items.length, 0) || 0,
    fw: capTypes.find(t => t.id === 'SolidPro Frameworks')?.groups.reduce((acc, g) => acc + g.items.length, 0) || 0,
  };

  // What this industry actually looks like, drawn from the solutions mapped to
  // it - their capabilities, standards and expertise text.
  const industryContext = (() => {
    if (selectedIndustry === 'All') return null;
    const sols = ((data && data.cats) || [])
      .flatMap(c => (c.subs || []).flatMap(x => x.sols || []))
      .filter(sol => (sol.industries || []).includes(selectedIndustry));
    const hay = sols.map(sol => [sol.title, sol.what, sol.when, sol.sub,
      ...(sol.cap || []), ...(sol.std || []), ...(sol.get || []),
      ...((sol.rich && sol.rich.exp) || [])].join(' ')).join(' ').toLowerCase();
    const exact = new Set(sols.flatMap(sol =>
      [...(sol.cap || []), ...(sol.std || []), ...((sol.rich && sol.rich.exp) || [])]
    ).map(x => String(x).toLowerCase()));
    return { ind: selectedIndustry, sols, hay, exact };
  })();

  // Delivery capability applies whoever the customer is, so it is never
  // narrowed. Industry Depth narrows to the one group that is the industry.
  const industryRelevant = (typeId, group, item) => {
    const ctx = industryContext;
    if (!ctx) return true;
    if (typeId === 'Labs, Infrastructure & Delivery') return true;
    if (typeId === 'Industry Depth') return group.name === ctx.ind;
    const text = String(item).toLowerCase();
    if (ctx.exact.has(text) || ctx.hay.includes(text)) return true;
    const words = text.split(/[^a-z0-9+#.]+/).filter(w => w.length > 3);
    return words.length > 0 && words.filter(w => ctx.hay.includes(w)).length >= Math.min(2, words.length);
  };

  // The Group list follows the chosen type, so it never offers a group that
  // cannot appear. A group that falls out of range resets itself to All.
  const allGroups = (() => {
    const base = selectedType === 'All' ? capTypes : capTypes.filter(t => t.id === selectedType);
    return [...new Set(base.flatMap(t => t.groups.map(g => g.name)))].sort();
  })();

  useEffect(() => {
    if (selectedGroup !== 'All' && !allGroups.includes(selectedGroup)) setSelectedGroup('All');
  }, [selectedType]);   // eslint-disable-line react-hooks/exhaustive-deps

  const filteredTypes = (() => {
    const q = searchQuery.toLowerCase().trim();
    const typeFilter = selectedType;
    const industryFilter = selectedIndustry;
    const groupFilter = selectedGroup;

    let types = capTypes;
    if (typeFilter !== 'All') {
      types = types.filter(t => t.id === typeFilter);
    }
    return types.map(type => {
      let groups = type.groups;
      if (groupFilter !== 'All') {
        groups = groups.filter(g => g.name === groupFilter);
      }
      groups = groups.map(group => ({
        ...group,
        items: group.items.filter(item => {
          if (!industryRelevant(type.id, group, item)) return false;
          return !q || (group.name + ' ' + item).toLowerCase().includes(q);
        })
      })).filter(group => group.items.length > 0);
      return { ...type, groups };
    }).filter(type => type.groups.length > 0);
  })();

  const totalItems = filteredTypes.reduce((acc, type) => acc + type.groups.reduce((sum, g) => sum + g.items.length, 0), 0);

  const renderIndustrySnapshot = (ind) => {
    if (ind === 'All' || !ind) return null;
    const ctx = industryContext;
    const sols = (ctx && ctx.sols) || [];
    const cases = ((data && data.cases) || []).filter(e => (e.industries || []).includes(ind));
    const areas = [...new Set(sols.map(sol => sol.cat).filter(Boolean))];
    // What comes up most often across the solutions mapped to this industry.
    const tally = (pick) => {
      const freq = {};
      sols.forEach(sol => (pick(sol) || []).forEach(x => { freq[x] = (freq[x] || 0) + 1; }));
      return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 14).map(x => x[0]);
    };
    const topCaps = tally(sol => sol.cap);
    const topStds = tally(sol => sol.std);
    const chip = 'px-3 py-1.5 bg-surface-container-low rounded text-label-md font-label-md text-on-surface-variant';
    const subhead = 'font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mt-6 mb-3';

    return (
      <div className="bg-surface-container-lowest rounded-xl shadow-sm p-8 mb-16">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 rounded bg-navy/10 flex items-center justify-center text-navy shrink-0">
            <span className="material-symbols-outlined">domain_verification</span>
          </div>
          <div>
            <h3 className="font-headline-md text-headline-md text-on-surface mb-1">{ind}</h3>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Capability snapshot based on the SolidPro solutions mapped to this industry.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {[[sols.length, 'solutions'], [cases.length, 'case studies'], [areas.length, 'solution areas']].map(([v, label]) => (
            <span key={label} className="px-4 py-2 rounded-lg bg-surface border border-outline-variant font-label-md text-label-md text-on-surface-variant">
              <b className="text-on-surface">{v}</b> {label}
            </span>
          ))}
        </div>

        <div className={subhead}>Relevant solution areas</div>
        <div className="flex flex-wrap gap-2">
          {areas.length
            ? areas.map(x => <span key={x} className={chip}>{x}</span>)
            : <span className={chip}>No solution areas mapped yet</span>}
        </div>

        <div className={subhead}>Frequently used capabilities</div>
        <div className="flex flex-wrap gap-2">
          {topCaps.length
            ? topCaps.map(x => <span key={x} className={chip}>{x}</span>)
            : <span className={chip}>Cross-functional engineering</span>}
        </div>

        {topStds.length > 0 && (
          <>
            <div className={subhead}>Relevant standards</div>
            <div className="flex flex-wrap gap-2">
              {topStds.map(x => (
                <span key={x} className="px-3 py-1.5 bg-navy/10 rounded text-label-md font-label-md text-navy">{x}</span>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // Each capability group carries an icon, taken from the reference design so
  // the same group reads the same way wherever it appears.
  const GROUP_ICON = {
    'CAD & Mechanical Design': 'view_in_ar',
    'Simulation & Analysis': 'analytics',
    'Electronics & PCB': 'memory',
    'PLM, PDM & Engineering Data': 'account_tree',
    'Automation & Controls': 'smart_toy',
    'Application & Platform Development': 'code',
    'Data, Cloud & Intelligence': 'cloud',
    'Enterprise Systems Integrations': 'lan',
    'Structural & Plant Engineering': 'architecture',
    'SolidPro Platforms': 'apps',
    'SolidPro Sustainability Platforms': 'eco',
    'Mechanical & Product Engineering': 'precision_manufacturing',
    'Electronics & Embedded': 'memory',
    'Software Engineering': 'code',
    'Testing & Validation': 'rule',
    'Structural Engineering': 'architecture',
    'Manufacturing Engineering': 'factory',
    'Quality & Regulatory': 'verified',
    'Sustainability Engineering': 'eco',
    'Digital Engineering': 'devices',
    'Technical Documentation': 'description',
    'Supply Chain Engineering': 'inventory_2',
    'Factory Performance Engineering': 'speed',
    'Sustainability Transformation': 'change_circle',
    'Quality & Product': 'verified',
    'Medical Devices': 'medical_services',
    'Energy, Utilities & Structures': 'power',
    'Machinery & Safety': 'precision_manufacturing',
    'Sustainability & Carbon': 'eco',
    'Documentation': 'description',
    'Discover & Validate': 'explore',
    'Design & Execute': 'design_services',
    'Scale & Sustain': 'trending_up',
    'Factory & Sustainability Transformation': 'change_circle',
    'Engineering Infrastructure': 'domain',
    'Testing & Lab Capability': 'science',
    'Delivery Footprint': 'public',
    'Governance & Security': 'security'
  };

  const TYPE_META = {
    'Tools & Technology': { icon: 'build', anchor: 'tools' },
    'Engineering Disciplines': { icon: 'engineering', anchor: 'engineering' },
    'Standards & Compliance': { icon: 'fact_check', anchor: 'standards' },
    'SolidPro Frameworks': { icon: 'account_tree', anchor: 'frameworks' },
    'Labs, Infrastructure & Delivery': { icon: 'domain', anchor: 'labs' },
    'Industry Depth': { icon: 'domain_verification', anchor: 'industry' }
  };

  const iconFor = (name) => GROUP_ICON[name] || 'category';
  const metaFor = (id) => TYPE_META[id] || { icon: 'category', anchor: 'section' };

  // The two sections with a proprietary/official character - compliance and
  // SolidPro's own IP - get the signature dark banner treatment instead of
  // the plain heading row every other capability type uses.
  const BANNER_META = {
    'Standards & Compliance': { eyebrow: 'Governance & Assurance', stat1: 'Standards Tracked', stat2: 'Compliance Domains', bg: 'bg-navy' },
    'SolidPro Frameworks': { eyebrow: 'Proprietary Methods', stat1: 'Frameworks & Methods', stat2: 'Delivery Stages', bg: "bg-white border border-slate-200", light: true },
  };

  // A framework item is written as "Name - what it does". The pillar cards show
  // the name; the full sentence stays as the tooltip.
  const splitFramework = (item) => {
    const at = item.indexOf(' - ');
    return at < 0 ? { name: item, desc: '' } : { name: item.slice(0, at), desc: item.slice(at + 3) };
  };

  const jumpTo = (anchor) => {
    const el = document.getElementById('cap-' + anchor);
    if (!el) return;
    const header = document.querySelector('header');
    const offset = (header ? header.offsetHeight : 0) + 24;
    window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - offset), behavior: 'smooth' });
  };

  const anyFilter = searchQuery || selectedType !== 'All' || selectedIndustry !== 'All' || selectedGroup !== 'All';

  // One brand accent for every compliance domain - a registry reads as
  // corporate/official when it is consistent, not colour-coded like a
  // consumer dashboard. Category is still distinguishable by icon and name.
  const renderStandards = (type) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {type.groups.map(group => (
        <div
          key={group.name}
          className="bg-white border border-outline-variant/30 rounded-xl shadow-sm overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-lg hover:border-navy/40 transition-all duration-300"
        >
          <div className="h-1.5 bg-navy" />
          <div className="p-6 flex flex-col grow">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 shadow-sm bg-navy">
                <span className="material-symbols-outlined text-white text-[20px]">{iconFor(group.name)}</span>
              </div>
              <h3 className="font-headline-md text-headline-md text-on-surface leading-tight">{group.name}</h3>
            </div>
            <p className="font-label-md text-label-md text-on-surface-variant/70 mb-4 ml-[56px] uppercase tracking-wide">
              {group.items.length} standard{group.items.length === 1 ? '' : 's'}
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {group.items.map(item => (
                <span key={item} className="inline-flex items-start gap-1.5 text-[12.5px] font-bold text-on-surface leading-snug min-w-0">
                  <span className="material-symbols-outlined text-[15px] shrink-0 mt-px text-navy">check_circle</span>
                  <span>{item}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // The frameworks are SolidPro's proprietary IP, so they keep the one dark,
  // signature treatment on the page - one accent colour throughout, same as
  // Standards, so neither section reads as a rainbow dashboard.
  const renderPillars = (type) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
      {type.groups.map((group, i) => (
        <div
          key={group.name}
          className="bg-white rounded-xl shadow-md border border-slate-200 relative overflow-hidden group hover:-translate-y-1 hover:shadow-lg hover:border-solid-red/40 transition-all duration-300"
        >
          <div className="absolute -right-6 -bottom-8 opacity-[0.06] group-hover:opacity-[0.10] transition-opacity pointer-events-none">
            <span className="material-symbols-outlined text-[140px] leading-none text-slate-900">{iconFor(group.name)}</span>
          </div>
          <div className="relative p-7">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-200">
              <div className="w-10 h-10 rounded-lg bg-solid-red flex items-center justify-center shrink-0 shadow-sm">
                <span className="material-symbols-outlined text-white text-[19px]">{iconFor(group.name)}</span>
              </div>
              <div>
                <div className="font-label-sm text-label-sm text-solid-red font-bold uppercase tracking-widest">Stage {String(i + 1).padStart(2, '0')}</div>
                <h3 className="font-headline-md text-headline-md text-[#001f3f] leading-tight">{group.name}</h3>
              </div>
            </div>
            <ul className="flex flex-col gap-4 relative z-10">
              {group.items.map(item => {
                const f = splitFramework(item);
                return (
                  <li key={item} className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-solid-red text-[16px] shrink-0 mt-0.5">arrow_right</span>
                    <span>
                      <b className="font-label-md text-label-md text-slate-900 block">{f.name}</b>
                      {f.desc && (
                        <span className="font-body-md text-[13px] leading-snug text-slate-600 block mt-0.5">{f.desc}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );

  // Named tools and platforms are short labels, so they sit better as chips.
  const renderChipCards = (type) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      {type.groups.map(group => (
        <div key={group.name} className="bg-surface-container-lowest rounded-xl p-8 shadow-sm flex flex-col h-full hover:-translate-y-1 transition-transform duration-300">
          <h3 className="font-headline-md text-headline-md text-on-surface flex items-center gap-3">
            <span className="material-symbols-outlined text-navy">{iconFor(group.name)}</span>
            {group.name}
          </h3>
          {group.note && (
            <p className="font-body-md text-body-md text-on-surface-variant mt-2">{group.note}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-6">
            {group.items.map(item => (
              <span key={item} className="px-3 py-1.5 bg-surface-container-low rounded text-label-md font-label-md text-on-surface-variant">{item}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Disciplines and delivery capability are full phrases, which wrap badly as
  // chips - they read as a list.
  const renderListCards = (type) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {type.groups.map(group => (
        <div key={group.name} className="bg-surface-container-lowest rounded-xl p-8 shadow-sm hover:-translate-y-1 transition-transform duration-300">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 rounded bg-navy/10 flex items-center justify-center text-navy mt-1 shrink-0">
              <span className="material-symbols-outlined">{iconFor(group.name)}</span>
            </div>
            <div>
              <h3 className="font-headline-md text-headline-md text-on-surface">{group.name}</h3>
              {group.note && (
                <p className="font-body-md text-body-md text-on-surface-variant mt-1">{group.note}</p>
              )}
            </div>
          </div>
          <ul className="flex flex-col gap-3">
            {group.items.map(item => (
              <li key={item} className="flex items-start gap-3 font-body-md text-body-md text-on-surface-variant">
                <span className="w-1.5 h-1.5 rounded-full bg-navy/40 shrink-0 mt-2.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  const renderType = (type) => {
    if (type.id === 'Standards & Compliance') return renderStandards(type);
    if (type.id === 'SolidPro Frameworks') return renderPillars(type);
    if (type.id === 'Tools & Technology') return renderChipCards(type);
    return renderListCards(type);
  };

  const statCards = [
    { n: counts.disc, label: 'Discipline capabilities' },
    { n: counts.tools, label: 'Tools & platforms' },
    { n: counts.std, label: 'Standards' },
    { n: counts.fw, label: 'SolidPro frameworks' },
    { n: industries.length || 10, label: 'Industries' }
  ];

  return (
    <div className="flex-grow pb-12 max-w-container-max mx-auto w-full relative z-10">
      {/* Header */}
      <section className=" pb-12 flex flex-col items-left text-left">
        <h1 className="font-display-lg text-display-lg text-navy mb-6">SolidPro Capabilities</h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant leading-relaxed">
          Explore capability by engineering discipline, factory and operations expertise, digital technology,
          standards, frameworks, infrastructure and industry depth. Use this when a customer asks about the
          technical strength behind a solution.
        </p>
      </section>

      {/* Stats overview */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-10 relative z-10">
        {statCards.map(s => (
          <div key={s.label} className="bg-surface-container-lowest rounded-xl p-8 shadow-sm flex flex-col items-center justify-center text-center transition-transform hover:-translate-y-1 duration-300">
            <span className="font-headline-lg text-headline-lg text-navy mb-2">{s.n}</span>
            <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">{s.label}</span>
          </div>
        ))}
      </section>

      {/* Filters */}
      <section className="bg-surface-container-lowest rounded-xl shadow-sm p-6 mb-10 relative z-20">
        <div className="flex flex-col gap-6">
          <div className="relative w-full">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none">search</span>
            <input
              className="w-full bg-surface py-4 pl-12 pr-4 rounded-lg border border-outline-variant text-body-md font-body-md text-on-surface focus:outline-none focus:border-navy focus:ring-2 focus:ring-navy/20 transition-all placeholder:text-on-surface-variant/60"
              placeholder="Search tool, discipline, standard, framework or platform"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
            {[
              { label: 'Capability type', value: selectedType, set: setSelectedType, opts: capTypes.map(t => t.id) },
              { label: 'Industry', value: selectedIndustry, set: setSelectedIndustry, opts: industries },
              { label: 'Group', value: selectedGroup, set: setSelectedGroup, opts: allGroups }
            ].map(f => (
              <div key={f.label} className="flex flex-col gap-2">
                <label className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">{f.label}</label>
                <div className="relative">
                  <select
                    className="w-full bg-surface border border-outline-variant rounded-lg py-3 pl-4 pr-10 appearance-none text-body-md font-body-md text-on-surface focus:outline-none focus:border-navy"
                    value={f.value}
                    onChange={(e) => f.set(e.target.value)}
                  >
                    <option value="All">All</option>
                    {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {/* <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none">expand_more</span> */}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-end h-full">
              <button
                className="px-6 py-3 rounded-lg border border-outline-variant text-label-md font-label-md text-on-surface hover:bg-surface-container-low transition-colors w-full md:w-auto h-[48px] disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={!anyFilter}
                onClick={() => { setSearchQuery(''); setSelectedType('All'); setSelectedIndustry('All'); setSelectedGroup('All'); }}
              >
                Clear filters
              </button>
            </div>
          </div>

          <div className="pt-4 border-t border-surface-container-highest flex flex-wrap justify-between items-center gap-3">
            <span className="font-label-md text-label-md text-on-surface-variant">{totalItems} matching capability items</span>
            <div className="flex flex-wrap gap-2">
              {[selectedType, selectedIndustry, selectedGroup].filter(x => x && x !== 'All').map(x => (
                <span key={x} className="px-3 py-1 rounded-full bg-navy/10 text-navy text-label-sm font-label-sm">{x}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industry snapshot */}
      {selectedIndustry !== 'All' && renderIndustrySnapshot(selectedIndustry)}

      {/* Quick navigation - only useful when the whole library is on screen */}
      {!anyFilter && (
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {capTypes.map(type => {
            const m = metaFor(type.id);
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => jumpTo(metaFor(type.id).anchor)}
                className="group bg-surface-container-lowest p-6 rounded-xl shadow-sm border border-transparent hover:border-outline-variant hover:shadow-md transition-all text-left"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-navy/10 flex items-center justify-center text-navy group-hover:bg-navy group-hover:text-white transition-colors">
                    <span className="material-symbols-outlined">{m.icon}</span>
                  </div>
                  <span className="material-symbols-outlined text-on-surface-variant opacity-0 group-hover:opacity-100 transition-opacity">arrow_forward</span>
                </div>
                <h3 className="font-headline-md text-headline-md text-on-surface mb-2">{type.id}</h3>
                <p className="font-body-md text-body-md text-on-surface-variant">{type.desc}</p>
              </button>
            );
          })}
        </section>
      )}

      {/* Capability content */}
      {filteredTypes.length > 0 ? (
        <div className="flex flex-col gap-12">
          {filteredTypes.map(type => {
            const items = type.groups.reduce((a, g) => a + g.items.length, 0);
            if (!items) return null;
            const m = metaFor(type.id);
            return (
              <section key={type.id} id={'cap-' + m.anchor} className="scroll-mt-24">
                {BANNER_META[type.id] ? (() => {
                  const b = BANNER_META[type.id];
                  return (
                    <div className={`relative overflow-hidden ${b.bg} rounded-2xl px-8 py-10 mt-12 mb-10 shadow-lg`} style={b.light ? { background: "linear-gradient(120deg,#eaf4ff,#ffffff 55%,#efeeff)" } : undefined}>
                      <div
                        className="absolute inset-0 opacity-[0.07]"
                        style={{ backgroundImage: `radial-gradient(${b.light ? "rgba(0,31,63,.9)" : "rgba(255,255,255,.9)"} 1px, transparent 1px)`, backgroundSize: '20px 20px' }}
                      />
                      <div
                        className="absolute -right-16 -top-24 w-80 h-80 rounded-full border border-white/10"
                        style={{ boxShadow: '0 0 0 60px rgba(255,255,255,.02), 0 0 0 120px rgba(255,255,255,.015)' }}
                      />
                      <div className="relative flex flex-wrap items-end justify-between gap-6">
                        <div>
                          <div className="flex items-center gap-2 text-solid-red font-label-md text-label-md uppercase tracking-widest font-bold mb-3">
                            <span className="material-symbols-outlined text-[18px]">{m.icon}</span>
                            {b.eyebrow}
                          </div>
                          <h2 className={`font-headline-lg text-headline-lg ${b.light ? "text-[#001f3f]" : "text-white"}`}>{type.id}</h2>
                          <p className={`${b.light ? "text-slate-600" : "text-white/60"} font-body-md text-body-md mt-2 max-w-xl`}>{type.desc}</p>
                        </div>
                        <div className={`flex gap-8 border-l ${b.light ? "border-slate-300" : "border-white/15"} pl-6`}>
                          <div>
                            <div className={`${b.light ? "text-[#001f3f]" : "text-white"} font-display-lg text-[32px] font-extrabold leading-none`}>{items}</div>
                            <div className={`${b.light ? "text-slate-500" : "text-white/50"} font-label-md text-label-md uppercase tracking-wider mt-1`}>{b.stat1}</div>
                          </div>
                          <div>
                            <div className={`${b.light ? "text-[#001f3f]" : "text-white"} font-display-lg text-[32px] font-extrabold leading-none`}>{type.groups.length}</div>
                            <div className={`${b.light ? "text-slate-500" : "text-white/50"} font-label-md text-label-md uppercase tracking-wider mt-1`}>{b.stat2}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })() : (
                  <div className="flex flex-wrap items-baseline justify-between gap-4 mt-12 mb-12 border-b border-surface-container-highest pb-4">
                    <h2 className="font-headline-lg text-headline-lg text-on-surface flex items-center gap-3 min-w-0">
                      <span className="material-symbols-outlined text-navy">{m.icon}</span>
                      {type.id}
                    </h2>
                    <span className="font-label-md text-label-md text-on-surface-variant whitespace-nowrap">{items} items</span>
                  </div>
                )}
                {renderType(type)}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 border-2 border-dashed border-outline-variant rounded-xl bg-surface-container-lowest">
          <span className="material-symbols-outlined text-outline-variant text-[40px]">search_off</span>
          <p className="font-body-md text-body-md text-on-surface-variant mt-2">No capability matches these filters.</p>
        </div>
      )}
    </div>
  );
};

// The library runs to several thousand pixels once the capability sections are
// open, so getting back to the filters means a long drag. This rides along the
// right edge and appears once there is enough scrolled past to be worth it.
const ScrollNav = () => {
  const [show, setShow] = useState(false);
  const [atBottom, setAtBottom] = useState(false);

  useEffect(() => {
    let queued = false;
    const read = () => {
      queued = false;
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setShow(y > 400);
      // Within a screen of the end there is nothing left to jump to.
      setAtBottom(max - y < 120);
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(read);
    };
    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const go = (top) => window.scrollTo({ top, behavior: 'smooth' });

  const btn = 'w-11 h-11 flex items-center justify-center text-navy hover:bg-navy hover:text-white ' +
    'transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-navy ' +
    'disabled:cursor-default';

  // Hidden once the footer is in view, not merely disabled - a fixed-position
  // widget this close to the viewport edge sits directly on top of the
  // footer's own text at that point, on any screen narrow enough that the
  // page ends before the button clears it.
  const visible = show && !atBottom;
  return (
    <div
      className={'fixed right-4 md:right-6 bottom-8 z-40 flex flex-col rounded-full overflow-hidden ' +
        'bg-surface-container-lowest border border-outline-variant shadow-md transition-opacity duration-300 ' +
        (visible ? 'opacity-100' : 'opacity-0 pointer-events-none')}
      aria-hidden={!visible}
    >
      <button type="button" className={btn} onClick={() => go(0)} title="Back to top" aria-label="Back to top">
        <span className="material-symbols-outlined">keyboard_arrow_up</span>
      </button>
      <span className="h-px bg-outline-variant" />
      <button
        type="button"
        className={btn}
        onClick={() => go(document.documentElement.scrollHeight)}
        disabled={atBottom}
        title="Jump to bottom"
        aria-label="Jump to bottom"
      >
        <span className="material-symbols-outlined">keyboard_arrow_down</span>
      </button>
    </div>
  );
};

// ===== DASHBOARD =====
// ===== PROPOSAL ADMIN =====
// SolidPro's standing information, set once. A salesperson should never retype
// the GST number. The Finance tab matters most: without a finance-approved
// USD->INR rate, rupee pricing stays blocked by design.
const ProposalAdmin = ({ onClose }) => {
  const [tab, setTab] = useState('company');
  const [a, setA] = useState(() => JSON.parse(JSON.stringify(ADMIN)));
  const [saved, setSaved] = useState('');

  const setC = (k, v) => setA({ ...a, company: { ...a.company, [k]: v } });
  const setW = (k, v) => setA({ ...a, wording: { ...a.wording, [k]: v } });
  const setF = (k, v) => setA({ ...a, finance: { ...(a.finance || {}), [k]: v } });
  const setVer = (k, patch) => setA({ ...a, verified: { ...(a.verified || {}), [k]: { ...((a.verified || {})[k] || {}), ...patch } } });
  const setFact = (k, text) => setA({ ...a, facts: { ...(a.facts || {}), [k]: { ...(COMPANY_FACTS[k] || {}), text, items: null } } });

  const commit = () => {
    ADMIN = JSON.parse(JSON.stringify(a));
    saveAdmin();
    setSaved('Saved. New proposals will use this.');
    setTimeout(() => setSaved(''), 3000);
  };

  const fxState = (() => {
    const f = a.finance || {};
    if (!Number(f.usdInr)) return { ok: false, why: 'No rate entered - rupee proposals cannot be priced.' };
    if (!f.updated) return { ok: false, why: 'No "last updated" date - rupee proposals cannot be priced.' };
    const days = Math.floor((Date.now() - new Date(f.updated).getTime()) / 864e5);
    if (days > Number(f.maxAgeDays || 30)) return { ok: false, why: `Rate is ${days} days old, beyond the ${f.maxAgeDays || 30}-day limit.` };
    return { ok: true, why: `Rate is ${days} day${days === 1 ? '' : 's'} old and within the limit.` };
  })();

  const COMPANY_FIELDS = [['legalName', 'Registered name'], ['address', 'Registered address'], ['gst', 'GSTIN'], ['pan', 'PAN'], ['cin', 'CIN'], ['msme', 'MSME / Udyam'], ['insurance', 'Insurance cover'], ['email', 'Standard contact email'], ['phone', 'Standard contact phone'], ['web', 'Website'], ['bank', 'Bank details for invoicing']];
  const WORDING_FIELDS = [['ip', 'Who owns what is produced'], ['quality', 'How quality is assured'], ['warranty', 'Warranty & support'], ['hse', 'Safety on customer sites'], ['payment', 'Payment terms'], ['tax', 'Taxes & expenses']];

  return (
    <div className="modalback" onClick={onClose}>
      <div className="modal wide" onClick={e => e.stopPropagation()}>
        <div className="kicker">Proposal admin</div>
        <h2>SolidPro standing information</h2>
        <p>Set this once. Every new proposal starts with it already filled in.</p>
        <div className="adtabs">
          {[['company', 'Company & wording'], ['facts', 'About SolidPro'], ['finance', 'Finance'], ['people', 'Team library'], ['rates', 'Rate card']].map(t => (
            <button key={t[0]} className={tab === t[0] ? 'on' : ''} onClick={() => setTab(t[0])}>{t[1]}</button>
          ))}
        </div>

        {tab === 'company' && (
          <>
            <div className="formgrid">
              {COMPANY_FIELDS.map(f => (
                <div className="formrow" key={f[0]}><label>{f[1]}</label><input value={a.company[f[0]] || ''} onChange={e => setC(f[0], e.target.value)} /></div>
              ))}
            </div>
            <div className="section"><h2>Standard wording</h2><span>pulled into every new proposal</span></div>
            {WORDING_FIELDS.map(f => (
              <div className="formrow" key={f[0]}><label>{f[1]}</label><textarea value={a.wording[f[0]] || ''} onChange={e => setW(f[0], e.target.value)} /></div>
            ))}
          </>
        )}

        {tab === 'finance' && (
          <>
            <div className="hint">Proposals in rupees use this rate. There is deliberately no default — an assumed exchange rate has no place in a priced document.</div>
            <div className="formgrid">
              <div className="formrow"><label>Finance-approved USD → INR rate</label><input type="number" step="0.01" value={(a.finance || {}).usdInr || ''} onChange={e => setF('usdInr', e.target.value)} placeholder="e.g. 87.40" /></div>
              <div className="formrow"><label>Last updated</label><input type="date" value={(a.finance || {}).updated || ''} onChange={e => setF('updated', e.target.value)} /></div>
              <div className="formrow"><label>Updated by</label><input value={(a.finance || {}).updatedBy || ''} onChange={e => setF('updatedBy', e.target.value)} placeholder="e.g. Finance" /></div>
              <div className="formrow"><label>Treat as stale after (days)</label><input type="number" value={(a.finance || {}).maxAgeDays || 30} onChange={e => setF('maxAgeDays', e.target.value)} /></div>
            </div>
            <div className={fxState.ok ? 'okBox' : 'warnBox'}>{fxState.ok ? '✓ ' : '⚠ '}{fxState.why}</div>
            <div className="noteBox">Until this is set, a rupee proposal cannot be priced. That is intentional.</div>
          </>
        )}

        {tab === 'facts' && (
          <>
            <div className="hint">These blocks are what the About SolidPro step offers. Only verified facts can be used in a proposal.</div>
            {Object.keys(COMPANY_FACTS).map(k => {
              const base = ((a.facts || {})[k]) || COMPANY_FACTS[k];
              const ver = (a.verified || {})[k] || {};
              const txt = base.text || (base.items || []).map(i => i.join(' — ')).join('\n');
              return (
                <div className="formrow" key={k}>
                  <label>{COMPANY_FACTS[k].label}<span className={`verchip ${ver.ok ? 'ok' : 'no'}`}>{ver.ok ? '✓ Verified' : '⚠ Needs verification'}</span></label>
                  <textarea rows="4" value={txt} onChange={e => setFact(k, e.target.value)} />
                  <div className="verrow">
                    <label className="switch"><input type="checkbox" checked={!!ver.ok} onChange={e => setVer(k, { ok: e.target.checked })} /><span>Verified for external use</span></label>
                    <input value={ver.by || ''} onChange={e => setVer(k, { by: e.target.value })} placeholder="Verified by" />
                    <input type="date" value={ver.date || ''} onChange={e => setVer(k, { date: e.target.value })} />
                    <input value={ver.source || ''} onChange={e => setVer(k, { source: e.target.value })} placeholder="Source" />
                  </div>
                </div>
              );
            })}
          </>
        )}

        {tab === 'people' && (
          <>
            <div className="hint">Store your people once, then pick "add a Technical Lead" instead of retyping a designation.</div>
            <div className="rowlist">
              {(a.people || []).map((x, i) => (
                <div className="rowcard" key={i}>
                  <div className="rowgrid5">
                    <input value={x.name || ''} onChange={e => { const pp = [...a.people]; pp[i] = { ...pp[i], name: e.target.value }; setA({ ...a, people: pp }); }} placeholder="Name" />
                    <select value={x.designation || ''} onChange={e => { const pp = [...a.people]; pp[i] = { ...pp[i], designation: e.target.value }; setA({ ...a, people: pp }); }}>
                      {['', ...DESIG_LIB].map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select value={x.role || ''} onChange={e => { const pp = [...a.people]; pp[i] = { ...pp[i], role: e.target.value }; setA({ ...a, people: pp }); }}>
                      {['', ...ROLE_LIB].map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <input value={x.exp || ''} onChange={e => { const pp = [...a.people]; pp[i] = { ...pp[i], exp: e.target.value }; setA({ ...a, people: pp }); }} placeholder="Experience" />
                    <input value={x.practice || ''} onChange={e => { const pp = [...a.people]; pp[i] = { ...pp[i], practice: e.target.value }; setA({ ...a, people: pp }); }} placeholder="Practice" />
                  </div>
                  <button className="lx" onClick={() => { const pp = [...a.people]; pp.splice(i, 1); setA({ ...a, people: pp }); }}>✕</button>
                </div>
              ))}
              {!(a.people || []).length && <div className="hint">Nobody stored yet.</div>}
            </div>
            <div className="proposal-toolbar"><button className="btn line" onClick={() => setA({ ...a, people: [...(a.people || []), { name: '', designation: '', role: '', exp: '', practice: '' }] })}>＋ Add person</button></div>
          </>
        )}

        {tab === 'rates' && (
          <>
            <div className="hint">Standard rates and packages. Adding one to a proposal fills the price and the basis in one click.</div>
            <div className="rowlist">
              {(a.rates || []).map((x, i) => (
                <div className="rowcard" key={i}>
                  <div className="rowgrid4">
                    <input value={x.name || ''} onChange={e => { const rr = [...a.rates]; rr[i] = { ...rr[i], name: e.target.value }; setA({ ...a, rates: rr }); }} placeholder="e.g. Engineering POD - Mechanical" />
                    <select value={x.basis || 'Fixed Fee'} onChange={e => { const rr = [...a.rates]; rr[i] = { ...rr[i], basis: e.target.value }; setA({ ...a, rates: rr }); }}>
                      {COM_MODELS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <input value={x.amount || ''} onChange={e => { const rr = [...a.rates]; rr[i] = { ...rr[i], amount: e.target.value }; setA({ ...a, rates: rr }); }} placeholder="Amount" />
                    <select value={x.currency || 'INR'} onChange={e => { const rr = [...a.rates]; rr[i] = { ...rr[i], currency: e.target.value }; setA({ ...a, rates: rr }); }}>
                      <option value="INR">INR</option><option value="USD">USD</option>
                    </select>
                  </div>
                  <button className="lx" onClick={() => { const rr = [...a.rates]; rr.splice(i, 1); setA({ ...a, rates: rr }); }}>✕</button>
                </div>
              ))}
              {!(a.rates || []).length && <div className="hint">No rates stored yet.</div>}
            </div>
            <div className="proposal-toolbar"><button className="btn line" onClick={() => setA({ ...a, rates: [...(a.rates || []), { name: '', basis: 'Fixed Fee', amount: '', currency: 'INR' }] })}>＋ Add rate</button></div>
          </>
        )}

        {saved && <div className="okBox">{saved}</div>}
        <div className="modalactions">
          <button className="btn line" onClick={onClose}>Close</button>
          <button className="btn primary" onClick={commit}>Save</button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { data, loading, error } = useLibraryData();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('solidpro-browse') || '{}').tab || 'solutions'; }
    catch (e) { return 'solutions'; }
  });
  const [solFilters, setSolFilters] = useState({ q: '', area: 'All', focus: 'All', industry: 'All', std: 'All' });
  const [caseFilters, setCaseFilters] = useState({ q: '', pillar: 'All', industry: 'All', area: 'All' });
  const [visibleSolutions, setVisibleSolutions] = useState(6);
  // Browsing the library is a drill-down: solution areas -> focus areas ->
  // solutions. The filter bar short-circuits it and shows a flat result list.
  // Opening a solution or a case unmounts this screen, so the position in the
  // drill-down has to survive outside React state or coming back always lands
  // on the top level. Session storage is right for this: it is per-tab and it
  // does not outlive the visit.
  const savedBrowse = (() => {
    try { return JSON.parse(sessionStorage.getItem(BROWSE_KEY) || '{}'); } catch (e) { return {}; }
  })();
  const [solMode, setSolMode] = useState(savedBrowse.solMode || 'areas');   // areas | cat | sub
  const [solCat, setSolCat] = useState(savedBrowse.solCat || null);         // category id
  const [solSub, setSolSub] = useState(savedBrowse.solSub || 0);            // index within that category
  const [subView, setSubView] = useState(savedBrowse.subView || 'sols');    // sols | cases

  useEffect(() => {
    try { sessionStorage.setItem(BROWSE_KEY, JSON.stringify({ solMode, solCat, solSub, subView, tab })); }
    catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solMode, solCat, solSub, subView, tab]);

  // Where the eye should land after a click: the content, not the page
  // heading. Measured against the sticky header so it is not tucked underneath.
  const browseTop = useRef(null);
  const landOnContent = () => {
    const el = browseTop.current;
    if (!el) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const header = document.querySelector('header');
    const offset = (header ? header.offsetHeight : 0) + 16;
    const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
    // On a short page the browser cannot scroll that far and clamps, leaving
    // the heading on screen anyway. Never ask for more than the page can give.
    const reachable = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo({ top: Math.max(0, Math.min(y, reachable)), behavior: 'smooth' });
  };

  // A Case Studies choice can stop being reachable once another filter moves.
  // Waits for the catalogue: until then there are no options to validate, and
  // the builders further down have not been assigned.
  useEffect(() => {
    if (!data) return;
    const valid = areaOptions().flatMap(g => [g.cat, ...g.subs.map(x => x.name)]);
    if (caseFilters.area !== 'All' && !valid.includes(caseFilters.area)) {
      setCaseFilters(f => ({ ...f, area: 'All' }));
      return;
    }
    const inds = industryOptions().map(x => x.name);
    if (caseFilters.industry !== 'All' && !inds.includes(caseFilters.industry)) {
      setCaseFilters(f => ({ ...f, industry: 'All' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, caseFilters.pillar, caseFilters.industry, caseFilters.area, caseFilters.q]);

  // Runs after the level has painted; a scroll issued during the click would
  // measure the outgoing screen. Skipped on the first render so a restored
  // position does not yank the page around.
  const firstBrowsePaint = useRef(true);
  useEffect(() => {
    if (firstBrowsePaint.current) { firstBrowsePaint.current = false; return; }
    if (solMode === 'cat' || solMode === 'sub') landOnContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solMode, solCat, solSub]);

  const SOLUTIONS_PER_PAGE = 6;
  const [propView, setPropView] = useState('list');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [activeUid, setActiveUid] = useState(null);
  const [showPropAdmin, setShowPropAdmin] = useState(false);
  const [showNewProposal, setShowNewProposal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [propToast, setPropToast] = useState('');
  const importRef = useRef(null);

  useEffect(() => {
    if (data) {
      window._SOLS = data.cats.flatMap(c => c.subs.flatMap(s => s.sols));
      window._CASES = data.cases;
      window._CATS = data.cats;
    }
  }, [data]);

  useEffect(() => {
    setVisibleSolutions(SOLUTIONS_PER_PAGE);
  }, [solFilters]);

  // Defense in depth: the Proposals tab can also be reached by a stale
  // sessionStorage tab value (e.g. left on 'proposal' from an earlier admin
  // session on a shared browser), not only by clicking the nav link the
  // TopBar already gates. Bounce a non-admin back out here too.
  useEffect(() => {
    if (tab === 'proposal' && user?.role !== 'admin') {
      setTab('solutions');
      setPropToast('Admin only has access. Please contact your admin for the proposal.');
      setTimeout(() => setPropToast(''), 4000);
    }
  }, [tab, user]);

  const loadProposals = async () => {
    setProposalsLoading(true);
    try {
      const list = await fetchProposals();
      setProposals(list);
    } catch (err) {
      console.error('Failed to load proposals:', err);
      alert('Could not load proposals.');
    } finally {
      setProposalsLoading(false);
    }
  };

  const deleteProposal = async (uid) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');
      const response = await api.delete(`/proposals/${uid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadProposals();
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      if (error.response?.status === 404) {
        setProposals(prev => prev.filter(p => p.uid !== uid));
        return true;
      }
      throw error;
    }
  };

  useEffect(() => {
    if (tab === 'proposal') {
      loadProposals();
    }
  }, [tab]);

  if (loading) return <div className="p-8 text-center">Loading library data...</div>;
  if (error) return <ApiError error={error} />;
  if (!data) return <div className="p-8 text-center">No data available</div>;

  const { cats, cases, industries } = data;
  const SOLS = cats.flatMap(c => c.subs.flatMap(s => s.sols));
  // Declared before orderedCats exists, so sort by the same list here too.
  const AREAS = [...cats]
    .sort((a, b) => {
      const O = ['ENG', 'DIG', 'INN', 'MFG', 'SCM', 'SUS', 'PKG', 'MED', 'PROC', 'EU', 'BEI', 'BFSI'];
      const ia = O.indexOf(a.id), ib = O.indexOf(b.id);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    })
    .map(c => c.name);
  const INDUSTRIES = industries || [];
  const STDS = uniq(SOLS.flatMap(s => s.std || [])).sort();
  // Focus areas narrow to the selected solution area - offering every focus in
  // the library when one area is chosen produces combinations that match nothing.
  const FOCUS = solFilters.area === 'All'
    ? uniq(cats.flatMap(c => c.subs.map(x => x.name))).sort()
    : ((cats.find(c => c.name === solFilters.area) || {}).subs || []).map(x => x.name);
  const SOL_DEFAULTS = { q: '', area: 'All', focus: 'All', industry: 'All', std: 'All' };
  const CASE_DEFAULTS = { q: '', pillar: 'All', industry: 'All', area: 'All' };

  // Each dropdown offers only what is actually reachable given the other
  // choices, with a count, so you cannot pick a combination that matches
  // nothing.
  const pillarOptions = () => {
    // Pillar sits at the top of the hierarchy, so it ignores the area filter -
    // otherwise choosing a narrow area hides every other pillar and strands you.
    const pool = cases.filter(c => caseMatchExcept(c, caseFilters, 'pillar', 'area'));
    const count = {};
    pool.forEach(c => { const p = caseMeta(c).pillar; count[p] = (count[p] || 0) + 1; });
    return PILLARS.map(p => ({ name: p, n: count[p] || 0 }));
  };
  const industryOptions = () => {
    const pool = cases.filter(c => caseMatchExcept(c, caseFilters, 'industry'));
    const count = {};
    pool.forEach(c => (c.industries || []).forEach(i => { count[i] = (count[i] || 0) + 1; }));
    return Object.keys(count).sort().map(i => ({ name: i, n: count[i] }));
  };
  // Grouped: each solution area, then the focus areas inside it.
  const areaOptions = () => {
    const pool = cases.filter(c => caseMatchExcept(c, caseFilters, 'area'));
    const g = {};
    pool.forEach(c => caseMeta(c).pairs.forEach(p => {
      const [cat, sub] = p.split('||');
      g[cat] = g[cat] || {};
      g[cat][sub] = (g[cat][sub] || 0) + 1;
    }));
    return Object.keys(g).sort().map(cat => ({
      cat, subs: Object.keys(g[cat]).sort().map(x => ({ name: x, n: g[cat][x] }))
    }));
  };

  const activeCaseChips = Object.entries(caseFilters).filter(([k, v]) => k !== 'q' && v !== 'All').map(([, v]) => v);
  const activeSolChips = Object.entries(solFilters).filter(([k, v]) => k !== 'q' && v !== 'All').map(([, v]) => v);
  const PILLARS = ['Engineering', 'Innovation', 'Digital Transformation', 'Sustainability'];

  const bySol = id => SOLS.find(s => s.id === id);
  const byCat = id => cats.find(c => c.id === id);
  const casesForSol = id => cases.filter(c => (c.sols || []).includes(id));
  const casesForSub = subObj => {
    const ids = new Set((subObj.sols || []).map(x => x.id));
    return cases.filter(c => (c.sols || []).some(x => ids.has(x)));
  };
  // Short codes shown on the area tiles, as in the HTML build.
  // The order the areas are presented in. Anything not listed falls to the end,
  // in the order the API returned it, so a new category still appears.
  const AREA_ORDER = ['ENG', 'DIG', 'INN', 'MFG', 'SCM', 'SUS', 'PKG', 'MED', 'PROC', 'EU', 'BEI', 'BFSI'];
  const orderedCats = [...cats].sort((a, b) => {
    const ia = AREA_ORDER.indexOf(a.id), ib = AREA_ORDER.indexOf(b.id);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });

  const AREA_ICON = { ENG: 'ENG', DIG: 'DT', INN: 'INN', MFG: 'MFG', SCM: 'SCM', SUS: 'ESG',
    PKG: 'PUB', MED: 'MED', PROC: 'P&E', EU: 'E&U', BEI: 'BIM', BFSI: 'BFSI' };
  const filtersActive = () => !!solFilters.q || activeSolChips.length > 0;

  // One solution, as a card. Used by the flat result list and by a focus area.
  const SolCard = ({ sol }) => {
    const rel = casesForSol(sol.id).length;
    return (
      <button className="solcard" onClick={() => navigate(`/solution/${sol.id}`)}>
        {richOf(sol) ? <span className="fulltag">Detailed</span> : null}
        <div className="casetop">
          <span className="caseid">{sol.id}</span>
        </div>
        <h3>{sol.title}</h3>
        <p>{sol.what}</p>
        <div className="solmeta">
          <span className="mini">{sol.cat}</span>
          <span className="mini">{sol.sub}</span>
          {rel ? <span className="mini">{rel} related case{rel > 1 ? 's' : ''}</span> : null}
        </div>
      </button>
    );
  };

  const CaseCard = ({ c }) => (
    <button className="solcard" onClick={() => navigate(`/case/${c.id}`)}>
      <div className="casetop">
        <span className="caseid">CASE ID: {c.caseId || c.case_id || c.id}</span>
        <span className="mini">{c.pillar}</span>
      </div>
      <h3>{c.title}</h3>
      <p>{(c.result || [])[0] || c.problem}</p>
      <div className="solmeta">
        {(c.industries || []).slice(0, 3).map(i => <span key={i} className="mini">{i}</span>)}
      </div>
    </button>
  );

  const Crumbs = ({ trail }) => (
    <div className="crumb">
      {trail.map((x, i) => i === trail.length - 1
        ? <span key={i}>{x.label}</span>
        : <React.Fragment key={i}>
            <button onClick={x.go}>{x.label}</button>
            <span className="sep">›</span>
          </React.Fragment>)}
    </div>
  );

  // One level up: a focus area returns to its solution area, a solution area
  // returns to the list of them.
  const browseBack = () => {
    if (solMode === 'sub') { setSolMode('cat'); return; }
    backToAreas();
  };

  const openCat = (id) => { setSolCat(id); setSolSub(0); setSolMode('cat'); };
  const openSub = (i) => { setSolSub(i); setSubView('sols'); setSolMode('sub'); };
  const backToAreas = () => { setSolMode('areas'); setSolCat(null); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const solMatch = (s, f) => {
    if (f.area !== 'All' && s.cat !== f.area) return false;
    if (f.focus !== 'All' && s.sub !== f.focus) return false;
    if (f.industry !== 'All' && !(s.industries || []).includes(f.industry)) return false;
    if (f.std !== 'All' && !(s.std || []).includes(f.std)) return false;
    if (f.q) {
      const hay = [s.title, s.what, s.when, s.cat, s.sub, ...(s.industries || []), ...(s.cap || []), ...(s.std || []), ...(s.get || [])].join(' ').toLowerCase();
      const words = f.q.toLowerCase().split(/\s+/).filter(Boolean);
      if (!words.every(w => hay.includes(w))) return false;
    }
    return true;
  };

  // A case inherits its solution areas from the solutions it is linked to -
  // there is no area field on the case itself.
  // A case has no areas of its own - it inherits them from the solutions it is
  // linked to, at both levels: the solution area and the focus area within it.
  const caseMeta = (c) => {
    const rel = (c.sols || []).map(id => bySol(id)).filter(Boolean);
    return {
      cats: uniq(rel.map(x => x.cat)),
      subs: uniq(rel.map(x => x.sub)),
      pairs: uniq(rel.map(x => x.cat + '||' + x.sub)),
      industries: uniq(c.industries || []),
      // An unset pillar counts as Engineering, as it does in the HTML build.
      pillar: c.pillar || 'Engineering'
    };
  };
  const caseAreas = (c) => { const m = caseMeta(c); return [...m.cats, ...m.subs]; };

  // One test per filter, so the option builders can ask "what would match if
  // this particular filter were not set?" - which is how each dropdown knows
  // what to offer.
  const caseTests = {
    pillar: (m, f) => f.pillar === 'All' || m.pillar === f.pillar,
    industry: (m, f) => f.industry === 'All' || m.industries.includes(f.industry),
    // the area filter accepts either a solution area or a focus area
    area: (m, f) => f.area === 'All' || m.cats.includes(f.area) || m.subs.includes(f.area),
    q: (m, f, c) => {
      if (!f.q) return true;
      const hay = [c.caseId, c.title, c.sector, c.client, c.problem, c.approach, c.source, c.pillar,
        ...m.industries, ...(c.delivered || []), ...(c.tech || []), ...(c.result || []), ...(c.metrics || []),
        ...m.cats, ...m.subs].join(' ').toLowerCase();
      return f.q.toLowerCase().split(/\s+/).filter(Boolean).every(w => hay.includes(w));
    }
  };
  const caseMatchExcept = (c, f, ...skip) => {
    const m = caseMeta(c);
    return Object.keys(caseTests).every(k => skip.includes(k) || caseTests[k](m, f, c));
  };
  const caseMatch = (c, f) => caseMatchExcept(c, f);

  const matchedSolutions = SOLS.filter(s => solMatch(s, solFilters));
  const matchedCases = cases.filter(c => caseMatch(c, caseFilters));
  const visibleSolutionsList = matchedSolutions.slice(0, visibleSolutions);
  const hasMoreSolutions = visibleSolutions < matchedSolutions.length;

  const notify = (msg) => { setPropToast(msg); setTimeout(() => setPropToast(''), 4000); };

  // Opens the finished document for a proposal that is not currently loaded in
  // the editor, so the list can generate without a round-trip through it.
  const handleGenerate = async (uid) => {
    if (!uid) { alert('That proposal has no identifier - reload the list and try again.'); return; }
    try {
      const full = hydrateProposal(await fetchProposal(uid));
      // Build the document before opening the tab: if it throws, the user is
      // told why instead of being left staring at a blank window.
      const html = generateProposalHTML(full);
      const win = window.open('', '_blank');
      if (!win) { alert('Please allow pop-ups to generate the proposal.'); return; }
      win.document.write(html);
      win.document.close();
    } catch (err) {
      console.error('Generate failed:', err, 'uid:', uid, 'base:', api.defaults.baseURL);
      // A 404 means the row is gone but this browser still lists it - refresh
      // rather than leaving the user clicking a card that cannot work.
      if (err.response?.status === 404) {
        alert('That proposal no longer exists on the server. Refreshing the list.');
        loadProposals();
        return;
      }
      alert('Could not generate that proposal: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleExport = async (uid) => {
    if (!uid) { alert('That proposal has no identifier - reload the list and try again.'); return; }
    try {
      const full = hydrateProposal(await fetchProposal(uid));
      const b = new Blob([JSON.stringify(full, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = safe(full.id) + '.json';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Could not export that proposal.');
    }
  };

  // An imported file is treated as untrusted: its fields are merged onto a fresh
  // blank proposal so a missing or renamed key cannot produce a broken record,
  // and the server issues a new uid rather than colliding with an existing one.
  const handleImport = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const raw = JSON.parse(text);
      const { uid, createdAt, updatedAt, ...rest } = raw;
      const merged = Object.assign(blankProposal(1), rest);
      const saved = await createProposalWith(merged);
      await loadProposals();
      notify('Imported ' + (saved.id || 'proposal'));
    } catch (err) {
      console.error('Import failed:', err);
      alert('That file could not be read as a proposal.');
    }
  };

  const handleDuplicate = async (uid) => {
    try {
      const copy = await duplicateProposal(uid);
      await loadProposals();
      notify('Duplicated as ' + (copy?.title || 'a new draft'));
    } catch (err) {
      console.error('Duplicate failed:', err);
      alert('Could not duplicate: ' + (err.response?.data?.error || err.message));
    }
  };

  const renderProposalList = () => {
    const items = proposals;
    return (
      <div className="flex-grow pt-10 pb-24 max-w-container-max mx-auto w-full relative z-10">
        <div className="mb-12 flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className="font-headline-xl text-headline-xl text-primary-container mb-2">Proposals</h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant">Create and manage proposals. Start from scratch or use the "Add to Proposal" button from any solution or case study.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button className="bg-primary-container text-on-primary font-bold px-6 py-3 rounded-lg hover:bg-tertiary-container transition-colors flex items-center gap-2" onClick={() => setShowNewProposal(true)}>
              <span className="material-symbols-outlined">add</span>
              New proposal
            </button>
            <button className="bg-white border border-outline-variant/40 text-primary font-bold px-5 py-3 rounded-lg hover:bg-surface-container-low transition-colors flex items-center gap-2" onClick={() => importRef.current && importRef.current.click()}>
              <span className="material-symbols-outlined">upload</span>
              Import
            </button>
            <button className="bg-white border border-outline-variant/40 text-primary font-bold px-5 py-3 rounded-lg hover:bg-surface-container-low transition-colors flex items-center gap-2" onClick={() => setShowPropAdmin(true)}>
              <span className="material-symbols-outlined">admin_panel_settings</span>
              Proposal admin
            </button>
            <button className="bg-white border border-outline-variant/40 text-primary font-bold px-5 py-3 rounded-lg hover:bg-surface-container-low transition-colors flex items-center gap-2" onClick={() => setShowSettings(true)}>
              <span className="material-symbols-outlined">settings</span>
              Settings
            </button>
            <input
              ref={importRef}
              type="file"
              accept="application/json"
              style={{ display: 'none' }}
              onChange={e => { handleImport(e.target.files && e.target.files[0]); e.target.value = ''; }}
            />
          </div>
        </div>
        {proposalsLoading ? (
          <div className="text-center py-12">Loading proposals...</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <span className="material-symbols-outlined text-6xl text-gray-300 mb-4">description</span>
            <h3 className="text-xl font-bold text-gray-600 mb-2">No proposals yet</h3>
            <p className="text-gray-500 mb-6">Create your first proposal, or add a solution or case study to automatically create one.</p>
            <button className="bg-navy text-white font-bold px-6 py-3 rounded-lg hover:bg-navy-light transition-colors" onClick={() => setShowNewProposal(true)}>Create proposal</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.map(p => {
              const readiness = (() => {
                const checks = [
                  ['Customer named', !!p.clientName],
                  ['Services selected', (p.solutions || []).length > 0],
                  ['Scope defined', (p.scopeItems || []).length > 0],
                  ['Team named', (p.team || []).length > 0]
                ];
                return Math.round(checks.filter(c => c[1]).length / checks.length * 100);
              })();

              const displayStatus = p.approval_status || p.approvalStatus || p.status || 'Draft';
              const getStatusColor = (status) => {
                const colors = {
                  'Draft': 'bg-gray-100 text-gray-600',
                  'Pending': 'bg-yellow-100 text-yellow-700',
                  'Approved': 'bg-green-100 text-green-700',
                  'Rejected': 'bg-red-100 text-red-700',
                  'Changes Requested': 'bg-blue-100 text-blue-700',
                  'Sent to customer': 'bg-purple-100 text-purple-700',
                  'Under negotiation': 'bg-orange-100 text-orange-700',
                  'Won': 'bg-green-100 text-green-700',
                  'Lost': 'bg-red-100 text-red-700',
                  'On hold': 'bg-gray-100 text-gray-600'
                };
                return colors[status] || 'bg-gray-100 text-gray-600';
              };
              const getStatusLabel = (status) => {
                const labels = {
                  'Draft': 'Draft',
                  'Pending': 'Pending Approval',
                  'Approved': 'Approved',
                  'Rejected': 'Rejected',
                  'Changes Requested': 'Changes Requested',
                  'Sent to customer': 'Sent to Customer',
                  'Under negotiation': 'Under Negotiation',
                  'Won': 'Won',
                  'Lost': 'Lost',
                  'On hold': 'On Hold'
                };
                return labels[status] || status || 'Draft';
              };

              return (
                <div key={p.uid} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold text-red-600 uppercase tracking-wider">{p.id}</span>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${getStatusColor(displayStatus)}`}>
                      {getStatusLabel(displayStatus)}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-1">{p.title || 'Untitled'}</h3>
                  <p className="text-gray-600 text-sm mb-3">{p.clientName || 'No customer specified'}</p>
                  <div className="flex gap-3 mb-3 text-sm text-gray-500">
                    <span>{p.solutions?.length || 0} solutions</span>
                    <span>•</span>
                    <span>{(p.scopeItems || []).length} scope items</span>
                    <span>•</span>
                    <span>{(p.team || []).length} team members</span>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-600 rounded-full" style={{ width: readiness + '%' }}></div>
                    </div>
                    <span className="text-xs font-bold text-gray-600">{readiness}%</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button className="bg-navy text-white font-bold px-4 py-2 rounded-lg text-sm hover:bg-navy-light transition-colors" onClick={() => {
                      setActiveUid(p.uid);
                      setPropView('editor');
                    }}>Open</button>
                    <button className="bg-white text-navy border border-gray-300 font-bold px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors" onClick={() => handleGenerate(p.uid)}>Generate</button>
                    <button className="bg-white text-navy border border-gray-300 font-bold px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors" onClick={() => handleDuplicate(p.uid)}>Duplicate</button>
                    <button className="bg-white text-navy border border-gray-300 font-bold px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors" onClick={() => handleExport(p.uid)}>Export</button>
                    <button className="bg-white text-red-700 border border-red-200 font-bold px-4 py-2 rounded-lg text-sm hover:bg-red-50 transition-colors"
                      onClick={() => setConfirmDelete(p.uid)}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showNewProposal && (
          <NewProposalModal
            onClose={() => setShowNewProposal(false)}
            onCreated={(saved, scopeCount) => {
              setShowNewProposal(false);
              notify('Draft created – ' + scopeCount + ' scope lines derived');
              loadProposals();
              setActiveUid(saved.uid);
              setPropView('editor');
            }}
          />
        )}

        {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}

        {confirmDelete && (
          <div className="modalback" onClick={() => setConfirmDelete(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <h2>Delete Proposal</h2>
              <p>Are you sure you want to delete this proposal? This action cannot be undone.</p>
              <div className="modalactions">
                <button className="btn line" onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button className="btn danger" onClick={async () => {
                  try {
                    await deleteProposal(confirmDelete);
                    setConfirmDelete(null);
                    notify('Proposal deleted');
                  } catch (err) {
                    console.error('Delete failed:', err);
                    alert('Could not delete: ' + (err.response?.data?.error || err.message));
                  }
                }}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSolutions = () => {
    const cat = solCat ? byCat(solCat) : null;
    const subObj = cat && cat.subs ? cat.subs[solSub] : null;

    // The filter bar always shows; a live filter collapses the drill-down into
    // a flat list of matches, exactly as the HTML build does.
    const showResults = filtersActive() || solMode !== 'areas';

    return (
      <div className="flex-grow pt-10 pb-24 max-w-container-max mx-auto w-full relative z-10">
        {solMode === 'areas' && (
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="pagekicker">SolidPro solutions</div>
              <h1 className="font-headline-xl text-headline-xl text-primary-container mb-2">Explore SolidPro solutions</h1>
              <p className="font-body-lg text-body-lg text-on-surface-variant">
                Start with a solution area, or use the filters to find the exact SolidPro solution for an industry, capability or standard.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="bg-surface-container-lowest px-3 py-1.5 rounded-md border border-outline-variant/40 font-semibold text-on-surface-variant">
                {matchedSolutions.length} of {SOLS.length} solutions
              </span>
            </div>
          </div>
        )}

        {solMode === 'cat' && cat && (
          <>
            <div className="detailnav">
              <button className="detailback" onClick={browseBack}>
                <span className="material-symbols-outlined">arrow_back</span> Back
              </button>
              <Crumbs trail={[{ label: 'Solutions', go: backToAreas }, { label: cat.name }]} />
            </div>
            <div className="mb-10">
              <div className="pagekicker">Solution area</div>
              <h1 className="font-headline-xl text-headline-xl text-primary-container mb-2">{cat.name}</h1>
              <p className="font-body-lg text-body-lg text-on-surface-variant">{cat.display_short || cat.short}</p>
            </div>
          </>
        )}

        {solMode === 'sub' && cat && subObj && (
          <>
            <div className="detailnav">
              <button className="detailback" onClick={browseBack}>
                <span className="material-symbols-outlined">arrow_back</span> Back
              </button>
              <Crumbs trail={[
                { label: 'Solutions', go: backToAreas },
                { label: cat.name, go: () => openCat(cat.id) },
                { label: subObj.name }
              ]} />
            </div>
            <div className="mb-8">
              <div className="pagekicker">Focus area</div>
              <h1 className="font-headline-xl text-headline-xl text-primary-container mb-2">{subObj.name}</h1>
              <p className="font-body-lg text-body-lg text-on-surface-variant">
                {subObj.description || 'Explore the SolidPro solutions and the case studies directly related to this focus area.'}
              </p>
            </div>
          </>
        )}

        {/* Only the landing page carries the filter. A deeper level is already
            a narrowing, and repeating it there pushes the content off screen. */}
        {solMode === 'areas' && (
          <div className="filterbar">
            {/* search first, full width - it is how most people start */}
            <div className="filtersearch">
              <input
                value={solFilters.q}
                onChange={e => setSolFilters({ ...solFilters, q: e.target.value })}
                placeholder="Search solution, capability, tool or standard"
              />
            </div>

            {/* then the narrowing options */}
            <div className="filterrow">
              <div className="ff">
                <label>Solution area</label>
                <select value={solFilters.area} onChange={e => setSolFilters({ ...solFilters, area: e.target.value, focus: 'All' })}>
                  <option value="All">All</option>
                  {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="ff">
                <label>Focus area</label>
                <select value={solFilters.focus} onChange={e => setSolFilters({ ...solFilters, focus: e.target.value })}>
                  <option value="All">All</option>
                  {FOCUS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div className="ff">
                <label>Industry</label>
                <select value={solFilters.industry} onChange={e => setSolFilters({ ...solFilters, industry: e.target.value })}>
                  <option value="All">All</option>
                  {INDUSTRIES.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="ff">
                <label>Standard</label>
                <select value={solFilters.std} onChange={e => setSolFilters({ ...solFilters, std: e.target.value })}>
                  <option value="All">All</option>
                  {STDS.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <button
                className="btn line filterclear"
                disabled={!activeSolChips.length && !solFilters.q}
                onClick={() => { setSolFilters({ ...SOL_DEFAULTS }); setVisibleSolutions(SOLUTIONS_PER_PAGE); }}
              >
                Clear
              </button>
            </div>

            {activeSolChips.length > 0 && (
              <div className="filterchips">
                {activeSolChips.map(x => <span key={x} className="activechip">{x}</span>)}
              </div>
            )}
          </div>
        )}

        {/* --- a live filter always wins: show the matches, wherever you are --- */}
        <div ref={browseTop} />

        {filtersActive() ? (
          <>
            <div className="subhead">
              <span className="subhead-l">Matching solutions</span>
              <span className="subhead-r">{matchedSolutions.length} result{matchedSolutions.length === 1 ? '' : 's'}</span>
            </div>
            <div className="grid2">
              {visibleSolutionsList.map(sol => <SolCard key={sol.id} sol={sol} />)}
            </div>
            {visibleSolutionsList.length === 0 && (
              <div className="empty">No solution matches these filters.</div>
            )}
            {hasMoreSolutions && (
              <div className="load-more-container">
                <button className="btn line" onClick={() => setVisibleSolutions(prev => prev + SOLUTIONS_PER_PAGE)}>
                  Show more solutions
                </button>
              </div>
            )}
          </>
        ) : solMode === 'areas' ? (
          /* --- the solution areas --- */
          <div className="grid2">
            {orderedCats.map(c => {
              const count = (c.subs || []).reduce((a, x) => a + (x.sols || []).length, 0);
              return (
                <button key={c.id} className="domain" onClick={() => openCat(c.id)}>
                  <div className="icon">{AREA_ICON[c.id] || c.id}</div>
                  <h3>{c.name}</h3>
                  <p>{c.display_short || c.short}</p>
                  {(c.hi || []).length ? <div className="promise">{c.hi.join(' · ')}</div> : null}
                  <div className="meta">{count} solutions · {(c.subs || []).length} focus areas</div>
                </button>
              );
            })}
          </div>
        ) : solMode === 'cat' && cat ? (
          /* --- the focus areas within one solution area --- */
          <div className="grid2">
            {(cat.subs || []).map((x, idx) => (
              <button key={x.name} className="subcard" onClick={() => openSub(idx)}>
                {x.tagline ? <div className="tagline">{x.tagline}</div> : null}
                <h3>{x.name}</h3>
                <p>{x.description || (x.sols || []).slice(0, 4).map(y => y.title).join(' · ')}</p>
                <div className="pills"><span className="pill">{(x.sols || []).length} solutions</span></div>
              </button>
            ))}
          </div>
        ) : subObj ? (
          /* --- one focus area: its solutions, or the cases that used them --- */
          <>
            <div className="seg">
              <button className={subView === 'sols' ? 'on' : ''} onClick={() => setSubView('sols')}>
                Solutions ({(subObj.sols || []).length})
              </button>
              <button className={subView === 'cases' ? 'on' : ''} onClick={() => setSubView('cases')}>
                Case Studies ({casesForSub(subObj).length})
              </button>
            </div>
            {subView === 'sols' ? (
              <div className="grid2">
                {(subObj.sols || []).map(sol => <SolCard key={sol.id} sol={sol} />)}
              </div>
            ) : casesForSub(subObj).length ? (
              <div className="grid2">
                {casesForSub(subObj).map(c => <CaseCard key={c.id} c={c} />)}
              </div>
            ) : (
              <div className="empty">No case study is linked to this focus area yet.</div>
            )}
          </>
        ) : null}
      </div>
    );
  };

  const renderCases = () => {
    return (
      <div className="flex-grow pt-10 pb-24 max-w-container-max mx-auto w-full relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <h1 className="font-headline-xl text-headline-xl text-primary-container mb-2">Case Studies</h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant">Real projects, real outcomes.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="bg-surface-container-lowest px-3 py-1.5 rounded-md border border-outline-variant/40 font-semibold text-on-surface-variant">{matchedCases.length} of {cases.length} case studies</span>
          </div>
        </div>
        <div className="filterbar">
          <div className="filtersearch">
            <input
              value={caseFilters.q}
              onChange={e => setCaseFilters({ ...caseFilters, q: e.target.value })}
              placeholder="Search Case ID, industry, result, technology or solution"
            />
          </div>

          <div className="filterrow filterrow-3">
            <div className="ff">
              <label>Pillar</label>
              <select value={caseFilters.pillar} onChange={e => setCaseFilters({ ...caseFilters, pillar: e.target.value })}>
                <option value="All">All</option>
                {pillarOptions().map(x => (
                  <option key={x.name} value={x.name} disabled={x.n === 0}>{x.name} ({x.n})</option>
                ))}
              </select>
            </div>
            <div className="ff">
              <label>Industry</label>
              <select value={caseFilters.industry} onChange={e => setCaseFilters({ ...caseFilters, industry: e.target.value })}>
                <option value="All">All</option>
                {industryOptions().map(x => <option key={x.name} value={x.name}>{x.name} ({x.n})</option>)}
              </select>
            </div>
            <div className="ff">
              <label>Solution area</label>
              <select value={caseFilters.area} onChange={e => setCaseFilters({ ...caseFilters, area: e.target.value })}>
                <option value="All">All ({areaOptions().reduce((a, g) => a + g.subs.length, 0)})</option>
                {areaOptions().map(g => (
                  <optgroup key={g.cat} label={g.cat}>
                    <option value={g.cat}>Everything in {g.cat}</option>
                    {g.subs.map(x => <option key={x.name} value={x.name}>{x.name} ({x.n})</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <button
              className="btn line filterclear"
              disabled={!activeCaseChips.length && !caseFilters.q}
              onClick={() => setCaseFilters({ ...CASE_DEFAULTS })}
            >
              Clear
            </button>
          </div>

          <div className="filterchips">
            <span className="filtercount">{matchedCases.length} of {cases.length} case studies</span>
            {activeCaseChips.map(x => <span key={x} className="activechip">{x}</span>)}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {matchedCases.map(c => (
            <div key={c.id} className="case-card bg-surface-container-lowest border border-outline-variant/40 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start gap-3 mb-2">
                <span className="text-xs font-bold text-solid-red uppercase tracking-wider">CASE ID: {c.caseId || c.case_id || c.id}</span>
                <span className="text-xs font-bold bg-surface-container-low px-2 py-1 rounded-md text-on-surface-variant whitespace-nowrap">{c.pillar}</span>
              </div>
              <h3 className="font-headline-lg-mobile text-headline-lg-mobile text-primary-container cursor-pointer" onClick={() => navigate(`/case/${c.id}`)}>{c.title}</h3>
              <p className="text-sm text-on-surface-variant mt-1">{c.sector}</p>
              {((c.result || [])[0] || c.problem) && (
                <p className="caseresult">{(c.result || [])[0] || c.problem}</p>
              )}
              {(c.industries || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {c.industries.slice(0, 3).map(i => <span key={i} className="mini">{i}</span>)}
                  {c.industries.length > 3 && <span className="mini">+{c.industries.length - 3}</span>}
                </div>
              )}
              {(c.metrics || []).length > 0 && (
                <div className="metrics">
                  {/* Accent only a genuine movement - a before/after arrow or a
                      percentage. Matching any digit accented four metrics in
                      five, which highlights nothing. */}
                  {c.metrics.slice(0, 4).map(m => (
                    <span key={m} className={`metric ${/→|->|%/.test(m) ? 'shift' : ''}`}>{m}</span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <button className="bg-navy text-white font-bold px-4 py-2 rounded-lg text-sm hover:bg-navy-light transition-colors" onClick={() => navigate(`/case/${c.id}`)}>Open full case</button>
                <button className="bg-white text-navy border border-gray-300 font-bold px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors" onClick={() => {
                  if (user?.role !== 'admin') { notify('Admin only has access. Please contact your admin to add this to a proposal.'); return; }
                  const count = addPending('cases', c.id);
                  notify(`"${c.title}" added to your proposal selection (${count} case${count > 1 ? 's' : ''} pending).`);
                }}>Add to Proposal</button>
                <button className="bg-white text-navy border border-gray-300 font-bold px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors" onClick={async (e) => {
                  const btn = e.currentTarget;
                  btn.disabled = true;
                  await downloadPdf(caseBriefHTML(c), `${safe(c.caseId || c.case_id || c.id)}.pdf`, m => { btn.textContent = m || 'Download'; });
                  btn.disabled = false; btn.textContent = 'Download';
                }}>Download</button>
              </div>
            </div>
          ))}
        </div>
        {matchedCases.length === 0 && (
          <div className="text-center py-16">
            <span className="material-symbols-outlined text-6xl text-gray-300">search</span>
            <p className="text-text-gray mt-4">No case studies match your filters.</p>
          </div>
        )}
      </div>
    );
  };

  if (tab === 'proposal') {
    if (propView === 'editor') {
      return (
        <div>
          <TopBar tab={tab} setTab={setTab} onSignOut={() => { logout(); navigate('/login'); }} user={user} navigate={navigate} />
          {showPropAdmin && <ProposalAdmin onClose={() => setShowPropAdmin(false)} />}
          <main className="w-full max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-8 md:py-12 relative">
            <div className="absolute top-0 left-0 w-full h-96 bg-radial-hero pointer-events-none z-[-1]"></div>
            <ProposalModule 
              activeUid={activeUid} 
              onBack={() => {
                setPropView('list');
                setActiveUid(null);
                loadProposals();
              }}
              onProposalUpdated={(updated) => {
                loadProposals();
              }}
            />
          </main>
          <ScrollNav />
          <Footer />
        </div>
      );
    }
    return (
      <div>
        <TopBar tab={tab} setTab={setTab} onSignOut={() => { logout(); navigate('/login'); }} user={user} navigate={navigate} />
        {propToast && <div className="proptoast">{propToast}</div>}
        {showPropAdmin && <ProposalAdmin onClose={() => setShowPropAdmin(false)} />}
        <main className="w-full max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-8 md:py-12 relative">
          <div className="absolute top-0 left-0 w-full h-96 bg-radial-hero pointer-events-none z-[-1]"></div>
          {renderProposalList()}
        </main>
        <ScrollNav />
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <TopBar tab={tab} setTab={setTab} onSignOut={() => { logout(); navigate('/login'); }} user={user} navigate={navigate} />
      {propToast && <div className="proptoast">{propToast}</div>}
      <main className="w-full max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-8 md:py-12 relative">
        <div className="absolute top-0 left-0 w-full h-96 bg-radial-hero pointer-events-none z-[-1]"></div>
        {showPropAdmin && <ProposalAdmin onClose={() => setShowPropAdmin(false)} />}
        {tab === 'solutions' && renderSolutions()}
        {tab === 'capabilities' && <CapabilitiesView data={data} industries={industries} />}
        {tab === 'cases' && renderCases()}
      </main>
      <ScrollNav />
      <Footer />
    </div>
  );
};

// A failed API call used to render a bare "Error: Network Error", which says
// nothing about which address was tried or how to recover. If a custom backend
// address is what broke it, offer the way back.
const ApiError = ({ error }) => {
  const base = api.defaults.baseURL;
  const custom = !!storedApiBase();
  return (
    <div className="p-8 text-center">
      <p className="text-red-600 font-bold">Could not reach the server: {error}</p>
      <p className="text-sm text-gray-600 mt-2">Tried <code>{base}</code></p>
      <p className="text-sm text-gray-600 mt-1">
        {custom
          ? 'That is a custom backend address saved under Proposals → Settings.'
          : 'Check the backend is running — in the backend folder run: node server.js'}
      </p>
      <div className="flex gap-3 justify-center mt-4 flex-wrap">
        <button className="bg-navy text-white font-bold px-6 py-2 rounded-lg" onClick={() => window.location.reload()}>Try again</button>
        {custom && (
          <button className="bg-white border border-gray-300 text-navy font-bold px-6 py-2 rounded-lg"
            onClick={() => { setApiBase(''); window.location.reload(); }}>
            Reset to the default address
          </button>
        )}
      </div>
    </div>
  );
};

// ===== DETAIL COMPONENTS =====
const useLibraryData = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        const res = await api.get('/library');
        setData(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching library data:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load library data');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return { data, loading, error };
};

// ===== ADD TO PROPOSAL (library -> proposal) =====
// The proposals screen tells users to use "Add to Proposal" from any solution or
// case study. Selections are parked in localStorage and picked up by the
// proposal editor, so this works from a detail page that has no proposal loaded.
const PENDING_KEY = 'solidpro-pending-additions';

const loadPending = () => {
  try { return Object.assign({ solutions: [], cases: [] }, JSON.parse(localStorage.getItem(PENDING_KEY) || '{}')); }
  catch (e) { return { solutions: [], cases: [] }; }
};
const savePending = (v) => { try { localStorage.setItem(PENDING_KEY, JSON.stringify(v)); } catch (e) {} };
const addPending = (kind, id) => {
  const cur = loadPending();
  if (!cur[kind].includes(id)) cur[kind].push(id);
  savePending(cur);
  return cur[kind].length;
};
const clearPending = () => savePending({ solutions: [], cases: [] });

// Applies anything parked in localStorage to a proposal, then clears it.
const applyPending = (p) => {
  const pend = loadPending();
  if (!pend.solutions.length && !pend.cases.length) return { p, added: 0 };
  const np = { ...p };
  np.solutions = uniq([...(np.solutions || []), ...pend.solutions]);
  np.manualCases = uniq([...(np.manualCases || []), ...pend.cases]);
  recomputeAuto(np);
  const added = pend.solutions.length + pend.cases.length;
  clearPending();
  return { p: np, added };
};

// ===== STANDALONE BRIEF EXPORT =====
// Self-contained HTML for a single solution or case, so a salesperson can email
// one file. Styles are inlined; nothing depends on the app being available.
const standaloneStyles = () => `
:root{--navy:#092b57;--red:#e52732;--ink:#14263a;--muted:#687b8d;--line:#dce5ed;--bg:#f3f6f9;--green:#187159}
*{box-sizing:border-box}body{margin:0;background:var(--bg);font-family:Inter,Arial,sans-serif;color:var(--ink);line-height:1.62}
.top{background:#fff;border-bottom:1px solid var(--line);padding:14px 22px}
.topin{max-width:1050px;margin:auto;display:flex;justify-content:space-between;align-items:center}
.sid{font-size:11px;color:var(--red);font-weight:900;letter-spacing:.07em;text-align:right}
.wrap{max-width:1050px;margin:auto;padding:28px 20px 55px}
.paper{background:#fff;border:1px solid var(--line);border-radius:17px;padding:32px}
.kicker{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--red);font-weight:900}
h1{font-size:36px;line-height:1.15;letter-spacing:-.035em;margin:10px 0 13px}
.lead{font-size:17px;color:#24425f}
.docsec{border-top:1px solid var(--line);padding-top:20px;margin-top:22px}
.docsec h2{font-size:18px;margin:0 0 9px}
.bullets{list-style:none;padding:0}
.bullets li{position:relative;padding-left:19px;margin:8px 0}
.bullets li:before{content:'';position:absolute;left:0;top:.62em;width:7px;height:7px;background:var(--red)}
.tags{display:flex;gap:7px;flex-wrap:wrap}
.tag{font-size:11px;padding:6px 8px;border-radius:7px;background:#f1f6fa;border:1px solid var(--line);color:var(--navy)}
.tag.std{background:#eef7f2;color:var(--green)}
.gets{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.get{border:1px solid var(--line);border-left:3px solid var(--red);border-radius:9px;padding:12px}
.get b{display:block}.get span{font-size:12px;color:var(--muted)}
table{width:100%;border-collapse:collapse;margin-top:16px}
th,td{border-top:1px solid var(--line);padding:16px 12px;vertical-align:top;text-align:left}
th{width:23%;font-size:10.5px;text-transform:uppercase;letter-spacing:.07em;color:#66788a}
td{font-size:14.5px}
.metrics{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:9px;margin:20px 0}
.metric{background:#f4f8fb;border:1px solid var(--line);border-radius:9px;padding:11px;color:var(--navy);font-weight:900;font-size:13px}
.story{font-size:16px;line-height:1.72;background:#f6f9fb;border-left:4px solid var(--red);padding:15px 17px;border-radius:9px}
.actions{display:flex;gap:8px;flex-wrap:wrap;margin-top:18px}
.btn{border:0;border-radius:9px;padding:10px 13px;font-weight:800;cursor:pointer}
.red{background:var(--red);color:#fff}.line{background:#fff;border:1px solid var(--line);color:var(--navy)}
.scope{background:#F7FBFF;border:1px solid #CFE0EE;border-radius:12px;padding:16px}
.scope h2{font-size:15px;margin:0 0 6px}
.scope p{margin:0;color:var(--muted);font-size:13px}
.twocolbrief{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.twocolbrief .card{border:1px solid var(--line);border-radius:10px;padding:12px}
.twocolbrief .card h2{font-size:13px;margin:0 0 6px}
.twocolbrief .card p{margin:0;color:var(--muted);font-size:12.5px}
@media(max-width:650px){.twocolbrief{grid-template-columns:1fr}}
@media(max-width:650px){.gets,.metrics{grid-template-columns:1fr}.paper{padding:20px 15px}h1{font-size:29px}}
@media print{body{background:#fff}.wrap{padding:0}.paper{border:0}.actions{display:none!important}}
`;

// The long-form write-up, or null when there is nothing in it. An empty object
// is what the seeder stores for a solution that has none, and {} is truthy -
// so this has to look at the contents, not the reference.
const RICH_SECTIONS = ['intro', 'come', 'helps', 'gets', 'diff', 'exp', 'journey', 'questions', 'scope', 'needFromYou', 'notCover'];
const richOf = (sol) => {
  const r = sol && sol.rich;
  if (!r || typeof r !== 'object') return null;
  const has = RICH_SECTIONS.some(k => Array.isArray(r[k]) ? r[k].length > 0 : !!r[k]);
  return has ? r : null;
};

const solutionBriefHTML = (s) => {
  const r = richOf(s) || {};
  const body = r ? `
    <p class="lead">${esc((r.intro || [])[0] || s.what)}</p>
    ${(r.intro || []).slice(1).map(x => `<p>${esc(x)}</p>`).join('')}
    ${(r.come || []).length
      ? `<section class="docsec"><h2>When customers come to us</h2><ul class="bullets">${r.come.map(x => `<li>${esc(x)}</li>`).join('')}</ul></section>`
      : (s.when ? `<section class="docsec"><h2>Where this helps</h2><p>${esc(s.when)}</p></section>` : '')}
    ${(r.helps || []).length ? `<section class="docsec"><h2>How SolidPro helps</h2>${r.helps.map(x => `<p>${esc(x)}</p>`).join('')}</section>` : ''}
    ${(r.gets || []).length
      ? `<section class="docsec"><h2>What you get from SolidPro</h2><div class="gets">${r.gets.map(g => `<div class="get"><b>${esc(g[0])}</b><span>${esc(g[1])}</span></div>`).join('')}</div></section>`
      : ((s.get || []).length ? `<section class="docsec"><h2>What SolidPro provides</h2><ul class="bullets">${s.get.map(x => `<li>${esc(x)}</li>`).join('')}</ul></section>` : '')}
    ${(r.diff || []).length ? `<section class="docsec"><h2>The SolidPro difference</h2>${r.diff.map(x => `<p>${esc(x)}</p>`).join('')}</section>` : ''}
    ${(r.exp || []).length
      ? `<section class="docsec"><h2>Experience behind the solution</h2><div class="tags">${r.exp.map(x => `<span class="tag">${esc(x)}</span>`).join('')}</div></section>`
      : ((s.cap || []).length ? `<section class="docsec"><h2>Skills, tools &amp; methods</h2><div class="tags">${s.cap.map(x => `<span class="tag">${esc(x)}</span>`).join('')}</div></section>` : '')}
    ${r.scope && r.scope.text ? `<section class="docsec"><div class="scope"><h2>${esc(r.scope.heading || 'Scope boundary / validation gate')}</h2><p>${esc(r.scope.text)}</p></div></section>` : ''}
    ${(r.needFromYou || r.notCover) ? `<section class="docsec"><div class="twocolbrief">${r.needFromYou ? `<div class="card"><h2>What we need from you</h2><p>${esc(r.needFromYou)}</p></div>` : ''}${r.notCover ? `<div class="card"><h2>What this solution does not cover</h2><p>${esc(r.notCover)}</p></div>` : ''}</div></section>` : ''}
  ` : `
    <p class="lead">${esc(s.what)}</p>
    ${s.when ? `<section class="docsec"><h2>Where this helps</h2><p>${esc(s.when)}</p></section>` : ''}
    ${(s.get || []).length ? `<section class="docsec"><h2>What SolidPro provides</h2><ul class="bullets">${s.get.map(x => `<li>${esc(x)}</li>`).join('')}</ul></section>` : ''}
    ${(s.cap || []).length ? `<section class="docsec"><h2>Skills, tools & methods</h2><div class="tags">${s.cap.map(x => `<span class="tag">${esc(x)}</span>`).join('')}</div></section>` : ''}
  `;
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${esc(s.id)} · ${esc(s.title)}</title><style>${standaloneStyles()}</style></head><body>
<div class="top"><div class="topin"><b>SolidPro</b><div class="sid">SOLUTION ID: ${esc(s.id)}<br>${esc(s.cat || '')} · ${esc(s.sub || '')}</div></div></div>
<main class="wrap"><article class="paper"><div class="kicker">SolidPro Solution Brief</div><h1>${esc((richOf(s) && richOf(s).hero) || s.title)}</h1>
${body}
${(s.industries || []).length ? `<section class="docsec"><h2>Industries</h2><div class="tags">${s.industries.map(x => `<span class="tag">${esc(x)}</span>`).join('')}</div></section>` : ''}
${(s.std || []).length ? `<section class="docsec"><h2>Standards & frameworks</h2><div class="tags">${s.std.map(x => `<span class="tag std">${esc(x)}</span>`).join('')}</div></section>` : ''}
<div class="actions"><button class="btn red" onclick="dl()">Download solution brief</button><button class="btn line" onclick="window.print()">Print / Save as PDF</button></div>
</article></main>
<script>function dl(){var b=new Blob([document.documentElement.outerHTML],{type:'text/html;charset=utf-8'}),a=document.createElement('a');a.href=URL.createObjectURL(b);a.download='solidpro-${safe(s.id)}.html';a.click()}<\/script>
</body></html>`;
};

const caseBriefHTML = (e) => {
  const story = `${e.client || ''} ${e.problem || ''} SolidPro approached the requirement by ${lower(e.approach || '')}${(e.result || []).length ? ' The result included ' + lower(e.result.join('; ')) + '.' : ''}`;
  const row = (h, v) => v ? `<tr><th>${esc(h)}</th><td>${v}</td></tr>` : '';
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${esc(e.caseId || e.id)} · ${esc(e.title)}</title><style>${standaloneStyles()}</style></head><body>
<div class="top"><div class="topin"><b>SolidPro</b><div class="sid">CASE ID: ${esc(e.caseId || e.id)}<br>${esc(e.pillar || '')}</div></div></div>
<main class="wrap"><article class="paper">
<div class="tags"><span class="tag">${esc(e.pillar || '')}</span>${(e.industries || []).map(x => `<span class="tag">${esc(x)}</span>`).join('')}</div>
<h1>${esc(e.title)}</h1><div style="color:#2367a4;font-weight:800">${esc(e.sector || '')}</div>
${(e.metrics || []).length ? `<div class="metrics">${e.metrics.slice(0, 8).map(x => `<div class="metric">${esc(x)}</div>`).join('')}</div>` : ''}
<div class="story">${esc(story)}</div>
<table>
${row('About the customer', esc(e.customerProfile || e.client || ''))}
${row('Challenge', esc(e.problem || ''))}
${row('SolidPro approach', esc(e.approach || ''))}
${row('Work delivered', (e.delivered || []).length ? `<ul>${e.delivered.map(x => `<li>${esc(x)}</li>`).join('')}</ul>` : '')}
${row('Tools & methods', (e.tech || []).map(esc).join(' · '))}
${row('Value / outcomes', (e.result || []).length ? `<ul>${e.result.map(x => `<li>${esc(x)}</li>`).join('')}</ul>` : '')}
${row('Timeline', esc(e.timeline || e.note || ''))}
${row('What this case shows', esc(e.proves || ''))}
</table>
<div class="actions"><button class="btn red" onclick="dl()">Download case study</button><button class="btn line" onclick="window.print()">Print / Save as PDF</button></div>
</article></main>
<script>function dl(){var b=new Blob([document.documentElement.outerHTML],{type:'text/html;charset=utf-8'}),a=document.createElement('a');a.href=URL.createObjectURL(b);a.download='${safe(e.caseId || e.id)}.html';a.click()}<\/script>
</body></html>`;
};

// "Back" should undo the last step, not jump to the top of the library. When
// the page was opened directly - a shared link, a new tab - there is nothing to
// go back to, so fall through to the library.
const goBackOrLibrary = (navigate) => {
  if (window.history.length > 1) navigate(-1);
  else navigate('/library');
};

// Where the library was left. The detail pages write to it too, so a crumb can
// open the library at the level it names instead of at the top.
const BROWSE_KEY = 'solidpro-browse';
const setBrowse = (position) => {
  try {
    const cur = JSON.parse(sessionStorage.getItem(BROWSE_KEY) || '{}');
    sessionStorage.setItem(BROWSE_KEY, JSON.stringify({ ...cur, ...position }));
  } catch (e) {}
};
const goToLibrary = (navigate, position) => { setBrowse(position); navigate('/library'); };

// The bar at the top of a detail page: a way back, and the trail that says
// where this sits. Without it the only way out is the browser button or a
// scroll to the bottom of the page.
const DetailNav = ({ navigate, trail }) => (
  <div className="detailnav">
    <button className="detailback" onClick={() => goBackOrLibrary(navigate)}>
      <span className="material-symbols-outlined">arrow_back</span> Back
    </button>
    <div className="crumb">
      {trail.map((x, i) => i === trail.length - 1
        ? <span key={i}>{x.label}</span>
        : <React.Fragment key={i}>
            <button onClick={x.go}>{x.label}</button>
            <span className="sep">›</span>
          </React.Fragment>)}
    </div>
  </div>
);

const downloadBrief = (html, filename) => {
  const b = new Blob([html], { type: 'text/html;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(b);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

// Turns one of the standalone brief documents into a PDF the browser saves
// directly, rather than handing the user an .html file to deal with.
//
// The brief is a complete document string, so it has to be mounted before it
// can be measured: html2pdf rasterises what is laid out on the page. It is
// mounted off-screen at A4 content width so the line breaks match the real
// thing, then removed again.
// Turns one of the standalone brief documents into a PDF the browser saves
// directly, rather than handing the user an .html file to deal with.
//
// The brief is rendered inside its own iframe. An earlier version mounted it in
// the host page at left:-10000px, but html2canvas clones the node along with
// that positioning and photographs empty space - which is why the PDF came out
// blank. Inside an iframe the document sits at the origin of its own viewport,
// which is what the capture expects.
const A4_CONTENT_PX = 794;   // 210mm at 96dpi

const downloadPdf = async (html, filename, onState) => {
  const note = (m) => { if (onState) onState(m); };
  let frame = null;
  try {
    note('Preparing…');
    const { default: html2pdf } = await import('html2pdf.js');

    frame = document.createElement('iframe');
    // Off-screen is fine for the frame itself; what matters is that the
    // document inside it lays out at full width.
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText =
      'position:fixed;left:0;top:0;width:' + A4_CONTENT_PX + 'px;height:1200px;' +
      'border:0;opacity:0;pointer-events:none;z-index:-1';
    document.body.appendChild(frame);

    const fdoc = frame.contentDocument;
    fdoc.open();
    // Strip the document's own on-screen controls - they belong in a browser,
    // not in a saved file.
    fdoc.write(html.replace(/<div class="actions"[\s\S]*?<\/div>/g, ''));
    fdoc.close();

    // Wait for the frame to finish, and for its webfonts, or the capture
    // catches a half-laid-out page in a fallback face.
    await new Promise(resolve => {
      if (fdoc.readyState === 'complete') return resolve();
      frame.onload = resolve;
      setTimeout(resolve, 3000);
    });
    if (fdoc.fonts && fdoc.fonts.ready) { try { await fdoc.fonts.ready; } catch (e) {} }
    await new Promise(r => setTimeout(r, 120));

    const bodyEl = fdoc.body;
    // Emptiness is about content. A failed measurement is not the same thing,
    // so it must not stop a perfectly good document from being rendered.
    if (!bodyEl.textContent.trim()) {
      throw new Error('the document came out empty');
    }
    const height = Math.max(bodyEl.scrollHeight, fdoc.documentElement.scrollHeight, 1200);
    // The frame has to be tall enough to contain the whole document, or the
    // capture stops at the fold.
    frame.style.height = height + 'px';

    // html2pdf's Worker clones only the node it's handed and re-attaches the
    // clone under THIS (outer) document's <body> before rasterizing it. Pass
    // it fdoc.body alone and the brief's own <style> tag - a sibling of body,
    // living in fdoc's <head> - never travels with the clone, so every custom
    // class (.paper, .get, .docsec, .tags...) renders with no styling at all:
    // exactly the "collapsed, no margin" look. Handing it the whole <html>
    // element instead carries the <style> tag along inside the same clone.
    const target = fdoc.documentElement;

    note('Building the PDF…');
    await html2pdf().set({
      margin: [10, 10, 12, 10],
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: A4_CONTENT_PX,
        height,
        windowWidth: A4_CONTENT_PX,
        windowHeight: height,
        scrollX: 0,
        scrollY: 0
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', '.get', '.factrow', '.metric', '.docsec', '.scope'] }
    }).from(target).save();
    note('');
    return true;
  } catch (err) {
    console.error('PDF export failed:', err);
    note('');
    // Never leave the user with nothing: fall back to the document itself.
    if (window.confirm('The PDF could not be produced. Download the document as HTML instead?\n\nYou can open it and use your browser\u2019s Print to PDF.')) {
      downloadBrief(html, filename.replace(/\.pdf$/i, '.html'));
    }
    return false;
  } finally {
    if (frame && frame.parentNode) frame.parentNode.removeChild(frame);
  }
};

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data, loading, error } = useLibraryData();
  const [toast, setToast] = useState('');
  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <ApiError error={error} />;
  if (!data) return <div className="p-8 text-center">No data available</div>;

  const caseData = data.cases.find(c => c.id === id);
  if (!caseData) return <div className="p-8 text-center">Case not found</div>;

  const displayCaseId = caseData.caseId || caseData.case_id || caseData.CaseId || caseData.CASE_ID || caseData.id || 'N/A';
  const story = `${caseData.client || ''} ${caseData.problem || ''} SolidPro approached the requirement by ${caseData.approach || ''}${caseData.result?.length ? ' The result included ' + caseData.result.join('; ') + '.' : ''}`;

  return (
    <div>
      {toast && <div className="proptoast">{toast}</div>}
      <TopBar tab="cases" setTab={(t) => setBrowse({ tab: t })} onSignOut={() => { logout(); navigate('/login'); }} user={user} navigate={navigate} />
      <main className="flex-grow w-full max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-8 md:py-12">
        <DetailNav
          navigate={navigate}
          trail={[
            { label: 'Case Studies', go: () => goToLibrary(navigate, { tab: 'cases' }) },
            { label: caseData.title }
          ]}
        />
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-8 md:p-12">
            <div className="mb-8"><img className="logo-size" src="/logo.svg" alt="SolidPro" onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 60"%3E%3Crect width="200" height="60" fill="%23002147"/%3E%3Ctext x="20" y="40" font-family="Arial" font-size="28" font-weight="bold" fill="white"%3ESolidPro%3C/text%3E%3C/svg%3E'; }} /></div>
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-bold text-navy bg-tag-bg border border-gray-200">{caseData.pillar}</span>
              {(caseData.industries || []).map(i => <span key={i} className="inline-flex items-center px-3 py-1 rounded-md text-xs font-bold text-navy bg-tag-bg border border-gray-200">{i}</span>)}
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-navy mb-3 leading-tight">{caseData.title}</h2>
            <p className="text-lg font-bold text-navy-light mb-6">{caseData.sector}</p>
            <div className="text-left mb-6 text-sm font-bold text-solid-red"><span className="text-xs font-extrabold uppercase tracking-wider">Case ID:</span> {displayCaseId}</div>
            {caseData.metrics && caseData.metrics.length > 0 && (
              <section aria-label="Key Metrics" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                {caseData.metrics.map(metric => <div key={metric} className="bg-metric-bg border border-border-gray rounded-lg p-4 flex items-center justify-center text-center shadow-sm"><span className="font-bold text-navy">{metric}</span></div>)}
              </section>
            )}
            <section className="summary-box p-6 mb-10 text-text-gray leading-relaxed text-sm shadow-sm rounded-r-lg"><p>{story}</p></section>
            <section aria-label="Case Study Details" className="mb-12 border-t border-border-gray">
              {caseData.client && <div className="detail-row flex flex-col md:flex-row"><div className="detail-label font-bold">About the customer</div><div className="detail-content text-text-dark text-md">{caseData.client}</div></div>}
              {caseData.problem && <div className="detail-row flex flex-col md:flex-row"><div className="detail-label">Challenge</div><div className="detail-content text-text-dark text-md">{caseData.problem}</div></div>}
              {caseData.approach && <div className="detail-row flex flex-col md:flex-row"><div className="detail-label">SolidPro Approach</div><div className="detail-content text-text-dark text-md">{caseData.approach}</div></div>}
              {caseData.delivered && caseData.delivered.length > 0 && <div className="detail-row flex flex-col md:flex-row"><div className="detail-label">Work Delivered</div><div className="detail-content text-text-dark text-md"><ul className="bullet-list">{caseData.delivered.map((item, i) => <li key={i}>{item}</li>)}</ul></div></div>}
              {caseData.tech && caseData.tech.length > 0 && <div className="detail-row flex flex-col md:flex-row"><div className="detail-label">Tools & Methods</div><div className="detail-content text-text-dark text-md">{caseData.tech.join(' · ')}</div></div>}
              {caseData.result && caseData.result.length > 0 && <div className="detail-row flex flex-col md:flex-row"><div className="detail-label">Value / Outcomes</div><div className="detail-content text-text-dark text-md"><ul className="bullet-list">{caseData.result.map((item, i) => <li key={i}>{item}</li>)}</ul></div></div>}
              {caseData.timeline && <div className="detail-row flex flex-col md:flex-row"><div className="detail-label">Timeline</div><div className="detail-content text-text-dark text-md">{caseData.timeline}</div></div>}
              {caseData.proves && <div className="detail-row flex flex-col md:flex-row"><div className="detail-label">What this case shows</div><div className="detail-content text-text-dark text-md">{caseData.proves}</div></div>}
              {caseData.note && <div className="detail-row flex flex-col md:flex-row"><div className="detail-label">Note</div><div className="detail-content text-text-dark text-md">{caseData.note}</div></div>}
            </section>
            {caseData.sols && caseData.sols.length > 0 && <section className="mb-10 text-md text-text-gray space-y-1"><p><strong className="text-text-dark">Related SolidPro solutions:</strong> {caseData.sols.map(id => { const sol = data.cats.flatMap(c => c.subs.flatMap(s => s.sols)).find(s => s.id === id); return sol?.title; }).filter(Boolean).join(' · ')}</p></section>}
            {/* The provenance line ("SolidPro Case Study Brochure / IGCP
                Brochure") is internal bookkeeping, not something a reader of the
                case needs. The field is still in the record and still searchable. */}
            <section aria-label="Call to Action" className="bg-navy rounded-xl p-8 flex flex-col md:flex-row items-center justify-between text-white shadow-md relative overflow-hidden p-10">
              <div className="z-10 w-full md:w-2/3 pr-0 md:pr-8 mb-6 md:mb-0">
                <div className="text-xs font-bold tracking-widest text-solid-red uppercase mb-2">Discuss a similar requirement</div>
                <h3 className="text-2xl font-bold mb-3">Want to go deeper on this case?</h3>
                <p className="text-sm text-gray-300 mb-6">Scan the QR or use the SolidPro contact page to discuss a similar requirement.</p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a className="bg-solid-red hover:bg-red-600 text-white font-bold py-2.5 px-6 rounded shadow-sm transition-colors text-sm" href="https://www.solidpro-es.com/contact" target="_blank" rel="noopener noreferrer">Book a discussion</a>
                  <a className="text-xs text-gray-400 hover:text-white transition-colors" href="https://www.solidpro-es.com/contact" target="_blank" rel="noopener noreferrer">https://www.solidpro-es.com/contact</a>
                </div>
              </div>
              <div className="z-10 bg-white p-3 rounded-lg shadow-lg flex-shrink-0">
                <img alt="QR Code to contact page" className="w-24 h-24 md:w-28 md:h-28 object-contain" src="/qrcode.png" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            </section>
          </div>
        </div>
        <div className="mt-6 flex gap-4 flex-wrap">
          <button className="bg-solid-red hover:bg-red-600 text-white font-bold py-2 px-6 rounded-md shadow-sm transition-colors text-sm flex items-center" onClick={() => {
            if (user?.role !== 'admin') { notify('Admin only has access. Please contact your admin to add this to a proposal.'); return; }
            const n = addPending('cases', caseData.id);
            notify(`"${caseData.title}" added to your proposal selection (${n} case${n > 1 ? 's' : ''} pending).`);
          }}>
            <i className="fa-solid fa-plus mr-1" aria-hidden="true"></i> Add to Proposal
          </button>
          <button className="bg-white hover:bg-gray-50 text-navy font-bold py-2 px-6 rounded-md border border-gray-300 shadow-sm transition-colors text-sm flex items-center" onClick={async (e) => {
            const btn = e.currentTarget, label = btn.innerHTML;
            btn.disabled = true;
            await downloadPdf(caseBriefHTML(caseData), `${safe(caseData.caseId || caseData.id)}.pdf`, m => { btn.textContent = m || label; if (!m) btn.innerHTML = label; });
            btn.disabled = false; btn.innerHTML = label;
          }}>
            <i className="fa-solid fa-download mr-1" aria-hidden="true"></i> Download case study
          </button>
          <button className="bg-navy hover:bg-navy-light text-white font-bold py-2 px-6 rounded-md shadow-sm transition-colors text-sm flex items-center" onClick={() => window.print()}>
            <i className="fa-solid fa-print mr-1" aria-hidden="true"></i> Print / Save as PDF
          </button>
          <button className="bg-white hover:bg-gray-50 text-navy font-bold py-2 px-6 rounded-md border border-gray-300 shadow-sm transition-colors text-sm flex items-center" onClick={() => goBackOrLibrary(navigate)}>
            <i className="fa-solid fa-arrow-left mr-1" aria-hidden="true"></i> Back
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const SolutionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data, loading, error } = useLibraryData();

  // Reading progress bar + back-to-top, for the document view. Declared
  // unconditionally (before the loading/error early returns below) so the
  // hook order never shifts between renders.
  const [readProgress, setReadProgress] = useState(0);
  const [showBackTop, setShowBackTop] = useState(false);
  const [toast, setToast] = useState('');
  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrollable = h.scrollHeight - h.clientHeight;
      setReadProgress(scrollable > 0 ? Math.min(100, (h.scrollTop / scrollable) * 100) : 0);
      // Not just "scrolled enough" - also not within reach of the footer,
      // which this fixed-position button would otherwise sit on top of.
      const nearBottom = scrollable > 0 && scrollable - h.scrollTop < 150;
      setShowBackTop(h.scrollTop > 420 && !nearBottom);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <ApiError error={error} />;
  if (!data) return <div className="p-8 text-center">No data available</div>;

  const sol = data.cats.flatMap(c => c.subs.flatMap(s => s.sols)).find(s => s.id === id);
  if (!sol) return <div className="p-8 text-center">Solution not found</div>;

  const relatedCases = data.cases.filter(c => (c.sols || []).includes(id));
  const r = richOf(sol);
  // Which focus area within the category this solution belongs to, so the
  // crumb can reopen the library at exactly that level.
  const subIndex = (() => {
    const cat = (data && data.cats || []).find(c => c.id === sol.catId);
    const i = cat ? (cat.subs || []).findIndex(x => x.name === sol.sub) : -1;
    return i < 0 ? 0 : i;
  })();

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="sd-doc">
      {toast && <div className="proptoast">{toast}</div>}
      <div className="sd-progress"><div className="sd-progress-bar" style={{ width: `${readProgress}%` }} /></div>
      <button className={`sd-backtop ${showBackTop ? 'on' : ''}`} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Back to top">
        <i className="fa-solid fa-arrow-up" aria-hidden="true"></i>
      </button>
      <TopBar tab="solutions" setTab={(t) => setBrowse({ tab: t })} onSignOut={() => { logout(); navigate('/login'); }} user={user} navigate={navigate} />
      <main className="flex-grow w-full max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-8 md:py-12">
        <DetailNav
          navigate={navigate}
          trail={[
            { label: 'Solutions', go: () => goToLibrary(navigate, { tab: 'solutions', solMode: 'areas', solCat: null }) },
            { label: sol.cat, go: () => goToLibrary(navigate, { tab: 'solutions', solMode: 'cat', solCat: sol.catId, solSub: 0 }) },
            { label: sol.sub, go: () => goToLibrary(navigate, { tab: 'solutions', solMode: 'sub', solCat: sol.catId, solSub: subIndex, subView: 'sols' }) },
            { label: sol.title }
          ]}
        />

        <div className="sd-actions">
          <button className="sd-btn red" onClick={() => {
            if (user?.role !== 'admin') { notify('Admin only has access. Please contact your admin to add this to a proposal.'); return; }
            const n = addPending('solutions', sol.id);
            notify(`"${sol.title}" added to your proposal selection (${n} service${n > 1 ? 's' : ''} pending).`);
          }}>
            <i className="fa-solid fa-plus" aria-hidden="true"></i> Add to Proposal
          </button>
          <button className="sd-btn line" onClick={async (e) => {
            const btn = e.currentTarget, label = btn.innerHTML;
            btn.disabled = true;
            await downloadPdf(solutionBriefHTML(sol), `solidpro-${safe(sol.id)}.pdf`, m => { btn.textContent = m || label; if (!m) btn.innerHTML = label; });
            btn.disabled = false; btn.innerHTML = label;
          }}>
            <i className="fa-solid fa-download" aria-hidden="true"></i> Download solution brief (PDF)
          </button>
          <button className="sd-btn line" onClick={() => window.print()}>
            <i className="fa-solid fa-print" aria-hidden="true"></i> Print / Save as PDF
          </button>
          <button className="sd-btn line" onClick={() => goBackOrLibrary(navigate)}>
            <i className="fa-solid fa-arrow-left" aria-hidden="true"></i> Back
          </button>
        </div>

        <section className="sd-hero">
          <div className="sd-eyebrow">{sol.id} · {sol.cat}{sol.sub ? ` · ${sol.sub}` : ''}</div>
          <h1>{r?.hero || sol.title}</h1>
          <p className="sd-lead">{r?.intro?.[0] || sol.what}</p>
          {r?.heroQ && r.heroQ.length > 0 && <div className="sd-heroq">{r.heroQ.map((item, idx) => <div key={idx} className="sd-qbox"><b>{item.q}</b><span>{item.label}</span></div>)}</div>}
        </section>

        {r?.intro && r.intro.length > 1 && (
          <div className="sd-section">
            <div className="sd-card">{r.intro.slice(1).map((p, idx) => <p key={`intro-${idx}`}>{p}</p>)}</div>
          </div>
        )}

        {r?.useCases && r.useCases.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>This solution is useful when…</h2><p>Typical situations where this service creates value.</p></div></div>
            <div className="sd-usegrid">{r.useCases.map((item, idx) => <div key={idx} className="sd-use">{item.icon && <div className="sd-icon">{item.icon}</div>}<b>{item.title}</b><span>{item.desc}</span></div>)}</div>
          </section>
        )}

        {r?.approach && r.approach.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>How SolidPro approaches it</h2><p>{r.approachIntro || 'A clear path from source information to controlled output.'}</p></div></div>
            <div className="sd-journey">{r.approach.map((step, idx) => <div key={idx} className="sd-step"><div className="sd-num">{pad(idx + 1)}</div><b>{step.title}</b><span>{step.desc}</span></div>)}</div>
          </section>
        )}

        {(!r || !r.approach || !r.approach.length) && r?.come && r.come.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>When customers come to us</h2></div></div>
            <div className="sd-card"><ul className="bullet-list">{r.come.map((item, idx) => <li key={idx}>{item}</li>)}</ul></div>
          </section>
        )}

        {r?.helps && r.helps.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>How SolidPro helps</h2></div></div>
            <div className="sd-card">
              {r.helps.map((item, idx) => <p key={idx}>{item}</p>)}
              {r.questions && r.questions.length > 0 && <div className="sd-tags" style={{ marginTop: 8 }}>{r.questions.map((q, idx) => <span key={idx} className="sd-tag">{q}</span>)}</div>}
            </div>
          </section>
        )}

        {r?.gets && r.gets.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>What the customer receives</h2><p>Practical outputs that remain usable after the engagement.</p></div></div>
            <div className="sd-outputgrid">{r.gets.map((item, idx) => <div key={idx} className="sd-out"><div className="sd-n">{pad(idx + 1)}</div><h3>{item[0]}</h3><p>{item[1]}</p></div>)}</div>
          </section>
        )}

        {r?.businessValue && (
          <section className="sd-section">
            <div className="sd-value">
              <div>
                <div className="sd-eyebrow" style={{ color: 'var(--sd-red)' }}>Business Value</div>
                {r.businessValue.heading && <h2>{r.businessValue.heading}</h2>}
                {r.businessValue.text && <p>{r.businessValue.text}</p>}
              </div>
              {r.businessValue.risks && r.businessValue.risks.length > 0 && <div className="sd-riskgrid">{r.businessValue.risks.map((item, idx) => <div key={idx} className="sd-risk">{item}</div>)}</div>}
            </div>
          </section>
        )}

        {((r?.qa && r.qa.length > 0) || (r?.exp && r.exp.length > 0)) && (
          <section className="sd-section">
            <div className="sd-twocol">
              {r?.qa && r.qa.length > 0 && <div className="sd-card"><h3>Questions we help answer</h3><div className="sd-qa">{r.qa.map((item, idx) => <div key={idx}><b>{item[0]}</b><span>{item[1]}</span></div>)}</div></div>}
              {r?.exp && r.exp.length > 0 && <div className="sd-card"><h3>Expertise we bring</h3><div className="sd-tags">{r.exp.map((item, idx) => <span key={idx} className="sd-tag">{item}</span>)}</div></div>}
            </div>
          </section>
        )}

        {r?.diff && r.diff.length > 0 && (
          <section className="sd-section">
            <div className="sd-diff">
              <h3>Why SolidPro</h3>
              {r.diff.map((item, idx) => <p key={idx}>{item}</p>)}
              {r.quote && <div className="sd-quote">{r.quote}</div>}
              {r.journey && r.journey.length > 0 && <div className="sd-journey" style={{ marginTop: 12 }}>{r.journey.map((step, idx) => <div key={idx} className="sd-step"><b>{step}</b></div>)}</div>}
            </div>
          </section>
        )}

        {r?.scope && (r.scope.text || r.scope.heading) && (
          <section className="sd-section">
            <div className="sd-scope">
              <h3>{r.scope.heading || 'Scope boundary / validation gate'}</h3>
              <p>{r.scope.text}</p>
            </div>
          </section>
        )}

        {(r?.needFromYou || r?.notCover) && (
          <section className="sd-section">
            <div className="sd-twocol">
              {r?.needFromYou && <div className="sd-card"><h3>What we need from you</h3><p>{r.needFromYou}</p></div>}
              {r?.notCover && <div className="sd-card"><h3>What this solution does not cover</h3><p>{r.notCover}</p></div>}
            </div>
          </section>
        )}

        {!(r && r.come && r.come.length) && !(r && r.useCases && r.useCases.length) && sol.when && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>Where this helps</h2></div></div>
            <div className="sd-card"><p>{sol.when}</p></div>
          </section>
        )}

        {!(r && r.gets && r.gets.length) && sol.get && sol.get.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>What SolidPro provides</h2></div></div>
            <div className="sd-card"><ul className="bullet-list">{sol.get.map((item, idx) => <li key={idx}>{item}</li>)}</ul></div>
          </section>
        )}

        {!(r && r.exp && r.exp.length) && sol.cap && sol.cap.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>Skills, tools &amp; methods</h2></div></div>
            <div className="sd-tags">{sol.cap.map((item, idx) => <span key={idx} className="sd-tag">{item}</span>)}</div>
          </section>
        )}

        {sol.industries && sol.industries.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>Industries where this is relevant</h2></div></div>
            <div className="sd-industrygrid">{sol.industries.map((item, idx) => <span key={idx} className="sd-industry">{item}</span>)}</div>
          </section>
        )}

        {sol.std && sol.std.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>Standards &amp; frameworks</h2></div></div>
            <div className="sd-tags">{sol.std.map((item, idx) => <span key={idx} className="sd-tag std">{item}</span>)}</div>
          </section>
        )}

        {relatedCases.length > 0 && (
          <section className="sd-section">
            <div className="sd-sechead"><div><h2>Related Case Studies</h2></div></div>
            <div className="sd-casegrid">
              {relatedCases.map(c => (
                <div key={c.id} className="sd-case" onClick={() => navigate(`/case/${c.id}`)}>
                  <div className="sd-case-main">
                    <div className="sd-caseid">CASE ID: {c.caseId || c.id}</div>
                    <h3>{c.title}</h3>
                    {c.pillar && <div className="sd-profile">{c.pillar}</div>}
                    <p>{(c.result || [])[0] || c.problem}</p>
                    {c.metrics && c.metrics.length > 0 && <div className="sd-metricrow">{c.metrics.slice(0, 3).map((m, idx) => <span key={idx} className="sd-metricpill">{m}</span>)}</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="sd-section">
          <div className="sd-cta">
            <div>
              <div className="sd-eyebrow">Discuss a similar requirement</div>
              <h2>{r?.cta?.heading || 'Want to explore this solution for your business?'}</h2>
              <p>{r?.cta?.text || 'Share the product, current context, markets and required release date. SolidPro can define a focused scope around the real gap.'}</p>
              <a className="sd-btn red" href="https://www.solidpro-es.com/contact" target="_blank" rel="noopener noreferrer">{r?.cta?.button || 'Discuss This Requirement'}</a>
            </div>
            <img alt="QR Code to contact page" src="/qrcode.png" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
        </section>

        {r?.internal && (
          <section className="sd-section">
            <details className="sd-internal">
              <summary>Internal Guidance — hidden automatically in print / customer PDF</summary>
              <div className="sd-ig-inner">
                <div className="sd-ig">
                  {r.internal.bestSalesEntry && <div><b>Best Sales Entry</b><span>{r.internal.bestSalesEntry}</span></div>}
                  {r.internal.discoveryQuestion && <div><b>Discovery Question</b><span>"{r.internal.discoveryQuestion}"</span></div>}
                  {r.internal.doNotPromise && <div><b>Do Not Promise</b><span>{r.internal.doNotPromise}</span></div>}
                  {r.internal.likelyRelatedServices && <div><b>Likely Related Services</b><span>{r.internal.likelyRelatedServices}</span></div>}
                  {r.internal.caseEvidence && <div><b>Case Evidence</b><span>{r.internal.caseEvidence}</span></div>}
                  {r.internal.positioning && <div><b>Positioning</b><span>{r.internal.positioning}</span></div>}
                </div>
              </div>
            </details>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

const ProposalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchProposal(id);
        setProposal(data);
      } catch (err) {
        console.error('Error loading proposal:', err);
        setProposal(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!proposal) return (
    <div className="p-8 text-center">
      <p className="text-gray-600">Proposal not found.</p>
      <button className="bg-navy text-white font-bold px-6 py-2 rounded-lg mt-4 hover:bg-navy-light transition-colors" onClick={() => navigate('/library')}>Back to Library</button>
    </div>
  );

  return <Navigate to="/library" state={{ tab: 'proposal', propView: 'editor' }} />;
};

// ===== ADMIN PANEL =====
const Admin = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: '', password: '', role: 'user' });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/users/${editingId}`, form);
        setEditingId(null);
      } else {
        await api.post('/users', form);
      }
      setForm({ email: '', password: '', role: 'user' });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/users/${id}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({ email: u.email, role: u.role, password: '' });
  };

  return (
    <div>
      <TopBar tab="solutions" setTab={(t) => setBrowse({ tab: t })} onSignOut={() => { logout(); navigate('/login'); }} user={user} navigate={navigate} />
      <div className="p-8 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Admin Panel – User Management</h1>
        <form onSubmit={handleSubmit} className="flex flex-wrap gap-4 mb-6">
          <input className="border rounded-lg px-4 py-2 flex-1 min-w-[200px]" type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
          <input className="border rounded-lg px-4 py-2 flex-1 min-w-[150px]" type="password" placeholder="Password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editingId} />
          <select className="border rounded-lg px-4 py-2" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <button className="bg-navy text-white font-bold px-6 py-2 rounded-lg hover:bg-navy-light transition-colors" type="submit">{editingId ? 'Update' : 'Create'}</button>
          {editingId && <button className="bg-gray-300 text-gray-700 font-bold px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors" type="button" onClick={() => { setEditingId(null); setForm({ email: '', password: '', role: 'user' }); }}>Cancel</button>}
          {error && <div className="text-red-600 text-sm w-full">{error}</div>}
        </form>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-gray-200">
                  <td className="px-4 py-2">{u.id}</td>
                  <td className="px-4 py-2">{u.email}</td>
                  <td className="px-4 py-2">{u.role}</td>
                  <td className="px-4 py-2">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    <button className="bg-blue-500 text-white px-3 py-1 rounded mr-2 hover:bg-blue-600 transition-colors" onClick={() => startEdit(u)}>Edit</button>
                    <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors" onClick={() => handleDelete(u.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// ===== TOPBAR, FOOTER, LOGIN =====
const TopBar = ({ tab, setTab, onSignOut, user, navigate }) => {
  const location = useLocation();
  const [toast, setToast] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };
  const openTab = (t) => {
    setMobileMenuOpen(false);
    if (t === 'proposal' && user?.role !== 'admin') {
      notify('Admin only has access. Please contact your admin for the proposal.');
      return;
    }
    setTab(t);
    if (location.pathname !== '/library') navigate('/library');
  };
  // Every route change (a tab link, the logo, Admin Panel, back/forward) should
  // close a menu left open from the previous page - nothing here should ever
  // require a second tap to dismiss.
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);
  return (
    <header className="bg-surface-container-lowest/95 backdrop-blur-sm border-b border-outline-variant/30 sticky top-0 z-50 w-full">
      {toast && <div className="proptoast">{toast}</div>}
      <div className="flex justify-between items-center w-full px-margin-mobile  py-4 max-w-container-max mx-auto">
        <div className="flex items-center gap-4 flex-shrink-0">
          <a className="font-headline-xl text-headline-xl font-extrabold text-primary flex items-center tracking-tight group cursor-pointer" onClick={() => { setTab('solutions'); navigate('/library'); }}>
            <img className="logo-size" src="/logo.svg" alt="SolidPro" onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 60"%3E%3Crect width="200" height="60" fill="%23002147"/%3E%3Ctext x="20" y="40" font-family="Arial" font-size="28" font-weight="bold" fill="white"%3ESolidPro%3C/text%3E%3C/svg%3E'; }} />
          </a>
          <div className="hidden lg:flex flex-col border-l border-outline-variant/30 pl-4">
            <span className="font-label-uppercase text-[10px] tracking-widest text-secondary">INDUSTRIAL SOLUTIONS</span>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          {/* Each tab records where it wants to land, then goes to the library.
              On the library screen setTab also switches the view directly; on a
              detail page the recorded tab is what the library restores. */}
          <a className={`font-bold text-sm transition-colors ${tab === 'solutions' ? 'text-primary relative after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-primary-container' : 'text-on-surface-variant hover:text-primary'}`} href="#" onClick={(e) => { e.preventDefault(); openTab('solutions'); }}>Solutions</a>
          <a className={`font-bold text-sm transition-colors ${tab === 'capabilities' ? 'text-primary relative after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-primary-container' : 'text-on-surface-variant hover:text-primary'}`} href="#" onClick={(e) => { e.preventDefault(); openTab('capabilities'); }}>Capabilities</a>
          <a className={`font-bold text-sm transition-colors ${tab === 'cases' ? 'text-primary relative after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-primary-container' : 'text-on-surface-variant hover:text-primary'}`} href="#" onClick={(e) => { e.preventDefault(); openTab('cases'); }}>Case Studies</a>
          <a className={`font-bold text-sm transition-colors ${tab === 'proposal' ? 'text-primary relative after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-primary-container' : 'text-on-surface-variant hover:text-primary'}`} href="#" onClick={(e) => { e.preventDefault(); openTab('proposal'); }}>Proposals</a>
        </nav>
        <div className="flex items-center gap-4 md:gap-6">
          <div className="hidden lg:flex items-center gap-3 pr-4 border-r border-outline-variant/30">
            <span className="material-symbols-outlined text-outline text-xl">account_circle</span>
            <div className="flex flex-col">
              <span className="font-label-input text-[10px] text-outline uppercase">Logged in as</span>
              <span className="font-body-md text-xs font-bold text-on-surface-variant">{user?.email || 'User'}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="font-label-input text-label-input uppercase px-4 py-2 text-on-surface-variant hover:text-primary transition-colors" onClick={onSignOut}>Sign out</button>
            {user?.role === 'admin' && <button className="font-label-input text-label-input uppercase px-5 py-2.5 bg-primary-container text-white rounded-lg hover:bg-tertiary-container transition-all shadow-sm hidden md:block" onClick={() => navigate('/admin')}>Admin Panel</button>}
            <button
              className="md:hidden p-2 text-primary hover:bg-surface-container-low rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              <span className="material-symbols-outlined">{mobileMenuOpen ? 'close' : 'menu'}</span>
            </button>
          </div>
        </div>
      </div>
      {mobileMenuOpen && (
          <nav className="md:hidden flex flex-col border-t border-outline-variant/30 px-margin-mobile py-2">
            <a className={`font-bold text-sm py-3 border-b border-outline-variant/20 ${tab === 'solutions' ? 'text-primary' : 'text-on-surface-variant'}`} href="#" onClick={(e) => { e.preventDefault(); openTab('solutions'); }}>Solutions</a>
            <a className={`font-bold text-sm py-3 border-b border-outline-variant/20 ${tab === 'capabilities' ? 'text-primary' : 'text-on-surface-variant'}`} href="#" onClick={(e) => { e.preventDefault(); openTab('capabilities'); }}>Capabilities</a>
            <a className={`font-bold text-sm py-3 border-b border-outline-variant/20 ${tab === 'cases' ? 'text-primary' : 'text-on-surface-variant'}`} href="#" onClick={(e) => { e.preventDefault(); openTab('cases'); }}>Case Studies</a>
            <a className={`font-bold text-sm py-3 ${user?.role === 'admin' ? 'border-b border-outline-variant/20' : ''} ${tab === 'proposal' ? 'text-primary' : 'text-on-surface-variant'}`} href="#" onClick={(e) => { e.preventDefault(); openTab('proposal'); }}>Proposals</a>
            {user?.role === 'admin' && (
              <a className="font-bold text-sm py-3 text-on-surface-variant" href="#" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/admin'); }}>Admin Panel</a>
            )}
          </nav>
        )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-surface-container-low border-t border-outline-variant mt-auto">
    <div className="w-full py-8 px-margin-mobile md:px-margin-desktop flex flex-col md:flex-row justify-between items-center gap-base max-w-container-max mx-auto">
      <div className="flex items-center gap-2">
        <img className="logo-size" src="/logo.svg" alt="SolidPro" onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 60"%3E%3Crect width="200" height="60" fill="%23002147"/%3E%3Ctext x="20" y="40" font-family="Arial" font-size="28" font-weight="bold" fill="white"%3ESolidPro%3C/text%3E%3C/svg%3E'; }} />
      </div>
      <nav className="flex flex-wrap justify-center gap-6 my-4 md:my-0">
        <a className="font-body-md text-body-md text-on-surface-variant hover:text-secondary transition-all opacity-80 hover:opacity-100" href="#">Privacy Policy</a>
        <a className="font-body-md text-body-md text-on-surface-variant hover:text-secondary transition-all opacity-80 hover:opacity-100" href="#">Terms of Service</a>
        <a className="font-body-md text-body-md text-on-surface-variant hover:text-secondary transition-all opacity-80 hover:opacity-100" href="#">Contact Support</a>
        <a className="font-body-md text-body-md text-on-surface-variant hover:text-secondary transition-all opacity-80 hover:opacity-100" href="#">Security</a>
      </nav>
      <div className="font-body-md text-body-md text-on-surface-variant text-sm text-center md:text-right">© 2026 SolidPro Solutions. All rights reserved.</div>
    </div>
  </footer>
);

// Self-serve accounts stay inside the company - the backend enforces this
// independently, this is only so the form can say why before a round trip.
const SIGNUP_DOMAIN = 'solidpro-es.com';

const LoginForm = () => {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Counted from the catalogue rather than asserted. Until they arrive the
  // block is not rendered at all - an invented number is worse than no number,
  // and these are shown to customers.
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    let live = true;
    api.get('/stats')
      .then(res => { if (live) setCounts(res.data); })
      .catch(() => { /* the strip simply stays hidden */ });
    return () => { live = false; };
  }, []);

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');

    if (mode === 'signup') {
      const trimmedEmail = email.trim().toLowerCase();
      if (!trimmedEmail.endsWith('@' + SIGNUP_DOMAIN)) {
        setError(`Sign-up is only available for @${SIGNUP_DOMAIN} email addresses.`);
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === 'signup') {
        await register(email.trim(), password);
      } else {
        await login(email.trim(), password);
      }
      navigate('/library', { replace: true });
    } catch (err) {
      console.error(`${mode === 'signup' ? 'Registration' : 'Login'} error:`, err);
      const message = err?.response?.data?.error || err?.response?.data?.message || err?.message ||
        (mode === 'signup' ? 'Could not create your account. Please try again.' : 'Invalid credentials. Please try again.');
      setError(message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background relative overflow-x-hidden">
      <style>{`
        @keyframes lp-float { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(30px,-40px,0) scale(1.12); } }
        @keyframes lp-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
        .lp-blob { position: absolute; border-radius: 9999px; filter: blur(70px); pointer-events: none; animation: lp-float 14s ease-in-out infinite; }
        .lp-blob-a { width: 460px; height: 460px; top: -120px; right: -100px; background: radial-gradient(circle, rgba(56,189,248,.55), transparent 70%); }
        .lp-blob-b { width: 420px; height: 420px; bottom: -140px; left: -120px; background: radial-gradient(circle, rgba(99,102,241,.5), transparent 70%); animation-delay: -6s; }
        .lp-title { background: linear-gradient(100deg, #ffffff 30%, #7dd3fc 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .lp-glass { background: rgba(255,255,255,.07); border: 1px solid rgba(255,255,255,.16); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
        .lp-card { animation: lp-rise .6s ease-out both; border: 1px solid rgba(15,23,42,.06); box-shadow: 0 30px 60px -20px rgba(0,31,63,.35), 0 8px 20px -8px rgba(0,31,63,.15); position: relative; overflow: hidden; }
        .lp-card::before { content: ""; position: absolute; left: 0; right: 0; top: 0; height: 4px; background: linear-gradient(90deg, #001f3f, #0ea5e9, #6366f1); }
        .lp-input:focus { box-shadow: 0 0 0 4px rgba(14,165,233,.18); border-color: #0ea5e9; }
        .lp-btn { background: linear-gradient(100deg, #001f3f, #0b4a8a 60%, #0ea5e9); box-shadow: 0 10px 24px -8px rgba(14,165,233,.55); }
        .lp-btn:hover:not(:disabled) { box-shadow: 0 14px 30px -8px rgba(14,165,233,.7); filter: brightness(1.08); }
      `}</style>
      <div className="absolute inset-0 z-0 pointer-events-none opacity-20">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <defs>
            <pattern id="login-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeOpacity="0.1" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#login-grid)" />
        </svg>
      </div>
      <main className="relative z-10 min-h-screen w-full flex flex-col lg:flex-row">
        <section className="w-full lg:w-[60%] min-h-[55vh] lg:min-h-screen bg-gradient-to-br from-[#000613] via-[#001f3f] to-[#000613] relative flex flex-col justify-between p-8 md:p-12 lg:p-16 lg:pb-12 text-on-primary overflow-hidden">
          <div className="lp-blob lp-blob-a" /><div className="lp-blob lp-blob-b" />
          <div className="absolute inset-0 z-0 pointer-events-none opacity-20">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <defs>
                <pattern id="technical-grid-left-login" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" strokeOpacity="0.3" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#technical-grid-left-login)" />
            </svg>
          </div>
          <div className="relative z-10 flex flex-col gap-6 mt-10 lg:mt-0 max-w-3xl">
            <span className="font-label-uppercase text-label-uppercase text-secondary-container tracking-widest">PRIVATE SOLUTIONS LIBRARY · SOLIDPRO INTERNAL</span>
            <h1 className="font-headline-xl text-4xl md:text-6xl lg:text-7xl font-extrabold lp-title leading-[1.05]">Explore. Prove. Deliver.</h1>
            <p className="font-body-lg text-lg md:text-xl max-w-xl leading-relaxed" style={{ color: "#e2ecff" }}>Use this library during customer conversations to explain what SolidPro can do, show relevant engineering proof, and share a clean solution or case brief.</p>
          </div>
          {counts && (
            <div className="hidden lg:flex gap-10 mt-12 relative z-10 flex-wrap">
              {[[counts.solutions, 'SOLUTIONS'], [counts.cases, 'CASE STUDIES'],
                [counts.tools, 'TOOLS & PLATFORMS'], [counts.industries, 'INDUSTRIES']].map(([v, label]) => (
                <div key={label} className="flex flex-col lp-glass px-5 py-4 rounded-2xl">
                  <span className="font-headline-lg text-4xl md:text-5xl font-extrabold text-on-primary flex items-baseline">{v}<span className="text-secondary text-2xl md:text-3xl ml-1">+</span></span>
                  <span className="font-label-uppercase text-label-uppercase mt-1" style={{ color: "#b9d0f5" }}>{label}</span>
                </div>
              ))}
            </div>
          )}
          <div className="relative z-10 mt-16 lg:mt-8 text-base font-semibold tracking-wide" style={{ color: "#ffffff" }}>Engineering with purpose &middot; Delivering with happiness</div>
        </section>
        <section className="w-full lg:w-[40%] min-h-[45vh] lg:min-h-screen bg-surface-container-lowest relative flex items-center justify-center p-6 md:p-12 overflow-hidden">
          <div className="absolute inset-0 z-0 pointer-events-none opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <defs>
                <pattern id="technical-grid-right-login" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" className="text-primary" strokeWidth="0.5" strokeOpacity="0.1" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#technical-grid-right-login)" />
            </svg>
          </div>
          <div className="relative z-10 w-full max-w-md bg-white rounded-3xl lp-card p-8 md:p-10">
            <div className="flex justify-center mb-8">
              <div className="bg-surface-container-lowest inline-flex px-6 py-4 rounded-lg">
                <img className="logo-size" style={{ maxWidth: 260 }} src="/logo.svg" alt="SolidPro" onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 60"%3E%3Crect width="200" height="60" fill="%23002147"/%3E%3Ctext x="20" y="40" font-family="Arial" font-size="28" font-weight="bold" fill="white"%3ESolidPro%3C/text%3E%3C/svg%3E'; }} />
              </div>
            </div>
            <div className="mb-8 text-center">
              <h2 className="font-headline-lg text-headline-lg mb-2 text-primary">{mode === 'signup' ? 'Create your account' : 'Welcome back'}</h2>
              <p className="font-body-md text-body-md text-on-primary-container text-primary">
                {mode === 'signup' ? `Sign-up is open to @${SIGNUP_DOMAIN} email addresses only.` : 'Use your SolidPro email and password to sign in.'}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="font-label-input text-label-input text-on-surface-variant uppercase" htmlFor="login-email">Work Email</label>
                <input id="login-email" className="w-full bg-surface-bright border border-outline-variant/40 rounded-xl px-4 py-3.5 lp-input text-primary placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary transition-all" type="email" placeholder={`name@${SIGNUP_DOMAIN}`} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" disabled={loading} required />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between">
                  <label className="font-label-input text-label-input text-on-surface-variant uppercase" htmlFor="login-password">Password</label>
                  {mode === 'signin' && (
                    <Link className="font-label-input text-label-input text-primary hover:underline" to="/forgot-password">Forgot password?</Link>
                  )}
                </div>
                <input id="login-password" className="w-full bg-surface-bright border border-outline-variant/40 rounded-xl px-4 py-3.5 lp-input text-primary placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary transition-all" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} disabled={loading} required minLength={mode === 'signup' ? 8 : undefined} />
              </div>
              {mode === 'signup' && (
                <div className="flex flex-col gap-2">
                  <label className="font-label-input text-label-input text-on-surface-variant uppercase" htmlFor="login-confirm-password">Confirm Password</label>
                  <input id="login-confirm-password" className="w-full bg-surface-bright border border-outline-variant/40 rounded-xl px-4 py-3.5 lp-input text-primary placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary transition-all" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" disabled={loading} required minLength={8} />
                </div>
              )}
              {error && <div className="w-full rounded-lg border border-error/30 bg-error-container/20 px-4 py-3 text-error text-sm" role="alert">{error}</div>}
              <button type="submit" disabled={loading} className="w-full lp-btn text-white font-bold py-4 rounded-xl hover:bg-primary/90 hover:-translate-y-0.5 transition-all mt-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 shadow-lg shadow-primary/20">
                {loading ? (mode === 'signup' ? 'Creating account...' : 'Signing in...') : (mode === 'signup' ? 'Create account →' : 'Open library →')}
              </button>
            </form>
            <p className="text-center font-body-md text-body-md text-on-surface-variant mt-6">
              {mode === 'signup' ? (
                <>Already have an account?{' '}
                  <button type="button" className="text-primary font-bold hover:underline" onClick={() => switchMode('signin')} disabled={loading}>Sign in</button>
                </>
              ) : (
                <>Need an account?{' '}
                  <button type="button" className="text-primary font-bold hover:underline" onClick={() => switchMode('signup')} disabled={loading}>Sign up</button>
                </>
              )}
            </p>
            {counts && (
              <div className="flex lg:hidden flex-wrap gap-6 mt-10 justify-center text-center">
                {[[counts.solutions, 'SOLUTIONS'], [counts.cases, 'CASE STUDIES'],
                  [counts.tools, 'TOOLS & PLATFORMS'], [counts.industries, 'INDUSTRIES']].map(([v, label]) => (
                  <div key={label} className="flex flex-col w-[40%]">
                    <span className="font-headline-lg-mobile text-headline-lg-mobile text-primary font-bold flex items-baseline justify-center">{v}<span className="text-secondary ml-0.5">+</span></span>
                    <span className="font-label-uppercase text-label-uppercase text-on-surface-variant mt-1">{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

// Shared shell for the forgot/reset password screens - a single centred card
// rather than Login's two-panel hero, since there is no marketing copy to
// carry here, just one task to finish.
const AuthCard = ({ children }) => (
  <div className="min-h-screen w-full bg-background relative overflow-x-hidden flex items-center justify-center p-6">
    <div className="absolute inset-0 z-0 pointer-events-none opacity-20">
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <defs>
          <pattern id="auth-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" className="text-primary" strokeOpacity="0.1" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#auth-grid)" />
      </svg>
    </div>
    <div className="relative z-10 w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 md:p-10">
      <div className="flex justify-center mb-8">
        <div className="bg-surface-container-lowest inline-flex px-6 py-4 rounded-lg">
          <img className="logo-size" src="/logo.svg" alt="SolidPro" onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 60"%3E%3Crect width="200" height="60" fill="%23002147"/%3E%3Ctext x="20" y="40" font-family="Arial" font-size="28" font-weight="bold" fill="white"%3ESolidPro%3C/text%3E%3C/svg%3E'; }} />
        </div>
      </div>
      {children}
    </div>
  </div>
);

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail.endsWith('@' + SIGNUP_DOMAIN)) {
      setError(`Password reset is only available for @${SIGNUP_DOMAIN} email addresses.`);
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: trimmedEmail });
      setSent(true);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthCard>
        <div className="text-center">
          <span className="material-symbols-outlined text-5xl text-green-600 mb-4 inline-block">mark_email_read</span>
          <h2 className="font-headline-lg text-headline-lg mb-2 text-primary">Check your email</h2>
          <p className="font-body-md text-body-md text-on-surface-variant mb-8">
            If an account exists for <b>{email.trim()}</b>, a password reset link is on its way. The link expires in 1 hour.
          </p>
          <Link className="text-primary font-bold hover:underline" to="/login">Back to sign in</Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <div className="mb-8 text-center">
        <h2 className="font-headline-lg text-headline-lg mb-2 text-primary">Reset your password</h2>
        <p className="font-body-md text-body-md text-on-primary-container text-primary">
          Enter your @{SIGNUP_DOMAIN} email and we'll send you a reset link.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label className="font-label-input text-label-input text-on-surface-variant uppercase" htmlFor="fp-email">Work Email</label>
          <input id="fp-email" className="w-full bg-surface-bright border border-outline-variant/40 rounded-lg px-4 py-3 text-primary placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary transition-all" type="email" placeholder={`name@${SIGNUP_DOMAIN}`} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" disabled={loading} required />
        </div>
        {error && <div className="w-full rounded-lg border border-error/30 bg-error-container/20 px-4 py-3 text-error text-sm" role="alert">{error}</div>}
        <button type="submit" disabled={loading} className="w-full bg-primary text-on-primary font-bold py-4 rounded-lg hover:bg-primary/90 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 shadow-lg shadow-primary/20">
          {loading ? 'Sending...' : 'Send reset link →'}
        </button>
      </form>
      <p className="text-center font-body-md text-body-md text-on-surface-variant mt-6">
        <Link className="text-primary font-bold hover:underline" to="/login">Back to sign in</Link>
      </p>
    </AuthCard>
  );
};

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (!token) {
      setError('This reset link is missing its token. Please request a new one.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Could not reset your password. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthCard>
        <div className="text-center">
          <span className="material-symbols-outlined text-5xl text-green-600 mb-4 inline-block">check_circle</span>
          <h2 className="font-headline-lg text-headline-lg mb-2 text-primary">Password updated</h2>
          <p className="font-body-md text-body-md text-on-surface-variant mb-8">Your password has been changed. You can now sign in with it.</p>
          <button type="button" className="w-full bg-primary text-on-primary font-bold py-4 rounded-lg hover:bg-primary/90 transition-all shadow-lg shadow-primary/20" onClick={() => navigate('/login', { replace: true })}>
            Go to sign in →
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <div className="mb-8 text-center">
        <h2 className="font-headline-lg text-headline-lg mb-2 text-primary">Choose a new password</h2>
        <p className="font-body-md text-body-md text-on-primary-container text-primary">Must be at least 8 characters.</p>
      </div>
      {!token && (
        <div className="w-full rounded-lg border border-error/30 bg-error-container/20 px-4 py-3 text-error text-sm mb-6" role="alert">
          This link is missing its reset token. Request a new link from the <Link className="underline font-bold" to="/forgot-password">forgot password</Link> page.
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label className="font-label-input text-label-input text-on-surface-variant uppercase" htmlFor="rp-password">New Password</label>
          <input id="rp-password" className="w-full bg-surface-bright border border-outline-variant/40 rounded-lg px-4 py-3 text-primary placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary transition-all" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" disabled={loading || !token} required minLength={8} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-label-input text-label-input text-on-surface-variant uppercase" htmlFor="rp-confirm">Confirm Password</label>
          <input id="rp-confirm" className="w-full bg-surface-bright border border-outline-variant/40 rounded-lg px-4 py-3 text-primary placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary transition-all" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" disabled={loading || !token} required minLength={8} />
        </div>
        {error && <div className="w-full rounded-lg border border-error/30 bg-error-container/20 px-4 py-3 text-error text-sm" role="alert">{error}</div>}
        <button type="submit" disabled={loading || !token} className="w-full bg-primary text-on-primary font-bold py-4 rounded-lg hover:bg-primary/90 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 shadow-lg shadow-primary/20">
          {loading ? 'Updating...' : 'Update password →'}
        </button>
      </form>
      <p className="text-center font-body-md text-body-md text-on-surface-variant mt-6">
        <Link className="text-primary font-bold hover:underline" to="/login">Back to sign in</Link>
      </p>
    </AuthCard>
  );
};

// Set to false and rebuild to bring the sign-in form back.
const LOGIN_UNDER_DEVELOPMENT = true;

const UnderDevelopment = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #001f3f, #0b4a8a)', fontFamily: 'Inter, system-ui, sans-serif', textAlign: 'center', padding: '24px' }}>
    <div style={{ background: '#fff', borderRadius: 16, padding: '48px 40px', maxWidth: 480, width: '100%', boxShadow: '0 30px 60px -20px rgba(0,31,63,.5)' }}>
      <img src="/logo.svg" alt="SolidPro" style={{ width: 240, maxWidth: '80%', height: 'auto', display: 'block', margin: '0 auto 32px' }} />
      <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 12px', color: '#001f3f' }}>Page under development</h1>
      <p style={{ margin: 0, color: '#475569' }}>SolidPro Solutions Library is being updated. Please check back soon.</p>
    </div>
  </div>
);

const Login = () => (LOGIN_UNDER_DEVELOPMENT ? <UnderDevelopment /> : <LoginForm />);

// ===== MAIN APP =====
function App() {
  useEffect(() => {
    injectResources();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
          <Route path="/library" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/solution/:id" element={<ProtectedRoute><SolutionDetail /></ProtectedRoute>} />
          <Route path="/case/:id" element={<ProtectedRoute><CaseDetail /></ProtectedRoute>} />
          <Route path="/proposal/:id" element={<ProtectedRoute><ProposalDetail /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/library" />} />
          <Route path="*" element={<Navigate to="/library" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;